import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'

const DEFAULT_LANGUAGE = 'ar'
const DEFAULT_LANGUAGE_VERSION = '2026-06-ar-default'

export const useLanguageStore = defineStore('language', () => {
  // State
  const currentLanguage = ref(DEFAULT_LANGUAGE)
  const direction = ref('rtl')
  const isRtl = ref(true)

  // Computed getters
  const isArabic = computed(() => currentLanguage.value === 'ar')
  const isEnglish = computed(() => currentLanguage.value === 'en')
  const oppositeLanguage = computed(() => currentLanguage.value === 'en' ? 'العربية' : 'English')
  const currentLanguageLabel = computed(() => currentLanguage.value === 'en' ? 'English' : 'العربية')

  // Language options
  const languages = {
    en: {
      code: 'en',
      name: 'English',
      nativeName: 'English',
      flag: 'EN',
      direction: 'ltr'
    },
    ar: {
      code: 'ar',
      name: 'Arabic',
      nativeName: 'العربية',
      flag: 'AR',
      direction: 'rtl'
    }
  }

  // Translation function
  const translations = {
    en: {
      // English translations use keys as values (fallback to key)
    },
    ar: {
      // Common Project Elements
      'View': 'عرض',
      'Edit': 'تعديل',
      'Delete': 'حذف',
      'Cancel': 'إلغاء',
      'Save': 'حفظ',
      'Loading': 'جاري التحميل',
      'No data available': 'لا توجد بيانات متاحة',
      'My Tasks': 'مهامي',
      'connections': 'ارتباط',
      'Review Objectives': 'مراجعة الأهداف',
      'versions': 'إصدار',
      'changes': 'تغيير',
      // Audit log and plan versioning.
      'Read-only': 'للقراءة فقط',
      '{n} entries': '{n} سجل',
      'Oldest': 'الأقدم',
      'All queues': 'كل القوائم',
      'Search tasks...': 'ابحث في المهام...',
      'No tasks match these filters.': 'لا توجد مهام تطابق عوامل التصفية.',
      '{n} task(s)': '{n} مهمة',
      'No field-level detail for this entry.': 'لا توجد تفاصيل على مستوى الحقول لهذا السجل.',
      'Audit Log': 'سجل التدقيق',
      'Every create, update, delete and status change. Entries cannot be edited or removed.': 'كل إنشاء وتعديل وحذف وتغيير حالة. لا يمكن تعديل السجلات أو حذفها.',
      'Export to Excel': 'تصدير إلى إكسل',
      'Entity': 'الكيان',
      'Event': 'الحدث',
      'Record': 'السجل',
      'Date / Time': 'التاريخ / الوقت',
      'Status changed': 'تغيّرت الحالة',
      'Restored': 'تمت الاستعادة',
      'No audit entries match these filters.': 'لا توجد سجلات تطابق عوامل التصفية.',
      'Unable to load the audit log': 'تعذّر تحميل سجل التدقيق',
      'Unable to export the audit log': 'تعذّر تصدير سجل التدقيق',
      '{n} field(s)': '{n} حقل',
      'Page {a} of {b}': 'صفحة {a} من {b}',
      'Plan Versions': 'إصدارات الخطة',
      'A full snapshot each time the plan moves through the approval cycle.': 'نسخة كاملة عند كل انتقال للخطة في دورة الاعتماد.',
      'This plan needs at least two versions before they can be compared.': 'تحتاج الخطة إلى إصدارين على الأقل لإجراء المقارنة.',
      'These two versions are identical.': 'الإصداران متطابقان.',
      '{n} change(s) between v{a} and v{b}.': '{n} تغيير بين الإصدار {a} والإصدار {b}.',
      'Compare': 'قارن',
      'With': 'مع',
      'Plan Details': 'تفاصيل الخطة',
      'Objectives': 'الأهداف',
      'Added': 'أُضيف',
      'Removed': 'أُزيل',
      'Changed': 'تغيّر',
      'Unable to load the plan versions': 'تعذّر تحميل إصدارات الخطة',
      'Unable to compare these versions': 'تعذّرت مقارنة الإصدارين',
      // My Tasks — everything pending on the signed-in user.
      'Everything waiting on you, oldest first.': 'كل ما ينتظر إجراءك، الأقدم أولاً.',
      'Nothing is waiting on you right now.': 'لا يوجد ما ينتظر إجراءك حالياً.',
      'Unable to load your tasks': 'تعذّر تحميل مهامك',
      'Review and record your decision': 'راجع القرار وسجّله',
      '1 day': 'يوم واحد',
      '{n} days': '{n} يوم',
      'Strategic Plan': 'الخطة الاستراتيجية',
      'Strategic KPI': 'مؤشر الأداء الاستراتيجي',
      'KPI Measurement': 'قياس مؤشر الأداء',
      'Initiative Change Request': 'طلب تغيير المبادرة',
      'Strategic Risk': 'المخاطر الاستراتيجية',

      // Kanban View
      'Add Card': 'إضافة بطاقة',
      'Move Card': 'نقل البطاقة',
      'To Do': 'للتنفيذ',
      'In Progress': 'قيد التنفيذ',
      'Done': 'منجز',
      'Add List': 'إضافة قائمة',
      'List Actions': 'إجراءات القائمة',
      'Card Details': 'تفاصيل البطاقة',

      // Calendar View
      'Today': 'اليوم',
      'Month': 'الشهر',
      'Week': 'الأسبوع',
      'Day': 'اليوم',
      'Previous': 'السابق',
      'Next': 'التالي',
      'All Day': 'طوال اليوم',
      'Start Time': 'وقت البدء',
      'End Time': 'وقت الانتهاء',

      // Grid/List View
      'Name': 'الاسم',
      'Status': 'الحالة',
      'Progress': 'التقدم',
      'Due Date': 'تاريخ الاستحقاق',
      'Assignee': 'المكلف',
      'Created': 'تم الإنشاء',
      'Modified': 'تم التعديل',
      'Actions': 'الإجراءات',

      // Project Analytics
      'Project Analytics': 'تحليلات المشروع',
      'Overview': 'نظرة عامة',
      'Timeline': 'الجدول الزمني',
      'Resources': 'الموارد',
      'Performance': 'الأداء',
      'Metrics': 'المقاييس',
      'Reports': 'التقارير',
      'Search by name, email, phone, or role...' : 'البحث بالاسم أو البريد الإلكتروني أو الهاتف أو الدور...',

      // Project Timeline
      'Timeline View': 'عرض الجدول الزمني',
      'Zoom Level': 'مستوى التكبير',
      'Days': 'أيام',
      'Weeks': 'أسابيع',
      'Months': 'شهور',
      'Quarters': 'أرباع السنة',
      'Years': 'سنوات',

      // Project Dashboard
      'Project Dashboard': 'لوحة معلومات المشروع',
      'Summary': 'ملخص',
      'Recent Activity': 'النشاط الأخير',
      'Team Members': 'أعضاء الفريق',
      'Upcoming Deadlines': 'المواعيد النهائية القادمة',
      'Project Health': 'تقدم المشروع',
      
      // Notifications
      'Success': 'نجاح',
      'Error': 'خطأ',
      'Warning': 'تحذير',
      'Info': 'معلومات',
      'Successfully saved': 'تم الحفظ بنجاح',
      'Failed to save': 'فشل في الحفظ',
      'Please confirm': 'يرجى التأكيد',
      'Are you sure?': 'هل أنت متأكد؟',

      // Skeleton Loading translations
      'Loading project data': 'جاري تحميل بيانات المشروع',
      'Please wait': 'يرجى الانتظار',

      // Project Management Header translations
      'Project Management Console': 'لوحة إدارة المشاريع',
      'Manage and track all projects efficiently': 'إدارة وتتبع جميع المشاريع بكفاءة',
      'Search Projects': 'البحث عن المشاريع',
      'Advanced Filters': 'تصفية متقدمة',
      'Export Data': 'تصدير البيانات',
      'Create Project': 'إنشاء مشروع',
      'Export as Excel': 'تصدير كملف Excel',
      'Export as PDF': 'تصدير كملف PDF',
      'Export as CSV': 'تصدير كملف CSV',
      'Total Projects': 'إجمالي المشاريع',
      'Active Projects': 'المشاريع النشطة',
      'Completed Projects': 'المشاريع المكتملة',
      
      // Welcome Card
      'Welcome back': 'حياك الله',
      'You have no tasks remaining today': 'لديك لا توجد مهام متبقية اليوم',
      'You have 1 task remaining today': 'لديك مهمة واحدة متبقية اليوم',
      'Great! no tasks remaining today': 'رائع! لا توجد مهام متبقية اليوم',
      'Error loading dashboard data': 'خطأ في تحميل بيانات لوحة المعلومات',
      'Please refresh the page': 'يرجى تحديث الصفحة',
      'Pending Approvals': 'الموافقات المعلقة',
      'Recent Updates': 'التحديثات الأخيرة',
      
      // Profile Sidebar
      'Failed to load profile': 'فشل في تحميل الملف الشخصي',
      'Try again': 'حاول مرة أخرى',
      'Active': 'نشط',
      'Team Member': 'عضو فريق',
      'User': 'مستخدم',
      'Projects': 'المشاريع',
      'Joined': 'انضم',
      'Email': 'البريد الإلكتروني',
      'Phone': 'الهاتف',
      'Last Active': 'آخر نشاط',
      'Recently active': 'نشط مؤخراً',
      'N/A': 'غير متاح',
      
      // Summary Stats
      'TOTAL PROJECTS': 'إجمالي المشاريع',
      'ACTIVE PROJECTS': 'المشاريع النشطة',
      'AT RISK': 'في خطر',
      'DELAYED': 'متأخر',
      'All projects you have access to': 'جميع المشاريع التي لديك صلاحية الوصول إليها',
      'Projects currently marked as active': 'المشاريع المحددة حالياً كنشطة',
      'At-risk insights coming soon': 'رؤى المخاطر قريباً',
      'Delayed metrics coming soon': 'مقاييس التأخير قريباً',
      'Live metric': 'مقياس مباشر',
      'vs last period': 'مقارنة بالفترة السابقة',
      'Failed to load summary stats': 'فشل في تحميل إحصائيات الملخص',
      'Retry': 'إعادة المحاولة',
      
      // Tasks & Notifications
      'Recent Activities & Tasks': 'الأنشطة والمهام الأخيرة',
      'Today\'s Tasks': 'مهام اليوم',
      'Notifications': 'الإشعارات',
      'Latest News': 'آخر الأخبار',
      'No Tasks for Today': 'لا توجد مهام لليوم',
      'You\'re all caught up! No pending tasks at the moment.': 'أنت على اطلاع! لا توجد مهام معلقة في الوقت الحالي.',
      'No Notifications': 'لا توجد إشعارات',
      'You have no new notifications at this time.': 'ليس لديك إشعارات جديدة في الوقت الحالي.',
      'No Latest News': 'لا توجد أخبار حديثة',
      'There are no news updates available at the moment.': 'لا توجد تحديثات إخبارية متاحة في الوقت الحالي.',
      'View All Activities': 'عرض جميع الأنشطة',
      'View Task': 'عرض المهمة',
      'View Details': 'عرض التفاصيل',
      'Update Now': 'تحديث الآن',
      'Prepare': 'تحضير',
      'Overdue by {days}': 'متأخر بـ {days}',
      'Due in {days}': 'مستحق خلال {days}',
      '2 days': 'يومين',
      '1 day': 'يوم واحد',
      '3 days': '٣ أيام',
      '1 week': 'أسبوع واحد',
      'New Project Management Tool Released': 'تم إصدار أداة إدارة مشاريع جديدة',
      'Enhanced collaboration features now available for all teams': 'ميزات التعاون المحسنة متاحة الآن لجميع الفرق',
      'Q3 Performance Review Complete': 'اكتمال مراجعة أداء الربع الثالث',
      'All departments exceeded their quarterly targets': 'تجاوزت جميع الأقسام أهدافها الربعية',
      'Remote Work Policy Update': 'تحديث سياسة العمل عن بُعد',
      'New hybrid work guidelines effective next month': 'إرشادات العمل الهجين الجديدة سارية الشهر القادم',
      'Team Building Event Scheduled': 'تم جدولة فعالية بناء الفريق',
      'Annual company retreat planned for November': 'تم التخطيط لمنتجع الشركة السنوي في نوفمبر',
      '2 hours ago': 'منذ ساعتين',
      '5 hours ago': 'منذ ٥ ساعات',
      '1 day ago': 'منذ يوم واحد',
      '2 days ago': 'منذ يومين',
      '5 minutes ago': 'منذ ٥ دقائق',
      '30 minutes ago': 'منذ ٣٠ دقيقة',
      '1 hour ago': 'منذ ساعة واحدة',
      '3 hours ago': 'منذ ٣ ساعات',
      'Q4 Strategy Review': 'مراجعة استراتيجية الربع الرابع',
      'Annual strategic review and planning session': 'جلسة المراجعة الاستراتيجية والتخطيط السنوي',
      'KPI Data Update': 'تحديث بيانات مؤشرات الأداء',
      'Monthly performance indicators need updating': 'مؤشرات الأداء الشهرية تحتاج إلى تحديث',
      'Board Presentation': 'عرض مجلس الإدارة',
      'Strategic performance presentation to board': 'عرض الأداء الاستراتيجي لمجلس الإدارة',
      'Team Performance Review': 'مراجعة أداء الفريق',
      'Quarterly team assessment and feedback': 'تقييم الفريق الربعي والتغذية الراجعة',
      'Project Completed Successfully': 'تم إنجاز المشروع بنجاح',
      'New mobile app development project': 'مشروع تطوير تطبيق الهاتف الجديد',
      'Deadline Approaching': 'اقتراب الموعد النهائي',
      'Website project - 1 day remaining': 'مشروع الموقع - متبقي يوم واحد',
      'New Team Meeting': 'اجتماع فريق جديد',
      'Project review meeting - tomorrow at 2:00 PM': 'اجتماع مراجعة المشروع - غداً الساعة 2:00 مساءً',
      'Task Delayed': 'تأخر المهمة',
      'UI design task - 2 days behind schedule': 'مهمة تصميم واجهة المستخدم - متأخرة يومين',
      'New Team Member': 'عضو فريق جديد',
      'Ahmed Mohamed joined the development team': 'انضم أحمد محمد إلى فريق التطوير',
      
      // Calendar
      'Calendar': 'التقويم',
      'Greg': 'ميلادي',
      'Hijri': 'هجري',
      'Gregorian': 'ميلادي',
      'Showing Gregorian calendar': 'عرض التقويم الميلادي',
      'Showing Hijri calendar': 'عرض التقويم الهجري',
      'Sat': 'السبت',
      'Sun': 'الأحد',
      'Mon': 'الإثنين',
      'Tue': 'الثلاثاء',
      'Wed': 'الأربعاء',
      'Thu': 'الخميس',
      'Fri': 'الجمعة',
      // Gregorian months
      'January': 'يناير',
      'February': 'فبراير',
      'March': 'مارس',
      'April': 'أبريل',
      'May': 'مايو',
      'June': 'يونيو',
      'July': 'يوليو',
      'August': 'أغسطس',
      'September': 'سبتمبر',
      'October': 'أكتوبر',
      'November': 'نوفمبر',
      'December': 'ديسمبر',
      
      // Common
      'Loading...': 'جاري التحميل...',
      'Error occurred': 'حدث خطأ',
      'Search': 'بحث',
      'Add': 'إضافة',
      'Remove': 'إزالة',
      
      // Navigation
      'Dashboard': 'لوحة المعلومات',
      'Tasks': 'المهام',
      'Chat': 'المحادثة',
      'Documents': 'المستندات',
      'Users': 'المستخدمين',
      'Settings': 'الإعدادات',

      // Settings Panel
      'Primary': 'اللون الأساسي',
      'Presets': 'القوالب',
      'Color Scheme': 'نظام الألوان',
      'Language': 'اللغة',
      'Menu Type': 'نوع القائمة',
      'Static': 'ثابت',
      'Overlay': 'تراكب',
      'Slim': 'نحيف',
      'Compact': 'مدمج',
      'Reveal': 'كشف',
      'Drawer': 'درج',
      'Horizontal': 'أفقي',
      
      // Auth
      'Login': 'تسجيل الدخول',
      'Logout': 'تسجيل الخروج',
      'Register': 'إنشاء حساب',
      'Forgot Password': 'نسيت كلمة المرور',
      'Reset Password': 'إعادة تعيين كلمة المرور',
      'Password': 'كلمة المرور',
      'Confirm Password': 'تأكيد كلمة المرور',

      // Profile Settings
      'Profile Settings': 'إعدادات الملف الشخصي',
      'Manage your personal information and account settings': 'إدارة معلوماتك الشخصية وإعدادات الحساب',
      'Personal Information': 'المعلومات الشخصية',
      'Update your personal details below': 'قم بتحديث بياناتك الشخصية أدناه',
      'First Name': 'الاسم الأول',
      'Last Name': 'اسم العائلة',
      'Username': 'اسم المستخدم',
      'Enter first name': 'أدخل الاسم الأول',
      'Enter last name': 'أدخل اسم العائلة',
      'Enter username': 'أدخل اسم المستخدم',
      'Enter email address': 'أدخل البريد الإلكتروني',
      'Enter phone number': 'أدخل رقم الهاتف',
      'Save Changes': 'حفظ التغييرات',
      'Change Password': 'تغيير كلمة المرور',
      'Ensure your account stays secure by updating your password': 'تأكد من أمان حسابك عن طريق تحديث كلمة المرور',
      'Current Password': 'كلمة المرور الحالية',
      'New Password': 'كلمة المرور الجديدة',
      'Confirm New Password': 'تأكيد كلمة المرور الجديدة',
      'Enter current password': 'أدخل كلمة المرور الحالية',
      'Enter new password': 'أدخل كلمة المرور الجديدة',
      'Confirm new password': 'تأكيد كلمة المرور الجديدة',
      'Update Password': 'تحديث كلمة المرور',
      'Upload': 'رفع',
      'JPG, PNG, GIF or WebP. Max 2MB.': 'JPG، PNG، GIF أو WebP. الحد الأقصى 2 ميغابايت.',
      'No username': 'لا يوجد اسم مستخدم',
      'No phone': 'لا يوجد رقم هاتف',
      'Last active:': 'آخر نشاط:',
      'Profile Updated': 'تم تحديث الملف الشخصي',
      'Your profile has been updated successfully.': 'تم تحديث ملفك الشخصي بنجاح.',
      'Update Failed': 'فشل التحديث',
      'Failed to update profile. Please try again.': 'فشل في تحديث الملف الشخصي. يرجى المحاولة مرة أخرى.',
      'Password Changed': 'تم تغيير كلمة المرور',
      'Your password has been changed successfully.': 'تم تغيير كلمة المرور بنجاح.',
      'Failed to change password. Please try again.': 'فشل في تغيير كلمة المرور. يرجى المحاولة مرة أخرى.',
      'Avatar Updated': 'تم تحديث الصورة الشخصية',
      'Your profile picture has been updated.': 'تم تحديث صورتك الشخصية.',
      'Avatar Removed': 'تم إزالة الصورة الشخصية',
      'Your profile picture has been removed.': 'تم إزالة صورتك الشخصية.',
      'Upload Failed': 'فشل الرفع',
      'Remove Failed': 'فشل الإزالة',
      'Failed to upload avatar.': 'فشل في رفع الصورة الشخصية.',
      'Failed to remove avatar.': 'فشل في إزالة الصورة الشخصية.',
      'Invalid File': 'ملف غير صالح',
      'Please select a valid image file (JPG, PNG, GIF or WebP).': 'يرجى اختيار ملف صورة صالح (JPG، PNG، GIF أو WebP).',
      'File Too Large': 'حجم الملف كبير جداً',
      'Image size must be less than 2MB.': 'يجب أن يكون حجم الصورة أقل من 2 ميغابايت.',
      'Adjust Profile Picture': 'تعديل الصورة الشخصية',
      'Zoom In': 'تكبير',
      'Zoom Out': 'تصغير',
      'Rotate Left': 'تدوير لليسار',
      'Rotate Right': 'تدوير لليمين',
      'Flip Horizontal': 'قلب أفقي',
      'Flip Vertical': 'قلب رأسي',
      'Reset': 'إعادة تعيين',
      'Save': 'حفظ',
      'First name is required': 'الاسم الأول مطلوب',
      'Last name is required': 'اسم العائلة مطلوب',
      'Email is required': 'البريد الإلكتروني مطلوب',
      'Please enter a valid email address': 'يرجى إدخال بريد إلكتروني صالح',
      'Current password is required': 'كلمة المرور الحالية مطلوبة',
      'New password is required': 'كلمة المرور الجديدة مطلوبة',
      'Password must be at least 8 characters': 'يجب أن تكون كلمة المرور 8 أحرف على الأقل',
      'Please confirm your new password': 'يرجى تأكيد كلمة المرور الجديدة',
      'Passwords do not match': 'كلمات المرور غير متطابقة',
      'Current password is incorrect': 'كلمة المرور الحالية غير صحيحة',

      // Analytics Dashboard
      'Analytics Dashboard': 'لوحة التحليلات',
      'Monitor your organizational performance': 'راقب أداء مؤسستك',
      'Select Date Range': 'اختر نطاق التاريخ',
      'Export Report': 'تصدير التقرير',
      'PDF Report': 'تقرير PDF',
      'Excel Spreadsheet': 'جدول بيانات Excel',
      'CSV Data': 'بيانات CSV',
      
      // Dashboard Tabs
      'Programs': 'البرامج',
      'Portfolios': 'المحافظ',
      
      // Summary Card Titles
      'ACTIVE PROGRAMS': 'البرامج النشطة',
      'STRATEGIC GOALS': 'الأهداف الاستراتيجية',
      'PROGRAM BUDGET': 'ميزانية البرنامج',
      'TEAM MEMBERS': 'أعضاء الفريق',
      'ACTIVE PORTFOLIOS': 'المحافظ النشطة',
      'TOTAL INVESTMENT': 'إجمالي الاستثمار',
      'ROI': 'العائد على الاستثمار',
      'STRATEGIC ALIGNMENT': 'التوافق الاستراتيجي',
      
      // Status Names
      'On Hold': 'معلق',
      'Active': 'نشط',
      'Under Review': 'قيد المراجعة',
      
      // Performance Chart
      'Performance Trends': 'اتجاهات الأداء',
      'Track progress over time': 'تتبع التقدم بمرور الوقت',
      'Monthly': 'شهري',
      'Weekly': 'أسبوعي',
      'Quarterly': 'ربع سنوي',
      'Planned': 'المخطط',
      'Budget': 'الميزانية',
      
      // Status Overview
      'Status Overview': 'نظرة عامة على الحالة',
      'Current distribution': 'التوزيع الحالي',
      
      // Risk Analysis
      'Risk Analysis': 'تحليل المخاطر',
      'Risk distribution by category': 'توزيع المخاطر حسب الفئة',
      'High Risk': 'مخاطر عالية',
      'Medium Risk': 'مخاطر متوسطة',
      'Low Risk': 'مخاطر منخفضة',
      'High': 'عالي',
      'Medium': 'متوسط',
      'Low': 'منخفض',
      
      // Risk Info Dialog
      'Risk Analysis Information': 'معلومات تحليل المخاطر',
      'Risk Categories': 'فئات المخاطر',
      'Financial constraints and cost overruns': 'القيود المالية وتجاوز التكاليف',
      'Schedule delays and milestone risks': 'تأخيرات الجدول الزمني ومخاطر المراحل',
      'Staffing and capacity issues': 'مشاكل التوظيف والقدرة الاستيعابية',
      'Standards and deliverable concerns': 'مخاوف المعايير والمخرجات',
      'Quality': 'الجودة',
      'Got it': 'فهمت',
      
      // Data Table
      'Details': 'التفاصيل',
      'Detailed view of current': 'عرض تفصيلي لـ',
      'projects': 'المشاريع',
      'programs': 'البرامج',
      'portfolios': 'المحافظ',
      
      // Table Headers
      'Progress': 'التقدم',
      
      // Table Data Items
      'Website Redesign': 'إعادة تصميم الموقع',
      'Mobile App': 'تطبيق الجوال',
      'Data Migration': 'ترحيل البيانات',
      'Security Audit': 'تدقيق الأمان',
      'API Integration': 'تكامل API',
      'Digital Transformation': 'التحول الرقمي',
      'Customer Experience': 'تجربة العميل',
      'Operational Excellence': 'التميز التشغيلي',
      'Technology Portfolio': 'محفظة التكنولوجيا',
      'Innovation Portfolio': 'محفظة الابتكار',
      'Infrastructure Portfolio': 'محفظة البنية التحتية',
      
      // Settings Dialog
      'Dashboard Settings': 'إعدادات لوحة المعلومات',
      'Refresh Interval': 'فترة التحديث',
      'Select refresh rate': 'اختر معدل التحديث',
      'Every 30 seconds': 'كل 30 ثانية',
      'Every minute': 'كل دقيقة',
      'Every 5 minutes': 'كل 5 دقائق',
      'Every 15 minutes': 'كل 15 دقيقة',
      'Theme': 'المظهر',
      'Light': 'فاتح',
      'Dark': 'داكن',
      'Email alerts': 'تنبيهات البريد الإلكتروني',
      'Push notifications': 'الإشعارات الفورية',
      'Save Settings': 'حفظ الإعدادات',
      
      // No Access State
      'No Dashboards Available': 'لا توجد لوحات معلومات متاحة',
      'You don\'t have access to any dashboards at your current access level. Please contact your administrator to request appropriate permissions.': 'ليس لديك صلاحية الوصول إلى أي لوحة معلومات بمستوى صلاحيتك الحالي. يرجى التواصل مع المسؤول لطلب الصلاحيات المناسبة.',
      'Request Access': 'طلب صلاحية',
      
      // Toast Messages
      'Export Started': 'بدأ التصدير',
      'Data Refreshed': 'تم تحديث البيانات',
      'Dashboard data has been updated': 'تم تحديث بيانات لوحة المعلومات',
      'Settings Saved': 'تم حفظ الإعدادات',
      'Your preferences have been updated': 'تم تحديث تفضيلاتك',
      'View Details': 'عرض التفاصيل',
      'Edit Item': 'تعديل العنصر',
      'Confirm Delete': 'تأكيد الحذف',
      'Are you sure you want to delete': 'هل أنت متأكد من حذف',
      'Deleted Successfully': 'تم الحذف بنجاح',
      'has been deleted': 'تم حذفه',
      'Stat Details': 'تفاصيل الإحصائيات',
      'Viewing details for': 'عرض تفاصيل',
      'View Mode Changed': 'تم تغيير وضع العرض',
      'Chart view changed to': 'تم تغيير عرض الرسم البياني إلى',
      'Status Filter': 'تصفية الحالة',
      'Filtering by': 'تصفية حسب',
      'Opening details for': 'فتح تفاصيل',
      'Editing': 'تعديل',
      'Generating': 'جاري إنشاء',
      'Generating...': 'جاري الإنشاء...',
      'report': 'تقرير',

      // Login experience
      'Toggle theme': 'تبديل المظهر',
      'Al Istedamah Holding': 'الاستدامة القابضة',
      'Secure access': 'دخول آمن',
      'Sign in to continue to Al Istedamah Holding': 'سجّل الدخول للمتابعة إلى الاستدامة القابضة',
      'Protected government healthcare environment': 'بيئة رعاية صحية حكومية محمية',
      'Strategic investment, built for lasting impact since 2017': 'استثمار استراتيجي، لتأثير دائم منذ عام 2017',
      'One connected platform': 'منصة مترابطة واحدة',
      'Clear visibility. Better decisions.': 'رؤية أوضح. قرارات أفضل.',
      'Track strategy, portfolios, programs, projects and performance from one secure workspace.': 'تابع الاستراتيجية والمحافظ والبرامج والمشاريع والأداء من مساحة عمل آمنة واحدة.',
      'Secure by design': 'آمنة في جوهرها',
      'Role-based access and protected authentication': 'وصول حسب الصلاحيات ومصادقة محمية',

      // DashboardDataTable
      'Details': 'التفاصيل',
      'Detailed view of current': 'عرض تفصيلي لـ',

      'View All Projects': 'عرض جميع المشاريع',
      'View All Members': 'عرض جميع الأعضاء',
      'Completion Rate': 'معدل الإنجاز',
      'Operational insights': 'المؤشرات التشغيلية',
      'Executive PMO control center': 'مركز التحكم التنفيذي لمكتب إدارة المشاريع',
      'Executive PMO': 'مكتب إدارة المشاريع التنفيذي',
      'Enterprise delivery control center': 'مركز التحكم في التسليم المؤسسي',
      'Database live': 'بيانات مباشرة',
      'Cross-domain coverage': 'تغطية المجالات المؤسسية',
      'Enterprise Scope': 'نطاق المؤسسة',
      'Programs, projects and strategy': 'البرامج والمشاريع والاستراتيجية',
      'Portfolio Overview': 'نظرة عامة على المحفظة',
      'actual progress': 'التقدم الفعلي',
      'schedule elapsed': 'المدة المنقضية من الجدول',
      'Actual progress': 'التقدم الفعلي',
      'Schedule elapsed': 'المدة المنقضية من الجدول',
      'Total recorded': 'الإجمالي المسجل',
      'Relative scale': 'النسبة المقارنة',
      'Portfolio metric': 'مؤشر المحفظة',
      'Selected metric': 'المؤشر المحدد',
      'Project timeline': 'الجدول الزمني للمشروع',
      'Deadline details': 'تفاصيل الموعد النهائي',
      'Remaining': 'المتبقي',
      'KPIs at or above target': 'المؤشرات المحققة أو المتجاوزة للمستهدف',
      'Project Delivery Pipeline': 'مسار تسليم المشاريع',
      'Strategic Execution': 'التنفيذ الاستراتيجي',
      'KPI achievement': 'تحقيق مؤشرات الأداء',
      'Initiative alignment': 'مواءمة المبادرات',
      'Attention Required': 'يتطلب الانتباه',
      'Overdue projects': 'المشاريع المتأخرة',
      'Due in 30 days': 'مستحقة خلال 30 يوماً',
      'Pending decisions': 'القرارات المعلقة',
      'On hold': 'متوقف مؤقتاً',
      'Strategic risks': 'المخاطر الاستراتيجية',
      'Open corrective plans': 'الخطط التصحيحية المفتوحة',
      'Not available for your access': 'غير متاح حسب صلاحياتك',
      'active': 'نشط',
      'Portfolio movement': 'حركة المحفظة',
      'Current portfolio values': 'قيم المحفظة الحالية',
      'Current schedule': 'الجدول الحالي',
      'elapsed': 'منقضٍ',
      'Not scheduled': 'غير مجدول',
      'No scheduled project': 'لا يوجد مشروع مجدول',
      'Nearest deadline': 'أقرب موعد نهائي',
      'Project Days Left': 'الأيام المتبقية للمشروع',
      'No scheduled deadline': 'لا يوجد موعد نهائي مجدول',
      'Day remaining': 'يوم متبقٍ',
      'Days remaining': 'أيام متبقية',
      'Delivery performance': 'أداء التسليم',
      'Success Rate': 'معدل النجاح',
      'Overall success': 'النجاح الإجمالي',
      'Live': 'مباشر',
      'Joined': 'تاريخ الانضمام ',
      'ON TRACK': 'على المسار الصحيح',
      'Today Tasks': 'مهام اليوم',
      'Project Type': 'نوع المشروع',
      'Project Status': 'حالة المشروع',
      'Project Description': 'وصف المشروع',
      'Start Date': 'تاريخ البدء',
      'End Date': 'تاريخ الانتهاء',
      'Project Owner': 'مالك المشروع',
      'Sponsoring Department': 'الراعي',
      'Priority': 'الأولوية',
      'Initial Budget': 'الميزانية الأولية',
      'Enter initial budget': 'أدخل الميزانية الأولية',
      'Enter project name...': 'أدخل اسم المشروع...',
      'Edit Project': 'تعديل المشروع',
      'Delete Project': 'حذف المشروع',
      'Due:': 'الموعد النهائي:',
      'Risk': 'المخاطر',
      'Project Management': 'إدارة المشاريع',
      'Manage and monitor all project activities': 'أدر وراقب جميع انشطة مشاريعك',
      'Planning': 'تخطيط',
      'Completed': 'مكتمل',
      'Project Navigation': 'التنقل في المشروع',
      'Reset': 'إعادة تعيين',
      'Apply': 'تطبيق',
      'Gates': ' البوابات المرحلية',
      'Schedule': 'الجدول الزمني',
      'Stakeholders Register ': 'سجل أصحاب المصلحة',
      'Risks Register': 'سجل المخاطر',
      'Issues Register': 'سجل التحديات',
      'Changes Log': 'سجل التغييرات',
      'Financial': 'المالية',
      'Escalations': 'التصعيد',
      'Benefits': 'المنافع',
      'Lessons Learned': 'الدروس المستفادة',
      'Benefits Realization': 'تحقيق المنافع',
      'Track and measure project benefits': 'تتبع وقياس منافع المشروع',
      'Add Benefit': 'إضافة منفعة',
      'Project Benefits': 'منافع المشروع',
      'Add First Benefit': 'إضافة أول منفعة',
      'No benefits yet': 'لا توجد منافع بعد',
      'Start by adding your first project benefit': 'ابدأ بإضافة أول منفعة للمشروع',
      '{count} benefits': '{count} منفعة',
      'Benefit Title': 'عنوان المنفعة',
      'Category': 'الفئة',
      'Target Value': 'القيمة المستهدفة',
      'Target Realization': 'موعد التحقيق',
      'Description': 'الوصف',
      'Measurement Criteria': 'معايير القياس',
      'Edit Project Benefit': 'تعديل منفعة المشروع',
      'Add Project Benefit': 'إضافة منفعة للمشروع',
      'Enter benefit title': 'أدخل عنوان المنفعة',
      'Select category': 'اختر الفئة',
      'Select status': 'اختر الحالة',
      'e.g., 50,000, 25%, 100 users': 'مثال: 50,000، 25%، 100 مستخدم',
      'Select target date': 'اختر تاريخ الهدف',
      'Describe the benefit and how it will be measured': 'صف المنفعة وكيفية قياسها',
      'Define how this benefit will be measured and tracked': 'حدد كيفية قياس وتتبع هذه المنفعة',
      'Update Benefit': 'تحديث المنفعة',
      'Benefit Details': 'تفاصيل المنفعة',
      'Edit Benefit': 'تعديل المنفعة',
      'Close': 'إغلاق',
      'Delete Benefit': 'حذف المنفعة',
      'More Actions': 'المزيد من الإجراءات',
      'Export to CSV': 'تصدير إلى CSV',
      'Table Settings': 'إعدادات الجدول',
      'Refresh': 'تحديث',
      'Column Visibility': 'ظهور الأعمدة',
      'Table Behavior': 'سلوك الجدول',
      'Layout Settings': 'إعدادات التخطيط',
      'Reset to Default': 'إعادة للإعدادات الافتراضية',
      'Apply Settings': 'تطبيق الإعدادات',

      // Change Request
      'Change Request': 'طلبات التغيير',
      'Track and manage project changes': 'تتبع وإدارة تغييرات المشروع',
      'Add Change': 'إضافة تغيير',
      'Project Changes': 'تغييرات المشروع',
      'Add First Change': 'إضافة أول تغيير',
      'No changes yet': 'لا توجد تغييرات بعد',
      'Start by adding your first project change': 'ابدأ بإضافة أول تغيير للمشروع',
      '{count} changes': '{count} تغيير',
      'Change Title': 'عنوان التغيير',
      'Type': 'النوع',
      'Impact Level': 'مستوى التأثير',
      'Requested By': 'مقدم الطلب',
      'Request Date': 'تاريخ الطلب',
      'Implementation Date': 'تاريخ التنفيذ',
      'Cost Impact': 'التأثير المالي',
      'Schedule Impact': 'التأثير على الجدول',
      'Justification': 'المبررات',
      'Edit Project Change': 'تعديل التغيير',
      'Add Project Change': 'إضافة تغيير للمشروع',
      'Enter change title': 'أدخل عنوان التغيير',
      'Select type': 'اختر النوع',
      'Select impact level': 'اختر مستوى التأثير',
      'Enter requestor name': 'أدخل اسم مقدم الطلب',
      'Select request date': 'اختر تاريخ الطلب',
      'Select implementation date': 'اختر تاريخ التنفيذ',
      'Enter cost impact': 'أدخل التأثير المالي',
      'Enter schedule impact': 'أدخل التأثير على الجدول',
      'Describe the change and its impacts': 'صف التغيير وتأثيراته',
      'Provide justification for the change': 'قدم مبررات التغيير',
      'Update Change': 'تحديث التغيير',
      'Change Details': 'تفاصيل التغيير',
      'Edit Change': 'تعديل التغيير',
      'Delete Change': 'حذف التغيير',
      'Scope': 'النطاق',
      'Cost': 'التكلفة',
      'Resource': 'الموارد',
      'Technical': 'تقني',
      'Quality': 'الجودة',
      'Documentation': 'التوثيق',
      'Other': 'أخرى',
      'High': 'مرتفع',
      'Medium': 'متوسط',
      'Low': 'منخفض',
      'Pending': 'قيد الانتظار',
      'Under Review': 'قيد المراجعة',
      'Approved': 'معتمد',
      'Rejected': 'مرفوض',
      'Implemented': 'منفذ',
      'Closed': 'مغلق',

      // Project Charter
      'Project Charter': 'ميثاق المشروع',
      'Initiation Phase Documents': 'وثائق مرحلة البدء',
      'Required documents for project initiation': 'الوثائق المطلوبة لبدء المشروع',
      'Search documents...': 'البحث في الوثائق...',
      'All Categories': 'جميع الفئات',
      'All File Types': 'جميع أنواع الملفات',
      'Filter': 'تصفية',
      'Mandatory Only': 'الوثائق الإلزامية فقط',
      'Clear Filter': 'مسح التصفية',
      'Toggle Filters': 'تبديل عوامل التصفية',
      'Send for Approval': 'إرسال للموافقة',
      'Approve all uploaded documents': 'الموافقة على جميع الوثائق المرفوعة',
      'Download': 'تنزيل',
      'Replace': 'استبدال',
      'Upload': 'رفع',
      'Browse Files': 'استعراض الملفات',
      'Choose a file or drag & drop': 'اختر ملفاً أو اسحب وأفلت',
      'Upload New Charter': 'رفع ميثاق جديد',
      'Charter Name': 'اسم الميثاق',
      'Enter charter name': 'أدخل اسم الميثاق',
      'Upload Charter': 'رفع الميثاق',
      'Charter Preview': 'معاينة الميثاق',
      'File Size': 'حجم الملف',
      'Upload Date': 'تاريخ الرفع',
      'Downloads': 'التنزيلات',
      'Pending Approval': 'في انتظار الموافقة',
      'Pending Upload': 'في انتظار الرفع',
      'No description provided': 'لم يتم تقديم وصف',
      'Scope Documents': 'وثائق النطاق',
      'Deliverables': 'المخرجات',
      'Project Plans': 'خطط المشروع',
      'Risk Assessment': 'تقييم المخاطر',
      'Procurement': 'المشتريات',
      'Stakeholder Analysis': 'تحليل أصحاب المصلحة',
      'Business Case': 'دراسة الجدوى',
      'Templates': 'القوالب',
      'Processes': 'العمليات',
      'Guidelines': 'الإرشادات',
      'Approvals': 'الموافقات',
      'Standards': 'المعايير',
      'Public': 'عام',
      'Internal': 'داخلي',
      'Restricted': 'مقيد',
      'Confidential': 'سري',
      'PDF': 'PDF',
      'Word Document': 'مستند Word',
      'Excel Spreadsheet': 'جدول Excel',
      'PowerPoint': 'PowerPoint',
      'Text File': 'ملف نصي',
      'Archive': 'ملف مضغوط',
      
      // Additional Charter translations
      'Choose files or drag & drop': 'اختر الملفات أو اسحب وأفلت',
      'PDF, Word, Excel, PowerPoint, or TXT files (Max 10MB)': 'ملفات PDF أو Word أو Excel أو PowerPoint أو TXT (بحد أقصى 10 ميجابايت)',
      'file': 'ملف',
      'files': 'ملفات',
      'selected': 'محدد',
      'Clear All': 'مسح الكل',
      'Uploading...': 'جارٍ الرفع...',
      'Upload Document': 'رفع المستند',
      'Document': 'مستند',
      'Uploaded': 'تم الرفع',
      'Not Uploaded': 'لم يتم الرفع',
      'Mandatory': 'إلزامي',
      'Add More': 'إضافة المزيد',
      'Uploaded Files:': 'الملفات المرفوعة:',
      'No documents uploaded yet': 'لم يتم رفع مستندات بعد',
      'Waiting for Approval': 'في انتظار الموافقة',
      'No Documents Submitted': 'لا توجد مستندات مقدمة',
      'No documents have been uploaded by the project manager yet.': 'لم يتم رفع أي مستندات من قبل مدير المشروع بعد.',
      'Description': 'الوصف',
      'Brief description of the charter': 'وصف موجز للميثاق',
      'Tags': 'الوسوم',
      'Enter tags separated by commas': 'أدخل الوسوم مفصولة بفواصل',
      'Access Level': 'مستوى الوصول',
      'Select access level': 'اختر مستوى الوصول',
      'Close': 'إغلاق',
      'Grid': 'شبكة',
      'List': 'قائمة',
      
      // Initiation Document Types
      'Formal document that authorizes the project and provides the project manager with authority': 'وثيقة رسمية تأذن بالمشروع وتمنح مدير المشروع الصلاحية',
      'Feasibility Study': 'دراسة الجدوى',
      'Analysis of the viability of an idea with emphasis on potential costs and benefits': 'تحليل جدوى الفكرة مع التركيز على التكاليف والفوائد المحتملة',
      'Stakeholder Register': 'سجل أصحاب المصلحة',
      'Document containing information about project stakeholders': 'وثيقة تحتوي على معلومات حول أصحاب مصلحة المشروع',
      'Initial Communication Plan': 'خطة الاتصال الأولية',
      'Plan outlining communication strategies for project stakeholders': 'خطة تحدد استراتيجيات التواصل مع أصحاب مصلحة المشروع',
      'Charter Documents': 'وثائق الميثاق',
      'Feasibility': 'الجدوى',
      'Stakeholder Management': 'إدارة أصحاب المصلحة',
      'Communication': 'الاتصالات',
      
      // Toast Messages for Charter
      'Warning': 'تحذير',
      'Please upload mandatory documents before submitting for approval': 'يرجى رفع المستندات الإلزامية قبل إرسالها للموافقة',
      'Success': 'نجح',
      'Initiation phase submitted for approval': 'تم إرسال مرحلة البدء للموافقة',
      'Error': 'خطأ',
      'Failed to submit phase for approval': 'فشل في إرسال المرحلة للموافقة',
      'Document deleted successfully': 'تم حذف المستند بنجاح',
      'Failed to delete document': 'فشل في حذف المستند',
      'Document uploaded successfully': 'تم رفع المستند بنجاح',
      'Failed to upload document': 'فشل في رفع المستند',
      'Upload Failed': 'فشل الرفع',
      'Submit Phase for Approval': 'إرسال المرحلة للموافقة',
      
      // File Size Units
      'Bytes': 'بايت',
      'KB': 'كيلوبايت',
      'MB': 'ميجابايت',
      'GB': 'جيجابايت',
      "Pending Approval": "في انتظار الموافقة",

      // Project Closure
      'Closure Phase Activities': 'أنشطة مرحلة الإغلاق',
      'Project closure activities and final documentation': 'أنشطة إغلاق المشروع والوثائق النهائية',
      'Search closure items...': 'البحث في عناصر الإغلاق...',
      'Final Only': 'النهائية فقط',
      'Approve all completed activities': 'الموافقة على جميع الأنشطة المكتملة',
      'Upload New Activity': 'رفع نشاط جديد',
      'Activity Name': 'اسم النشاط',
      'Enter activity name': 'أدخل اسم النشاط',
      'Brief description of the activity': 'وصف موجز للنشاط',
      'Activity Preview': 'معاينة النشاط',
      'Upload Activity': 'رفع النشاط',
      'Not Started': 'لم يبدأ',
      'Due': 'تاريخ الاستحقاق',
      'Final Deliverables': 'التسليمات النهائية',
      'Project Archival': 'أرشفة المشروع',
      'Team Release': 'تسريح الفريق',
      'Administrative Closure': 'الإغلاق الإداري',
      'Project Review': 'مراجعة المشروع',
      'Knowledge Transfer': 'نقل المعرفة',
      'Financial Closure': 'الإغلاق المالي',
      'Quality Assurance': 'ضمان الجودة',
      'Risk Closure': 'إغلاق المخاطر',
      'Stakeholder Sign-off': 'موافقة أصحاب المصلحة',
      'Critical': 'حرج',

      // Project Details
      'Project Manager Information': 'معلومات مدير المشروع',
      'Last published': 'آخر تحديث',
      'Days ago': 'أيام مضت',
      'Project Manager': 'مدير المشروع',
      'Not Assigned': 'غير معين',
      'Detailed Project Information': 'معلومات المشروع التفصيلية',
      'Project Name': 'اسم المشروع',
      'Department': 'القسم',
      'Project Dates': 'تواريخ المشروع',
      'Start': 'البداية',
      'End': 'النهاية',
      'Progress Status': 'حالة التقدم',
      'Complete': 'مكتمل',
      'Team Size': 'حجم الفريق',
      'Members': 'أعضاء',
      'Days Remaining': 'الأيام المتبقية',
      'Budget Spent': 'الميزانية المنفقة',
      'Not set': 'غير محدد',
      'Due Today': 'مستحق اليوم',
      'Overdue': 'متأخر',
      'On Hold': 'معلق',
      'Cancelled': 'ملغي',
      'Project Info': 'معلومات المشروع',
      'Failed to load project details': 'فشل تحميل تفاصيل المشروع',
      'Sponsor': 'الراعي',
      'Created At': 'تاريخ الإنشاء',
      'Not Set': 'غير محدد',

      // Document Management
      'Document Management': 'إدارة المستندات',
      'Manage project documents and deliverables': 'إدارة مستندات ومخرجات المشروع',
      'Upload Document': 'رفع مستند',
      'Project Documents': 'مستندات المشروع',
      '{count} documents': '{count} مستند',
      'No documents found': 'لم يتم العثور على مستندات',
      'Start by uploading your first project document': 'ابدأ برفع أول مستند للمشروع',
      'Document Name': 'اسم المستند',
      'Enter document name': 'أدخل اسم المستند',
      'Enter document description': 'أدخل وصف المستند',
      'Select Document File': 'اختر ملف المستند',
      'Click to upload or drag and drop': 'انقر للرفع أو اسحب وأفلت',
      'Ready to upload': 'جاهز للرفع',
      'Document Type': 'نوع المستند',
      'Missing Information': 'معلومات ناقصة',
      'Please fill in all required fields and select a file': 'يرجى تعبئة جميع الحقول المطلوبة واختيار ملف',
      'File Too Large': 'الملف كبير جداً',
      'File size must be less than 10MB': 'يجب أن يكون حجم الملف أقل من 10 ميجابايت',
      'Document uploaded successfully': 'تم رفع المستند بنجاح',
      'Failed to upload document': 'فشل في رفع المستند',
      'Document updated successfully': 'تم تحديث المستند بنجاح',
      'Failed to update document': 'فشل في تحديث المستند',
      'Document deleted successfully': 'تم حذف المستند بنجاح',
      'Failed to delete document': 'فشل في حذف المستند',
      'Documents refreshed successfully': 'تم تحديث المستندات بنجاح',
      'Are you sure you want to delete': 'هل أنت متأكد أنك تريد حذف',
      'Edit Document': 'تعديل المستند',
      'Upload New Document': 'رفع مستند جديد',

      // Escalations Management
      'Escalations Management': 'إدارة التصعيدات',
      'Track and manage project escalations': 'تتبع وإدارة تصعيدات المشروع',
      'Create Escalation': 'إنشاء تصعيد',
      'Project Escalations': 'تصعيدات المشروع',
      '{count} escalations': '{count} تصعيد',
      'No escalations found': 'لم يتم العثور على تصعيدات',
      'No escalations have been reported for this project': 'لم يتم الإبلاغ عن أي تصعيدات لهذا المشروع',
      'Escalation Title': 'عنوان التصعيد',
      'Enter escalation title': 'أدخل عنوان التصعيد',
      'Severity': 'الخطورة',
      'Escalate To': 'تصعيد إلى',
      'Date Reported': 'تاريخ الإبلاغ',
      'Expected Resolution Date': 'تاريخ الحل المتوقع',
      'Escalation Duration (Days)': 'مدة التصعيد (أيام)',
      'Enter duration in days': 'أدخل المدة بالأيام',
      'Auto-escalate to next level when duration expires': 'تصعيد تلقائي للمستوى التالي عند انتهاء المدة',
      'Escalation Duration': 'مدة التصعيد',
      'Auto Escalated': 'تصعيد تلقائي',
      'Expired': 'منتهية',
      'days remaining': 'أيام متبقية',
      'Duration': 'المدة',
      'Days': 'أيام',
      'Describe the escalation details': 'وصف تفاصيل التصعيد',
      'Resolution Notes': 'ملاحظات الحل',
      'Resolution notes (if applicable)': 'ملاحظات الحل (إن وجدت)',
      'Edit Escalation': 'تعديل التصعيد',
      'Update Status': 'تحديث الحالة',
      'Send Notification': 'إرسال إشعار',
      'Delete Escalation': 'حذف التصعيد',
      'Cannot Delete': 'لا يمكن الحذف',
      'Cannot delete an escalation with status "Escalated". Please close it first.': 'لا يمكن حذف تصعيد بحالة "مُصعّد". يرجى إغلاقه أولاً.',
      'Compliance': 'امتثال',
      'Communication': 'تواصل',
      'Open': 'مفتوح',
      'Escalated': 'مُصعّد',
      'Resolved': 'محلول',
      'Not escalated': 'غير مصعد',
      'Not resolved': 'غير محلول',
      'Export Successful': 'تم التصدير بنجاح',
      'Export Failed': 'فشل التصدير',
      'Settings Reset': 'تم إعادة تعيين الإعدادات',
      'Settings Applied': 'تم تطبيق الإعدادات',
      'Data Refreshed': 'تم تحديث البيانات',
      'Escalation Created': 'تم إنشاء التصعيد',
      'Escalation Deleted': 'تم حذف التصعيد',
      'View Escalation': 'عرض التصعيد',
      'Viewing details for': 'عرض تفاصيل',
      'Editing': 'تعديل',
      'Updating status for': 'تحديث حالة',
      'Sending notification for': 'إرسال إشعار لـ',
      'has been deleted': 'تم حذفه',

      // Project Execution
      'Execution Phase Deliverables': 'تسليمات مرحلة التنفيذ',
      'Project deliverables and execution tracking': 'تسليمات المشروع وتتبع التنفيذ',
      'Search execution items...': 'البحث في عناصر التنفيذ...',
      'Critical Only': 'الحرجة فقط',
      'Approve all completed deliverables': 'الموافقة على جميع التسليمات المكتملة',
      'Technical Deliverables': 'التسليمات التقنية',
      'Development': 'التطوير',
      'Security': 'الأمن',
      'Testing': 'الاختبار',
      'Training': 'التدريب',
      'Integration': 'التكامل',
      'Deployment': 'النشر',
      'Monitoring': 'المراقبة',
      'Support': 'الدعم',
      'Maintenance': 'الصيانة',
      'Upload New Deliverable': 'رفع تسليم جديد',
      'Deliverable Name': 'اسم التسليم',
      'Enter deliverable name': 'أدخل اسم التسليم',
      'Brief description of the deliverable': 'وصف موجز للتسليم',
      'Tags': 'الوسوم',
      'Enter tags separated by commas': 'أدخل الوسوم مفصولة بفواصل',
      'Select priority': 'اختر الأولوية',
      'Upload Deliverable': 'رفع التسليم',

      // Financial Management
      'Financial Tracking': 'التتبع المالي',
      'Monitor budget and financial performance': 'مراقبة الميزانية والأداء المالي',
      'Add Item': 'إضافة عنصر',
      'Financial Items': 'العناصر المالية',
      '{count} items': '{count} عنصر',
      'No financial items': 'لا توجد عناصر مالية',
      'Start adding your project financial items': 'ابدأ بإضافة العناصر المالية لمشروعك',
      'Item': 'العنصر',
      'Quantity': 'الكمية',
      'Revised Quantity': 'الكمية المعدلة',
      'CoC Date': 'تاريخ الشهادة',
      'CoC Status': 'حالة الشهادة',
      'CoC Document': 'وثيقة الشهادة',
      'Payment Date': 'تاريخ الدفع',
      'Payment Status': 'حالة الدفع',
      'Payment Document': 'وثيقة الدفع',
      'Add Financial Item': 'إضافة عنصر مالي',
      'Edit Financial Item': 'تعديل عنصر مالي',
      'View Financial Item': 'عرض العنصر المالي',
      'Item Details': 'تفاصيل العنصر',
      'Certificate of Compliance (CoC)': 'شهادة المطابقة',
      'Payment Information': 'معلومات الدفع',
      'Enter item description': 'أدخل وصف العنصر',
      'Enter quantity': 'أدخل الكمية',
      'Enter revised quantity': 'أدخل الكمية المعدلة',
      'Select CoC date': 'اختر تاريخ الشهادة',
      'Select CoC status': 'اختر حالة الشهادة',
      'Choose CoC Document': 'اختر وثيقة الشهادة',
      'Select payment date': 'اختر تاريخ الدفع',
      'Select payment status': 'اختر حالة الدفع',
      'Choose Payment Document': 'اختر وثيقة الدفع',
      'Processing': 'قيد المعالجة',
      'Paid': 'مدفوع',
      'Failed': 'فشل',
      'Item Added': 'تمت إضافة العنصر',
      'Item Updated': 'تم تحديث العنصر',
      'Item Deleted': 'تم حذف العنصر',
      'Validation Error': 'خطأ في التحقق',
      'Please fill in all required fields': 'يرجى ملء جميع الحقول المطلوبة',
      'has been added': 'تمت إضافته',
      'has been updated': 'تم تحديثه',
      "Validation Error": "أخطاء التحقق",
       'error': 'حدث خطأ',
      'summary': 'Validation Error',
      'Please fix all validation errors before submitting': 'يرجى إصلاح جميع أخطاء التحقق قبل الإرسال',
      
      // Financial Validation Messages
      'Item description is required': 'وصف العنصر مطلوب',
      'Item description must be at least 3 characters': 'يجب أن يحتوي وصف العنصر على 3 أحرف على الأقل',
      'Quantity is required': 'الكمية مطلوبة',
      'Quantity must be a valid number': 'يجب أن تكون الكمية رقمًا صالحًا',
      'Quantity must be greater than 0': 'يجب أن تكون الكمية أكبر من 0',
      'Cost is required': 'التكلفة مطلوبة',
      'Cost must be a valid number': 'يجب أن تكون التكلفة رقمًا صالحًا',
      'Cost must be greater than 0': 'يجب أن تكون التكلفة أكبر من 0',
      'Revised quantity must be a valid number': 'يجب أن تكون الكمية المعدلة رقمًا صالحًا',
      'Revised quantity must be greater than 0': 'يجب أن تكون الكمية المعدلة أكبر من 0',
      'CoC status is required': 'حالة الشهادة مطلوبة',
      'Payment status is required': 'حالة الدفع مطلوبة',
      'Expected CoC date must be between': 'يجب أن يكون تاريخ الشهادة المتوقع بين',
      'and': 'و',
      'Expected CoC date cannot be before': 'لا يمكن أن يكون تاريخ الشهادة المتوقع قبل',
      'Expected CoC date cannot be after': 'لا يمكن أن يكون تاريخ الشهادة المتوقع بعد',
      'Expected CoC date is invalid': 'تاريخ الشهادة المتوقع غير صالح',
      'No projects yet': 'لا توجد مشاريع بعد',
      'Get started by creating your first project': 'ابدأ بإنشاء مشروعك الأول',
      "No projects yet.": "لا توجد مشاريع بعد.",
      'Try adjusting your search or filter criteria': 'حاول تعديل معايير البحث أو التصفية الخاصة بك',
      'Create Project': 'إنشاء مشروع',


      // Project Gates
      'Project Gates': 'بوابات المشروع',
      'Manage project phases and milestones': 'إدارة مراحل ومعالم المشروع',
      'Reset Progress': 'إعادة تعيين التقدم',
      'Initiation': 'البدء',
      'Execution': 'التنفيذ',
      'Closure': 'الإغلاق',
      'Define project scope, objectives and charter': 'تحديد نطاق وأهداف وميثاق المشروع',
      'Create detailed project plans and strategies': 'إنشاء خطط واستراتيجيات المشروع التفصيلية',
      'Execute project deliverables and monitor progress': 'تنفيذ مخرجات المشروع ومراقبة التقدم',
      'Finalize deliverables and close project': 'إنهاء المخرجات وإغلاق المشروع',
      'Move to Planning': 'الانتقال إلى التخطيط',
      'Back to Initiation': 'العودة إلى البدء',
      'Move to Execution': 'الانتقال إلى التنفيذ',
      'Back to Planning': 'العودة إلى التخطيط',
      'Move to Closure': 'الانتقال إلى الإغلاق',
      'Back to Execution': 'العودة إلى التنفيذ',
      'Phase Completed': 'اكتملت المرحلة',
      '{phase} phase completed successfully': 'اكتملت مرحلة {phase} بنجاح',
      'Progress Reset': 'تم إعادة تعيين التقدم',
      'Project progress has been reset to the beginning': 'تمت إعادة تعيين تقدم المشروع إلى البداية',
      'Project Completed': 'اكتمل المشروع',
      'Congratulations! The project has been completed successfully': 'تهانينا! تم إكمال المشروع بنجاح',

      // Project Gate Approvals
      'Submit for Approval': 'إرسال للموافقة',
      'Pending Approval': 'في انتظار الموافقة',
      'Approved': 'موافق عليه',
      'Rejected': 'مرفوض',
      'Approval Comment': 'تعليق الموافقة',
      'Rejection Reason': 'سبب الرفض',
      'Phase must be approved before proceeding': 'يجب الموافقة على المرحلة قبل المتابعة',
      'Submit Phase for Approval': 'إرسال المرحلة للموافقة',
      'Approve Phase': 'الموافقة على المرحلة',
      'Reject Phase': 'رفض المرحلة',
      'Resubmit for Approval': 'إعادة الإرسال للموافقة',
      'Resubmit Initiative': 'إعادة تقديم المبادرة',
      'Submission History': 'سجل التقديمات',
      'Resubmitted for Approval': 'أعيد التقديم للموافقة',
      'Submitted for Approval': 'تم التقديم للموافقة',
      'Auto-Approved': 'موافقة تلقائية',
      'Previous Rejection Reason': 'سبب الرفض السابق',
      'Resubmission': 'إعادة التقديم',
      'Are you sure you want to resubmit': 'هل أنت متأكد أنك تريد إعادة تقديم',
      'for approval?': 'للموافقة؟',
      'This will send the initiative back to the approver for review.': 'سيتم إرسال المبادرة مجدداً إلى المعتمد للمراجعة.',
      'Cannot Submit': 'لا يمكن التقديم',
      'Deliverable weights must total 100% before submission. Current total: {total}%': 'يجب أن تبلغ الأوزان الإجمالية للمخرجات 100% قبل التقديم. المجموع الحالي: {total}%',
      'Deliverables do not total 100%': 'أوزان المخرجات لا تساوي 100%',
      'The total weight of all deliverables must equal 100% before this initiative can be submitted for approval.': 'يجب أن يساوي مجموع أوزان جميع المخرجات 100% قبل تقديم هذه المبادرة للموافقة.',
      'Current total': 'المجموع الحالي',
      'No deliverables added': 'لم تتم إضافة أي مخرجات',
      'At least one deliverable is required before this initiative can be submitted for approval.': 'يجب إضافة مخرج واحد على الأقل قبل تقديم هذه المبادرة للموافقة.',
      'At least one deliverable is required before submission': 'يجب إضافة مخرج واحد على الأقل قبل التقديم',
      'Phase Submitted': 'تم إرسال المرحلة',
      'Phase submitted for approval successfully': 'تم إرسال المرحلة للموافقة بنجاح',
      'Phase Approved': 'تمت الموافقة على المرحلة',
      'Phase approved successfully': 'تمت الموافقة على المرحلة بنجاح',
      'Phase Rejected': 'تم رفض المرحلة',
      'Phase rejected successfully': 'تم رفض المرحلة بنجاح',
      'Phase Resubmitted': 'تمت إعادة إرسال المرحلة',
      'Phase resubmitted for approval successfully': 'تمت إعادة إرسال المرحلة للموافقة بنجاح',
      'Enter approval comment (optional)': 'أدخل تعليق الموافقة (اختياري)',
      'Enter rejection reason': 'أدخل سبب الرفض',
      'Approval comment is optional': 'تعليق الموافقة اختياري',
      'Rejection reason is required': 'سبب الرفض مطلوب',
      'This phase is pending approval': 'هذه المرحلة في انتظار الموافقة',
      'This phase has been approved': 'تمت الموافقة على هذه المرحلة',
      'This phase has been rejected': 'تم رفض هذه المرحلة',
      'Submitted by': 'تم الإرسال بواسطة',
      'Approved by': 'تمت الموافقة بواسطة',
      'Rejected by': 'تم الرفض بواسطة',
      'Submitted at': 'تم الإرسال في',
      'Approved at': 'تمت الموافقة في',
      'Rejected at': 'تم الرفض في',
      'Upload mandatory documents before submitting': 'تحميل المستندات الإلزامية قبل الإرسال',
      'All mandatory documents must be uploaded': 'يجب تحميل جميع المستندات الإلزامية',
      'You can proceed to the next phase': 'يمكنك المتابعة إلى المرحلة التالية',
      'Previous phase must be approved first': 'يجب الموافقة على المرحلة السابقة أولاً',
      'Approve': 'الموافقة',
      'Reject': 'رفض',
      'Complete': 'إكمال',
      'Approval Status': 'حالة الموافقة',
      'Approval History': 'سجل الموافقات',
      'No approval history available': 'لا يوجد سجل موافقات متاح',
      'Phase Approval': 'موافقة المرحلة',
      'View Approval Details': 'عرض تفاصيل الموافقة',

      // Project Closure
      'Project Closure': 'إغلاق المشروع',
      'Program Closure': 'إغلاق البرنامج',
      'Manage project closure activities and final documentation': 'إدارة أنشطة إغلاق المشروع والتوثيق النهائي',
      'Manage program closure activities and final documentation': 'إدارة أنشطة إغلاق البرنامج والتوثيق النهائي',
      'Add Closure': 'إضافة إغلاق',
      'Edit Closure': 'تعديل الإغلاق',
      'Closure Details': 'تفاصيل الإغلاق',
      'Delete Closure': 'حذف الإغلاق',
      'Closure Records': 'سجلات الإغلاق',
      'records': 'سجلات',
      'No closure records found': 'لم يتم العثور على سجلات إغلاق',
      'No closure records have been added yet': 'لم تتم إضافة أي سجلات إغلاق بعد',
      'Closure record created successfully': 'تم إنشاء سجل الإغلاق بنجاح',
      'Closure record updated successfully': 'تم تحديث سجل الإغلاق بنجاح',
      'Closure record deleted successfully': 'تم حذف سجل الإغلاق بنجاح',
      'Closure saved as draft': 'تم حفظ الإغلاق كمسودة',
      'Created by': 'أنشئ بواسطة',
      'Failed to load closure records': 'فشل في تحميل سجلات الإغلاق',
      'Failed to create closure record': 'فشل في إنشاء سجل الإغلاق',
      'Failed to update closure record': 'فشل في تحديث سجل الإغلاق',
      'Failed to delete closure record': 'فشل في حذف سجل الإغلاق',
      'Are you sure you want to delete this closure record?': 'هل أنت متأكد أنك تريد حذف سجل الإغلاق هذا؟',
      'Closure records refreshed': 'تم تحديث سجلات الإغلاق',
      'Exporting closure records to CSV...': 'جاري تصدير سجلات الإغلاق إلى CSV...',
      'Benefit Realization Plan': 'خطة تحقيق الفوائد',
      'Benefit Achieved': 'تم تحقيق الفوائد',
      'Benefit not achieved': 'لم يتم تحقيق الفوائد',
      'Not applicable': 'غير قابل للتطبيق',
      'Lesson Learned': 'الدروس المستفادة',
      'Not completed': 'لم يكتمل',
      'Archive Project Files': 'أرشفة ملفات المشروع',
      'Project files archived': 'تمت أرشفة ملفات المشروع',
      'Project files have not been archived': 'لم تتم أرشفة ملفات المشروع',
      'Differences over time period': 'الاختلافات في الفترة الزمنية',
      'Differences over Time Period': 'الاختلافات في الفترة الزمنية',
      'Expected Time Period': 'الفترة الزمنية المتوقعة',
      'Actual Time Period': 'الفترة الزمنية الفعلية',
      'Time Period Difference': 'فرق الفترة الزمنية',
      'Enter expected time period': 'أدخل الفترة الزمنية المتوقعة',
      'Enter actual time period': 'أدخل الفترة الزمنية الفعلية',
      'Enter time period difference': 'أدخل فرق الفترة الزمنية',
      'Enter time difference': 'أدخل فرق الوقت',
      'Differences in project cost': 'الاختلافات في تكلفة المشروع',
      'Differences in Project Cost': 'الاختلافات في تكلفة المشروع',
      'Expected Project Cost': 'تكلفة المشروع المتوقعة',
      'Actual Project Cost': 'تكلفة المشروع الفعلية',
      'Project Cost Difference': 'فرق تكلفة المشروع',
      'Enter expected project cost': 'أدخل تكلفة المشروع المتوقعة',
      'Enter actual project cost': 'أدخل تكلفة المشروع الفعلية',
      'Enter project cost difference': 'أدخل فرق تكلفة المشروع',
      'Enter expected cost': 'أدخل التكلفة المتوقعة',
      'Enter actual cost': 'أدخل التكلفة الفعلية',
      'Enter cost difference': 'أدخل فرق التكلفة',
      'Deliverable List': 'قائمة المخرجات',
      'Deliverable': 'المخرج',
      'Add Deliverable': 'إضافة مخرج',
      'No deliverables': 'لا توجد مخرجات',
      'Enter notes': 'أدخل الملاحظات',
      'Select status': 'اختر الحالة',
      'This field is required': 'هذا الحقل مطلوب',

      // Project Closure - Deliverables API
      'Manage project deliverables': 'إدارة مخرجات المشروع',
      'Edit Deliverable': 'تعديل المخرج',
      'Deliverables': 'المخرجات',
      'No deliverables have been added yet': 'لم تتم إضافة أي مخرجات بعد',
      'Enter deliverable name': 'أدخل اسم المخرج',
      'Contract Number': 'رقم العقد',
      'Enter contract number': 'أدخل رقم العقد',
      'Contract Name': 'اسم العقد',
      'Enter contract name': 'أدخل اسم العقد',
      'Completion Date': 'تاريخ الإنجاز',
      'Deliverable created successfully': 'تم إنشاء المخرج بنجاح',
      'Deliverable updated successfully': 'تم تحديث المخرج بنجاح',
      'Deliverable deleted successfully': 'تم حذف المخرج بنجاح',
      'Failed to create deliverable': 'فشل في إنشاء المخرج',
      'Failed to update deliverable': 'فشل في تحديث المخرج',
      'Failed to delete deliverable': 'فشل في حذف المخرج',
      'Failed to load deliverables': 'فشل في تحميل المخرجات',
      'Are you sure you want to delete this deliverable?': 'هل أنت متأكد أنك تريد حذف هذا المخرج؟',
      'Add deliverables from the deliverables section below': 'أضف المخرجات من قسم المخرجات أدناه',

      // Issue Management
      'Issue Register': 'سجل المشكلات',
      'Register and resolve project issues': 'تسجيل وحل مشكلات المشروع',
      'Add Issue': 'إضافة تحدي',
      'Project Issues': 'مشكلات المشروع',
      '{count} issues': '{count} تحدي',
      'No issues found': 'لم يتم العثور على مشكلات',
      'Great! No issues have been reported yet': 'رائع! لم يتم الإبلاغ عن أي مشكلات حتى الآن',
      'Issue Title': 'عنوان التحدي',
      'Impact': 'التأثير',
      'Owner': 'المسؤول',
      'Linked to Risk': 'مرتبط بالمخاطر',
      'Report Date': 'تاريخ التقرير',
      'Notes': 'الملاحظات',
      'Enter issue title': 'أدخل عنوان التحدي',
      'Describe the issue in detail': 'وصف التحدي بالتفصيل',
      'Enter owner name': 'أدخل اسم المسؤول',
      'Select a risk (optional)': 'اختر خطر (اختياري)',
      'Select report date': 'اختر تاريخ التقرير',
      'Select due date': 'اختر تاريخ الاستحقاق',
      'Enter resolution notes or comments': 'أدخل ملاحظات الحل أو التعليقات',
      'Regulatory': 'تنظيمي',
      'External': 'خارجي',
      'Issue Added': 'تمت إضافة التحدي',
      'Issue Updated': 'تم تحديث التحدي',
      'Issue Deleted': 'تم حذف التحدي',
      'has been added successfully': 'تمت إضافتها بنجاح',
      'has been updated successfully': 'تم تحديثها بنجاح',
      'has been deleted successfully': 'تم حذفها بنجاح',
      'Edit Issue': 'تعديل التحدي',
      'Delete Issue': 'حذف التحدي',
      'Not Linked': 'غير مرتبط',
      'Missing Project': 'مشروع مفقود',
      'Project information is required': 'معلومات المشروع مطلوبة',

      // Tab Placeholder
      'Coming Soon': 'قريباً',
      'This section is currently under development.': 'هذا القسم قيد التطوير حالياً.',
      'Request Feature': 'طلب ميزة',
      'Learn More': 'اعرف المزيد',

      // Project Form
      'Select project type': 'اختر نوع المشروع',
      'Describe the project goals, objectives, and scope...': 'صف أهداف ونطاق المشروع...',
      'Select start date': 'اختر تاريخ البدء',
      'Select end date': 'اختر تاريخ الانتهاء',
      'Select project owner': 'اختر مالك المشروع',
      'Select department (optional)': 'اختر القسم (اختياري)',
      'Project name is required': 'اسم المشروع مطلوب',
      'Project type is required': 'نوع المشروع مطلوب',
      'Project description is required': 'وصف المشروع مطلوب',
      'Start date is required': 'تاريخ البدء مطلوب',
      'End date is required': 'تاريخ الانتهاء مطلوب',
      'Project owner is required': 'مالك المشروع مطلوب',
      'Project status is required': 'حالة المشروع مطلوبة',
      'End date must be after start date': 'يجب أن يكون تاريخ الانتهاء بعد تاريخ البدء',
      
      // Create Project Validation Messages
      'Please enter project name': 'الرجاء إدخال اسم المشروع',
      'Project name must be at least 3 characters': 'يجب أن يكون اسم المشروع 3 أحرف على الأقل',
      'Please select department': 'الرجاء اختيار القسم',
      'Please select project manager': 'الرجاء اختيار مدير المشروع',
      'Please select start date': 'الرجاء اختيار تاريخ البدء',
      'Please select end date': 'الرجاء اختيار تاريخ الانتهاء',
      'End date must be greater than or equal to start date': 'يجب أن يكون تاريخ الانتهاء أكبر من أو يساوي تاريخ البدء',
      'Please enter total budget': 'الرجاء إدخال الميزانية الإجمالية',
      'Budget must be greater than 0': 'يجب أن تكون الميزانية أكبر من 0',
      'Validation Error': 'خطأ في التحقق',
      'Please fill all required fields correctly': 'الرجاء ملء جميع الحقول المطلوبة بشكل صحيح',
      'Planned date is required': 'تاريخ التخطيط مطلوب',
      'Milestone name is required': 'اسم المعلم مطلوب',
      'Please select a valid planned date': 'يرجى اختيار تاريخ تخطيط صالح',
      'Planned date must be on or after the project start date': 'يجب أن يكون تاريخ التخطيط في أو بعد تاريخ بدء المشروع',
      'Planned date must be on or before the project end date': 'يجب أن يكون تاريخ التخطيط في أو قبل تاريخ انتهاء المشروع',
      'Milestone budget is required': 'ميزانية المعلم مطلوبة',
      'Milestone budget must be greater than 0': 'يجب أن تكون ميزانية المعلم أكبر من 0',
      'Total allocated milestone budget cannot exceed the project budget': 'لا يمكن أن يتجاوز إجمالي ميزانية المعالم ميزانية المشروع',
      'Allocated value must be greater than 0': 'يجب أن تكون القيمة المخصصة أكبر من 0',
      'Total allocated milestone budget cannot exceed the project budget': 'لا يمكن أن يتجاوز إجمالي ميزانية المعالم ميزانية المشروع',
      'Total allocated milestone budget cannot exceed the project budget¸': 'لا يمكن أن يتجاوز إجمالي ميزانية المعالم ميزانية المشروع',
      'Milestone saved successfully': 'تم حفظ المعلم بنجاح',
      'Milestone deleted successfully': 'تم حذف المعلم بنجاح',
      'Deliverable name is required': 'اسم التسليم مطلوب',
      'Unit is required': 'الوحدة مطلوبة',
      'Delivery date is required': 'تاريخ التسليم مطلوب',
      
      'Strategic': 'استراتيجي',
      'High': 'عالية',
      'Medium': 'متوسطة',
      'Low': 'منخفضة',

      // Project Card
      'Approve': 'موافقة',
      'Reject': 'رفض',
      'Waiting for Approval...': 'في انتظار الموافقة...',
      'View Rejection Details': 'عرض تفاصيل الرفض',
      'Edit & Resubmit': 'تعديل وإعادة التقديم',
      'Phase 1 Approved - Waiting for Manager...': 'تمت الموافقة على المرحلة 1 - في انتظار المدير...',
      'View Comments': 'عرض التعليقات',
      'View Approval Comments': 'عرض تعليقات الموافقة',
      'Complete Phase 2': 'إكمال المرحلة 2',
      'Waiting for Final Approval...': 'في انتظار الموافقة النهائية...',
      'Edit Phase 2 & Resubmit': 'تعديل المرحلة 2 وإعادة التقديم',
      'View Phase 2 Comments': 'عرض تعليقات المرحلة 2',
      'Manage Project': 'إدارة المشروع',
      'Pending for Phase 1': 'في انتظار المرحلة 1',
      'Pending for Phase 2': 'في انتظار المرحلة 2',
      'Approved Phase 1': 'تمت الموافقة على المرحلة 1',
      'Approved Phase 2': 'تمت الموافقة على المرحلة 2',
      'Rejected Phase 1': 'تم رفض المرحلة 1',
      'Rejected Phase 2': 'تم رفض المرحلة 2',

      // Project Content
      'Adding milestone for project': 'إضافة معلم للمشروع',
      'Viewing all activities for project': 'عرض جميع الأنشطة للمشروع',
      'Viewing analytics for project': 'عرض التحليلات للمشروع',
      'Milestone Saved': 'تم حفظ المعلم',
      'has been saved successfully': 'تم الحفظ بنجاح',
      'Total allocated milestone budget cannot exceed the project budget': 'لا يمكن أن يتجاوز إجمالي ميزانية المعالم ميزانية المشروع',
      
      // Project Review (existing translations)
      'Project Review': 'مراجعة المشروع',
      'Review and approve project submission': 'مراجعة واعتماد تقديم المشروع',
      'Previous Review': 'المراجعة السابقة',
      'Phase 1 Approval Comments': 'تعليقات الموافقة على المرحلة الأولى',
      'Approved by': 'تمت الموافقة بواسطة',
      'on': 'في',
      'Phase 1 - Project Definition': 'المرحلة الأولى - تعريف المشروع',
      'Phase 2 - Execution Planning': 'المرحلة الثانية - تخطيط التنفيذ',
      'Review Status': 'حالة المراجعة',
      'Submitted': 'مقدم',
      'Submitted by': 'مقدم من',
      'Your Decision': 'قرارك',
      'Approval Comments': 'تعليقات الموافقة',
      'Rejection Reason': 'سبب الرفض',
      'Add any comments or feedback (optional)...': 'أضف أي تعليقات أو ملاحظات (اختياري)...',
      'Please provide a reason for rejection (required)...': 'يرجى تقديم سبب للرفض (مطلوب)...',
      'Please provide a reason for rejection': 'يرجى تقديم سبب للرفض',
      'Review Project Definition': 'مراجعة تعريف المشروع',
      'Review Execution Plan': 'مراجعة خطة التنفيذ',
      'Approve or reject the project definition and objectives': 'الموافقة أو الرفض لتعريف المشروع وأهدافه',
      'Approve or reject the project execution plan and milestones': 'الموافقة أو الرفض لخطة تنفيذ المشروع ومعالمه',
      
      // Project Review - Confirmation Dialogs
      'Confirm Approval': 'تأكيد الموافقة',
      'Are you sure you want to approve this project?': 'هل أنت متأكد من أنك تريد الموافقة على هذا المشروع؟',
      'Yes, Approve': 'نعم، أوافق',
      'Confirm Rejection': 'تأكيد الرفض',
      'Are you sure you want to reject this project? The manager will need to resubmit.': 'هل أنت متأكد من أنك تريد رفض هذا المشروع؟ سيحتاج المدير إلى إعادة التقديم.',
      'Yes, Reject': 'نعم، ارفض',
      
      // Project Review - Status & Actions
      'Approve': 'موافقة',
      'Reject': 'رفض',
      'Approved': 'موافق عليه',
      'Rejected': 'مرفوض',
      'Please select a Project Classification before approving': 'يرجى اختيار تصنيف المشروع قبل الموافقة',
      'Project has been approved successfully': 'تمت الموافقة على المشروع بنجاح',
      'Project has been rejected': 'تم رفض المشروع',
      'Failed to approve project': 'فشل في الموافقة على المشروع',
      'Failed to reject project': 'فشل في رفض المشروع',
      'Failed to load project details': 'فشل في تحميل تفاصيل المشروع',
      'Project Not Found': 'المشروع غير موجود',
      
      // Project Review - Information Sections
      'Project Information': 'معلومات المشروع',
      'Project Name': 'اسم المشروع',
      'Project Code': 'رمز المشروع',
      'Project Manager': 'مدير المشروع',
      'Project Sponsor': 'راعي المشروع',
      'Manager': 'المدير',
      'Department': 'القسم',
      'Start Date': 'تاريخ البدء',
      'End Date': 'تاريخ الانتهاء',
      'Completion Date': 'تاريخ الإنجاز',
      'Budget': 'الميزانية',
      'Currency': 'العملة',
      'Created By': 'أنشئ بواسطة',
      'Unknown User': 'مستخدم غير معروف',
      'Notes': 'الملاحظات',
      
      // Project Review - Details
      'Project Objectives': 'أهداف المشروع',
      'Project Scope': 'نطاق المشروع',
      'Project Justifications': 'مبررات المشروع',
      'Strategic Objectives': 'الأهداف الاستراتيجية',
      'Owning Administration': 'الإدارة المالكة',
      
      // Project Review - Classification Options
      '(a) Complex, large-scale, ambiguous project': '(أ) مشروع معقد، واسع النطاق، غامض',
      '(b) Project is complex and medium in size': '(ب) مشروع معقد ومتوسط الحجم',
      '(c) Simple, small, clear project': '(ج) مشروع بسيط، صغير، واضح',
      
      // Project Review - Stakeholders
      'Stakeholder Registry': 'سجل أصحاب المصلحة',
      'Stakeholders will appear here once added': 'سيظهر أصحاب المصلحة هنا بمجرد إضافتهم',
      'No stakeholders added yet': 'لم تتم إضافة أصحاب المصلحة بعد',
      'Name': 'الاسم',
      'Role': 'الدور',
      'Pending Review':'في انتظار المراجعة',
      'Approved': 'موافق عليه',
      'Rejected': 'مرفوض',
      
      // Project Review - Risks
      'Expected Risks': 'المخاطر المتوقعة',
      'Risk assessment will appear here': 'سيظهر تقييم المخاطر هنا',
      'No risks identified': 'لم يتم تحديد مخاطر',
      'Risk Description': 'وصف الخطر',
      'Probability': 'الاحتمالية',
      'Impact': 'التأثير',
      'Risk Rating': 'تقييم الخطر',
      'Cause of Risk': 'سبب الخطر',
      'Expected Impact': 'التأثير المتوقع',
      
      // Project Review - Outputs & Contracts
      'Main Outputs': 'المخرجات الرئيسية',
      'Main outputs will be listed here': 'سيتم إدراج المخرجات الرئيسية هنا',
      'No main outputs defined': 'لم يتم تحديد مخرجات رئيسية',
      'Deliverable Name': 'اسم المخرج',
      'Budget & Contracts': 'الميزانية والعقود',
      'Contracts': 'العقود',
      'No contract details provided': 'لم يتم تقديم تفاصيل العقد',
      'Contract Name': 'اسم العقد',
      'Contract Number': 'رقم العقد',
      
      // Project Review - Board Members
      'Project Board Members': 'أعضاء مجلس المشروع',
      'Project A': 'المشروع أ',
      'Project B': 'المشروع ب',
      'Project C': 'المشروع ج',
  // New Project Schedule
  'Project Schedule': 'جدول المشروع',
  "We're currently building an advanced project scheduling tool with:": 'نحن نعمل حالياً على بناء أداة متقدمة لجدولة المشاريع تتضمن:',
  'Interactive Gantt Chart': 'مخطط جانت تفاعلي',
  'Task Dependencies & Milestones': 'اعتماديات المهام والمعالم',
  'Resource Allocation': 'تخصيص الموارد',
  'Timeline Visualization': 'تصور المخطط الزمني',
  'Import': 'استيراد',
  'Add Task': 'إضافة مهمة',
  'Fit to View': 'ملاءمة مع العرض',
  'Zoom In': 'تكبير',
  'Zoom Out': 'تصغير',
  'Critical Path': 'المسار الحرج',
  'Drag & Drop Enabled': 'السحب والإفلات مفعّل',
  'Drag columns to reorder': 'اسحب الأعمدة لإعادة ترتيبها',
  'Drag tasks to move': 'اسحب المهام لتحريكها',
  'Drag task edges to resize': 'اسحب حواف المهمة لتغيير الحجم',
  'Drag progress bar to update': 'اسحب شريط التقدم للتحديث',
  'Double-click column border to auto-fit': 'انقر مزدوجاً على حدود العمود للضبط التلقائي',
  'Import Project Data': 'استيراد بيانات المشروع',
  'Select File to Import': 'اختر الملف للاستيراد',
  'Supports MS Project XML (.xml), MS Project (.mpp), JSON, Excel (.xlsx), and Primavera P6 (.xml)': 'يدعم ملفات MS Project XML (.xml) وMS Project (.mpp) وJSON وExcel (.xlsx) وPrimavera P6 (.xml)',
  'Choose File': 'اختر ملفاً',
  'Import Options': 'خيارات الاستيراد',
  'Replace existing data': 'استبدال البيانات الحالية',
  'Append to existing data': 'إضافة إلى البيانات الحالية',
  'Merge (update existing tasks)': 'دمج (تحديث المهام الحالية)',
  'Import Settings': 'إعدادات الاستيراد',
  'Import baseline data': 'استيراد بيانات خط الأساس',
  'Import resource assignments': 'استيراد تخصيصات الموارد',
  'Import task dependencies': 'استيراد اعتماديات المهام',
  'Import task constraints': 'استيراد قيود المهام',
  'Export Project Data': 'تصدير بيانات المشروع',
  'Select Export Format': 'اختر صيغة التصدير',
  'Export Options': 'خيارات التصدير',
  'Include baseline data': 'تضمين بيانات خط الأساس',
  'Include resource assignments': 'تضمين تخصيصات الموارد',
  'Please wait...': 'يرجى الانتظار...',
  'Task Information': 'معلومات المهمة',
  'General': 'عام',
  'Duration': 'المدة',
  'Percent Complete': 'نسبة الإنجاز',
  'Schedule Mode': 'وضع الجدولة',
  'Auto Scheduled': 'مجدول تلقائياً',
  'Manually Scheduled': 'مجدول يدوياً',
  'Finish': 'الانتهاء',
  'Select resources': 'اختر الموارد',
  'Predecessors': 'المهام السابقة',
  'Task Name': 'اسم المهمة',
  'Select task': 'اختر مهمة',
  'Lag': 'الفاصل الزمني',
  'Add Predecessor': 'إضافة مهمة سابقة',
  'Business Analyst': 'محلل أعمال',
  'Developer': 'مطوّر',
  'Designer': 'مصمم',
  'QA Engineer': 'مهندس ضمان الجودة',
  'DevOps Engineer': 'مهندس ديف أوبس',
  'Scrum Master': 'سكريوم ماستر',
  'Product Owner': 'مالك المنتج',
  'Technical Lead': 'قائد تقني',
  'UX Designer': 'مصمم تجربة المستخدم',
  'Urgent': 'عاجل',
  'Finish-to-Start': 'من الانتهاء إلى البدء',
  'Start-to-Start': 'من البدء إلى البدء',
  'Finish-to-Finish': 'من الانتهاء إلى الانتهاء',
  'Start-to-Finish': 'من البدء إلى الانتهاء',
  '0 days': '0 أيام',
  '1 day': 'يوم واحد',
  '{count} days': '{count} يوم',
  'Task Information...': 'معلومات المهمة...',
  'Insert Task': 'إدراج مهمة',
  'Delete Task': 'حذف المهمة',

  // Approved project schedule template
  'Phase Description': 'وصف المرحلة',
  'Task Owner': 'المسؤول عن المهمة',
  'Number of Months': 'عدد الأشهر',
  'Actual Progress': 'نسبة التقدم الفعلي',
  'Planned Progress': 'نسبة التقدم المخطط',
  'Project Actual Progress': 'نسبة الإنجاز الفعلي',
  'Project Planned Progress': 'نسبة الإنجاز المخطط',
  'Days Since Start': 'الأيام من تاريخ البداية',
  'Project Performance': 'أداء المشروع',
  'Full Screen': 'ملء الشاشة',
  'Exit Full Screen': 'إنهاء ملء الشاشة',
  'Fit the whole project in view': 'عرض المشروع بالكامل',
  'Apply Fit': 'تطبيق الملاءمة',
  'Reset Fit': 'إعادة ضبط الملاءمة',
  'Restore the view from before Fit was applied': 'استعادة العرض السابق قبل تطبيق الملاءمة',
  '{count} tasks': '{count} مهمة',
  '{count} completed': '{count} مكتملة',
  '{count} in progress': '{count} قيد التنفيذ',
  'Phase weights': 'أوزان المراحل',
  'Pending approval': 'بانتظار الاعتماد',
  'Project Manager Name': 'اسم مدير المشروع',
  'Project Start Date': 'تاريخ بداية المشروع',
  'Months between start and end date': 'عدد الأشهر بين تاريخ البداية وتاريخ النهاية',
  'End Date minus Start Date, in days': 'تاريخ النهاية ناقص تاريخ البداية بالأيام',
  'Today minus Start Date, or 0 before it starts': 'تاريخ اليوم ناقص تاريخ البداية، و0 قبل أن تبدأ',
  'Days Since Start divided by Duration': 'الأيام منذ البداية مقسومة على المدة',
  'Insert Task Below': 'إدراج مهمة أدناه',
  'Add Sub-task': 'إضافة مهمة فرعية',
  'New Sub-task': 'مهمة فرعية جديدة',
  'Indent': 'خفض المستوى',
  'Outdent': 'رفع المستوى',
  'Nest under the task above (Alt+Shift+Arrow)': 'إدراجها ضمن المهمة الأعلى (Alt+Shift+سهم)',
  'Move up one level (Alt+Shift+Arrow)': 'نقلها مستوى واحد لأعلى (Alt+Shift+سهم)',
  'Add a task one level below the selected task': 'إضافة مهمة في مستوى أدنى من المهمة المحددة',
  '{count} sub-tasks': '{count} مهمة فرعية',
  'Earliest start of its sub-tasks': 'أبكر تاريخ بدء بين مهامها الفرعية',
  'Latest finish of its sub-tasks': 'أحدث تاريخ انتهاء بين مهامها الفرعية',
  'Average progress of its sub-tasks': 'متوسط إنجاز مهامها الفرعية',
  'Average planned progress of its sub-tasks': 'متوسط الإنجاز المخطط لمهامها الفرعية',
  'Failed to indent task': 'تعذر خفض مستوى المهمة',
  'Failed to outdent task': 'تعذر رفع مستوى المهمة',
  'Deleting this task also deletes its {count} sub-task(s). This action cannot be undone.': 'سيؤدي حذف هذه المهمة إلى حذف {count} من مهامها الفرعية أيضاً. لا يمكن التراجع عن هذا الإجراء.',
  'Cut Cell': 'قص الخلية',
  'Copy Cell': 'نسخ الخلية',
  'Paste': 'لصق',
  'Scroll to Task': 'الانتقال إلى المهمة',
  'Manually Schedule': 'جدولة يدوية',
  'Auto Schedule': 'جدولة تلقائية',
  'Activate Task': 'تفعيل المهمة',
  'Inactivate Task': 'تعطيل المهمة',
  'MS Project XML (.xml)': 'ملف MS Project XML (.xml)',
  'Compatible with Microsoft Project 2010-2021': 'متوافق مع Microsoft Project 2010-2021',
  'MS Project (.mpp)': 'ملف MS Project (.mpp)',
  'XML format with .mpp extension': 'صيغة XML بامتداد .mpp',
  'JSON Format': 'صيغة JSON',
  'JavaScript Object Notation': 'ترميز كائنات JavaScript',
  'Excel (.xlsx)': 'ملف Excel (.xlsx)',
  'Microsoft Excel format': 'صيغة Microsoft Excel',
  'PDF Document': 'مستند PDF',
  'Printable PDF format': 'صيغة PDF قابلة للطباعة',
  'Primavera P6 (.xml)': 'ملف Primavera P6 (.xml)',
  'Oracle Primavera P6 XML format': 'صيغة Oracle Primavera P6 XML',
  'Task Mode': 'وضع المهمة',
  'Predecessor': 'المهمة السابقة',
  'Resource Name': 'اسم المورد',
  'Gantt chart initialized successfully': 'تم تهيئة مخطط جانت بنجاح',
  'Failed to initialize Gantt chart': 'فشل في تهيئة مخطط جانت',
  'Task "{task}" moved to {date}': 'تم نقل المهمة "{task}" إلى {date}',
  'Task "{task}" duration changed to 1 day': 'تم تغيير مدة المهمة "{task}" إلى يوم واحد',
  'Task "{task}" duration changed to {count} days': 'تم تغيير مدة المهمة "{task}" إلى {count} يوم',
  'Task "{task}" progress updated to {percent}%': 'تم تحديث تقدم المهمة "{task}" إلى {percent}%',
  'Task Updated': 'تم تحديث المهمة',
  'Dependency Created': 'تم إنشاء الاعتمادية',
  '"{source}" linked to "{target}"': 'تم ربط "{source}" بـ "{target}"',
  'Dependency Removed': 'تمت إزالة الاعتمادية',
  'Task dependency has been removed': 'تمت إزالة اعتماد المهمة',
  'Task "{task}" has been updated': 'تم تحديث المهمة "{task}"',
  'New Task': 'مهمة جديدة',
  'Task Added': 'تمت إضافة المهمة',
  'New task has been inserted': 'تم إدراج مهمة جديدة',
  'Task Deleted': 'تم حذف المهمة',
  'Task "{task}" has been deleted': 'تم حذف المهمة "{task}"',
  'Task Cut': 'تم قص المهمة',
  'Task data copied to clipboard': 'تم نسخ بيانات المهمة إلى الحافظة',
  'Task Copied': 'تم نسخ المهمة',
  'No Target': 'لا يوجد هدف',
  'Please select a target task first': 'يرجى اختيار مهمة الهدف أولاً',
  'Task Pasted': 'تم لصق المهمة',
  'Task has been pasted': 'تم لصق المهمة',
  'No Task Selected': 'لم يتم اختيار أي مهمة',
  'Please select a task first': 'يرجى اختيار مهمة أولاً',
  'Scrolled to Task': 'تم الانتقال إلى المهمة',
  'Viewing "{task}"': 'عرض "{task}"',
  'Schedule Mode Changed': 'تم تغيير وضع الجدولة',
  'Task is now {mode}': 'أصبحت المهمة الآن في وضع {mode}',
  'Task Inactivated': 'تم تعطيل المهمة',
  'Task Activated': 'تم تفعيل المهمة',
  'Task "{task}" is now inactive': 'المهمة "{task}" أصبحت الآن غير مفعلة',
  'Task "{task}" is now active': 'المهمة "{task}" أصبحت الآن مفعلة',
  'Column Auto-Fitted': 'تم ضبط العمود تلقائياً',
  '"{column}" column width adjusted to {width}px': 'تم ضبط عرض العمود "{column}" إلى {width} بكسل',
  'Project Planning': 'تخطيط المشروع',
  'Define Requirements': 'تحديد المتطلبات',
  'Create Project Plan': 'إنشاء خطة المشروع',
  'File Selected': 'تم اختيار الملف',
  '{file} is ready to import': 'الملف {file} جاهز للاستيراد',
  'Please select a file to import': 'يرجى اختيار ملف للاستيراد',
  'Importing project data...': 'جارٍ استيراد بيانات المشروع...',
  'Unsupported file format': 'صيغة ملف غير مدعومة',
  'Project data imported successfully': 'تم استيراد بيانات المشروع بنجاح',
  'Import Failed': 'فشل الاستيراد',
  'Failed to import project data': 'فشل استيراد بيانات المشروع',
  'Invalid XML format': 'صيغة XML غير صالحة',
  'Not a valid MS Project XML file': 'ملف MS Project XML غير صالح',
  'No tasks found in the file': 'لم يتم العثور على مهام في الملف',
  'Binary .mpp files must be converted to XML format. Please export from MS Project as XML.': 'يجب تحويل ملفات .mpp الثنائية إلى صيغة XML. يرجى التصدير من MS Project بصيغة XML.',
  'Invalid JSON format. Expected { data: [], links: [] }': 'صيغة JSON غير صالحة. الصيغة المتوقعة هي { data: [], links: [] }',
  'Excel import requires server-side conversion or xlsx library': 'يتطلب استيراد Excel تحويلًا من جهة الخادم أو مكتبة xlsx',
  'Exporting project data...': 'جارٍ تصدير بيانات المشروع...',
  'Unsupported export format': 'صيغة تصدير غير مدعومة',
  'Project data exported successfully': 'تم تصدير بيانات المشروع بنجاح',
  'Failed to export project data': 'فشل تصدير بيانات المشروع',
  'Excel export requires dhtmlxGantt export plugin or server-side conversion': 'يتطلب تصدير Excel إضافة تصدير dhtmlxGantt أو تحويلًا من جهة الخادم',
  'PDF export requires dhtmlxGantt export plugin': 'يتطلب تصدير PDF إضافة تصدير dhtmlxGantt',
  'Primavera export requires dhtmlxGantt export plugin': 'يتطلب تصدير Primavera إضافة تصدير dhtmlxGantt',
  'New task has been added': 'تمت إضافة مهمة جديدة',
  'Timeline adjusted to show all tasks': 'تم ضبط المخطط الزمني لإظهار جميع المهام',
  'Zoomed In': 'تم التكبير',
  'View: {level}': 'العرض: {level}',
  'Zoomed Out': 'تم التصغير',
  'Task Mode Changed': 'تم تغيير وضع المهمة',
  '"{task}" is now {mode}': '"{task}" أصبح الآن في وضع {mode}',

  // Schedule Toolbar & Format Tab
  'Plan and track project timeline with Gantt chart': 'خطط وتابع المخطط الزمني للمشروع باستخدام مخطط جانت',
  'Format': 'التنسيق',
  'Work Time Setting': 'إعدادات وقت العمل',
  'Zoom': 'التكبير/التصغير',
  'Columns': 'الأعمدة',
  'Bar Styles': 'أنماط الأشرطة',
  'Choose the information this column will store': 'اختر نوع المعلومات التي سيخزنها هذا العمود',
  'Configure the new schedule column': 'إعداد عمود الجدول الزمني الجديد',
  'Column Type': 'نوع العمود',
  'Select one type to continue': 'اختر نوعاً واحداً للمتابعة',
  'Column Details': 'تفاصيل العمود',
  'Give the column a clear and recognizable name': 'امنح العمود اسماً واضحاً وسهل التعرف',
  'Dropdown Options': 'خيارات القائمة المنسدلة',
  'Add at least two choices for this column': 'أضف خيارين على الأقل لهذا العمود',
  'Type an option and press Enter': 'اكتب خياراً ثم اضغط Enter',
  'Text Settings': 'إعدادات النص',
  'Set the maximum content length': 'حدد الحد الأقصى لطول المحتوى',
  'Untitled Column': 'عمود بلا عنوان',
  'Create Column': 'إنشاء عمود',
  'Customize how schedule bars appear on the timeline': 'تخصيص مظهر أشرطة الجدول الزمني',
  'Live Preview': 'معاينة مباشرة',
  'Changes are applied after you confirm': 'تُطبّق التغييرات بعد التأكيد',
  'Colors': 'الألوان',
  'Choose a color for each schedule element': 'اختر لوناً لكل عنصر في الجدول الزمني',
  'Control the visual density of the timeline': 'التحكم في الكثافة المرئية للجدول الزمني',
  'Gridlines': 'خطوط الشبكة',
  'Timescale': 'المقياس الزمني',
  'Unbalanced': 'غير متوازن',
  'Phase Weights Unbalanced': 'أوزان المراحل غير متوازنة',
  'Total phase weight is {total}% — exceeds 100% by {excess}%. Adjust weights in Phase Manager.': 'إجمالي وزن المراحل {total}% — يتجاوز 100% بنسبة {excess}%. قم بتعديل الأوزان في إدارة المراحل.',
  'Total phase weight is {total}% — {remaining}% remaining to reach 100%. Adjust weights in Phase Manager.': 'إجمالي وزن المراحل {total}% — يتبقى {remaining}% للوصول إلى 100%. قم بتعديل الأوزان في إدارة المراحل.',
  'Customize bar styles and colors': 'تخصيص أنماط وألوان الأشرطة',
  'Hide gridlines': 'إخفاء خطوط الشبكة',
  'Show gridlines': 'إظهار خطوط الشبكة',
  'Adjust timescale settings': 'ضبط إعدادات المقياس الزمني',
  'Task Bar Color': 'لون شريط المهمة',
  'Progress Color': 'لون التقدم',
  'Milestone Color': 'لون المعلم',
  'Critical Path Color': 'لون المسار الحرج',
  'Bar Height': 'ارتفاع الشريط',
  'Full': 'كامل',
  'Large (20px)': 'كبير (20 بكسل)',
  'Medium (16px)': 'متوسط (16 بكسل)',
  'Small (12px)': 'صغير (12 بكسل)',
  'Timescale Settings': 'إعدادات المقياس الزمني',
  'Time Unit': 'وحدة الوقت',
  'Day': 'يوم',
  'Week': 'أسبوع',
  'Month': 'شهر',
  'Quarter': 'ربع سنة',
  'Year': 'سنة',
  'Preview': 'معاينة',
  'Daily view with individual days': 'عرض يومي مع الأيام الفردية',
  'Weekly view with week numbers': 'عرض أسبوعي مع أرقام الأسابيع',
  'Monthly view with months and weeks': 'عرض شهري مع الأشهر والأسابيع',
  'Quarterly view with Q1, Q2, Q3, Q4': 'عرض ربع سنوي مع الربع الأول والثاني والثالث والرابع',
  'Yearly view': 'عرض سنوي',
  'Apply': 'تطبيق',

  // Zoom Tab
  'Fit': 'ملاءمة',
  'Exit Fullscreen': 'الخروج من ملء الشاشة',
  'Fullscreen View': 'عرض ملء الشاشة',

  // Columns Tab
  'Add Column': 'إضافة عمود',
  'Add a new column to the schedule': 'إضافة عمود جديد إلى الجدول',

  // Column Resized Toast
  'Column Resized': 'تم تغيير حجم العمود',

  // Select Resource & % Complete column
  'Select Resource': 'اختر المورد',
  '% Complete': '% الإنجاز',

  // Column Type Options
  'Text': 'نص',
  'Dropdown': 'قائمة منسدلة',
  'Date': 'تاريخ',
  'Long Text': 'نص طويل',
  'Number': 'رقم',
  'Labels': 'تسميات',
  'Checkbox': 'مربع اختيار',
  'Currency': 'عملة',
  'Single line of text': 'سطر نص واحد',
  'Select from predefined options': 'الاختيار من خيارات محددة مسبقاً',
  'Date picker field': 'حقل اختيار التاريخ',
  'Multiple lines of text': 'أسطر نص متعددة',
  'Numeric value': 'قيمة رقمية',
  'Multiple tags/labels': 'تسميات/وسوم متعددة',
  'True/False toggle': 'تبديل صحيح/خطأ',
  'Progress bar with percentage': 'شريط تقدم بالنسبة المئوية',
  'Currency value': 'قيمة مالية',
  'Field name': 'اسم الحقل',
  'Enter name...': 'أدخل الاسم...',
  'Type or paste options': 'اكتب أو الصق الخيارات',
  'Labels options': 'خيارات التسميات',
  'Max Characters': 'الحد الأقصى للأحرف',
  'e.g., 500': 'مثال: 500',

  // Add Deliverable Dialog
  'Add Deliverable': 'إضافة مخرج',
  'Deliverable Name': 'اسم المخرج',
  'Enter deliverable name': 'أدخل اسم المخرج',
  'Contract Number': 'رقم العقد',
  'Enter contract number': 'أدخل رقم العقد',
  'Contract Name': 'اسم العقد',
  'Enter contract name': 'أدخل اسم العقد',
  'Completion Date': 'تاريخ الإنجاز',
  'Select date': 'اختر التاريخ',

  // Task Dialog strings
  'Task Dependencies': 'اعتماديات المهام',
  'Define which tasks must be completed before this task can start': 'حدد المهام التي يجب إنجازها قبل بدء هذه المهمة',
  'Valid predecessor': 'مهمة سابقة صالحة',
  'Type ID or name': 'اكتب المعرف أو الاسم',
  'Enter task name': 'أدخل اسم المهمة',
  'Automatically managed': 'مُدار تلقائياً',
  'You control the dates': 'أنت تتحكم بالتواريخ',
  'Task dates must be within project timeline': 'يجب أن تكون تواريخ المهمة ضمن المخطط الزمني للمشروع',
  'Select priority': 'اختر الأولوية',
  'No delay': 'لا تأخير',
  'Delay: Task starts {days} days after predecessor': 'تأخير: تبدأ المهمة بعد {days} أيام من المهمة السابقة',
  'Lead: Task starts {days} days before predecessor finishes': 'سبق: تبدأ المهمة قبل {days} أيام من انتهاء المهمة السابقة',

  // Task Management Dialog
  'Create Task': 'إنشاء مهمة',
  'Edit Task': 'تعديل مهمة',
  'Enter task description': 'أدخل وصف المهمة',
  'Start Date': 'تاريخ البدء',
  'Enter assignee name': 'أدخل اسم المكلف',
  'Estimated Hours': 'الساعات المقدرة',
  'Actual Hours': 'الساعات الفعلية',
  'Additional notes': 'ملاحظات إضافية',

  // Milestone & Deliverable Dialogs
  'Milestone Details': 'تفاصيل المعلم',
  'Planned Date': 'التاريخ المخطط',
  'Allocated Value': 'القيمة المخصصة',
  'Deliverables': 'المخرجات',
  'No deliverables linked yet': 'لم يتم ربط مخرجات بعد',
  'Deliverable Details': 'تفاصيل المخرج',
  'Planned Delivery Date': 'تاريخ التسليم المخطط',

  // Schedule toast messages
  'Task Created': 'تم إنشاء المهمة',
  'Project Complete': 'اكتمل المشروع',
  'Failed to load task details': 'فشل تحميل تفاصيل المهمة',
  'Failed to sync task changes': 'فشل مزامنة تغييرات المهمة',
  'Failed to load task information': 'فشل تحميل معلومات المهمة',
  'Unnamed Resource': 'مورد بدون اسم',
  'Failed to save changes to "{name}"': 'فشل حفظ التغييرات لـ "{name}"',
  'Untitled Task': 'مهمة بدون عنوان',

  // Work Time Setting Tab
  'Change Working Time': 'تغيير وقت العمل',
  'Launch the full calendar editor to adjust working days, exceptions, and work weeks.': 'افتح محرر التقويم الكامل لضبط أيام العمل والاستثناءات وأسابيع العمل.',
  'For calendar': 'للتقويم',
  'Create Calendar': 'إنشاء تقويم',
  'Legend': 'دليل الألوان',
  'Working': 'عمل',
  'Nonworking': 'غير عمل',
  'Exception day': 'يوم استثنائي',
  'Edited working hours': 'ساعات عمل معدّلة',
  'On this calendar': 'في هذا التقويم',
  'Nondefault work week': 'أسبوع عمل غير افتراضي',
  'Exceptions': 'الاستثناءات',
  'Work Weeks': 'أسابيع العمل',
  'New': 'جديد',
  'Details...': 'التفاصيل...',
  'Previous month': 'الشهر السابق',
  'Next month': 'الشهر التالي',
  'Options': 'خيارات',
  'Options...': 'خيارات...',

  // Exception Details Dialog
  'Details for': 'تفاصيل',
  'Exception Details': 'تفاصيل الاستثناء',
  'Working times': 'أوقات العمل',
  'Start date': 'تاريخ البدء',
  'Finish date': 'تاريخ الانتهاء',
  'Set working times for these exceptions': 'تعيين أوقات العمل لهذه الاستثناءات',
  'From': 'من',
  'To': 'إلى',
  'hh:mm': 'سس:دد',
  'Enter up to 5 working periods. AM/PM is added automatically.': 'أدخل حتى 5 فترات عمل. يتم إضافة صباحاً/مساءً تلقائياً.',
  'Recurrence pattern': 'نمط التكرار',
  'Every': 'كل',
  'days': 'أيام',
  'Recur every': 'تكرار كل',
  'week(s) on': 'أسبوع/أسابيع في',
  'of every': 'من كل',
  'month(s)': 'شهر/أشهر',
  'The': 'الـ',
  'months': 'أشهر',
  'On': 'في',
  'of': 'من',
  'Range of recurrence': 'نطاق التكرار',
  'End after': 'الانتهاء بعد',
  'occurrences': 'مرات',
  'End by': 'الانتهاء بحلول',

  // Yearly Week Options
  'First': 'الأول',
  'Second': 'الثاني',
  'Third': 'الثالث',
  'Fourth': 'الرابع',
  'Last': 'الأخير',

  // Weekday Names (full)
  'Sunday': 'الأحد',
  'Monday': 'الإثنين',
  'Tuesday': 'الثلاثاء',
  'Wednesday': 'الأربعاء',
  'Thursday': 'الخميس',
  'Friday': 'الجمعة',
  'Saturday': 'السبت',

  // Work Week Details Dialog
  'Set working time for this work week': 'تعيين وقت العمل لهذا الأسبوع',
  'Select day(s)': 'اختر اليوم/الأيام',
  'Use Project default times for these days.': 'استخدام أوقات المشروع الافتراضية لهذه الأيام.',
  'Set days to nonworking time.': 'تعيين الأيام كأوقات غير عمل.',
  'Set day(s) to these specific working times:': 'تعيين اليوم/الأيام لأوقات العمل التالية:',

  // Create Calendar Dialog
  'Create New Calendar': 'إنشاء تقويم جديد',
  'Calendar name': 'اسم التقويم',

  // Project Options Dialog
  'Project Options': 'خيارات المشروع',
  'Display': 'العرض',
  'Schedule': 'الجدول',
  'Calendar options for this project': 'خيارات التقويم لهذا المشروع',
  'Week starts on': 'يبدأ الأسبوع في',
  'Fiscal year starts in': 'تبدأ السنة المالية في',
  'Default start time': 'وقت البدء الافتراضي',
  'Default end time': 'وقت الانتهاء الافتراضي',
  'Hours per day': 'ساعات في اليوم',
  'Hours per week': 'ساعات في الأسبوع',
  'Days per month': 'أيام في الشهر',
  'These times are assigned to tasks when you enter a start or finish date without specifying a time.': 'يتم تعيين هذه الأوقات للمهام عند إدخال تاريخ بدء أو انتهاء بدون تحديد وقت.',
  'Set working days': 'تعيين أيام العمل',
  'Select which days are working days for this project. Unchecked days will be considered non-working days.': 'اختر أيام العمل لهذا المشروع. الأيام غير المحددة ستعتبر أيام غير عمل.',
  'Calendar': 'التقويم',
  'Calendar Type': 'نوع التقويم',
  'Gregorian Calendar': 'التقويم الميلادي',
  'Hijri Calendar': 'التقويم الهجري',
  'Saudi Georgian Calendar': 'التقويم الجورجي السعودي',
  'Saudi Hijri Calendar': 'التقويم الهجري السعودي',
  'This section is not available in this preview.': 'هذا القسم غير متوفر في هذه المعاينة.',

  // Instructions Tab
  'Reorder Columns': 'إعادة ترتيب الأعمدة',
  'Click and drag column headers to rearrange them in your preferred order.': 'انقر واسحب رؤوس الأعمدة لإعادة ترتيبها حسب تفضيلك.',
  'Resize Tasks': 'تغيير حجم المهام',
  'Drag the left or right edge of task bars to adjust their start and end dates.': 'اسحب الحافة اليمنى أو اليسرى لأشرطة المهام لضبط تواريخ البدء والانتهاء.',
  'Auto-fit Columns': 'ضبط تلقائي للأعمدة',
  'Double-click on any column border to automatically resize it to fit the content.': 'انقر مزدوجاً على أي حدود عمود لتغيير حجمه تلقائياً ليتناسب مع المحتوى.',
  'Select Columns': 'اختيار الأعمدة',
  'Click on column headers to select the entire column for quick visibility.': 'انقر على رؤوس الأعمدة لاختيار العمود بالكامل للعرض السريع.',
  'Add Custom Columns': 'إضافة أعمدة مخصصة',
  'Use the Columns tab to add custom fields like text, number, date, or dropdown columns.': 'استخدم علامة تبويب الأعمدة لإضافة حقول مخصصة مثل النص والأرقام والتاريخ والقوائم المنسدلة.',
  'Zoom Controls': 'أدوات التكبير',
  'Use the Zoom tab to adjust the timeline view - zoom in for detailed view or zoom out for overview.': 'استخدم علامة تبويب التكبير لضبط عرض المخطط الزمني - كبّر للعرض التفصيلي أو صغّر للنظرة العامة.',

  // Additional Schedule & Dialog strings
  'Hour': 'ساعة',
  'Yearly': 'سنوي',
  'Target Completion': 'الإنجاز المستهدف',
  'Approved Budget': 'الميزانية المعتمدة',
  'Actual Spend': 'الإنفاق الفعلي',
  'Assigned To': 'مكلف إلى',
  'Project Manager': 'مدير المشروع',
  'Click to add a new task dependency': 'انقر لإضافة اعتمادية مهمة جديدة',
  'ID': 'المعرف',
  'Planned': 'المخطط',
  'Project': 'مشروع',
  'select resources': 'اختر الموارد',

      'Are you sure you want to approve Phase {phase}?': 'هل أنت متأكد أنك تريد الموافقة على المرحلة {phase}؟',
      'Confirm Approval': 'تأكيد الموافقة',
      'Yes, Approve': 'نعم، الموافقة',
      'Please provide a reason for rejection': 'يرجى تقديم سبب للرفض',
      'Are you sure you want to reject Phase {phase}? The manager will need to resubmit.': 'هل أنت متأكد أنك تريد رفض المرحلة {phase}؟ سيحتاج المدير إلى إعادة التقديم.',
      'Confirm Rejection': 'تأكيد الرفض',
      'Yes, Reject': 'نعم، الرفض',
      'INFLUENCE & INTEREST': 'التأثير والاهتمام',
      'COMMUNICATION': 'الاتصال',
      'Method': 'الطريقة',
      'Frequency': 'التكرار',
      'CONTACT INFO': 'معلومات الاتصال',
      'TARGET GOAL': 'الهدف المستهدف',
      'No KPIs Defined': 'لم يتم تحديد مؤشرات أداء',
      'No quantitative success metrics have been set for this project': 'لم يتم تحديد مقاييس نجاح كمية لهذا المشروع',
      'Milestones will appear here once Phase 2 is submitted': 'ستظهر المعالم هنا بمجرد تقديم المرحلة الثانية',
  'Project Not Found': 'المشروع غير موجود',
  "The project you're trying to review doesn't exist or has been deleted.": 'المشروع الذي تحاول مراجعته غير موجود أو تم حذفه.',
  "Project Information": "معلومات المشروع",
  "Phase 2: Execution Planning": "المرحلة الثانية: تخطيط التنفيذ",
  "No deliverables added for this milestone": "لم تتم إضافة أي تسليمات لهذه المرحلة حتى الآن.",
  "Projects Overview": "نظرة عامة على المشاريع",
  "Probability": "الاحتمالية",
  "Entry Date" : "تاريخ الإدخال",

  "Review Date": "تاريخ المراجعة",
      // Welcome Card (from translations.js)
      'Welcome back': 'حياك الله',
      'You have no tasks remaining today': 'لديك لا توجد مهام متبقية اليوم',
      'You have 1 task remaining today': 'لديك مهمة واحدة متبقية اليوم',
      'Great! no tasks remaining today': 'رائع! لا توجد مهام متبقية اليوم',
      'Active Projects': 'المشاريع النشطة',
      
      // Common
      'Search': 'بحث',
      'Filter': 'تصفية',
      'Save': 'حفظ',
      'Cancel': 'إلغاء',
      'Delete': 'حذف',
      'Edit': 'تعديل',
      'Add': 'إضافة',
      'Create': 'إنشاء',
      'Update': 'تحديث',
      'Loading': 'جاري التحميل',
      'Loading...': 'جاري التحميل...',
      'Success': 'نجح',
      'Error': 'خطأ',
      'Error occurred': 'حدث خطأ',
      'View': 'عرض',
      'Remove': 'إزالة',
      'Warning': 'تحذير',
      'Info': 'معلومات',
      'Yes': 'نعم',
      'No': 'لا',
      'OK': 'موافق',
      'Close': 'إغلاق',
      'Back': 'رجوع',
      'Next': 'التالي',
      'Previous': 'السابق',
      'Home': 'الرئيسية',
      'Dashboard': 'لوحة المعلومات',
      'Projects': 'المشاريع',
      'Tasks': 'المهام',
      'Chat': 'المحادثة',
      'Documents': 'المستندات',
      'Reports': 'التقارير',
      'Users': 'المستخدمين',
      'Settings': 'الإعدادات',
      'Profile': 'الملف الشخصي',
      'Logout': 'تسجيل الخروج',
      'Log Out': 'تسجيل الخروج',
      'Logging out...': 'جاري تسجيل الخروج...',
      'Terms of Usage': 'شروط الاستخدام',
      'Support': 'الدعم',

      // Sidebar Menu
      'All Projects': 'جميع المشاريع',
      'Roles': 'الأدوار',

      // Logout Messages
      'Logout Successful': 'تم تسجيل الخروج بنجاح',
      'Logout Error': 'خطأ في تسجيل الخروج',
      'Failed to logout. Please try again.': 'فشل في تسجيل الخروج. يرجى المحاولة مرة أخرى.',

      // Header
      'Search': 'بحث',

      // Notifications Dropdown
      'Notifications': 'الإشعارات',
      'Mark all as read': 'تعيين الكل كمقروء',
      'Inbox': 'صندوق الوارد',
      'General': 'عام',
      'Archived': 'مؤرشف',
      'Confirm': 'تأكيد',

      'Login': 'تسجيل الدخول',
      "Validation failed": "فشل التحقق",
      'OR': 'أو',
      'Welcome': 'مرحباً',
      
      // Auth related
      'Sign In': 'تسجيل الدخول',
      'Sign Out': 'تسجيل الخروج',
      'Email Address': 'البريد الإلكتروني',
      'Password': 'كلمة المرور',
      'Confirm Password': 'تأكيد كلمة المرور',
      'First Name': 'الاسم الأول',
      'Last Name': 'اسم العائلة',
      'Phone Number': 'رقم الهاتف',
      'Remember me': 'تذكرني',
      'Forgot Password?': 'نسيت كلمة المرور؟',
      'Welcome back! Please sign in to continue.': 'حياك الله! يرجى تسجيل الدخول للمتابعة.',
      'Create your account to get started.': 'أنشئ حسابك للبدء.',
      'you@example.com': 'you@example.com',
      'Enter your password': 'أدخل كلمة المرور',
      'Confirm your password': 'أكد كلمة المرور',
      'Continue with Google': 'المتابعة مع جوجل',
      'Continue with Microsoft': 'المتابعة مع مايكروسوفت',
      "Don't have an account?": 'ليس لديك حساب؟',
      'Already have an account?': 'لديك حساب بالفعل؟',
      'Create one here': 'أنشئ واحداً هنا',
      'Sign in here': 'سجل الدخول هنا',
      'Please enter a valid email address': 'يرجى إدخال عنوان بريد إلكتروني صحيح',
      'Password must be at least 6 characters': 'يجب أن تكون كلمة المرور 6 أحرف على الأقل',
      'Passwords do not match': 'كلمات المرور غير متطابقة',
      'Please check your email and password': 'يرجى التحقق من البريد الإلكتروني وكلمة المرور',
      'Incomplete Form': 'نموذج غير مكتمل',
      'Welcome back!': 'حياك الله!',
      'Login Successful': 'تم تسجيل الدخول بنجاح',
      'Please check your credentials and try again': 'يرجى التحقق من بياناتك والمحاولة مرة أخرى',
      'Login Failed': 'فشل تسجيل الدخول',
      'Google sign-in integration coming soon': 'قريباً دمج تسجيل الدخول عبر جوجل',
      'Google OAuth': 'تسجيل الدخول عبر جوجل',
      'Microsoft sign-in integration coming soon': 'قريباً دمج تسجيل الدخول عبر مايكروسوفت',
      'Microsoft OAuth': 'تسجيل الدخول عبر مايكروسوفت',
      
      // Landing page
      'Get Started': 'ابدأ الآن',
      'Learn More': 'اعرف المزيد',
      'Features': 'المميزات',
      'About': 'حول',
      'Contact': 'اتصل بنا',
      'Pricing': 'الأسعار',
      
      // User Management
      'Project Users': 'مستخدمو المشروع',
      'Manage project team members and their permissions': 'إدارة أعضاء فريق المشروع وصلاحياتهم',
      'Total Users': 'إجمالي المستخدمين',
      'Active Users': 'المستخدمون النشطون',
      'Inactive Users': 'المستخدمون غير النشطين',
      'Add User': 'إضافة مستخدم',
      'Export': 'تصدير',
      'Search by name, email, or phone...': 'البحث بالاسم أو البريد الإلكتروني أو الهاتف...',
      'Filter by role': 'تصفية حسب الدور',
      'Filter by status': 'تصفية حسب الحالة',
      'User': 'المستخدم',
      'Role': 'الدور',
      'Phone': 'الهاتف',
      'Status': 'الحالة',
      'Join Date': 'تاريخ الانضمام',
      'Actions': 'الإجراءات',
      'Active': 'نشط',
      'Inactive': 'غير نشط',
      'Not provided': 'غير متوفر',
      'No users found': 'لم يتم العثور على مستخدمين',
      
      // Navigation
      'Project Management': 'إدارة المشاريع',
      'Users': 'المستخدمون',
      'Projects': 'المشاريع',
      'Tasks': 'المهام',
      'Reports': 'التقارير',
      'User Management': 'إدارة المستخدمين',
      'System Settings': 'إعدادات النظام',

      // User Management
      'External Representatives': 'الممثلون الخارجيون',
      'Manage External Representatives': 'إدارة المستخدمين الخارجيين',
      'User Name': 'اسم المستخدم',
      'Organization': 'المنظمة',
      'Position': 'المنصب',
      'Representation Level': 'مستوى التمثيل',
      'Add External Representative': 'إضافة ممثل خارجي',
      'Edit External Representative': 'تعديل ممثل خارجي',

      // councils Management
      'Council Management': 'إدارة المجالس',
      'Manage councils, assignments, and governance structure' : 'إدارة المجالس، المهام، وهيكل الحوكمة',
      'Councils Overview': 'نظرة عامة على المجالس',
      'Total Councils': 'إجمالي المجالس',
      'Expiring': 'تنتهي',
      'Permanent': 'دائمة',
      'Temporary': 'مؤقتة',
      'Meeting Frequency': 'تكرار الاجتماعات',
      'Committee Types': 'أنواع اللجان',
      'Members Overview': 'نظرة عامة على الأعضاء',
      'Total Members': 'إجمالي الأعضاء',
      'Chairpersons': 'رؤساء اللجان',
      'Secretaries': 'الأمناء',
      'Create Council': 'إنشاء مجلس',
      'Search councils, chairperson, or description...': 'البحث في المجالس، رئيس المجلس، أو الوصف...',
      'Filter by Status': 'تصفية حسب الحالة',
      'Filter by Type': 'تصفية حسب النوع',
      'Filter by Frequency': 'تصفية حسب التكرار',
      'Clear Filters': 'مسح الفلاتر',

      //council management
      'Council Management': 'إدارة المجالس',
      'Manage councils, assignments, and governance structure' : 'إدارة المجالس، المهام، وهيكل الحوكمة',
      'Councils Overview': 'نظرة عامة على المجالس',
      'Total Councils': 'إجمالي المجالس',
      'Council Types': 'أنواع المجالس',
      'Create Council': 'إنشاء مجلس',
      'Search councils, chairperson, or description...': 'البحث في المجالس، رئيس المجلس، أو الوصف...',
      
      // Profile Sidebar
      'Failed to load profile': 'فشل في تحميل الملف الشخصي',
      'Try again': 'حاول مرة أخرى',
      'Active': 'نشط',
      'Team Member': 'عضو فريق',
      'User': 'مستخدم',
      'Joined': 'انضم',
      'Email': 'البريد الإلكتروني',
      'Phone': 'الهاتف',
      'Last Active': 'آخر نشاط',
      'Recently active': 'نشط مؤخراً',
      'N/A': 'غير متاح',
      
      // Summary Stats
      'TOTAL PROJECTS': 'إجمالي المشاريع',
      'ACTIVE PROJECTS': 'المشاريع النشطة',
      'AT RISK': 'في خطر',
      'DELAYED': 'متأخر',
      'All projects you have access to': 'جميع المشاريع التي لديك صلاحية الوصول إليها',
      'Projects currently marked as active': 'المشاريع المحددة حالياً كنشطة',
      'At-risk insights coming soon': 'رؤى المخاطر قريباً',
      'Delayed metrics coming soon': 'مقاييس التأخير قريباً',
      'Live metric': 'مقياس مباشر',
      'vs last period': 'مقارنة بالفترة السابقة',
      'Failed to load summary stats': 'فشل في تحميل إحصائيات الملخص',
      'Retry': 'إعادة المحاولة',
      
      // Welcome Card
      'Pending Approvals': 'الموافقات المعلقة',
      'Recent Updates': 'التحديثات الأخيرة',
      'Please refresh the page': 'يرجى تحديث الصفحة',
      
      // Tasks & Notifications
      'Recent Activities & Tasks': 'الأنشطة والمهام الأخيرة',
      'Today\'s Tasks': 'مهام اليوم',
      'Notifications': 'الإشعارات',
      'Latest News': 'آخر الأخبار',
      'No Tasks for Today': 'لا توجد مهام لليوم',
      'You\'re all caught up! No pending tasks at the moment.': 'أنت على اطلاع! لا توجد مهام معلقة في الوقت الحالي.',
      'No Notifications': 'لا توجد إشعارات',
      'You have no new notifications at this time.': 'ليس لديك إشعارات جديدة في الوقت الحالي.',
      'No Latest News': 'لا توجد أخبار حديثة',
      'There are no news updates available at the moment.': 'لا توجد تحديثات إخبارية متاحة في الوقت الحالي.',
      'View All Activities': 'عرض جميع الأنشطة',
      'View Task': 'عرض المهمة',
      'View Details': 'عرض التفاصيل',
      
      // Calendar
      'Calendar': 'التقويم',
      'Greg': 'ميلادي',
      'Hijri': 'هجري',
      'Today': 'اليوم',
      'Clear': 'مسح',
      'Showing Gregorian calendar': 'عرض التقويم الميلادي',
      'Showing Hijri calendar': 'عرض التقويم الهجري',
      'Sat': 'السبت',
      'Sun': 'الأحد',
      'Mon': 'الإثنين',
      'Tue': 'الثلاثاء',
      'Wed': 'الأربعاء',
      'Thu': 'الخميس',
      'Fri': 'الجمعة',
      
      // Gregorian Months
      'January': 'يناير',
      'February': 'فبراير',
      'March': 'مارس',
      'April': 'أبريل',
      'May': 'مايو',
      'June': 'يونيو',
      'July': 'يوليو',
      'August': 'أغسطس',
      'September': 'سبتمبر',
      'October': 'أكتوبر',
      'November': 'نوفمبر',
      'December': 'ديسمبر',
      
      // Hijri Months
      'Muharram': 'محرم',
      'Safar': 'صفر',
      'Rabi\' al-awwal': 'ربيع الأول',
      'Rabi\' al-thani': 'ربيع الثاني',
      'Jumada al-awwal': 'جمادى الأولى',
      'Jumada al-thani': 'جمادى الثانية',
      'Rajab': 'رجب',
      'Sha\'ban': 'شعبان',
      'Ramadan': 'رمضان',
      'Shawwal': 'شوال',
      'Dhu al-Qi\'dah': 'ذو القعدة',
      'Dhu al-Hijjah': 'ذو الحجة',
      
      // Dashboard
      'Project Dashboard': 'لوحة تحكم المشروع',
      'We\'re currently building a powerful project dashboard with:': 'نحن نقوم حاليًا ببناء لوحة تحكم قوية للمشروع مع:',
      'Real-time Project Analytics': 'تحليلات المشروع في الوقت الفعلي',
      'Performance Tracking & KPIs': 'تتبع الأداء ومؤشرات الأداء الرئيسية',
      'Risk & Status Monitoring': 'مراقبة المخاطر والحالة',
      'Interactive Charts & Reports': 'رسوم بيانية وتقارير تفاعلية',
      'This feature will be available soon': 'ستتوفر هذه الميزة قريبًا',
      'No Dashboards Available': 'لا توجد لوحات تحكم متاحة',
      'You don\'t have access to any dashboards at your current access level.': 'ليس لديك حق الوصول إلى أي لوحات تحكم في مستوى الوصول الحالي الخاص بك.',
      'Please contact your administrator to request appropriate permissions.': 'يرجى الاتصال بالمسؤول لطلب الأذونات المناسبة.',
      'Request Access': 'طلب الوصول',
      'Dashboard Settings': 'إعدادات لوحة المعلومات',
      'Refresh Interval': 'فترة التحديث',
      'Select refresh rate': 'حدد معدل التحديث',
      'Theme': 'السمة',
      'Notifications': 'الإشعارات',
      'Email alerts': 'تنبيهات البريد الإلكتروني',
      'Push notifications': 'الإشعارات الفورية',
      'Save Settings': 'حفظ الإعدادات',
      'Risk Analysis': 'تحليل المخاطر',
      'Risk distribution by category': 'توزيع المخاطر حسب الفئة',
      'High Risk': 'مخاطر عالية',
      'Medium Risk': 'مخاطر متوسطة',
      'Low Risk': 'مخاطر منخفضة',
      'Risk Analysis Information': 'معلومات تحليل المخاطر',
      'Risk Categories': 'فئات المخاطر',
      'Budget:': 'الميزانية:',
      'Financial constraints and cost overruns': 'القيود المالية وتجاوز التكاليف',
      'Timeline:': 'الجدول الزمني:',
      'Schedule delays and milestone risks': 'تأخير الجدول الزمني ومخاطر المعالم',
      'Resources:': 'الموارد:',
      'Staffing and capacity issues': 'مشاكل التوظيف والقدرة',
      'Quality:': 'الجودة:',
      'Standards and deliverable concerns': 'معايير ومخاوف التسليم',
      'Got it': 'فهمت',
      'Export Started': 'بدأ التصدير',
      'Generating {format} report...': 'جاري إنشاء تقرير {format}...',
      'Data Refreshed': 'تم تحديث البيانات',
      'Dashboard data has been updated': 'تم تحديث بيانات لوحة المعلومات',
      'Settings Saved': 'تم حفظ الإعدادات',
      'Your preferences have been updated': 'تم تحديث تفضيلاتك',
      'View Details': 'عرض التفاصيل',
      'Opening details for {name}': 'فتح تفاصيل {name}',
      'Edit Item': 'تعديل العنصر',
      'Editing {name}': 'تعديل {name}',
      'Confirm Delete': 'تأكيد الحذف',
      'Are you sure you want to delete {name}?': 'هل أنت متأكد من حذف {name}؟',
      'Deleted Successfully': 'تم الحذف بنجاح',
      '{name} has been deleted': 'تم حذف {name}',
      'Stat Details': 'تفاصيل الإحصائية',
      'Viewing details for {title}': 'عرض تفاصيل {title}',
      'View Mode Changed': 'تم تغيير وضع العرض',
      'Chart view changed to {mode}': 'تم تغيير عرض الرسم البياني إلى {mode}',
      'Status Filter': 'تصفية الحالة',
      'Filtering by {name}': 'التصفية حسب {name}',
      'ACTIVE PROJECTS': 'المشاريع النشطة',
      'ON TRACK': 'على المسار الصحيح',
      'AT RISK': 'في خطر',
      'DELAYED': 'متأخر',
      'ACTIVE PROGRAMS': 'البرامج النشطة',
      'STRATEGIC GOALS': 'الأهداف الاستراتيجية',
      'PROGRAM BUDGET': 'ميزانية البرنامج',
      'TEAM MEMBERS': 'أعضاء الفريق',
      'ACTIVE PORTFOLIOS': 'المحافظ النشطة',
      'TOTAL INVESTMENT': 'إجمالي الاستثمار',
      'ROI': 'العائد على الاستثمار',
      'STRATEGIC ALIGNMENT': 'التوافق الاستراتيجي',
      'Every 30 seconds': 'كل 30 ثانية',
      'Every minute': 'كل دقيقة',
      'Every 5 minutes': 'كل 5 دقائق',
      'Every 15 minutes': 'كل 15 دقيقة',
      'Light': 'فاتح',
      'Dark': 'داكن',
      'Programs': 'البرامج',
      'Portfolios': 'المحافظ',
      'Website Redesign': 'إعادة تصميم الموقع',
      'Mobile App': 'تطبيق الجوال',
      'Data Migration': 'ترحيل البيانات',
      'Security Audit': 'تدقيق الأمان',
      'API Integration': 'تكامل API',
      'Digital Transformation': 'التحول الرقمي',
      'Customer Experience': 'تجربة العملاء',
      'Operational Excellence': 'التميز التشغيلي',
      'Technology Portfolio': 'محفظة التكنولوجيا',
      'Innovation Portfolio': 'محفظة الابتكار',
      'Infrastructure Portfolio': 'محفظة البنية التحتية',
      'In Progress': 'قيد التنفيذ',
      'Planning': 'التخطيط',
      'Completed': 'مكتمل',
      'On Hold': 'معلق',
      'High': 'عالي',
      'Medium': 'متوسط',
      'Low': 'منخفض',
      'Active': 'نشط',
      'Under Review': 'قيد المراجعة',
      
      // Task Management
      'Task Management': 'إدارة المهام',
      'We\'re currently building a powerful task management system with:': 'نحن نقوم حاليًا ببناء نظام قوي لإدارة المهام مع:',
      'Kanban & List Views': 'عرض كانبان والقوائم',
      'Task Dependencies & Subtasks': 'تبعيات المهام والمهام الفرعية',
      'Team Collaboration & Comments': 'التعاون الجماعي والتعليقات',
      'Progress Tracking & Analytics': 'تتبع التقدم والتحليلات',
      
      // Document Management
      'Document Management': 'إدارة المستندات',
      'We\'re currently building a powerful document management system with:': 'نحن نقوم حاليًا ببناء نظام قوي لإدارة المستندات مع:',
      'Drag & Drop File Upload': 'سحب وإفلات الملفات',
      'Version Control & History': 'التحكم في الإصدار والسجل',
      'Advanced Search & Filtering': 'البحث والتصفية المتقدمة',
      'Sharing & Permission Management': 'المشاركة وإدارة الأذونات',
      'Document Preview & Annotations': 'معاينة المستندات والتعليقات التوضيحية',
      
      // Reports & Analytics
      'Project Reports & Analytics': 'تقارير وتحليلات المشروع',
      'We\'re currently building comprehensive reporting and analytics capabilities with:': 'نحن نقوم حاليًا ببناء قدرات شاملة للتقارير والتحليلات مع:',
      'Custom Report Builder': 'منشئ التقارير المخصص',
      'Advanced Data Visualization': 'تصور البيانات المتقدم',
      'Export to PDF/Excel': 'التصدير إلى PDF/Excel',
      'Scheduled Reports & Automation': 'التقارير المجدولة والأتمتة',
      'Real-time Analytics Dashboard': 'لوحة التحليلات في الوقت الفعلي',
      
      // Organizational Structure
      'Organizational Structure': 'الهيكل التنظيمي',
      'Org. Structure': 'الهيكل التنظيمي',
      'We\'re currently building a comprehensive organizational structure visualization system with:': 'نحن نقوم حاليًا ببناء نظام شامل لتصور الهيكل التنظيمي مع:',
      'Interactive Organization Chart': 'مخطط تنظيمي تفاعلي',
      'Multi-View Display (Chart, Table, Cards)': 'عرض متعدد (مخطط، جدول، بطاقات)',
      'Hierarchical Unit Management': 'إدارة الوحدات الهرمية',
      'Advanced Filtering & Search': 'التصفية والبحث المتقدم',
      'Export & Reporting Tools': 'أدوات التصدير والتقارير',

      // PMO Library
      'PMO Library': 'مكتبة إدارة المشاريع',
      'Central repository for project management documents and resources': 'مستودع مركزي لوثائق وموارد إدارة المشاريع',
      'Library Documents': 'وثائق المكتبة',
      'Total Documents': 'إجمالي المستندات',
      'PDF Documents': 'مستندات PDF',
      'Ready to view and download': 'جاهزة للعرض والتنزيل',
      'Document Categories': 'فئات المستندات',
      'Available categories': 'الفئات المتاحة',
      'Added This Month': 'المضاف هذا الشهر',
      'Recent library additions': 'أحدث إضافات المكتبة',
      'Search library documents...': 'البحث في مستندات المكتبة...',
      'No documents match the selected filters.': 'لا توجد مستندات تطابق عوامل التصفية المحددة.',
      'Start by uploading your first document': 'ابدأ برفع مستندك الأول',
      'Select File': 'اختر ملفًا',
      'Replace File (Optional)': 'استبدال الملف (اختياري)',
      'Click to select files or drag and drop': 'انقر لاختيار الملفات أو اسحبها وأفلتها',
      'PDF, DOC, XLS, PPT, TXT (Max 10MB per file)': 'PDF، DOC، XLS، PPT، TXT (بحد أقصى 10 ميجابايت لكل ملف)',
      'Current file will be kept if no new file is selected': 'سيتم الاحتفاظ بالملف الحالي إذا لم يتم اختيار ملف جديد',
      'Add More Files': 'إضافة ملفات أخرى',
      'Brief description of the document': 'وصف موجز للمستند',
      'Enter custom category name': 'أدخل اسم الفئة المخصصة',
      'Uploaded By': 'تم الرفع بواسطة',
      'Policy': 'سياسة',
      'Template': 'قالب',
      'Document name must be at least 3 characters': 'يجب ألا يقل اسم المستند عن 3 أحرف',
      'Custom category must be at least 2 characters': 'يجب ألا يقل اسم الفئة المخصصة عن حرفين',
      'Document replaced successfully': 'تم استبدال المستند بنجاح',
      'Document URL not available': 'رابط المستند غير متاح',
      'Duplicate File': 'ملف مكرر',
      'File Added': 'تمت إضافة الملف',
      'File Removed': 'تمت إزالة الملف',
      'File has been removed from the selection': 'تمت إزالة الملف من التحديد',
      'is already selected': 'محدد بالفعل',
      'added successfully': 'تمت إضافته بنجاح',
      '{count} document(s) uploaded successfully': 'تم رفع {count} مستند (مستندات) بنجاح',
      '{count} document(s) failed to upload: {files}': 'فشل رفع {count} مستند (مستندات): {files}',
      'We\'re currently building a comprehensive document management system with:': 'نحن نقوم حاليًا ببناء نظام شامل لإدارة المستندات يتضمن:',
      'Document Repository & Organization': 'مستودع وتنظيم المستندات',
      'Version Control & History': 'التحكم في الإصدار والتاريخ',
      'Templates & Best Practices': 'القوالب وأفضل الممارسات',
      'Search & Categorization': 'البحث والتصنيف',

      // Team Chat
      'Team Chat': 'محادثة الفريق',
      'We\'re currently building a real-time team collaboration chat with:': 'نحن نقوم حاليًا ببناء دردشة تعاون جماعي في الوقت الفعلي تتضمن:',
      'Real-time Messaging': 'المراسلة في الوقت الفعلي',
      'File Sharing & Attachments': 'مشاركة الملفات والمرفقات',
      'Group Conversations': 'المحادثات الجماعية',
      'Message History & Search': 'سجل الرسائل والبحث',

      // Governance Management
      'Governance Management': 'إدارة الحوكمة',
      'We\'re currently building a comprehensive governance system with:': 'نحن نقوم حاليًا ببناء نظام حوكمة شامل يتضمن:',
      'Organizational Structure Management': 'إدارة الهيكل التنظيمي',
      'Workflow & Approval Processes': 'سير العمل وعمليات الموافقة',
      'Role-Based Access Control': 'التحكم في الوصول حسب الدور',
      'Compliance & Audit Trails': 'الامتثال ومسارات التدقيق',

      // User Management
      'Project Users': 'مستخدمي المشروع',
      'Manage project team members and their permissions': 'إدارة أعضاء فريق المشروع وصلاحياتهم',
      'Export': 'تصدير',
      'Add User': 'إضافة مستخدم',
      'Total Users': 'إجمالي المستخدمين',
      'Active Users': 'المستخدمون النشطون',
      'Inactive Users': 'المستخدمون غير النشطين',
      'Search by name, email, or phone...': 'البحث بالاسم أو البريد الإلكتروني أو الهاتف...',
      'Filter by role': 'تصفية حسب الدور',
      'Filter by status': 'تصفية حسب الحالة',
      'All Roles': 'جميع الأدوار',
      'All Status': 'جميع الحالات',
      'Active': 'نشط',
      'Inactive': 'غير نشط',
      'User': 'المستخدم',
      'Role': 'الدور',
      'Phone': 'الهاتف',
      'Status': 'الحالة',
      'Join Date': 'تاريخ الانضمام',
      'Actions': 'الإجراءات',
      'Not provided': 'غير متوفر',
      'Protected Super Admin': 'مسؤول محمي',
      'User Actions': 'إجراءات المستخدم',
      'Protected': 'محمي',
      'Edit': 'تعديل',
      'Reset Password': 'إعادة تعيين كلمة المرور',
      'Delete': 'حذف',
      'Edit User': 'تعديل المستخدم',
      'Add User': 'إضافة مستخدم',
      'First Name': 'الاسم الأول',
      'Last Name': 'اسم العائلة',
      'Email': 'البريد الإلكتروني',
      'Phone Number': 'رقم الهاتف',
      'Password': 'كلمة المرور',
      'Select a role': 'اختر دورًا',
      'Super Administrator role cannot be changed': 'لا يمكن تغيير دور المسؤول الأعلى',
      'Enter password': 'أدخل كلمة المرور',
      'Cancel': 'إلغاء',
      'Create': 'إنشاء',
      'Update': 'تحديث',
      'Confirm Deletion': 'تأكيد الحذف',
      'Are you sure you want to delete the user': 'هل أنت متأكد من حذف المستخدم',
      
      // Validation Messages
      'First name is required': 'الاسم الأول مطلوب',
      'Last name is required': 'اسم العائلة مطلوب',
      'Email is required': 'البريد الإلكتروني مطلوب',
      'Please enter a valid email address': 'يرجى إدخال عنوان بريد إلكتروني صالح',
      'Phone number is required': 'رقم الهاتف مطلوب',
      'Phone number must start with 05 and be exactly 10 digits (e.g., 0512345678)': 'يجب أن يبدأ رقم الهاتف بـ 05 ويتكون من 10 أرقام بالضبط (مثال: 0512345678)',
      'Role is required': 'الدور مطلوب',
      'Password is required': 'كلمة المرور مطلوبة',
      'Password must be at least 8 characters': 'يجب أن تتكون كلمة المرور من 8 أحرف على الأقل',
      
      // Toast Messages
      'Success': 'نجح',
      'Error': 'خطأ',
      'User updated successfully': 'تم تحديث المستخدم بنجاح',
      'User created successfully': 'تم إنشاء المستخدم بنجاح',
      'Failed to save user': 'فشل حفظ المستخدم',
      'Failed to load users': 'فشل تحميل المستخدمين',
      'User deleted successfully': 'تم حذف المستخدم بنجاح',
      'Failed to delete user': 'فشل حذف المستخدم',
      'Action Not Allowed': 'الإجراء غير مسموح',
      'Super Administrator cannot be deleted': 'لا يمكن حذف المسؤول الأعلى',
      'Password Reset': 'إعادة تعيين كلمة المرور',
      'Password reset instructions sent to': 'تم إرسال تعليمات إعادة تعيين كلمة المرور إلى',
      'Failed to reset password': 'فشل إعادة تعيين كلمة المرور',
      'Initialization Error': 'خطأ في التهيئة',
      'Failed to load user management data': 'فشل تحميل بيانات إدارة المستخدمين',
      'No users found': 'لم يتم العثور على مستخدمين',
      'Never': 'أبداً',
      'Unknown': 'غير معروف',
      'Unknown User': 'مستخدم غير معروف',
      'No Role': 'لا يوجد دور',
      'Invalid Date': 'تاريخ غير صالح',
      'Users Management': 'إدارة المستخدمين',
      'Manage system users and their permissions': 'إدارة مستخدمي النظام وصلاحياتهم',
      'No department': 'بدون قسم',
      'No Department': 'بدون قسم',
      'Select a department (optional)': 'اختر قسم (اختياري)',
      'Assign user to a department (optional)': 'تعيين المستخدم لقسم (اختياري)',
      'Phone number must start with 05 and be exactly 10 digits': 'يجب أن يبدأ رقم الهاتف بـ 05 ويتكون من 10 أرقام',
      'Showing {first} to {last} of {totalRecords} users': 'عرض {first} إلى {last} من {totalRecords} مستخدم',
      'Department': 'القسم',

      // Department Management
      'Departments': 'الأقسام',
      'Manage organizational departments and their hierarchy': 'إدارة الأقسام التنظيمية وهيكلها الهرمي',
      'Add Department': 'إضافة قسم',
      'Total Departments': 'إجمالي الأقسام',
      'Parent Departments': 'الأقسام الرئيسية',
      'Sub Departments': 'الأقسام الفرعية',
      'Search by department name or description...': 'البحث بالاسم أو الوصف...',
      'Filter by parent': 'تصفية حسب القسم الرئيسي',
      'All Departments': 'جميع الأقسام',
      'Root Departments': 'الأقسام الجذرية',
      'Department': 'القسم',
      'Code': 'الرمز',
      'Description': 'الوصف',
      'Parent Department': 'القسم الرئيسي',
      'Created Date': 'تاريخ الإنشاء',
      'Department Actions': 'إجراءات القسم',
      'No departments found': 'لم يتم العثور على أقسام',
      'No description': 'لا يوجد وصف',
      'Top Level': 'المستوى الأعلى',
      'None': 'لا يوجد',
      'View Details': 'عرض التفاصيل',
      'Department Details': 'تفاصيل القسم',
      'Edit Department': 'تعديل القسم',
      'Close': 'إغلاق',
      'Department Name': 'اسم القسم',
      'Department Code': 'رمز القسم',
      'Select parent department (optional)': 'اختر القسم الرئيسي (اختياري)',
      'Leave empty for root department': 'اتركه فارغاً للقسم الجذري',
      'Select status': 'اختر الحالة',
      'Full Hierarchy Path': 'المسار الهرمي الكامل',
      'No description provided': 'لم يتم تقديم وصف',
      'Top Level Department': 'قسم المستوى الأعلى',
      'No sub-departments': 'لا توجد أقسام فرعية',
      'Code is auto-generated and cannot be changed': 'الرمز يتم إنشاؤه تلقائياً ولا يمكن تغييره',
      'e.g., Information Technology': 'مثال: تقنية المعلومات',
      'e.g., IT or IT-DEV': 'مثال: IT أو IT-DEV',
      'Brief description of the department\'s purpose and responsibilities': 'وصف موجز لغرض القسم ومسؤولياته',
      
      // Departments Page Additional Translations
      'Departments Management': 'إدارة الأقسام',
      'Manage organizational departments and their hierarchy. Departments help organize your company structure.': 'إدارة الأقسام التنظيمية وهيكلها الهرمي. تساعد الأقسام في تنظيم هيكل شركتك.',
      'Create Department': 'إنشاء قسم',
      'Create a new department with hierarchy': 'إنشاء قسم جديد مع التسلسل الهرمي',
      'Total number of departments in the system': 'إجمالي عدد الأقسام في النظام',
      'Total Employees': 'إجمالي الموظفين',
      'Number of employees across all departments': 'عدد الموظفين عبر جميع الأقسام',
      'Number of sub-departments in the system': 'عدد الأقسام الفرعية في النظام',
      'Search departments by name or code...': 'البحث عن الأقسام بالاسم أو الرمز...',
      'Filter by Type': 'تصفية حسب النوع',
      'Getting Started with Departments': 'البدء مع الأقسام',
      'Departments help you organize your company structure. Create parent departments and sub-departments to reflect your organizational hierarchy.': 'تساعدك الأقسام في تنظيم هيكل شركتك. أنشئ أقساماً رئيسية وأقساماً فرعية لتعكس التسلسل الهرمي التنظيمي.',
      'Click "Create Department" to add your first department': 'انقر على "إنشاء قسم" لإضافة قسمك الأول',
      'Assign managers to oversee each department': 'عيّن مديرين للإشراف على كل قسم',
      'Create sub-departments to organize teams within departments': 'أنشئ أقساماً فرعية لتنظيم الفرق داخل الأقسام',
      'No Departments Found': 'لم يتم العثور على أقسام',
      'No departments match your search': 'لا توجد أقسام تطابق بحثك',
      'No code': 'لا يوجد رمز',
      'Employees': 'الموظفين',
      'Number of employees in this department': 'عدد الموظفين في هذا القسم',
      'Sub-departments': 'الأقسام الفرعية',
      '{count} more sub-departments': '{count} أقسام فرعية أخرى',
      'Department Manager': 'مدير القسم',
      'Short code identifier (e.g., HR, IT, FIN)': 'رمز تعريف قصير (مثل: HR, IT, FIN)',
      'Leave empty for top-level department': 'اتركه فارغاً للقسم الرئيسي',
      'Select department manager (optional)': 'اختر مدير القسم (اختياري)',
      'Duplicate': 'تكرار',
      'Department "{name}" has been deleted successfully': 'تم حذف القسم "{name}" بنجاح',
      'Are you sure you want to delete the department': 'هل أنت متأكد من حذف القسم',
      'Copy': 'نسخ',
      
      // Department Validation Messages
      'Department name is required': 'اسم القسم مطلوب',
      'Department code is required': 'رمز القسم مطلوب',
      'Department code must contain only uppercase letters, numbers, and dashes': 'يجب أن يحتوي رمز القسم على أحرف كبيرة وأرقام وشرطات فقط',
      
      // Department Toast Messages
      'Department updated successfully': 'تم تحديث القسم بنجاح',
      'Department created successfully': 'تم إنشاء القسم بنجاح',
      'Failed to save department': 'فشل حفظ القسم',
      'Failed to load departments': 'فشل تحميل الأقسام',
      'Department deleted successfully': 'تم حذف القسم بنجاح',
      'Failed to delete department': 'فشل حذف القسم',
      'Validation Error': 'خطأ في التحقق',
      'Please check the form for errors': 'يرجى التحقق من النموذج بحثاً عن أخطاء',
      'Failed to load department management data': 'فشل تحميل بيانات إدارة الأقسام',
      'Deleted': 'تم الحذف',
      'Delete Failed': 'فشل الحذف',
      'This department has {count} sub-department(s). They will become top-level departments.': 'هذا القسم يحتوي على {count} قسم فرعي. سيصبحون أقساماً رئيسية.',
      'This action cannot be undone.': 'لا يمكن التراجع عن هذا الإجراء.',
      'Delete "{name}"?': 'حذف "{name}"؟',
      'has been deleted successfully': 'تم حذفه بنجاح',
      
      // Role Management
      'Project Roles': 'أدوار المشروع',
      'Roles Management': 'إدارة الأدوار',
      'Manage user roles and their associated permissions. Roles define what actions users can perform in the system.': 'إدارة أدوار المستخدمين والصلاحيات المرتبطة بها. تحدد الأدوار الإجراءات التي يمكن للمستخدمين تنفيذها في النظام.',
      'Manage user roles and permissions': 'إدارة أدوار المستخدمين والصلاحيات',
      'Create a new role with custom permissions': 'إنشاء دور جديد بصلاحيات مخصصة',
      'Create Role': 'إنشاء دور',
      'Total Roles': 'إجمالي الأدوار',
      'Total number of roles in the system': 'إجمالي عدد الأدوار في النظام',
      'Number of users currently assigned to roles': 'عدد المستخدمين المعينين حالياً للأدوار',
      'Total permissions available to assign to roles': 'إجمالي الصلاحيات المتاحة لتعيينها للأدوار',
      'Active Users': 'المستخدمون النشطون',
      'Permissions': 'الصلاحيات',
      'Search roles by name or description...': 'البحث في الأدوار بالاسم أو الوصف...',
      'Search roles...': 'البحث في الأدوار...',
      'Filter by Status': 'تصفية حسب الحالة',
      'Filter by Permission': 'تصفية حسب الصلاحية',
      'All Statuses': 'جميع الحالات',
      'All Permissions': 'جميع الصلاحيات',
      'Users': 'المستخدمون',
      'Number of users assigned to this role': 'عدد المستخدمين المعينين لهذا الدور',
      'More actions': 'المزيد من الإجراءات',
      'Created': 'تم الإنشاء',
      'Edit Role': 'تعديل الدور',
      'Duplicate': 'تكرار',
      'Role Name': 'اسم الدور',
      'Display Name': 'الاسم المعروض',
      'Friendly name to display in the UI': 'اسم مألوف لعرضه في الواجهة',
      'No permissions available': 'لا توجد صلاحيات متاحة',
      'No permissions assigned': 'لم يتم تعيين صلاحيات',
      '{count} more permissions': '{count} صلاحيات إضافية',
      'Role name is required': 'اسم الدور مطلوب',
      'Description is required': 'الوصف مطلوب',
      'Role updated successfully': 'تم تحديث الدور بنجاح',
      'Role created successfully': 'تم إنشاء الدور بنجاح',
      'Failed to save role': 'فشل حفظ الدور',
      'Failed to load roles': 'فشل تحميل الأدوار',
      'Failed to load permissions': 'فشل تحميل الصلاحيات',
      'Role deleted successfully': 'تم حذف الدور بنجاح',
      'Failed to delete role': 'فشل حذف الدور',
      'Deleted': 'تم الحذف',
      'Delete Failed': 'فشل الحذف',
      'Role "{name}" has been deleted successfully': 'تم حذف الدور "{name}" بنجاح',
      'Are you sure you want to delete the role': 'هل أنت متأكد من حذف الدور',
      'This role has {count} user(s) assigned. They will lose this role.': 'هذا الدور يحتوي على {count} مستخدم. سيفقدون هذا الدور.',
      'Getting Started with Roles': 'البدء مع الأدوار',
      'Roles help you organize user permissions efficiently. Create roles like "Admin", "Project Manager", or "Editor" and assign relevant permissions to each.': 'تساعدك الأدوار في تنظيم صلاحيات المستخدمين بكفاءة. قم بإنشاء أدوار مثل "مدير المشروع" أو "مشرف" أو "محرر" وقم بتعيين الصلاحيات المناسبة لكل منها.',
      'Click "Create Role" to add your first role': 'انقر على "إنشاء دور" لإضافة دورك الأول',
      'Assign permissions to define what users can do': 'قم بتعيين الصلاحيات لتحديد ما يمكن للمستخدمين فعله',
      'Assign users to roles in User Management': 'قم بتعيين المستخدمين للأدوار في إدارة المستخدمين',
      'No Roles Found': 'لم يتم العثور على أدوار',
      'No roles match your search': 'لا توجد أدوار تطابق بحثك',
      'Clear Search': 'مسح البحث',
      'Copy': 'نسخ',
      
      // Permission Names
      'view users': 'عرض المستخدمين',
      'create users': 'إنشاء المستخدمين',
      'edit users': 'تعديل المستخدمين',
      'delete users': 'حذف المستخدمين',
      'manage users': 'إدارة المستخدمين',
      'view projects': 'عرض المشاريع',
      'create projects': 'إنشاء المشاريع',
      'edit projects': 'تعديل المشاريع',
      'delete projects': 'حذف المشاريع',
      'manage projects': 'إدارة المشاريع',
      'view roles': 'عرض الأدوار',
      'create roles': 'إنشاء الأدوار',
      'edit roles': 'تعديل الأدوار',
      'delete roles': 'حذف الأدوار',
      'manage roles': 'إدارة الأدوار',
      'view departments': 'عرض الأقسام',
      'create departments': 'إنشاء الأقسام',
      'edit departments': 'تعديل الأقسام',
      'delete departments': 'حذف الأقسام',
      'manage departments': 'إدارة الأقسام',
      'view reports': 'عرض التقارير',
      'create reports': 'إنشاء التقارير',
      'view analytics': 'عرض التحليلات',
      'manage settings': 'إدارة الإعدادات',
      'system admin': 'مسؤول النظام',
      "Keep everyone informed by recording influence, communication preferences, and expectations for each stakeholder.": "حافظ على إطلاع الجميع من خلال تسجيل التأثير وتفضيلات الاتصال والتوقعات لكل صاحب مصلحة.",
      "Save Stakeholder": "حفظ صاحب المصلحة",
      "Total allocated milestone budget cannot exceed the project budget": "إجمالي ميزانية المعالم المخصصة لا يمكن أن تتجاوز ميزانية المشروع",
      "Allocate the remaining project budget of": "تخصيص ميزانية المشروع المتبقية قدرها",
      "to milestones before submitting Phase 2": "إلى المعالم قبل تقديم المرحلة 2",
      "Reduce milestone allocation by": "تقليل تخصيص المعالم بمقدار",
      "to match the project budget": "لمطابقة ميزانية المشروع",
      "Add at least one milestone and allocate the full project budget to proceed": "إضافة معلم واحد على الأقل وتخصيص الميزانية الكاملة للمشروع للمتابعة",
      
      // Role Names and Descriptions
      'Manager': 'مدير',
      'Admin': 'مسؤول',
      'Project and team management with reporting access': 'إدارة المشاريع والفريق مع إمكانية الوصول إلى التقارير',
      'Full system access with all administrative privileges': 'الوصول الكامل للنظام مع جميع الصلاحيات الإدارية',
      "Submit Phase 2 for Approval": "تقديم المرحلة 2 للموافقة",
      
      // Permissions Management Page
      'Permissions Management': 'إدارة الصلاحيات',
      'Manage system permissions and access control. Permissions define specific actions users can perform.': 'إدارة صلاحيات النظام والتحكم في الوصول. تحدد الصلاحيات الإجراءات المحددة التي يمكن للمستخدمين تنفيذها.',
      'Create Permission': 'إنشاء صلاحية',
      'Create a new permission': 'إنشاء صلاحية جديدة',
      'Total Permissions': 'إجمالي الصلاحيات',
      'Total number of permissions in the system': 'إجمالي عدد الصلاحيات في النظام',
      'Number of different permission categories': 'عدد فئات الصلاحيات المختلفة',
      'Permissions currently assigned to roles': 'الصلاحيات المعينة حالياً للأدوار',
      'Search permissions by name or description...': 'البحث عن الصلاحيات بالاسم أو الوصف...',
      'Filter by Category': 'تصفية حسب الفئة',
      'All Categories': 'جميع الفئات',
      'Getting Started with Permissions': 'البدء مع الصلاحيات',
      'Permissions are individual actions that can be performed in the system. Group related permissions together and assign them to roles.': 'الصلاحيات هي إجراءات فردية يمكن تنفيذها في النظام. قم بتجميع الصلاحيات ذات الصلة معاً وتعيينها للأدوار.',
      'Create specific permissions for each action (e.g., "view users", "edit users")': 'إنشاء صلاحيات محددة لكل إجراء (مثل "عرض المستخدمين"، "تعديل المستخدمين")',
      'Organize permissions by category for easy management': 'تنظيم الصلاحيات حسب الفئة لسهولة الإدارة',
      'Assign permissions to roles rather than individual users': 'تعيين الصلاحيات للأدوار بدلاً من المستخدمين الأفراد',
      'No Permissions Found': 'لم يتم العثور على صلاحيات',
      'No permissions match your search': 'لا توجد صلاحيات تطابق بحثك',
      'No description available': 'لا يوجد وصف متاح',
      'Assigned to Roles': 'معينة للأدوار',
      'Number of roles using this permission': 'عدد الأدوار التي تستخدم هذه الصلاحية',
      'Unassigned': 'غير معينة',
      'Edit Permission': 'تعديل الصلاحية',
      'Permission Name': 'اسم الصلاحية',
      'e.g., view-users, edit-users, delete-users': 'مثال: view-users, edit-users, delete-users',
      'Use lowercase with hyphens (e.g., view-users)': 'استخدم الأحرف الصغيرة مع الشرطات (مثل: view-users)',
      'Select a category': 'اختر فئة',
      'Describe what this permission allows users to do...': 'صف ما تسمح هذه الصلاحية للمستخدمين بفعله...',
      'Provide a clear description of what this permission controls': 'قدم وصفاً واضحاً لما تتحكم به هذه الصلاحية',
      'Permission name is required': 'اسم الصلاحية مطلوب',
      'Category is required': 'الفئة مطلوبة',
      'Permission updated successfully': 'تم تحديث الصلاحية بنجاح',
      'Permission created successfully': 'تم إنشاء الصلاحية بنجاح',
      'Failed to save permission': 'فشل في حفظ الصلاحية',
      'Permission deleted successfully': 'تم حذف الصلاحية بنجاح',
      'Failed to delete permission': 'فشل في حذف الصلاحية',
      'Failed to load permissions': 'فشل في تحميل الصلاحيات',
      'Roles Using This Permission': 'الأدوار التي تستخدم هذه الصلاحية',
      'This permission is assigned to {count} role(s):': 'هذه الصلاحية معينة لـ {count} دور:',
      'This permission is not assigned to any roles yet.': 'هذه الصلاحية غير معينة لأي أدوار بعد.',
      'Number of users with this role': 'عدد المستخدمين الذين لديهم هذا الدور',
      'Are you sure you want to delete the permission': 'هل أنت متأكد من حذف الصلاحية',
      'This action cannot be undone. The permission will be removed from all roles.': 'لا يمكن التراجع عن هذا الإجراء. ستتم إزالة الصلاحية من جميع الأدوار.',
      'View Roles': 'عرض الأدوار',
      'User Management': 'إدارة المستخدمين',
      'Role Management': 'إدارة الأدوار',
      'Permission Management': 'إدارة الصلاحيات',
      'Department Management': 'إدارة الأقسام',
      'Dashboard': 'لوحة التحكم',
      'Reporting': 'التقارير',
      'Settings': 'الإعدادات',
      'Other': 'أخرى',
      'Unknown': 'غير معروف',
      'Invalid Date': 'تاريخ غير صالح',
      'Operation failed': 'فشلت العملية',
      'Delete failed': 'فشل الحذف',

      // Roles Management - missing keys
      'Add New Role': 'إضافة دور جديد',
      'Create New Role': 'إنشاء دور جديد',
      'Update Role': 'تحديث الدور',
      'Assigned Users': 'المستخدمون المعينون',
      'Feature': 'الميزة',
      'perms': 'صلاحية',
      'users': 'مستخدم',
      'selected': 'محدد',
      'entries': 'سجلات',
      'Showing': 'عرض',
      'of': 'من',
      'to': 'إلى',
      'Select All': 'تحديد الكل',
      'Clear All': 'مسح الكل',
      'No roles found': 'لم يتم العثور على أدوار',
      'Get started by creating your first role': 'ابدأ بإنشاء دورك الأول',
      'Create First Role': 'إنشاء الدور الأول',
      'Try a different search term': 'جرب مصطلح بحث مختلف',
      'Manage system roles and their permissions. Each role controls what users can access.': 'إدارة أدوار النظام وصلاحياتها. يتحكم كل دور في ما يمكن للمستخدمين الوصول إليه.',
      'Manage system permissions and see which roles they are assigned to.': 'إدارة صلاحيات النظام ومعرفة الأدوار المعينة لها.',
      'Manage system users and their permissions': 'إدارة مستخدمي النظام وصلاحياتهم',
      'Delete this role?': 'حذف هذا الدور؟',
      'Yes, Delete': 'نعم، حذف',
      'You are about to permanently delete': 'أنت على وشك الحذف الدائم لـ',
      'Users assigned to this role will lose its permissions. This cannot be undone.': 'سيفقد المستخدمون المعينون لهذا الدور صلاحياته. لا يمكن التراجع عن هذا الإجراء.',
      'Are you sure you want to delete': 'هل أنت متأكد من حذف',
      'Showing {first} to {last} of {totalRecords} roles': 'عرض {first} إلى {last} من {totalRecords} دور',
      'permissions from hidden categories are also selected': 'صلاحيات من فئات مخفية محددة أيضاً',
      'Brief description of responsibilities': 'وصف موجز للمسؤوليات',

      // Permissions Management - missing keys
      'Add Permission': 'إضافة صلاحية',
      'Search permissions...': 'البحث في الصلاحيات...',
      'No permissions found': 'لم يتم العثور على صلاحيات',
      'No permissions yet': 'لا توجد صلاحيات بعد',
      'Module': 'الوحدة',
      'Action': 'الإجراء',
      'Guided Builder': 'المنشئ الإرشادي',
      'Format:': 'التنسيق:',
      'Generated permission name:': 'اسم الصلاحية المولّد:',
      'Select or type a module...': 'اختر أو اكتب وحدة...',
      'Select or type a resource... (optional)': 'اختر أو اكتب مرجعاً... (اختياري)',
      'Select a module and action above to generate the permission name': 'اختر الوحدة والإجراء أعلاه لإنشاء اسم الصلاحية',
      'Use dot-notation: module.resource.action (e.g. users.create)': 'استخدم التدوين النقطي: وحدة.مرجع.إجراء (مثال: users.create)',
      'e.g. users.profile.edit': 'مثال: users.profile.edit',
      'e.g. Project Manager': 'مثال: مدير المشروع',
      'By adding the permission name, you might break the system permissions functionality.': 'إضافة اسم الصلاحية يدوياً قد يؤثر على وظائف صلاحيات النظام.',
      'Warning!': 'تحذير!',
      'Permission deleted': 'تم حذف الصلاحية',
      'Permission created': 'تم إنشاء الصلاحية',
      'Permission updated': 'تم تحديث الصلاحية',
      'Assigned to Roles': 'معينة للأدوار',
      'No roles assigned': 'لم يتم تعيين أدوار',
      'No roles': 'لا توجد أدوار',
      'Failed to delete': 'فشل الحذف',
      'Failed to load permissions': 'فشل تحميل الصلاحيات',
      'Failed to save permission': 'فشل حفظ الصلاحية',
      'Permissions Management': 'إدارة الصلاحيات',
      'Manage system permissions and access control. Permissions define specific actions users can perform.': 'إدارة صلاحيات النظام والتحكم في الوصول.',

      // Users Management - missing keys
      'No users found': 'لم يتم العثور على مستخدمين',
      'Not provided': 'غير متوفر',
      'Assign user to a department (optional)': 'تعيين المستخدم لقسم (اختياري)',
      'Enter password': 'أدخل كلمة المرور',
      'Select a department (optional)': 'اختر قسماً (اختياري)',
      'Confirm Delete': 'تأكيد الحذف',
      'Confirm Deletion': 'تأكيد الحذف',
      'Are you sure you want to delete the user': 'هل أنت متأكد من حذف المستخدم',
      'Validation Error': 'خطأ في التحقق',
      'Initialization Error': 'خطأ في التهيئة',
      'Please fill in all required fields': 'يرجى ملء جميع الحقول المطلوبة',
      'Please fix all validation errors before submitting': 'يرجى إصلاح جميع أخطاء التحقق قبل الإرسال',
      'Password Reset': 'إعادة تعيين كلمة المرور',
      'Password reset instructions sent to': 'تم إرسال تعليمات إعادة تعيين كلمة المرور إلى',
      'Reset Password': 'إعادة تعيين كلمة المرور',
      'Inactive': 'غير نشط',
      'No Department': 'بدون قسم',
      'Show': 'عرض',
      'Advanced': 'متقدم',
      'All Roles': 'جميع الأدوار',
      'All Status': 'جميع الحالات',
      'Created Date': 'تاريخ الإنشاء',
      'Success': 'نجاح',
      'Error': 'خطأ',

      // Sidebar & Logout
      'Workspace': 'مساحة العمل',
      'Skip to main content': 'تخطَّ إلى المحتوى الرئيسي',
      'Designed & Developed by': 'صُمم وطُوّر بواسطة',
      'Empower Co. (opens in a new window)': 'شركة إمباور (يفتح في نافذة جديدة)',
      '(opens in a new window)': '(يفتح في نافذة جديدة)',
      'Saudi Tech': 'التقنية السعودية',
      'Logging out...': 'جاري تسجيل الخروج...',
      'Project Type is Research': 'نوع المشروع هو بحثي',
      'You have been logged out successfully': 'تم تسجيل خروجك بنجاح',
      'Error during logout. Please try again.': 'خطأ أثناء تسجيل الخروج. يرجى المحاولة مرة أخرى.',
      
      // Sidebar Menu Items
      'Home': 'الرئيسية',
      'Dashboard': 'لوحة المعلومات',
      'Projects': 'المشاريع',
      'Tasks': 'المهام',
      'Documents': 'المستندات',
      'Reports': 'التقارير',
      'Organizational Structure': 'الهيكل التنظيمي',
      'PMO Library': 'مكتبة PMO',
      'Chat': 'الدردشة',
      'Settings': 'الإعدادات',
      'Logout': 'تسجيل الخروج',
      'Governance': 'الحوكمة',
      'Permissions': 'الصلاحيات',
      "Roles": "الأدوار",

      // Breadcrumbs
      'Home': 'الرئيسية',
      'Create Project': 'إنشاء مشروع',
      'Review Project': 'مراجعة المشروع',
      'Project Detail': 'تفاصيل المشروع',
      'Documents': 'المستندات',
      'Organizational Structure': 'الهيكل التنظيمي',
      'Department Management': 'إدارة الأقسام',
      'Departments': 'الأقسام',
      'Roles & Permissions': 'الأدوار والصلاحيات',
      'External Users': 'المستخدمون الخارجيون',
      'Committee List': 'قائمة اللجان',
      'Council List': 'قائمة المجالس',
      
      // UI Kit Breadcrumbs
      'UI Kit': 'مجموعة واجهة المستخدم',
      'Form Layout': 'تخطيط النموذج',
      'Input': 'إدخال',
      'Button': 'زر',
      'Table': 'جدول',
      'List': 'قائمة',
      'Tree': 'شجرة',
      'Panel': 'لوحة',
      'Overlay': 'تراكب',
      'Media': 'وسائط',
      'Menu': 'قائمة',
      'Message': 'رسالة',
      'File': 'ملف',
      'Charts': 'الرسوم البيانية',
      'Timeline': 'الجدول الزمني',
      'Misc': 'متنوع',
      'Prime Blocks': 'كتل Prime',
      'Free Blocks': 'كتل مجانية',

      // Projects Page
      'Project Management Console': 'وحدة إدارة المشاريع',
      'Manage and track all projects efficiently': 'إدارة وتتبع جميع المشاريع بكفاءة',
      'Projects Overview': 'نظرة عامة على المشاريع',
      'Total Projects': 'إجمالي المشاريع',
      'Active Projects': 'المشاريع النشطة',
      'Completed Projects': 'المشاريع المكتملة',
      'Active': 'نشط',
      'Planning': 'التخطيط',
      'Completed': 'مكتمل',
      'On Hold': 'معلق',
      'Issues': 'التحديات',
      'High': 'عالي',
      'Medium': 'متوسط',
      'Low': 'منخفض',
      'Risks': 'المخاطر',
      'Invoiced': 'مفوتر',
      'Consumed': 'مستهلك',
      'Allocated': 'مخصص',
      'Project by Phases': 'المشاريع حسب المراحل',
      'Initiation': 'البدء',
      'Execution': 'التنفيذ',
      'Closure': 'الإغلاق',
      
      // Project Actions
      'Create Project': 'إنشاء مشروع',
      'Export Data': 'تصدير البيانات',
      'Export': 'تصدير',
      'Export as Excel': 'تصدير كـ Excel',
      'Export as PDF': 'تصدير كـ PDF',
      'Export as Excel': 'تصدير كـ Excel',
      'Export as CSV': 'تصدير كـ CSV',
      'Export Started': 'بدأ التصدير',
      'Exporting projects as {type}...': 'جاري تصدير المشاريع كـ {type}...',
      
      // Filters
      'Filter': 'تصفية',
      'Sort by': 'ترتيب حسب',
      'View Mode': 'وضع العرض',
      'Grid View': 'عرض شبكي',
      'List View': 'عرض قائمة',
      'Filtered Count': 'عدد النتائج المصفاة',
      'of': 'من',
      'results': 'نتيجة',
      
      // Empty States
      'No projects found': 'لم يتم العثور على مشاريع',
      'No projects yet': 'لا توجد مشاريع بعد',
      'Try adjusting your search or filter criteria': 'حاول تعديل البحث أو معايير التصفية',
      'Get started by creating your first project': 'ابدأ بإنشاء مشروعك الأول',
      
      // Project Status
      'Approved': 'تمت الموافقة',
      'Rejected': 'مرفوض',
      'Pending': 'قيد الانتظار',
      'In Progress': 'قيد التنفيذ',
      'Draft': 'مسودة',
      
      // Dialogs
      'Delete Project': 'حذف المشروع',
      'Are you sure you want to delete the project "{name}"?': 'هل أنت متأكد أنك تريد حذف المشروع "{name}"؟',
      'Project Deleted': 'تم حذف المشروع',
      'Project "{name}" has been permanently deleted': 'تم حذف المشروع "{name}" نهائياً',
      'Delete Failed': 'فشل الحذف',
      'Failed to delete project': 'فشل في حذف المشروع',
      
      // Approval Dialog
      'Approve Project Phase': 'الموافقة على مرحلة المشروع',
      'You are approving': 'أنت توافق على',
      'Phase': 'المرحلة',
      "Edit Mode": "وضع التعديل",
      "You are editing Phase 2 (Execution Planning)" : "أنت تقوم بتعديل المرحلة 2 (تخطيط التنفيذ)",
      'for project:': 'للمشروع:',
      'Approval Comments (Optional)': 'تعليقات الموافقة (اختياري)',
      'Add any comments or feedback...': 'أضف أي تعليقات أو ملاحظات...',
      'Approve': 'موافقة',
      'has been approved successfully': 'تمت الموافقة عليه بنجاح',
      'Failed to approve project': 'فشل في الموافقة على المشروع',
      
      // Rejection Dialog
      'Reject Project Phase': 'رفض مرحلة المشروع',
      'You are rejecting': 'أنت ترفض',
      'Rejection Reason': 'سبب الرفض',
      'Please provide a reason for rejection...': 'يرجى تقديم سبب للرفض...',
      'Reject': 'رفض',
      'Validation Error': 'خطأ في التحقق',
      'Please provide a rejection reason': 'يرجى تقديم سبب الرفض',
      'has been rejected': 'تم رفضه',
      'Failed to reject project': 'فشل في رفض المشروع',
      
      // Rejection Details Dialog
      'Rejection Details': 'تفاصيل الرفض',
      "Delete Project": "حذف المشروع",
      'Your project was rejected': 'تم رفض مشروعك',
      'Phase 1 (Project Definition)': 'المرحلة 1 (تعريف المشروع)',
      'Phase 2 (Execution Planning)': 'المرحلة 2 (تخطيط التنفيذ)',
      'was rejected by the administrator. Please review the feedback below and make the necessary changes.': 'تم رفضه من قبل المسؤول. يرجى مراجعة الملاحظات أدناه وإجراء التغييرات اللازمة.',
      'Rejection Reason:': 'سبب الرفض:',
      'No rejection reason provided': 'لم يتم تقديم سبب للرفض',
      'Please click "Edit & Resubmit" to make the required changes and resubmit your project for approval.': 'يرجى النقر على "تعديل وإعادة إرسال" لإجراء التغييرات المطلوبة وإعادة إرسال مشروعك للموافقة.',
      'Close': 'إغلاق',
      'Edit & Resubmit': 'تعديل وإعادة إرسال',
      
      // Stats & Metrics
      'Admin View': 'عرض المسؤول',
      'Project Manager': 'مدير المشروع',
      'Viewing all projects': 'عرض جميع المشاريع',
      'Retry': 'إعادة المحاولة',
      'Failed to load project statistics': 'فشل في تحميل إحصائيات المشروع',
      'Error loading projects': 'خطأ في تحميل المشاريع',
      'Failed to load projects. Please refresh the page.': 'فشل في تحميل المشاريع. يرجى تحديث الصفحة.',
      
      // Project Types
      'Project': 'مشروع',
      'Research': 'بحث',
      
      // Priority Levels
      'Critical': 'حرج',
      'High Priority': 'أولوية عالية',
      'Medium Priority': 'أولوية متوسطة',
      'Low Priority': 'أولوية منخفضة',
      
      // Common Actions
      'Edit': 'تعديل',
      'Delete': 'حذف',
      'View': 'عرض',
      'Manage': 'إدارة',
      'Complete': 'إكمال',
      'Review': 'مراجعة',
      
      // Additional Project Messages
      'Export Started': 'بدأ التصدير',
      'Exporting projects as {type}...': 'جاري تصدير المشاريع بصيغة {type}...',
      'Project Created': 'تم إنشاء المشروع',
      'Project "{name}" has been created successfully': 'تم إنشاء المشروع "{name}" بنجاح',
      'Are you sure you want to delete the project "{name}"?': 'هل أنت متأكد من حذف المشروع "{name}"؟',
      'Delete Project': 'حذف المشروع',
      'Project Deleted': 'تم حذف المشروع',
      'Project "{name}" has been permanently deleted': 'تم حذف المشروع "{name}" نهائياً',
      'Delete Failed': 'فشل الحذف',
      'Failed to delete project': 'فشل حذف المشروع',
      'Approved': 'تمت الموافقة',
      'Phase {phase} has been approved successfully': 'تمت الموافقة على المرحلة {phase} بنجاح',
      'Error': 'خطأ',
      'Failed to approve project': 'فشلت الموافقة على المشروع',
      'Validation Error': 'خطأ في التحقق',
      'Please provide a rejection reason': 'يرجى تقديم سبب الرفض',
      'Rejected': 'تم الرفض',
      'Phase {phase} has been rejected': 'تم رفض المرحلة {phase}',
      'Failed to reject project': 'فشل رفض المشروع',
      'Failed to load projects. Please refresh the page.': 'فشل تحميل المشاريع. يرجى تحديث الصفحة.',
      'You are viewing': 'أنت تشاهد',
      'your own projects': 'مشاريعك الخاصة',
      'Admins can view all projects in the system.': 'يمكن للمسؤولين مشاهدة جميع المشاريع في النظام.',
      'No projects yet': 'لا توجد مشاريع بعد',
      'Try adjusting your search or filter criteria': 'حاول تعديل معايير البحث أو التصفية',
      'Get started by creating your first project': 'ابدأ بإنشاء مشروعك الأول',
      'Create Project': 'إنشاء مشروع',
      'You are approving': 'أنت توافق على',
      'for project:': 'للمشروع:',
      'Approval Comments (Optional)': 'تعليقات الموافقة (اختياري)',
      'Add any comments or feedback...': 'أضف أي تعليقات أو ملاحظات...',
      'You are rejecting': 'أنت ترفض',
      'Rejection Reason': 'سبب الرفض',
      'Please provide a reason for rejection...': 'يرجى تقديم سبب الرفض...',
      'Your project was rejected': 'تم رفض مشروعك',
      'Phase 1 (Project Definition)': 'المرحلة 1 (تعريف المشروع)',
      'Phase 2 (Execution Planning)': 'المرحلة 2 (تخطيط التنفيذ)',
      'was rejected by the administrator. Please review the feedback below and make the necessary changes.': 'تم رفضه من قبل المسؤول. يرجى مراجعة الملاحظات أدناه وإجراء التغييرات اللازمة.',
      'Rejection Reason:': 'سبب الرفض:',
      'Please click "Edit & Resubmit" to make the required changes and resubmit your project for approval.': 'يرجى النقر على "تعديل وإعادة تقديم" لإجراء التغييرات المطلوبة وإعادة تقديم مشروعك للموافقة.',
      'Edit & Resubmit': 'تعديل وإعادة تقديم',
      'Projects Overview': 'نظرة عامة على المشاريع',
      'Project by Phases': 'المشاريع حسب المراحل',
      'Initiation': 'البدء',
      'Execution': 'التنفيذ',
      'Closure': 'الإغلاق',
      
      // CreateProjectForm translations
      'Project Name': 'اسم المشروع',
      'Enter project name...': 'أدخل اسم المشروع...',
      'Project Type': 'نوع المشروع',
      'Select project type': 'اختر نوع المشروع',
      'Internal': 'داخلي',
      'Strategic': 'استراتيجي',
      'External': 'خارجي',
      'Project Status': 'حالة المشروع',
      'Select status': 'اختر الحالة',
      'Project Description': 'وصف المشروع',
      'Describe the project goals, objectives, and scope...': 'صف أهداف المشروع وغاياته ونطاقه...',
      'Start Date': 'تاريخ البدء',
      'Select start date': 'اختر تاريخ البدء',
      'End Date': 'تاريخ الانتهاء',
      'Select end date': 'اختر تاريخ الانتهاء',
      'Project Owner': 'مالك المشروع',
      'Select project owner': 'اختر مالك المشروع',
      'Sponsoring Department': 'القسم الراعي',
      'Select department (optional)': 'اختر القسم (اختياري)',
      'Priority': 'الأولوية',
      'Select priority': 'اختر الأولوية',
      'Initial Budget': 'الميزانية المبدئية',
      'Enter initial budget': 'أدخل الميزانية المبدئية',
      'Project name is required': 'اسم المشروع مطلوب',
      'Project type is required': 'نوع المشروع مطلوب',
      'Project description is required': 'وصف المشروع مطلوب',
      'Start date is required': 'تاريخ البدء مطلوب',
      'End date is required': 'تاريخ الانتهاء مطلوب',
      'Project owner is required': 'مالك المشروع مطلوب',
      'Project status is required': 'حالة المشروع مطلوبة',
      'End date must be after start date': 'يجب أن يكون تاريخ الانتهاء بعد تاريخ البدء',
      
      // ProjectCard translations
      'Progress': 'التقدم',
      'Tasks': 'المهام',
      'Due': 'الموعد النهائي',
      'Risk': 'المخاطرة',
      'Waiting for Approval...': 'في انتظار الموافقة...',
      'Waiting for Final Approval...': 'في انتظار الموافقة النهائية...',
      'Waiting for manager to resubmit': 'في انتظار إعادة التقديم من المدير',
      'View Comments': 'عرض التعليقات',
      'View Approval Comments': 'عرض تعليقات الموافقة',
      'Complete Phase 2': 'إكمال المرحلة 2',
      'Phase 1 Approved - Waiting for Manager...': 'تمت الموافقة على المرحلة 1 - في انتظار المدير...',
      'Phase 1 Approval Comments': 'تعليقات الموافقة على المرحلة 1',
      'View Phase 2 Comments': 'عرض تعليقات المرحلة 2',
      'Phase 2 Approval Comments': 'تعليقات الموافقة على المرحلة 2',
      'Manage Project': 'إدارة المشروع',
  'Continue Draft': 'متابعة المسودة',
  'Delete Draft': 'حذف المسودة',
      'Edit Phase 2 & Resubmit': 'تعديل المرحلة 2 وإعادة التقديم',
      'Action Not Allowed': 'الإجراء غير مسموح',
      'You can only edit draft projects': 'يمكنك فقط تعديل المشاريع المسودة',
      'You can only delete draft projects': 'يمكنك فقط حذف المشاريع المسودة',
      'Pending for Phase 1': 'في انتظار المرحلة 1',
      'Pending for Phase 2': 'في انتظار المرحلة 2',
  'Pending Phase 1': 'في انتظار المرحلة 1',
  'Pending Phase 2': 'في انتظار المرحلة 2',
      'Approved Phase 1': 'تمت الموافقة على المرحلة 1',
      'Approved Phase 2': 'تمت الموافقة على المرحلة 2',
  'Phase 1 Approved': 'تمت الموافقة على المرحلة 1',
  'Phase 2 Approved': 'تمت الموافقة على المرحلة 2',
      'Rejected Phase 1': 'تم رفض المرحلة 1',
      'Rejected Phase 2': 'تم رفض المرحلة 2',
  'Phase 1 Rejected': 'تم رفض المرحلة 1',
  'Phase 2 Rejected': 'تم رفض المرحلة 2',
      'In Progress': 'قيد التنفيذ',
      
      // ProjectContent translations
      'Adding milestone for project': 'إضافة معلم للمشروع',
      'Viewing all activities for project': 'عرض جميع الأنشطة للمشروع',
      'Viewing analytics for project': 'عرض التحليلات للمشروع',
      'Milestone Saved': 'تم حفظ المعلم',
      '{name} has been saved successfully': 'تم حفظ {name} بنجاح',
      
      // ProjectHeader translations
      'Project Management': 'إدارة المشاريع',
      'Manage and monitor all project activities': 'إدارة ومراقبة جميع أنشطة المشروع',
      'Filter Projects': 'تصفية المشاريع',
      'Advanced Filters': 'تصفية متقدمة',
      'Export': 'تصدير',
      'New Project': 'مشروع جديد',
      'Export as PDF': 'تصدير بصيغة PDF',
      'Export as Excel': 'تصدير بصيغة Excel',
      'Export as CSV': 'تصدير بصيغة CSV',
      
      // ProjectManagementFilters translations
      'Search projects by name, description, or owner...': 'البحث في المشاريع بالاسم أو الوصف أو المالك...',
      'Filter by Status': 'تصفية حسب الحالة',
      'Filter by Priority': 'تصفية حسب الأولوية',
      'Filter by Department': 'تصفية حسب القسم',
      'Sort by': 'ترتيب حسب',
      'Reset Filters': 'إعادة تعيين التصفية',
      'Showing': 'عرض',
      'of': 'من',
      'projects': 'مشاريع',
      'Grid View': 'عرض شبكي',
      'List View': 'عرض قائمة',
      'All Departments': 'جميع الأقسام',
      'IT': 'تقنية المعلومات',
      'Marketing': 'التسويق',
      'Finance': 'المالية',
      'HR': 'الموارد البشرية',
      'Operations': 'العمليات',
      'Search programs by name, description, or owner...': 'ابحث عن البرامج بالاسم أو الوصف أو المالك...',
      'Name (A-Z)': 'الاسم (أ-ي)',
      'Name (Z-A)': 'الاسم (ي-أ)',
      'Date Created': 'تاريخ الإنشاء',
      'Due Date': 'تاريخ الاستحقاق',
      
      // ProgramManagementHeader translations
      'Program Management': 'إدارة البرامج',
      'Manage and track all programs efficiently': 'إدارة وتتبع جميع البرامج بكفاءة',
      'Create Program': 'إنشاء برنامج',
      'Total Programs': 'إجمالي البرامج',
      'Active Programs': 'البرامج النشطة',
      'Completed Programs': 'البرامج المكتملة',
      'Draft Programs': 'البرامج المسودة',
      'Closed Programs': 'البرامج المغلقة',

      // Program Create/Edit Form translations
      'Edit Program': 'تعديل البرنامج',
      'Create New Program': 'إنشاء برنامج جديد',
      'Update your program details': 'تحديث تفاصيل برنامجك',
      'Define your program scope': 'حدد نطاق برنامجك',
      'Back to Programs': 'العودة إلى البرامج',
      'Program Definition': 'تعريف البرنامج',
      'Define the core program parameters and objectives': 'حدد المعايير الأساسية والأهداف للبرنامج',
      'Loading program data...': 'جاري تحميل بيانات البرنامج...',
      'Program Name': 'اسم البرنامج',
      'Enter program name': 'أدخل اسم البرنامج',
      'Program Code': 'رمز البرنامج',
      'Program Sponsor': 'راعي البرنامج',
      'Enter program sponsor name': 'أدخل اسم راعي البرنامج',
      'Program Director': 'مدير البرنامج',
      'Enter program director name': 'أدخل اسم مدير البرنامج',
      'Program Start Date': 'تاريخ بدء البرنامج',
      'Program End Date': 'تاريخ انتهاء البرنامج',
      'Program Classification': 'تصنيف البرنامج',
      'Select program classification': 'اختر تصنيف البرنامج',
      'Program Owner': 'مالك البرنامج',
      'Enter program owner name': 'أدخل اسم مالك البرنامج',
      'Select projects': 'اختر المشاريع',
      'projects selected': 'مشاريع محددة',
      'Supplementary program details and documentation': 'تفاصيل ووثائق البرنامج التكميلية',
      'Program Justification': 'مبررات البرنامج',
      'Describe the justification and rationale for this program...': 'اصف مبررات وأسباب هذا البرنامج...',
      'Program Objectives': 'أهداف البرنامج',
      'Define the key objectives of this program...': 'حدد الأهداف الرئيسية لهذا البرنامج...',
      'Program Scope': 'نطاق البرنامج',
      'Define the boundaries and scope of this program...': 'حدد حدود ونطاق هذا البرنامج...',
      'Enter any additional notes, comments, or special considerations for this program...': 'أدخل أي ملاحظات إضافية أو تعليقات أو اعتبارات خاصة لهذا البرنامج...',
      'Submit Program': 'تقديم البرنامج',
      'Update & Resubmit': 'تحديث وإعادة التقديم',
      'Program draft updated successfully': 'تم تحديث مسودة البرنامج بنجاح',
      'Program has been saved as draft': 'تم حفظ البرنامج كمسودة',
      'Please fill all required fields correctly': 'يرجى ملء جميع الحقول المطلوبة بشكل صحيح',
      'Program updated and resubmitted': 'تم تحديث البرنامج وإعادة تقديمه',
      'Program created successfully': 'تم إنشاء البرنامج بنجاح',
      'Program updated and auto-approved': 'تم تحديث البرنامج والموافقة التلقائية',
      'Program created and auto-approved': 'تم إنشاء البرنامج والموافقة التلقائية',
      'Program resubmitted for PMO review': 'تم إعادة تقديم البرنامج لمراجعة مكتب إدارة المشاريع',
      'Program submitted for PMO review': 'تم تقديم البرنامج لمراجعة مكتب إدارة المشاريع',
      'Failed to create program': 'فشل في إنشاء البرنامج',
      'Program not found': 'البرنامج غير موجود',
      'Failed to load program data': 'فشل في تحميل بيانات البرنامج',

      // Program Review translations (review.vue)
      'Program Review': 'مراجعة البرنامج',
      'Review and approve program submission': 'مراجعة والموافقة على تقديم البرنامج',
      'Review Program Definition': 'مراجعة تعريف البرنامج',
      'Program Information': 'معلومات البرنامج',
      'Submitted': 'مقدم',
      'Submitted by': 'مقدم من',
      'Manager': 'المدير',
      'Sponsor': 'الراعي',
      'Director': 'المدير',
      'Classification': 'التصنيف',
      'Justification': 'المبررات',
      'Objectives': 'الأهداف',
      'Scope': 'النطاق',
      'Select Program Classification': 'اختر تصنيف البرنامج',
      '(a) Complex, Large, and Ambiguous': '(أ) معقد وكبير وغامض',
      '(a) Program is complex, large, and ambiguous in scope': '(أ) البرنامج معقد وكبير وغامض في النطاق',
      '(b) Moderately Complex and Medium': '(ب) معقد ومتوسط بشكل معتدل',
      '(b) Program is moderately complex and medium in size': '(ب) البرنامج معقد بشكل معتدل ومتوسط الحجم',
      '(c) Simple, Small, and Clear': '(ج) بسيط وصغير وواضح',
      '(c) Program is simple, small, and clear': '(ج) البرنامج بسيط وصغير وواضح',
      'Your Decision': 'قرارك',
      'Approve or reject the program definition and objectives': 'الموافقة على أو رفض تعريف البرنامج والأهداف',
      'Approval Comments': 'تعليقات الموافقة',
      'Add any comments or feedback (optional)...': 'أضف أي تعليقات أو ملاحظات (اختياري)...',
      'Review Status': 'حالة المراجعة',
      'Previous Rejection': 'الرفض السابق',
      'Rejection Reason': 'سبب الرفض',
      'Approve Program': 'الموافقة على البرنامج',
      'Reject Program': 'رفض البرنامج',
      'Confirm Approval': 'تأكيد الموافقة',
      'Are you sure you want to approve this program?': 'هل أنت متأكد من أنك تريد الموافقة على هذا البرنامج؟',
      'Yes, Approve': 'نعم، أوافق',
      'Confirm Rejection': 'تأكيد الرفض',
      'Are you sure you want to reject this program? The manager will need to resubmit.': 'هل أنت متأكد من أنك تريد رفض هذا البرنامج؟ سيحتاج المدير إلى إعادة التقديم.',
      'Please provide a reason for rejection (required)...': 'يرجى تقديم سبب للرفض (مطلوب)...',
      'Please provide a reason for rejection': 'يرجى تقديم سبب للرفض',
      'Yes, Reject': 'نعم، ارفض',
      'Please select a program classification before approving': 'يرجى اختيار تصنيف البرنامج قبل الموافقة',
      'Program has been approved successfully': 'تمت الموافقة على البرنامج بنجاح',
      'Failed to approve program': 'فشل في الموافقة على البرنامج',
      'Program has been rejected': 'تم رفض البرنامج',
      'Failed to reject program': 'فشل في رفض البرنامج',

      // ProjectTable translations
      'Project': 'المشروع',
      'Status': 'الحالة',
      'Owner': 'المالك',
      'Team': 'الفريق',
      'Budget': 'الميزانية',
      'used': 'مستخدم',
      'Actions': 'الإجراءات',
      'View Project': 'عرض المشروع',
      'Edit Project': 'تعديل المشروع',
      'Delete Project': 'حذف المشروع',
      '{days} days overdue': '{days} يوم متأخر',
      'Due today': 'اليوم هو الموعد النهائي',
      'Due tomorrow': 'غداً هو الموعد النهائي',
      '{days} days left': '{days} يوم متبقي',
      'Phase': 'المرحلة',

      // ProjectTabs translations
      'Project Navigation': 'التنقل في المشروع',
      'Refresh Data': 'تحديث البيانات',
      'Tab Settings': 'إعدادات التبويبات',
      'Gates': 'البوابات',
      'Project phases: Initiation, Planning, Execution, Closure': 'مراحل المشروع: البدء، التخطيط، التنفيذ، الإغلاق',
      'Stakeholders Register': 'سجل أصحاب المصلحة',
      'Risks Register': 'سجل المخاطر',
      'Issues Register': 'سجل التحديات',
      'Changes Log': 'سجل التغييرات',
      'Escalations': 'التصعيدات',
      'Refreshed': 'تم التحديث',
      'Project data has been refreshed successfully': 'تم تحديث بيانات المشروع بنجاح',
      'Failed to refresh project data': 'فشل تحديث بيانات المشروع',
      'Tab Visibility': 'رؤية التبويبات',
      'Auto Refresh': 'التحديث التلقائي',
      'Enable auto refresh': 'تفعيل التحديث التلقائي',
      'Refresh interval (minutes)': 'فترة التحديث (بالدقائق)',
      '1 min': '1 دقيقة',
      '5 min': '5 دقائق',
      '10 min': '10 دقائق',
      '15 min': '15 دقيقة',
      '30 min': '30 دقيقة',
      'Display Options': 'خيارات العرض',
      'Show tab counts': 'إظهار عدد العناصر في التبويبات',
      'Show progress indicator': 'إظهار مؤشر التقدم',
      'Reset': 'إعادة تعيين',
      'Apply': 'تطبيق',
      'Settings Reset': 'تم إعادة تعيين الإعدادات',
      'All settings have been reset to default': 'تم إعادة تعيين جميع الإعدادات إلى الافتراضية',
      'Settings Applied': 'تم تطبيق الإعدادات',
      'Tab settings have been saved successfully': 'تم حفظ إعدادات التبويبات بنجاح',
      'Overview': 'نظرة عامة',
      'Schedule': 'الجدول الزمني',
      'Resources': 'الموارد',
      'Financials': 'الشؤون المالية',
      'Financial': 'المالية',
      'Benefits': 'المنافع',
      'Lessons': 'الدروس',

      // ProjectBenefits translations
      'Benefits Realization': 'تحقيق المنافع',
      'Track and measure project benefits': 'تتبع وقياس منافع المشروع',
      'Add Benefit': 'إضافة منفعة',
      'Project Benefits': 'منافع المشروع',
      '{count} benefits': '{count} منفعة',
      'No benefits yet': 'لا توجد منافع بعد',
      'Start by adding your first project benefit': 'ابدأ بإضافة منفعة المشروع الأولى',
      'Add First Benefit': 'إضافة المنفعة الأولى',
      'More Actions': 'المزيد من الإجراءات',
      'View': 'عرض',
      'Edit': 'تعديل',
      'Export to CSV': 'تصدير إلى CSV',
      'Table Settings': 'إعدادات الجدول',
      'Refresh': 'تحديث',
      'No benefits defined': 'لم يتم تعريف منافع',
      'Start tracking your project benefits and value realization': 'ابدأ في تتبع منافع المشروع وتحقيق القيمة',
      'Benefit Title': 'عنوان المنفعة',
      'Category': 'الفئة',
      'Target Value': 'القيمة المستهدفة',
      'Target Realization': 'التحقيق المستهدف',
      'Measurement Criteria': 'معايير القياس',
      'Edit Project Benefit': 'تعديل منفعة المشروع',
      'Add Project Benefit': 'إضافة منفعة مشروع',
      'Enter benefit title': 'أدخل عنوان المنفعة',
      'Select category': 'اختر الفئة',
      'Select status': 'اختر الحالة',
      'e.g., 50,000, 25%, 100 users': 'مثال: 50,000، 25%، 100 مستخدم',
      'Target Realization Date': 'تاريخ التحقيق المستهدف',
      'Select target date': 'اختر التاريخ المستهدف',
      'Project dates not set': 'لم يتم تعيين تواريخ المشروع',
      'Must be between {start} and {end}': 'يجب أن يكون بين {start} و {end}',
      'Describe the benefit and how it will be measured': 'اصف المنفعة وكيف سيتم قياسها',
      'Define how this benefit will be measured and tracked': 'حدد كيف سيتم قياس وتتبع هذه المنفعة',
      'Update Benefit': 'تحديث المنفعة',
      'Benefit Details': 'تفاصيل المنفعة',
      'Edit Benefit': 'تعديل المنفعة',
      'Close': 'إغلاق',
      'Column Visibility': 'رؤية الأعمدة',
      'Table Behavior': 'سلوك الجدول',
      'Rows per Page': 'الصفوف لكل صفحة',
      '10 rows': '10 صفوف',
      '25 rows': '25 صف',
      '50 rows': '50 صف',
      '100 rows': '100 صف',
      'Striped Rows': 'صفوف مخططة',
      'Grid Lines': 'خطوط الشبكة',
      'Resizable Columns': 'أعمدة قابلة لتغيير الحجم',
      'Layout Settings': 'إعدادات التخطيط',
      'Table Size': 'حجم الجدول',
      'Small': 'صغير',
      'Normal': 'عادي',
      'Large': 'كبير',
      'Selection Mode': 'وضع التحديد',
      'None': 'لا شيء',
      'Single': 'مفرد',
      'Multiple': 'متعدد',
      'Show Row Numbers': 'إظهار أرقام الصفوف',
      'Scrollable': 'قابل للتمرير',
      'Sortable Columns': 'أعمدة قابلة للفرز',
      'Reset to Default': 'إعادة تعيين إلى الافتراضي',
      'Apply Settings': 'تطبيق الإعدادات',
      'View Details': 'عرض التفاصيل',
      'Delete Benefit': 'حذف المنفعة',
      'Efficiency': 'الكفاءة',
      'Customer Experience': 'تجربة العملاء',
      'Growth': 'النمو',
      'Process': 'العملية',
      'Revenue': 'الإيرادات',
      'Cost Savings': 'توفير التكاليف',
      'Quality': 'الجودة',
      'Innovation': 'الابتكار',
      'Compliance': 'الامتثال',
      'Realized': 'محقق',
      'Export Successful': 'نجح التصدير',
      'Benefits data exported successfully': 'تم تصدير بيانات المنافع بنجاح',
      'Export Failed': 'فشل التصدير',
      'Failed to export benefits data': 'فشل تصدير بيانات المنافع',
      'Table settings have been applied successfully': 'تم تطبيق إعدادات الجدول بنجاح',
      'Data Refreshed': 'تم تحديث البيانات',
      'Benefits data has been refreshed': 'تم تحديث بيانات المنافع',
      'Failed to load benefits': 'فشل تحميل المنافع',
      'Benefit Added': 'تمت إضافة المنفعة',
      'Benefit created successfully': 'تم إنشاء المنفعة بنجاح',
      'Failed to create benefit': 'فشل إنشاء المنفعة',
      'Validation Error': 'خطأ في التحقق',
      'Please fill in all required fields': 'يرجى ملء جميع الحقول المطلوبة',
      'Benefit Updated': 'تم تحديث المنفعة',
      'Benefit updated successfully': 'تم تحديث المنفعة بنجاح',
      'Failed to update benefit': 'فشل تحديث المنفعة',
      'Are you sure you want to delete "{title}"?': 'هل أنت متأكد أنك تريد حذف "{title}"؟',
      'Benefit Deleted': 'تم حذف المنفعة',
      '{title} has been deleted': 'تم حذف {title}',
      'Failed to delete benefit': 'فشل حذف المنفعة',
      'No description provided': 'لم يتم تقديم وصف',
      'No criteria defined': 'لم يتم تعريف معايير',

      // Program Benefits translations
      'Program Benefits': 'منافع البرنامج',
      'Track and measure program benefits': 'تتبع وقياس منافع البرنامج',
      'Start tracking your program benefits and value realization': 'ابدأ بتتبع منافع برنامجك وتحقيق القيمة',
      'Start by adding your first program benefit': 'ابدأ بإضافة أول منفعة للبرنامج',
      'Add Program Benefit': 'إضافة منفعة البرنامج',
      'Edit Program Benefit': 'تعديل منفعة البرنامج',
      'Benefit Name': 'اسم المنفعة',
      'Benefit name is required': 'اسم المنفعة مطلوب',
      'Benefit name must be at least 3 characters': 'يجب أن يكون اسم المنفعة 3 أحرف على الأقل',
      'Enter benefit name': 'أدخل اسم المنفعة',
      'Describe the benefit': 'صف المنفعة',
      'Type of Benefit': 'نوع المنفعة',
      'Type of benefit is required': 'نوع المنفعة مطلوب',
      'Select type of benefit': 'اختر نوع المنفعة',
      'Beneficiary': 'المستفيد',
      'Enter beneficiary': 'أدخل المستفيد',
      'Date of Transfer': 'تاريخ التحويل',
      'Date of Transfer of Benefit': 'تاريخ تحويل المنفعة',
      'Add any additional notes': 'أضف أي ملاحظات إضافية',
      'benefits': 'منافع',
      'Reduce costs': 'تقليل التكاليف',
      'Increase revenue': 'زيادة الإيرادات',
      'Improve operational efficiency': 'تحسين الكفاءة التشغيلية',
      'Improve product quality for beneficiaries': 'تحسين جودة المنتج للمستفيدين',
      'Improve beneficiary satisfaction': 'تحسين رضا المستفيدين',
      'Improve education and training': 'تحسين التعليم والتدريب',
      'Enhancing skills and capabilities': 'تعزيز المهارات والقدرات',
      'Enhancing economic sustainability': 'تعزيز الاستدامة الاقتصادية',
      'Cancel': 'إلغاء',
      'Status': 'الحالة',
      'Description': 'الوصف',

      // ProjectChanges translations
      'Change Request': 'طلب تغيير',
      'Track and approve project changes': 'تتبع والموافقة على تغييرات المشروع',
      'Track and approve program changes': 'تتبع والموافقة على تغييرات البرنامج',
      'Program change requests': 'طلبات تغيير البرنامج',
      'Program Navigation': 'التنقل في البرنامج',
      'Program summary and key metrics': 'ملخص البرنامج والمقاييس الرئيسية',
      'Projects within this program': 'المشاريع ضمن هذا البرنامج',
      'Program schedule hierarchy': 'التسلسل الهرمي لجدول البرنامج',
      'Program risk register': 'سجل مخاطر البرنامج',
      'Program issues tracker': 'متتبع مشاكل البرنامج',
      'Program escalations': 'تصعيدات البرنامج',
      'Benefits realization tracking': 'تتبع تحقيق الفوائد',
      'Program lessons learned': 'الدروس المستفادة من البرنامج',
      'Program closure': 'إغلاق البرنامج',
      'Failed to refresh program data': 'فشل في تحديث بيانات البرنامج',
      'Comprehensive view of program details and metrics': 'عرض شامل لتفاصيل ومقاييس البرنامج',
      'Management': 'الإدارة',
      'Program Description': 'وصف البرنامج',
      'Program Summary': 'ملخص البرنامج',
      'Program Projects': 'مشاريع البرنامج',
      'Manage all projects under this program': 'إدارة جميع المشاريع ضمن هذا البرنامج',
      'No projects are linked to this program': 'لا توجد مشاريع مرتبطة بهذا البرنامج',
      'No projects match your filters': 'لا توجد مشاريع تطابق الفلاتر',
      'Table View': 'عرض الجدول',
      'All Priorities': 'جميع الأولويات',
      'Search projects...': 'البحث عن مشاريع...',
      'Open Project': 'فتح المشروع',
      'Failed to load projects': 'فشل في تحميل المشاريع',
      'Program Schedule': 'جدول البرنامج',
      'Hierarchical view of program projects, deliverables, and tasks': 'عرض هرمي لمشاريع البرنامج والمخرجات والمهام',
      'Export CSV': 'تصدير CSV',
      'No Schedule Data': 'لا توجد بيانات جدول',
      'No projects, deliverables, or tasks found for this program.': 'لم يتم العثور على مشاريع أو مخرجات أو مهام لهذا البرنامج.',
      'Failed to load program schedule': 'فشل في تحميل جدول البرنامج',
      'Schedule exported successfully': 'تم تصدير الجدول بنجاح',
      'Change Request Plan': 'خطة طلب التغيير',
      'Change Requests': 'طلبات التغيير',
      '{count} requests': '{count} طلب',
      '{count} documents': '{count} مستند',
      'No change requests': 'لا توجد طلبات تغيير',
      'No change requests have been submitted yet': 'لم يتم تقديم طلبات تغيير بعد',
      'Change Request': 'طلب التغيير',
      'Edit Change Request': 'تعديل طلب التغيير',
      'Edit & Resubmit Change Request': 'تعديل وإعادة تقديم طلب التغيير',
      'Rejection Feedback': 'ملاحظات الرفض',
      'Rejected by': 'تم الرفض بواسطة',
      'on': 'في',
      'Scope': 'النطاق',
      'Technical': 'التقني',
      'Design': 'التصميم',
      'Security': 'الأمان',
      'Performance': 'الأداء',
      'Timeline': 'الجدول الزمني',
      'Resource': 'المورد',
      'Impact': 'التأثير',
      'Cost Impact': 'تأثير التكلفة',
      'Cost Impact (SAR)': 'تأثير التكلفة (ريال)',
      'N/A': 'غير متوفر',
      'Justification': 'المبرر',
      'Implementation Plan': 'خطة التنفيذ',
      'No description': 'لا يوجد وصف',
      'No justification': 'لا يوجد مبرر',
      'Not specified': 'غير محدد',
      'Requested By': 'طلب من قبل',
      'Request Date': 'تاريخ الطلب',
      'Approved By': 'تمت الموافقة من قبل',
      'Approval Comment': 'تعليق الموافقة',
      'Rejected By': 'رفض من قبل',
      'Approval Date': 'تاريخ الموافقة',
      'Rejection Date': 'تاريخ الرفض',
      'Draft': 'مسودة',
      'Edit Request': 'تعديل الطلب',
      'Approve Change': 'الموافقة على التغيير',
      'Reject Change': 'رفض التغيير',
      'View Rejection Feedback': 'عرض ملاحظات الرفض',
      'View Approval Comment': 'عرض تعليق الموافقة',
      'Reopen for Review': 'إعادة فتح للمراجعة',
      'Delete Change': 'حذف التغيير',
      
      // Dialog headers and actions
      'Edit Change Request': 'تعديل طلب التغيير',
      'Reject Change Request': 'رفض طلب التغيير',
      'Submit Request': 'تقديم الطلب',
      'Save as Draft': 'حفظ كمسودة',
      'Submit for Review': 'تقديم للمراجعة',
      'Resubmit for Review': 'إعادة التقديم للمراجعة',
      'Update Request': 'تحديث الطلب',
      'Reject with Reason': 'رفض مع السبب',
      'Edit & Resubmit': 'تعديل وإعادة التقديم',
      'Edit & Submit': 'تعديل وتقديم',
      'Edit': 'تعديل',
      'Approve': 'موافقة',
      'Reject': 'رفض',
      'Unknown': 'غير معروف',
      
      // Form labels and placeholders
      'Change Title': 'عنوان التغيير',
      'Category': 'الفئة',
      'Description': 'الوصف',
      'Priority': 'الأولوية',
      'Enter change request title': 'أدخل عنوان طلب التغيير',
      'Describe the proposed change': 'صف التغيير المقترح',
      'Describe the change in detail': 'صف التغيير بالتفصيل',
      'Select category': 'اختر الفئة',
      'Select priority': 'اختر الأولوية',
      'Select impact level': 'اختر مستوى التأثير',
      'Select status': 'اختر الحالة',
      'Enter estimated cost impact in SAR': 'أدخل تقدير تأثير التكلفة بالريال',
      'Explain why this change is necessary': 'اشرح لماذا هذا التغيير ضروري',
      'Describe how to implement this change': 'صف كيفية تنفيذ هذا التغيير',
      'Provide reason for rejection': 'قدم سبب الرفض',
      'Add optional approval comment': 'أضف تعليق موافقة اختياري',
      'Reason for Rejection': 'سبب الرفض',
      'Rejection Comment': 'تعليق الرفض',
      'Optional Approval Comment': 'تعليق الموافقة الاختياري',
      'Enter amount in Saudi Riyal (SAR)': 'أدخل المبلغ بالريال السعودي',
      
      // Role-based banners
      'Approval Required': 'الموافقة مطلوبة',
      'Auto-Approval Active': 'الموافقة التلقائية نشطة',
      'As a Project Manager, your change requests will be submitted for approval. Super Admin and Admin roles can directly approve changes.': 'كمدير مشروع، سيتم تقديم طلبات التغيير الخاصة بك للموافقة. أدوار المسؤول الأعلى والمسؤول يمكنهم الموافقة المباشرة على التغييرات.',
      'Your {role} role allows direct approval of change requests.': 'دور {role} الخاص بك يسمح بالموافقة المباشرة على طلبات التغيير.',
      'Super Admin': 'المسؤول الأعلى',
      'Admin': 'المسؤول',
      'Status (Admin Control)': 'الحالة (تحكم المسؤول)',
      'As {role}, you can change the status directly': 'ك{role}، يمكنك تغيير الحالة مباشرة',

      // Closure Approval translations
      'As a Project Manager, your closure records will be submitted for approval by Super Admin or Admin before they are finalized.': 'كمدير مشروع، سيتم تقديم سجلات الإغلاق الخاصة بك للموافقة من قبل المسؤول الأعلى أو المسؤول قبل اعتمادها.',
      'Your role allows direct approval of closure records. You can also approve or reject closures submitted by Project Managers.': 'دورك يسمح بالموافقة المباشرة على سجلات الإغلاق. يمكنك أيضًا الموافقة على أو رفض الإغلاقات المقدمة من مديري المشاريع.',
      'Closure created and submitted for approval': 'تم إنشاء الإغلاق وتقديمه للموافقة',
      'Closure approved successfully': 'تمت الموافقة على الإغلاق بنجاح',
      'Closure rejected': 'تم رفض الإغلاق',
      'Failed to approve closure': 'فشل في الموافقة على الإغلاق',
      'Failed to reject closure': 'فشل في رفض الإغلاق',
      'Approve this closure record?': 'هل تريد الموافقة على سجل الإغلاق هذا؟',
      'This will mark the closure as approved.': 'سيتم تعليم الإغلاق كموافق عليه.',
      'Reject this closure record?': 'هل تريد رفض سجل الإغلاق هذا؟',
      'The manager will be notified and can edit and resubmit.': 'سيتم إخطار المدير ويمكنه التعديل وإعادة التقديم.',
      'Approval Status': 'حالة الموافقة',
      'Approved by': 'موافق عليه من قبل',
      'Submitted by': 'مقدم من',
      'Approve Closure': 'الموافقة على الإغلاق',
      'Reject Closure': 'رفض الإغلاق',
      'Please provide a reason for rejection': 'يرجى تقديم سبب للرفض',
      'Benefit': 'الفائدة',
      
      // Toast messages
      'Load Failed': 'فشل التحميل',
      'Failed to load change requests': 'فشل تحميل طلبات التغيير',
      'Export Successful': 'نجح التصدير',
      'Change requests exported successfully': 'تم تصدير طلبات التغيير بنجاح',
      'Export Failed': 'فشل التصدير',
      'Failed to export change requests': 'فشل تصدير طلبات التغيير',
      'Table Settings Reset': 'إعادة تعيين إعدادات الجدول',
      'Table settings reset to default': 'تم إعادة تعيين إعدادات الجدول إلى الافتراضية',
      'Settings Applied': 'تم تطبيق الإعدادات',
      'Table settings applied successfully': 'تم تطبيق إعدادات الجدول بنجاح',
      'Data Refreshed': 'تم تحديث البيانات',
      'Change requests refreshed successfully': 'تم تحديث طلبات التغيير بنجاح',
      'Change Request Submitted': 'تم تقديم طلب التغيير',
      '{title} has been submitted for review': 'تم تقديم {title} للمراجعة',
      'Failed to submit change request': 'فشل تقديم طلب التغيير',
      'Draft Saved': 'تم حفظ المسودة',
      '{title} has been saved as draft': 'تم حفظ {title} كمسودة',
      'Failed to save draft': 'فشل حفظ المسودة',
      'Change Request Updated': 'تم تحديث طلب التغيير',
      '{title} has been updated successfully': 'تم تحديث {title} بنجاح',
      'Failed to update change request': 'فشل تحديث طلب التغيير',
      'Change Approved': 'تمت الموافقة على التغيير',
      '{title} has been approved': 'تمت الموافقة على {title}',
      'Failed to approve change request': 'فشل الموافقة على طلب التغيير',
      'Change Rejected': 'تم رفض التغيير',
      '{title} has been rejected': 'تم رفض {title}',
      'Failed to reject change request': 'فشل رفض طلب التغيير',
      'Validation Error': 'خطأ في التحقق',
      'Please fill in all required fields': 'يرجى ملء جميع الحقول المطلوبة',
      'Please provide a reason for rejection': 'يرجى تقديم سبب للرفض',
      'Data Loaded': 'تم تحميل البيانات',
      'Change requests loaded successfully': 'تم تحميل طلبات التغيير بنجاح',
      "Planned": "مخطط",
      "At Risk": "في خطر",

      // ProjectChanges - Dialog headers and content
      'Review & Reject Change Request': 'مراجعة ورفض طلب التغيير',
      'Review & Approve Change Request': 'مراجعة والموافقة على طلب التغيير',
      'Change Request Details': 'تفاصيل طلب التغيير',
      'Change Request Rejected': 'تم رفض طلب التغيير',
      'Change Request Approved': 'تمت الموافقة على طلب التغيير',
      'Review Requested Changes': 'مراجعة التغييرات المطلوبة',
      'You are about to reject:': 'أنت على وشك رفض:',
      'You are about to approve:': 'أنت على وشك الموافقة على:',
      'Requested by:': 'مقدم الطلب:',
      'Rejected by:': 'تم الرفض بواسطة:',
      'Approved by:': 'تمت الموافقة بواسطة:',
      'on': 'في',
      'Current Budget': 'الميزانية الحالية',
      'Requested Budget': 'الميزانية المطلوبة',
      'Current End Date': 'تاريخ الانتهاء الحالي',
      'Requested End Date': 'تاريخ الانتهاء المطلوب',
      'Current Project End Date': 'تاريخ انتهاء المشروع الحالي',
      'Impact Description': 'وصف التأثير',
      'Budget Impact Description': 'وصف تأثير الميزانية',
      'Timeline Impact Description': 'وصف تأثير الجدول الزمني',
      'Scope Impact Description': 'وصف تأثير النطاق',
      'Proposed Budget': 'الميزانية المقترحة',
      'Requested On': 'تاريخ الطلب',
      'Approval Comment (Optional)': 'تعليق الموافقة (اختياري)',
      'Add a comment to provide context or additional notes (optional)...': 'أضف تعليقاً لتوفير سياق أو ملاحظات إضافية (اختياري)...',
      'This comment will be visible to the manager who submitted the change request.': 'سيكون هذا التعليق مرئياً للمدير الذي قدم طلب التغيير.',
      'This message will be visible to the manager who submitted the change request.': 'ستكون هذه الرسالة مرئية للمدير الذي قدم طلب التغيير.',
      'Provide a reason for rejection so the manager can address the issues and resubmit...': 'قدم سبباً للرفض حتى يتمكن المدير من معالجة المشكلات وإعادة التقديم...',
      'Approve Change': 'الموافقة على التغيير',
      "Admin's Feedback:": 'ملاحظات المسؤول:',
      "Admin's Comment:": 'تعليق المسؤول:',
      'No comment provided': 'لم يتم تقديم تعليق',
      'You can edit this change request to address the feedback and resubmit it for approval.': 'يمكنك تعديل طلب التغيير هذا لمعالجة الملاحظات وإعادة تقديمه للموافقة.',
      'Approval Date:': 'تاريخ الموافقة:',
      'Rejection Date:': 'تاريخ الرفض:',
      'Approval Comment:': 'تعليق الموافقة:',
      'Previously Approved': 'تمت الموافقة سابقاً',
      'Previously Rejected': 'تم الرفض سابقاً',
      'Not Set': 'غير محدد',
      
      // ProjectChanges - Toast messages
      'Settings Reset': 'إعادة تعيين الإعدادات',
      'Table settings have been reset to default': 'تم إعادة تعيين إعدادات الجدول إلى الافتراضية',
      'Table settings have been applied successfully': 'تم تطبيق إعدادات الجدول بنجاح',
      'Change requests have been refreshed': 'تم تحديث طلبات التغيير',
      'Change Request Deleted': 'تم حذف طلب التغيير',
      '{title} has been deleted': 'تم حذف {title}',
      'Delete Failed': 'فشل الحذف',
      'Failed to delete change request': 'فشل حذف طلب التغيير',
      'You do not have permission to delete this change request': 'ليس لديك صلاحية لحذف طلب التغيير هذا',
      'You do not have permission to approve changes': 'ليس لديك صلاحية للموافقة على التغييرات',
      'You do not have permission to reject changes': 'ليس لديك صلاحية لرفض التغييرات',
      'You do not have permission to reopen changes': 'ليس لديك صلاحية لإعادة فتح التغييرات',
      'Already Approved': 'تمت الموافقة مسبقاً',
      '{title} is already approved': 'تمت الموافقة على {title} مسبقاً',
      'Already Rejected': 'تم الرفض مسبقاً',
      '{title} is already rejected': 'تم رفض {title} مسبقاً',
      'Change Reopened': 'تم إعادة فتح التغيير',
      '{title} has been reopened for review (was {status})': 'تم إعادة فتح {title} للمراجعة (كان {status})',
      'Reopen Failed': 'فشل إعادة الفتح',
      'Failed to reopen change': 'فشل إعادة فتح التغيير',
      'Project ID not found': 'لم يتم العثور على معرف المشروع',
      'Removed from Pending': 'تمت الإزالة من المعلق',
      '{title} has been removed from pending changes': 'تمت إزالة {title} من التغييرات المعلقة',
      'Pending Changes Cleared': 'تم مسح التغييرات المعلقة',
      '{count} pending changes have been cleared': 'تم مسح {count} تغييرات معلقة',
      'No Pending Changes': 'لا توجد تغييرات معلقة',
      'There are no pending changes to submit': 'لا توجد تغييرات معلقة للتقديم',
      'Changes Submitted': 'تم تقديم التغييرات',
      '{count} changes have been submitted for approval': 'تم تقديم {count} تغييرات للموافقة',
      'Partial Success': 'نجاح جزئي',
      '{count} changes failed to submit': 'فشل تقديم {count} تغييرات',
      'Failed to submit pending changes': 'فشل تقديم التغييرات المعلقة',
      'Delete Request': 'حذف الطلب',
      '{title} has been approved successfully': 'تمت الموافقة على {title} بنجاح',
      '{title} has been rejected and removed from the system': 'تم رفض {title} وإزالته من النظام',
      'Change Rejected & Deleted': 'تم رفض التغيير وحذفه',
      
      // Table settings
      'ON': 'تشغيل',
      'OFF': 'إيقاف',
      '10 rows': '10 صفوف',
      '25 rows': '25 صفوف',
      '50 rows': '50 صفوف',
      '100 rows': '100 صفوف',
      'Small': 'صغير',
      'Normal': 'عادي',
      'Large': 'كبير',
      'None': 'لا شيء',
      'Single': 'فردي',
      'Multiple': 'متعدد',

      // ProjectChanges - Additional missing translations
      'No change requests found': 'لم يتم العثور على طلبات تغيير',
      'Get started by creating your first change request': 'ابدأ بإنشاء أول طلب تغيير لك',
      'Add Change Request': 'إضافة طلب تغيير',
      'Budget (SAR)': 'الميزانية (ريال)',
      'Budget Impact': 'تأثير الميزانية',
      'Timeline Impact': 'تأثير الجدول الزمني',
      'Scope Impact': 'تأثير النطاق',
      'Reason for Request': 'سبب الطلب',
      'Explain the reason for this change request': 'اشرح سبب طلب التغيير هذا',
      'Provide a clear explanation of why this change is being requested': 'قدم شرحاً واضحاً لسبب طلب هذا التغيير',
      'Does this change impact the budget?': 'هل يؤثر هذا التغيير على الميزانية؟',
      'Total Budget (SAR)': 'إجمالي الميزانية (ريال)',
      'Enter total budget in SAR': 'أدخل إجمالي الميزانية بالريال',
      'Impact of Change on Budget': 'تأثير التغيير على الميزانية',
      'Describe how this change impacts the budget...': 'صف كيف يؤثر هذا التغيير على الميزانية...',
      'Explain the financial impact and any budget adjustments required': 'اشرح التأثير المالي وأي تعديلات مطلوبة على الميزانية',
      'Does this change impact the timeline?': 'هل يؤثر هذا التغيير على الجدول الزمني؟',
      'Project End Date': 'تاريخ انتهاء المشروع',
      'Select project end date': 'اختر تاريخ انتهاء المشروع',
      'Select the new expected project completion date': 'اختر تاريخ إتمام المشروع المتوقع الجديد',
      'Impact of Change on Timeline': 'تأثير التغيير على الجدول الزمني',
      'Describe how this change impacts the timeline...': 'صف كيف يؤثر هذا التغيير على الجدول الزمني...',
      'Explain the timeline impact and any schedule adjustments required': 'اشرح تأثير الجدول الزمني وأي تعديلات مطلوبة على المواعيد',
      'Does this change impact the scope?': 'هل يؤثر هذا التغيير على النطاق؟',
      'Describe how this change impacts the project scope (milestones, deliverables, etc.)': 'صف كيف يؤثر هذا التغيير على نطاق المشروع (المراحل الرئيسية، المخرجات، إلخ.)',
      'Please provide details about milestones, deliverables, or any scope-related changes': 'يرجى تقديم تفاصيل حول المراحل الرئيسية والمخرجات أو أي تغييرات متعلقة بالنطاق',
      'Privacy Notice:': 'إشعار الخصوصية:',
      'Your draft changes are completely private and only visible to you until submitted for review.': 'تغييرات المسودة الخاصة بك خاصة تماماً ومرئية لك فقط حتى يتم تقديمها للمراجعة.',
      'Private draft - only visible to you': 'مسودة خاصة - مرئية لك فقط',
      'Save privately - only visible to you and admins': 'حفظ خاص - مرئي لك وللمسؤولين فقط',
      'Submit for admin approval - will be visible to everyone': 'تقديم لموافقة المسؤول - سيكون مرئياً للجميع',
      'Tip: Save as draft to keep your work private. Only you can see your drafts until you submit them for review.': 'نصيحة: احفظ كمسودة لإبقاء عملك خاصاً. أنت فقط يمكنك رؤية مسوداتك حتى تقدمها للمراجعة.',
      'Save & Approve': 'حفظ والموافقة',
      'Update & Approve': 'تحديث والموافقة',
      'Basic Information': 'المعلومات الأساسية',
      'Keep as private draft': 'الإبقاء كمسودة خاصة',
      'Submit for approval - will be visible to everyone': 'تقديم للموافقة - سيكون مرئياً للجميع',
      'This draft is private. Only you can see it until you submit it for review.': 'هذه المسودة خاصة. أنت فقط يمكنك رؤيتها حتى تقدمها للمراجعة.',
      'Describe the scope changes including affected milestones, deliverables, and their impact on the project...': 'صف تغييرات النطاق بما في ذلك المراحل الرئيسية المتأثرة والمخرجات وتأثيرها على المشروع...',
      'Provide details about the scope changes, affected areas, and expected outcomes.': 'قدم تفاصيل حول تغييرات النطاق والمجالات المتأثرة والنتائج المتوقعة.',
      'Approval Comment (Optional)': 'تعليق الموافقة (اختياري)',
      'Add a comment to provide context or additional notes (optional)...': 'أضف تعليقاً لتوفير سياق أو ملاحظات إضافية (اختياري)...',
      'Review Requested Changes': 'مراجعة التغييرات المطلوبة',
      'You are about to reject:': 'أنت على وشك رفض:',
      'You are about to approve:': 'أنت على وشك الموافقة على:',
      'Requested by:': 'مقدم الطلب:',
      'Rejected by:': 'تم الرفض بواسطة:',
      'Approved by:': 'تمت الموافقة بواسطة:',
      'Rejection Reason:': 'سبب الرفض:',
      'Rejection Reason': 'سبب الرفض',
      'Provide a reason for rejection so the manager can address the issues and resubmit...': 'قدم سبباً للرفض حتى يتمكن المدير من معالجة المشكلات وإعادة التقديم...',
      'This comment will be visible to the manager who submitted the change request.': 'سيكون هذا التعليق مرئياً للمدير الذي قدم طلب التغيير.',
      'This message will be visible to the manager who submitted the change request.': 'ستكون هذه الرسالة مرئية للمدير الذي قدم طلب التغيير.',
      'Reject with Reason': 'رفض مع السبب',
      "Admin's Feedback:": 'ملاحظات المسؤول:',
      "Admin's Comment:": 'تعليق المسؤول:',
      'No comment provided': 'لم يتم تقديم تعليق',
      'No rejection reason provided': 'لم يتم تقديم سبب للرفض',
      'No reason provided': 'لم يتم تقديم سبب',
      'You can edit this change request to address the feedback and resubmit it for approval.': 'يمكنك تعديل طلب التغيير هذا لمعالجة الملاحظات وإعادة تقديمه للموافقة.',
      'Approval Date:': 'تاريخ الموافقة:',
      'Rejection Date:': 'تاريخ الرفض:',
      'Approval Comment:': 'تعليق الموافقة:',
      'Previously Approved': 'تمت الموافقة سابقاً',
      'Previously Rejected': 'تم الرفض سابقاً',
      'Requested On': 'تاريخ الطلب',
      'Proposed Budget': 'الميزانية المقترحة',
      'Current Budget': 'الميزانية الحالية',
      'Requested Budget': 'الميزانية المطلوبة',
      'Current End Date': 'تاريخ الانتهاء الحالي',
      'Requested End Date': 'تاريخ الانتهاء المطلوب',
      'Current Project End Date': 'تاريخ انتهاء المشروع الحالي',
      'Impact Description': 'وصف التأثير',
      'Your change request has been submitted for approval.': 'تم تقديم طلب التغيير الخاص بك للموافقة.',
      'Draft Saved Privately': 'تم حفظ المسودة بشكل خاص',
      '{title} has been saved as a private draft. Only you can see it.': 'تم حفظ {title} كمسودة خاصة. أنت فقط يمكنك رؤيتها.',
      'Creation Failed': 'فشل الإنشاء',
      'Failed to create change request': 'فشل إنشاء طلب التغيير',
      'Incomplete Data': 'بيانات غير مكتملة',
      'Title and category are required': 'العنوان والفئة مطلوبان',
      'Permission Denied': 'تم رفض الصلاحية',
      'You do not have permission to edit this change request': 'ليس لديك صلاحية لتعديل طلب التغيير هذا',
      'Please fix all errors before submitting': 'يرجى إصلاح جميع الأخطاء قبل التقديم',
      'Project ID is required': 'معرف المشروع مطلوب',
      'Change Resubmitted': 'تم إعادة تقديم التغيير',
      '{title} has been resubmitted for review': 'تم إعادة تقديم {title} للمراجعة',
      'Change Submitted': 'تم تقديم التغيير',
      'Change Updated': 'تم تحديث التغيير',
      'Update Failed': 'فشل التحديث',
      'Failed to update change request': 'فشل تحديث طلب التغيير',
      'Draft Updated': 'تم تحديث المسودة',
      '{title} has been updated. It remains private to you and admins.': 'تم تحديث {title}. تبقى خاصة لك وللمسؤولين.',
      'Failed to update draft': 'فشل تحديث المسودة',
      'Change Updated & Approved': 'تم تحديث التغيير والموافقة عليه',
      'Failed to update and approve change': 'فشل تحديث والموافقة على التغيير',
      'Approval Failed': 'فشل الموافقة',
      'Failed to approve change': 'فشل الموافقة على التغيير',
      'Rejection Failed': 'فشل الرفض',
      'Failed to reject change': 'فشل رفض التغيير',
      'Invalid Status': 'حالة غير صالحة',
      'Only draft changes can be submitted for review': 'يمكن تقديم التغييرات المسودة فقط للمراجعة',
      'Submitted for Review': 'تم التقديم للمراجعة',
      '{title} is now under review and visible to everyone': '{title} الآن قيد المراجعة ومرئي للجميع',
      'Submission Failed': 'فشل التقديم',
      'Failed to submit for review': 'فشل التقديم للمراجعة',
      'Reopen Failed': 'فشل إعادة الفتح',
      'Failed to reopen change': 'فشل إعادة فتح التغيير',
      'Are you sure you want to delete "{title}"?': 'هل أنت متأكد أنك تريد حذف "{title}"؟',
      'Delete Confirmation': 'تأكيد الحذف',
      'Delete': 'حذف',
      'change': 'تغيير',
      'Scope impact description is required': 'وصف تأثير النطاق مطلوب',
      'Change title is required': 'عنوان التغيير مطلوب',
      'Change title must not exceed 255 characters': 'يجب ألا يتجاوز عنوان التغيير 255 حرفاً',
      'Category is required': 'الفئة مطلوبة',
      'Priority is required': 'الأولوية مطلوبة',
      'Impact is required': 'التأثير مطلوب',
      'Total budget is required': 'إجمالي الميزانية مطلوب',
      'Total budget must be a positive number': 'يجب أن يكون إجمالي الميزانية رقماً موجباً',
      'Project end date is required': 'تاريخ انتهاء المشروع مطلوب',
      'Under Review': 'قيد المراجعة',
      'Approved': 'تمت الموافقة',
      'Rejected': 'مرفوض',
      'Major': 'رئيسي',
      'Significant': 'كبير',
      'Moderate': 'متوسط',
      'Minor': 'بسيط',
      'Budget': 'الميزانية',
      'Scope': 'النطاق',
      'Export to CSV': 'تصدير إلى CSV',
      'Table Settings': 'إعدادات الجدول',
      'Refresh': 'تحديث',
      'Export Successful': 'نجح التصدير',
      'Change requests exported successfully': 'تم تصدير طلبات التغيير بنجاح',
      'Export Failed': 'فشل التصدير',
      'Failed to export change requests': 'فشل تصدير طلبات التغيير',

      // ProjectChanges - Form improvement translations
      'Fields marked with': 'الحقول المميزة بـ',
      'are required.': 'مطلوبة.',
      'are required. All other fields are optional.': 'مطلوبة. جميع الحقول الأخرى اختيارية.',
      'Description is required': 'الوصف مطلوب',
      'Reason for request is required': 'سبب الطلب مطلوب',

      // ProgramChangeRequest - Additional missing translations
      'Track and manage program change requests': 'تتبع وإدارة طلبات تغيير البرنامج',
      'This program is closed. Change requests are in read-only mode.': 'هذا البرنامج مغلق. طلبات التغيير في وضع القراءة فقط.',
      'Pending Changes': 'التغييرات المعلقة',
      'Submit All': 'تقديم الكل',
      'No Change Requests': 'لا توجد طلبات تغيير',
      'No change requests found.': 'لم يتم العثور على طلبات تغيير.',
      'Get started by creating a new change request': 'ابدأ بإنشاء طلب تغيير جديد',
      'Describe the change request in detail': 'صف طلب التغيير بالتفصيل',
      'Explain why this change is needed...': 'اشرح لماذا هذا التغيير مطلوب...',
      'Describe the budget impact...': 'صف تأثير الميزانية...',
      'Describe the timeline impact...': 'صف تأثير الجدول الزمني...',
      'Describe the scope impact...': 'صف تأثير النطاق...',
      'Program End Date': 'تاريخ انتهاء البرنامج',
      'Program end date is required': 'تاريخ انتهاء البرنامج مطلوب',
      'Program ID is required': 'معرف البرنامج مطلوب',
      'Current Program End Date': 'تاريخ انتهاء البرنامج الحالي',
      'Save Draft': 'حفظ المسودة',
      'This change was rejected. Please address the feedback below:': 'تم رفض هذا التغيير. يرجى معالجة الملاحظات أدناه:',
      'This is a private draft. Only you and admins can see it until submitted for review.': 'هذه مسودة خاصة. أنت والمسؤولون فقط يمكنكم رؤيتها حتى يتم تقديمها للمراجعة.',
      'Title is required to save as draft': 'العنوان مطلوب للحفظ كمسودة',
      'Save & Approve': 'حفظ والموافقة',
      'Update & Approve': 'تحديث والموافقة',
      'Select end date': 'اختر تاريخ الانتهاء',
      'Enter total budget': 'أدخل إجمالي الميزانية',

      // ProjectCharter translations
      'Search documents...': 'البحث في المستندات...',
      'All Categories': 'جميع الفئات',
      'All File Types': 'جميع أنواع الملفات',
      'Filter': 'تصفية',
      'Mandatory Only': 'إلزامي فقط',
      'Clear Filter': 'مسح التصفية',
      'Initiation Phase Documents': 'مستندات مرحلة البدء',
      'Required documents for project initiation': 'المستندات المطلوبة لبدء المشروع',
      'Toggle Filters': 'تبديل التصفية',
      'Send for Approval': 'إرسال للموافقة',
      'Mandatory': 'إلزامي',
      'Due': 'موعد الاستحقاق',
      'Download': 'تنزيل',
      'Replace': 'استبدال',
      'Upload': 'رفع',
      'Upload New Charter': 'رفع ميثاق جديد',
      'Choose a file or drag & drop': 'اختر ملف أو اسحبه وأفلته',
      'PDF, Word, Excel, PowerPoint, Text, or Archive files': 'ملفات PDF أو Word أو Excel أو PowerPoint أو Text أو Archive',
      'Browse Files': 'تصفح الملفات',
      'Scope Documents': 'مستندات النطاق',
      'Deliverables': 'المخرجات',
      'Project Plans': 'خطط المشروع',
      'Risk Assessment': 'تقييم المخاطر',
      'Procurement': 'المشتريات',
      'Stakeholder Analysis': 'تحليل أصحاب المصلحة',
      'Business Case': 'دراسة الجدوى',
      'Templates': 'القوالب',
      'Processes': 'العمليات',
      'Guidelines': 'الإرشادات',
      'Approvals': 'الموافقات',
      'Standards': 'المعايير',
      'PDF': 'PDF',
      'Word Document': 'مستند Word',
      'Excel Spreadsheet': 'جدول بيانات Excel',
      'PowerPoint': 'PowerPoint',
      'Text File': 'ملف نصي',
      'Archive': 'أرشيف',
      'Public': 'عام',
      'Internal': 'داخلي',
      'Restricted': 'محدود',
      'Confidential': 'سري',
      'Grid': 'شبكة',
      'List': 'قائمة',

      // ProjectClosure translations
      'Search closure items...': 'البحث في عناصر الإغلاق...',
      'Final Only': 'نهائي فقط',
      'Closure Phase Activities': 'أنشطة مرحلة الإغلاق',
      'Project closure activities and final documentation': 'أنشطة إغلاق المشروع والوثائق النهائية',
      'Final Deliverables': 'المخرجات النهائية',
      'Lessons Learned': 'الدروس المستفادة',
      
      // Lessons Learned Management
      'Document and share project lessons': 'توثيق ومشاركة دروس المشروع',
      'Add Lesson': 'إضافة درس',
      'Project Lessons': 'دروس المشروع',
      '{count} lessons': '{count} درس',
      'No lessons found': 'لم يتم العثور على دروس',
      'Great! No lessons have been documented yet': 'رائع! لم يتم توثيق أي دروس حتى الآن',
      '#': '#',
      'Lesson Title': 'عنوان الدرس',
      'Type': 'النوع',
      'Impact Level': 'مستوى التأثير',
      'Project Phase': 'مرحلة المشروع',
      'Applicability': 'قابلية التطبيق',
      'Date Recorded': 'تاريخ التسجيل',
      'Summary': 'الملخص',
      'Recommendations': 'التوصيات',
      'Author': 'المؤلف',
      'Enter lesson title': 'أدخل عنوان الدرس',
      'Select category': 'اختر الفئة',
      'Select type': 'اختر النوع',
      'Select impact': 'اختر التأثير',
      'Select phase': 'اختر المرحلة',
      'Select applicability': 'اختر قابلية التطبيق',
      'Select date': 'اختر التاريخ',
      'Select status': 'اختر الحالة',
      'Enter summary of the lesson': 'أدخل ملخص الدرس',
      'Provide actionable recommendations': 'قدم توصيات قابلة للتنفيذ',
      'Communication': 'تواصل',
      'Technical': 'تقني',
      'Process': 'عملية',
      'Resource Management': 'إدارة الموارد',
      'Risk Management': 'إدارة المخاطر',
      'Budget Management': 'إدارة الميزانية',
      'Stakeholder Management': 'إدارة أصحاب المصلحة',
      'Change Request': 'طلب تغيير',
      'Success': 'نجاح',
      'Challenge': 'تحدي',
      'Best Practice': 'أفضل ممارسة',
      'High': 'مرتفع',
      'Medium': 'متوسط',
      'Low': 'منخفض',
      'Initiation': 'البدء',
      'Planning': 'التخطيط',
      'Execution': 'التنفيذ',
      'Monitoring': 'المراقبة',
      'Closure': 'الإغلاق',
      'All Projects': 'جميع المشاريع',
      'Software Projects': 'مشاريع البرمجيات',
      'Large Projects': 'المشاريع الكبيرة',
      'Agile Projects': 'المشاريع الرشيقة',
      'Waterfall Projects': 'المشاريع التقليدية',
      'Remote Teams': 'الفرق البعيدة',
      'Cross-Functional Teams': 'الفرق متعددة الوظائف',
      'Active': 'نشط',
      'Inactive': 'غير نشط',
      'Draft': 'مسودة',
      'Export to CSV': 'تصدير إلى CSV',
      'Table Settings': 'إعدادات الجدول',
      'Refresh': 'تحديث',
      'Column Visibility': 'إظهار الأعمدة',
      'On': 'مفعل',
      'Off': 'غير مفعل',
      'Table Behavior': 'سلوك الجدول',
      'Rows Per Page': 'الصفوف لكل صفحة',
      '10 rows': '10 صفوف',
      '25 rows': '25 صف',
      '50 rows': '50 صف',
      '100 rows': '100 صف',
      'Striped Rows': 'صفوف مخططة',
      'Grid Lines': 'خطوط الشبكة',
      'Resizable Columns': 'أعمدة قابلة لتغيير الحجم',
      'Layout Settings': 'إعدادات التخطيط',
      'Table Size': 'حجم الجدول',
      'Small': 'صغير',
      'Normal': 'عادي',
      'Large': 'كبير',
      'Selection Mode': 'وضع التحديد',
      'None': 'لا شيء',
      'Single': 'فردي',
      'Multiple': 'متعدد',
      'Show Row Numbers': 'إظهار أرقام الصفوف',
      'Scrollable': 'قابل للتمرير',
      'Sortable Columns': 'أعمدة قابلة للفرز',
      'Reset to Default': 'إعادة تعيين إلى الافتراضي',
      'Apply Settings': 'تطبيق الإعدادات',
      'Settings Reset': 'إعادة تعيين الإعدادات',
      'Table settings reset to default': 'تم إعادة تعيين إعدادات الجدول إلى الافتراضي',
      'Settings Applied': 'تم تطبيق الإعدادات',
      'Table settings applied successfully': 'تم تطبيق إعدادات الجدول بنجاح',
      'Edit Lesson': 'تعديل الدرس',
      'Update Lesson': 'تحديث الدرس',
      'Delete Lesson': 'حذف الدرس',
      'View Details': 'عرض التفاصيل',
      'More Actions': 'المزيد من الإجراءات',
      'Lesson Details': 'تفاصيل الدرس',
      'Unknown': 'غير معروف',
      'Close': 'إغلاق',
      'Export Successful': 'نجح التصدير',
      'Lessons data exported successfully': 'تم تصدير بيانات الدروس بنجاح',
      'Export Failed': 'فشل التصدير',
      'Failed to export lessons data': 'فشل تصدير بيانات الدروس',
      'Data Refreshed': 'تم تحديث البيانات',
      'Lessons data refreshed successfully': 'تم تحديث بيانات الدروس بنجاح',
      'Error': 'خطأ',
      'Failed to load lessons': 'فشل تحميل الدروس',
      'Project ID is required': 'معرف المشروع مطلوب',
      'Fill all required fields': 'املأ جميع الحقول المطلوبة',
      'Validation Error': 'خطأ في التحقق',
      'Lesson Added': 'تمت إضافة الدرس',
      'Lesson created successfully': 'تم إنشاء الدرس بنجاح',
      'Failed to add lesson': 'فشل إضافة الدرس',
      'Lesson Updated': 'تم تحديث الدرس',
      'Lesson updated successfully': 'تم تحديث الدرس بنجاح',
      'Failed to update lesson': 'فشل تحديث الدرس',
      'Are you sure you want to delete this lesson?': 'هل أنت متأكد من أنك تريد حذف هذا الدرس؟',
      'Lesson Deleted': 'تم حذف الدرس',
      'Lesson deleted successfully': 'تم حذف الدرس بنجاح',
      'Failed to delete lesson': 'فشل حذف الدرس',
      'No summary provided': 'لم يتم تقديم ملخص',
      'No recommendations provided': 'لم يتم تقديم توصيات',

      // Program Lessons translations
      'Program Lessons': 'دروس البرنامج',
      'Document and share program lessons': 'توثيق ومشاركة دروس البرنامج',
      'Person Responsible': 'الشخص المسؤول',
      'Person Responsible for Processing': 'الشخص المسؤول عن المعالجة',
      'Enter person responsible': 'أدخل الشخص المسؤول',
      'How to Resolve': 'كيفية الحل',
      'How to Resolve the Issue': 'كيفية حل المشكلة',
      'Describe how to resolve the issue': 'صف كيفية حل المشكلة',
      'Describe the support needed': 'صف الدعم المطلوب',
      'Please select status': 'يرجى اختيار الحالة',

      // Program Issues translations
      'Issue Register': 'سجل المشكلات',
      'Register and resolve program issues': 'تسجيل وحل مشكلات البرنامج',
      'Add Issue': 'إضافة مشكلة',
      'This program is closed. Issues are in read-only mode.': 'هذا البرنامج مغلق. المشكلات في وضع القراءة فقط.',
      'Program Issues': 'مشكلات البرنامج',
      'issues': 'مشكلات',
      'Export to CSV': 'تصدير إلى CSV',
      'Table Settings': 'إعدادات الجدول',
      'Refresh': 'تحديث',
      'No issues found': 'لم يتم العثور على مشكلات',
      'Great! No issues have been reported yet': 'رائع! لم يتم الإبلاغ عن أي مشكلات بعد',
      'Issue Title': 'عنوان المشكلة',
      'Description': 'الوصف',
      'No description provided': 'لم يتم تقديم وصف',
      'Person Responsible': 'الشخص المسؤول',
      'Start Date': 'تاريخ البدء',
      'Status': 'الحالة',
      'How to Resolve the Issue': 'كيفية حل المشكلة',
      'Not specified': 'غير محدد',
      'Support Required': 'الدعم المطلوب',
      'Actions': 'الإجراءات',
      'More Actions': 'المزيد من الإجراءات',
      'Enter issue title': 'أدخل عنوان المشكلة',
      'Describe the issue in detail': 'صف المشكلة بالتفصيل',
      'Enter person responsible': 'أدخل الشخص المسؤول',
      'Select start date': 'اختر تاريخ البدء',
      'Project start date not set': 'لم يتم تعيين تاريخ بدء المشروع',
      'Problem Status': 'حالة المشكلة',
      'Select status': 'اختر الحالة',
      'Enter resolution steps or approach': 'أدخل خطوات أو نهج الحل',
      'Enter support or resources needed': 'أدخل الدعم أو الموارد المطلوبة',
      'Cancel': 'إلغاء',
      'Column Visibility': 'ظهور الأعمدة',
      'ON': 'تشغيل',
      'OFF': 'إيقاف',
      'Table Behavior': 'سلوك الجدول',
      'Rows per Page': 'الصفوف لكل صفحة',
      '10 rows': '10 صفوف',
      '25 rows': '25 صف',
      '50 rows': '50 صف',
      '100 rows': '100 صف',
      'Striped Rows': 'صفوف مخططة',
      'Grid Lines': 'خطوط الشبكة',
      'Resizable Columns': 'أعمدة قابلة لتغيير الحجم',
      'Layout Settings': 'إعدادات التخطيط',
      'Table Size': 'حجم الجدول',
      'Small': 'صغير',
      'Normal': 'عادي',
      'Large': 'كبير',
      'Selection Mode': 'وضع التحديد',
      'None': 'لا شيء',
      'Single': 'واحد',
      'Multiple': 'متعدد',
      'Show Row Numbers': 'إظهار أرقام الصفوف',
      'Scrollable': 'قابل للتمرير',
      'Sortable Columns': 'أعمدة قابلة للفرز',
      'Reset to Default': 'إعادة تعيين إلى الافتراضي',
      'Apply Settings': 'تطبيق الإعدادات',
      'Issue Details': 'تفاصيل المشكلة',
      'Close': 'إغلاق',
      'Edit': 'تعديل',
      'Edit Issue': 'تعديل المشكلة',
      
      // Lessons Learned - Validation Messages
      'Please enter lesson title': 'يرجى إدخال عنوان الدرس المستفاد',
      'Lesson title must be at least 3 characters': 'يجب أن يكون عنوان الدرس المستفاد 3 أحرف على الأقل',
      'Please select lesson category': 'يرجى اختيار فئة الدرس المستفاد',
      'Please select lesson type': 'يرجى اختيار نوع الدرس المستفاد',
      'Please select impact level': 'يرجى اختيار مستوى التأثير',
      'Please select project phase': 'يرجى اختيار مرحلة المشروع',
      'Please select applicability': 'يرجى اختيار قابلية التطبيق',
      'Please select recorded date': 'يرجى اختيار تاريخ التسجيل',
      'Please enter lesson summary': 'يرجى إدخال ملخص الدرس المستفاد',
      'Lesson summary must be at least 10 characters': 'يجب أن يكون ملخص الدرس المستفاد 10 أحرف على الأقل',
      'Please fill all required fields correctly': 'يرجى ملء جميع الحقول المطلوبة بشكل صحيح',

      "Please enter project name": "يرجى إدخال اسم المشروع",
      "Please select department": "يرجى اختيار القسم",
      "Please select start date": "يرجى اختيار تاريخ البدء",
      "Please select end date": "يرجى اختيار تاريخ الانتهاء",
      "Budget must be greater than 0": "يجب أن تكون الميزانية أكبر من 0",
      "Name is required": "الاسم مطلوب",
      "Role is required": "الدور مطلوب",
      "Type is required": "النوع مطلوب",
      "Level of influence is required": "مستوى التأثير مطلوب",
      "Level of interest is required": "مستوى الاهتمام مطلوب",
      "Communication method is required": "طريقة الاتصال مطلوبة",
      "Communication frequency is required": "تكرار الاتصال مطلوب",
      "Engagement strategy is required": "استراتيجية المشاركة مطلوبة",
      "Email is required": "البريد الإلكتروني مطلوب",
      "Phone number is required": "رقم الهاتف مطلوب",
      "Select communication frequency": "اختر تكرار الاتصال",

      "Please enter project name": "يرجى إدخال اسم المشروع",
      "Please select department": "يرجى اختيار القسم",
      "Please select start date": "يرجى اختيار تاريخ البدء",
      "Please select end date": "يرجى اختيار تاريخ الانتهاء",


      'Project Archival': 'أرشفة المشروع',
      'Team Release': 'إطلاق الفريق',
      'Administrative Closure': 'الإغلاق الإداري',
      'Project Review': 'مراجعة المشروع',
      'Knowledge Transfer': 'نقل المعرفة',
      'Financial Closure': 'الإغلاق المالي',
      'Quality Assurance': 'ضمان الجودة',
      'Risk Closure': 'إغلاق المخاطر',
      'Stakeholder Sign-off': 'موافقة أصحاب المصلحة',

      // ProjectDetails translations
      'Project Manager Information': 'معلومات مدير المشروع',
      'Last published': 'آخر نشر',
      'Days ago': 'أيام مضت',
      'Project Manager': 'مدير المشروع',
      'Phone': 'الهاتف',
      'Email': 'البريد الإلكتروني',
      'N/A': 'غير متوفر',
      'Detailed Project Information': 'معلومات المشروع التفصيلية',
      'Project Name': 'اسم المشروع',
      'Project Type': 'نوع المشروع',
      'Not Set': 'غير محدد',
      'Project Status': 'حالة المشروع',
      'Priority': 'الأولوية',
      'Department': 'القسم',
      'Project Dates': 'تواريخ المشروع',
      'Start': 'البدء',
      'End': 'الانتهاء',
      'Project Owner': 'مالك المشروع',
      'Not Assigned': 'غير مخصص',
      'Initial Budget': 'الميزانية الأولية',
      'Approved': 'موافق عليه',
      'Progress Status': 'حالة التقدم',
      'Complete': 'مكتمل',
      'Team Size': 'حجم الفريق',
      'Members': 'أعضاء',
      'Days Remaining': 'الأيام المتبقية',
      'Budget Spent': 'الميزانية المنفقة',
      'Project Info': 'معلومات المشروع',
      'Failed to load project details': 'فشل تحميل تفاصيل المشروع',
      'Error': 'خطأ',

      // ProjectDocuments translations
      'Document Management': 'إدارة المستندات',
      'Manage project documents and deliverables': 'إدارة مستندات المشروع والنواتج',
      'Upload Document': 'رفع مستند',
      'Project Documents': 'مستندات المشروع',
      'documents': 'مستندات',
      'Export to CSV': 'تصدير إلى CSV',
      'Table Settings': 'إعدادات الجدول',
      'Refresh': 'تحديث',
      'No documents found': 'لا توجد مستندات',
      'Start by uploading your first project document': 'ابدأ برفع أول مستند للمشروع',
      'Document Name': 'اسم المستند',
      'Category': 'الفئة',
      'Type': 'النوع',
      'Size': 'الحجم',
      'Upload Date': 'تاريخ الرفع',
      'Actions': 'الإجراءات',
      'View': 'عرض',
      'Download': 'تحميل',
      'Edit': 'تعديل',
      'Delete': 'حذف',
      'More Actions': 'المزيد من الإجراءات',
      
      // Dialog and Form
      'Upload New Document': 'رفع مستند جديد',
      'Edit Document': 'تعديل المستند',
      'Document Name': 'اسم المستند',
      'Enter document name': 'أدخل اسم المستند',
      'Description (Optional)': 'الوصف (اختياري)',
      'Enter document description (optional)': 'أدخل وصف المستند (اختياري)',
      'Optional': 'اختياري',
      'Select document category': 'اختر فئة المستند',
      'Custom Category': 'فئة مخصصة',
      'Enter custom category': 'أدخل فئة مخصصة',
      'Replace Document File (Optional)': 'استبدال ملف المستند (اختياري)',
      'Select Document File': 'اختر ملف المستند',
      'Click to upload or drag and drop': 'انقر للرفع أو اسحب وأفلت',
      'PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, TXT (max 10MB)': 'PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, TXT (حد أقصى 10 ميجابايت)',
      'Ready to upload': 'جاهز للرفع',
      'Current file': 'الملف الحالي',
      'Remove file': 'إزالة الملف',
      'Replace file': 'استبدال الملف',
      'Document Type': 'نوع المستند',
      'File Size': 'حجم الملف',
      'Add Document': 'إضافة مستند',
      'Update Document': 'تحديث المستند',
      'Cancel': 'إلغاء',
      
      // Categories
      'Contract': 'عقد',
      'Specification': 'مواصفات',
      'Report': 'تقرير',
      'Presentation': 'عرض تقديمي',
      'Other': 'أخرى',
      
      // Toast Messages
      'File Too Large': 'الملف كبير جداً',
      'File size must be less than 10MB': 'يجب أن يكون حجم الملف أقل من 10 ميجابايت',
      'Failed to load documents': 'فشل تحميل المستندات',
      'Load Failed': 'فشل التحميل',
      'Document downloaded successfully': 'تم تحميل المستند بنجاح',
      'Download Successful': 'نجح التحميل',
      'Failed to download document': 'فشل تحميل المستند',
      'Download Failed': 'فشل التحميل',
      'Delete Confirmation': 'تأكيد الحذف',
      'Are you sure you want to delete "{name}"?': 'هل أنت متأكد من حذف "{name}"؟',
      'This action cannot be undone.': 'لا يمكن التراجع عن هذا الإجراء.',
      'Document deleted successfully': 'تم حذف المستند بنجاح',
      'Delete Successful': 'نجح الحذف',
      'Failed to delete document': 'فشل حذف المستند',
      'Delete Failed': 'فشل الحذف',
      'Refreshed': 'تم التحديث',
      'Documents refreshed successfully': 'تم تحديث المستندات بنجاح',
      'Missing Information': 'معلومات ناقصة',
      'Please provide document name and select a file': 'يرجى إدخال اسم المستند واختيار ملف',
      'Please provide a custom category name': 'يرجى إدخال اسم فئة مخصص',
      'Please provide document name': 'يرجى إدخال اسم المستند',
      'Document uploaded successfully': 'تم رفع المستند بنجاح',
      'Upload Successful': 'نجح الرفع',
      'Failed to upload document': 'فشل رفع المستند',
      'Upload Failed': 'فشل الرفع',
      'Document updated successfully': 'تم تحديث المستند بنجاح',
      'Update Successful': 'نجح التحديث',
      'Failed to update document': 'فشل تحديث المستند',
      'Update Failed': 'فشل التحديث',
      'Export Successful': 'نجح التصدير',
      'Documents exported successfully': 'تم تصدير المستندات بنجاح',
      'Export Failed': 'فشل التصدير',
      'Failed to export documents': 'فشل تصدير المستندات',
      
      // Table Settings
      'Column Visibility': 'رؤية الأعمدة',
      'Table Behavior': 'سلوك الجدول',
      'Layout Settings': 'إعدادات التخطيط',
      'Show Row Numbers': 'إظهار أرقام الصفوف',
      'Sortable Columns': 'أعمدة قابلة للفرز',
      'Scrollable': 'قابل للتمرير',
      'Striped Rows': 'صفوف مخططة',
      'Show Gridlines': 'إظهار خطوط الشبكة',
      'Resizable Columns': 'أعمدة قابلة لتغيير الحجم',
      'Rows Per Page': 'صفوف لكل صفحة',
      'Table Size': 'حجم الجدول',
      'Selection Mode': 'وضع التحديد',
      'Small': 'صغير',
      'Normal': 'عادي',
      'Large': 'كبير',
      'None': 'لا شيء',
      'Single': 'مفرد',
      'Multiple': 'متعدد',
      'ON': 'تشغيل',
      'OFF': 'إيقاف',
      'Reset to Default': 'إعادة تعيين للافتراضي',
      'Apply Settings': 'تطبيق الإعدادات',
      'Table Settings Reset': 'تم إعادة تعيين إعدادات الجدول',
      'Table settings have been reset to default': 'تم إعادة تعيين إعدادات الجدول إلى الافتراضية',
      'Settings Applied': 'تم تطبيق الإعدادات',
      'Table settings have been applied successfully': 'تم تطبيق إعدادات الجدول بنجاح',

      // Project Details Page ([id].vue)
      'Back to Projects': 'العودة إلى المشاريع',
      'Project Details': 'تفاصيل المشروع',
      'Loading project details...': 'جاري تحميل تفاصيل المشروع...',
      
      // Statistics Panel
      'Project Overview': 'نظرة عامة على المشروع',
      'Overall Progress': 'التقدم الإجمالي',
      'Duration': 'المدة',
      'Resources': 'الموارد',
      'members': 'أعضاء',
      'Priority': 'الأولوية',
      'Risk Level': 'مستوى المخاطر',
      
      // Budget Section
      'Budget Overview': 'نظرة عامة على الميزانية',
      'Budget Utilization': 'استخدام الميزانية',
      'Total Budget': 'إجمالي الميزانية',
      'Spent': 'المنفق',
      'Remaining': 'المتبقي',
      
      // Timeline Section
      'Timeline': 'الجدول الزمني',
      'Start Date': 'تاريخ البدء',
      'End Date': 'تاريخ الانتهاء',
      'Not set': 'غير محدد',
      
      // Tasks Overview
      'Tasks Overview': 'نظرة عامة على المهام',
      'Completed': 'مكتمل',
      'Total': 'الإجمالي',
      
      // Error States
      'Project Not Found': 'المشروع غير موجود',
      "The project you're looking for doesn't exist or has been deleted.": 'المشروع الذي تبحث عنه غير موجود أو تم حذفه.',
      
      // Dialogs
      'Add Milestone': 'إضافة معلم',
      'Milestone creation functionality will be implemented here': 'سيتم تطبيق وظيفة إنشاء المعالم هنا',
      'Create Milestone': 'إنشاء معلم',
      'All Activities': 'جميع الأنشطة',
      'Detailed activities view will be implemented here': 'سيتم تطبيق عرض الأنشطة التفصيلية هنا',
      'View Timeline': 'عرض الجدول الزمني',
      'Edit': 'تعديل',
      
      // Toast Messages - Project Details
      'Invalid project': 'مشروع غير صالح',
      'Unable to determine which project to load': 'تعذر تحديد المشروع المراد تحميله',
      'Project not found': 'المشروع غير موجود',

      // Program [id].vue translations
      'Program Overview': 'نظرة عامة على البرنامج',
      'Program Detail': 'تفاصيل البرنامج',
      'Program Details': 'تفاصيل البرنامج',
      'Closed - Read Only': 'مغلق - للقراءة فقط',
      'Program Not Found': 'البرنامج غير موجود',
      'Not Found': 'غير موجود',
      'Failed to load program details': 'فشل في تحميل تفاصيل البرنامج',
      'Failed to refresh project data': 'فشل في تحديث بيانات المشروع',
      'All project data has been refreshed successfully': 'تم تحديث جميع بيانات المشروع بنجاح',
      "The program you're looking for doesn't exist or has been deleted.": 'البرنامج الذي تبحث عنه غير موجود أو تم حذفه.',
      'We could not load details for this project': 'تعذر تحميل تفاصيل هذا المشروع',
      'Project not available': 'المشروع غير متاح',
      'Project details could not be retrieved': 'تعذر استرجاع تفاصيل المشروع',
      'Overview unavailable': 'النظرة العامة غير متاحة',
      'Project overview metrics are missing from the response': 'مقاييس النظرة العامة للمشروع مفقودة من الاستجابة',
      'Partial data loaded': 'تم تحميل بيانات جزئية',
      'Some project metrics could not be retrieved': 'تعذر استرجاع بعض مقاييس المشروع',
      'Error': 'خطأ',
      'Failed to load project details': 'فشل تحميل تفاصيل المشروع',

      // ProgramOverview.vue - Progress Breakdown
      'Progress Overview': 'نظرة عامة على التقدم',
      'Comprehensive view of program details and metrics': 'عرض شامل لتفاصيل ومقاييس البرنامج',
      'Overall': 'الإجمالي',
      'Weight': 'الوزن',
      'Progress Hierarchy': 'التسلسل الهرمي للتقدم',
      'No projects linked to this program': 'لا توجد مشاريع مرتبطة بهذا البرنامج',
      'No deliverables in this project': 'لا توجد مخرجات في هذا المشروع',
      'Unable to load progress data': 'تعذر تحميل بيانات التقدم',
      'Created By': 'أنشئ بواسطة',

      // Landing Page - HomeHero Component
      'Boost Efficiency': 'عزز الكفاءة',
      'Simplify, Smarter, Faster': 'بساطة، ذكاء، وسرعة',
      'Transform Your Business Operations': 'حول عملياتك التجارية',
      'With Intelligent Project Management': 'مع إدارة المشاريع الذكية',
      'Optimize your operations and streamline workflows effortlessly with our SaaS solutions, designed to enhance productivity and simplify your daily business processes.': 'قم بتحسين عملياتك وتبسيط سير العمل بسهولة مع حلول SaaS المصممة لتعزيز الإنتاجية وتبسيط عمليات عملك اليومية.',
      'Nexera your team with cutting-edge project management tools that deliver real-time insights, automated workflows, and seamless collaboration—all in one powerful platform designed for success.': 'قم بتمكين فريقك بأدوات إدارة المشاريع المتطورة التي توفر رؤى فورية، وسير عمل آلي، وتعاوناً سلساً - كل ذلك في منصة قوية واحدة مصممة للنجاح.',
      
      // CTA Buttons
      'Get Started Free': 'ابدأ مجاناً',
      'Start Your Free Trial': 'ابدأ تجربتك المجانية',
      'Watch Demo': 'شاهد العرض التوضيحي',
      'See How It Works': 'شاهد كيف يعمل',
      "I agree to the": 'أوافق على',
      'Terms of Service': 'شروط الخدمة',
      'and': 'و',
      'Privacy Policy': 'سياسة الخصوصية',
      "Sign in": 'تسجيل الدخول',
      "Create Account": 'إنشاء حساب',
      "Please fill in your details to register": 'يرجى ملء تفاصيلك للتسجيل',
      
      // Trust Indicators
      'Trusted by 10,000+ businesses worldwide': 'موثوق به من قبل أكثر من 10,000 شركة حول العالم',
      'Trusted by {count}+ businesses worldwide': 'موثوق به من قبل أكثر من {count} شركة حول العالم',
      'Trusted by industry leaders worldwide': 'موثوق به من قبل قادة الصناعة حول العالم',
      '4.9/5 from 2,500+ reviews': '4.9/5 من أكثر من 2,500 تقييم',
      '{rating}/5 from {count}+ reviews': '{rating}/5 من أكثر من {count} تقييم',
      'Join thousands of successful teams': 'انضم إلى آلاف الفرق الناجحة',
      
      // Video Modal
      'Product Demo Video': 'فيديو العرض التوضيحي للمنتج',
      'Hero Dashboard Preview': 'معاينة لوحة المعلومات الرئيسية',
      "Pending Changes for Submission": "التغييرات المعلقة للتقديم",
      "changes": "التغييرات",

      // Landing Page - HomeSolutions Component
      'Comprehensive Solutions for Modern Enterprises': 'حلول شاملة للمؤسسات الحديثة',
      'Discover our integrated suite of tools designed to transform how your organization manages projects, strategies, and governance.': 'اكتشف مجموعتنا المتكاملة من الأدوات المصممة لتحويل طريقة إدارة مؤسستك للمشاريع والاستراتيجيات والحوكمة.',
      
      // Solution Cards
      'Strategic Portfolio Management': 'الإدارة الاستراتيجية',
      'Align your strategic initiatives with organizational goals through comprehensive portfolio management. Track KPIs, manage resources, and ensure every project drives measurable business value.': 'قم بمواءمة مبادراتك الاستراتيجية مع أهداف المؤسسة من خلال إدارة المحفظة الشاملة. تتبع مؤشرات الأداء الرئيسية، وإدارة الموارد، وضمان تحقيق كل مشروع قيمة تجارية قابلة للقياس.',
      
      'Enterprise Project Management': 'إدارة المشاريع المؤسسية',
      'Execute projects with precision using our advanced project management platform. Features real-time collaboration, automated workflows, risk management, and comprehensive reporting to keep teams aligned and projects on track.': 'نفذ المشاريع بدقة باستخدام منصة إدارة المشاريع المتقدمة لدينا. تتميز بالتعاون الفوري، وسير العمل الآلي، وإدارة المخاطر، والتقارير الشاملة للحفاظ على توافق الفرق والمشاريع على المسار الصحيح.',
      
      'Governance & Committee Management': 'إدارة اللجان والمجالس',
      'Streamline board meetings, committee decisions, and organizational governance. Manage agendas, track resolutions, ensure compliance, and maintain comprehensive audit trails with our specialized governance module.': 'قم بتبسيط اجتماعات مجلس الإدارة، وقرارات اللجان، والحوكمة المؤسسية. إدارة جداول الأعمال، وتتبع القرارات، وضمان الامتثال، والحفاظ على سجلات تدقيق شاملة مع وحدة الحوكمة المتخصصة لدينا.',
      
      'Explore Solution': 'استكشف الحل',

      // Landing Page - FAQ Component
      'Frequently Asked Questions': 'الأسئلة الشائعة',
      'Find answers to common questions about our project management platform': 'اعثر على إجابات للأسئلة الشائعة حول منصة إدارة المشاريع لدينا',
      
      // FAQ Items
      'How does your platform improve project success rates?': 'كيف تحسن منصتكم معدلات نجاح المشاريع؟',
      'Our platform combines proven project management methodologies with intelligent automation and real-time analytics. Features like automated risk detection, resource optimization, and stakeholder communication ensure projects stay on track. Organizations using our platform report up to 40% improvement in on-time delivery and budget adherence.': 'تجمع منصتنا بين منهجيات إدارة المشاريع المعتمدة والأتمتة الذكية والتحليلات الفورية. تضمن ميزات مثل الكشف التلقائي عن المخاطر، وتحسين الموارد، والتواصل مع أصحاب المصلحة بقاء المشاريع على المسار الصحيح. تشير المؤسسات التي تستخدم منصتنا إلى تحسن يصل إلى 40٪ في التسليم في الوقت المحدد والالتزام بالميزانية.',
      
      'Can I integrate this with our existing tools and systems?': 'هل يمكنني الدمج مع أدواتنا وأنظمتنا الحالية؟',
      'Yes, our platform offers comprehensive integration capabilities with popular enterprise tools including Microsoft Teams, Slack, JIRA, SAP, and more. Our REST API allows custom integrations with proprietary systems. We also provide dedicated integration support to ensure seamless data flow across your technology ecosystem.': 'نعم، توفر منصتنا إمكانيات تكامل شاملة مع أدوات المؤسسات الشائعة بما في ذلك Microsoft Teams و Slack و JIRA و SAP والمزيد. تسمح واجهة برمجة التطبيقات REST الخاصة بنا بالتكامل المخصص مع الأنظمة الخاصة. نوفر أيضاً دعم تكامل مخصص لضمان تدفق البيانات السلس عبر نظام التكنولوجيا البيئي الخاص بك.',
      
      'What security measures protect our project data?': 'ما هي التدابير الأمنية التي تحمي بيانات مشاريعنا؟',
      'We implement enterprise-grade security including end-to-end encryption, multi-factor authentication, role-based access control, and regular security audits. Our infrastructure is ISO 27001 certified and SOC 2 Type II compliant. All data is stored in secure, geographically redundant data centers with automated backup and disaster recovery protocols.': 'نطبق أماناً على مستوى المؤسسات بما في ذلك التشفير الشامل، والمصادقة متعددة العوامل، والتحكم في الوصول على أساس الدور، وعمليات التدقيق الأمني المنتظمة. بنيتنا التحتية معتمدة ISO 27001 ومتوافقة مع SOC 2 Type II. يتم تخزين جميع البيانات في مراكز بيانات آمنة ومتكررة جغرافياً مع بروتوكولات النسخ الاحتياطي الآلي والاستعادة من الكوارث.',
      
      'How quickly can our team get started?': 'ما مدى سرعة بدء فريقنا؟',
      'Most teams are up and running within 24-48 hours. Our onboarding process includes guided setup, data migration assistance, customized training sessions, and dedicated support. We provide comprehensive documentation, video tutorials, and a responsive support team to ensure smooth adoption across your organization.': 'معظم الفرق تصبح جاهزة للعمل في غضون 24-48 ساعة. تتضمن عملية الإعداد لدينا إعداداً موجهاً، ومساعدة في ترحيل البيانات، وجلسات تدريبية مخصصة، ودعماً مخصصاً. نوفر وثائق شاملة، ودروس فيديو، وفريق دعم سريع الاستجابة لضمان التبني السلس عبر مؤسستك.',
      
      'What kind of support and training do you provide?': 'ما نوع الدعم والتدريب الذي تقدمونه؟',
      'We offer multiple support tiers including 24/7 priority support, dedicated account managers, and on-demand training. Our training programs cover basic to advanced features with live webinars, recorded sessions, and interactive workshops. Premium plans include personalized coaching and best practice consulting to maximize your platform ROI.': 'نقدم مستويات دعم متعددة بما في ذلك الدعم ذو الأولوية على مدار الساعة طوال أيام الأسبوع، ومديري حسابات مخصصين، والتدريب عند الطلب. تغطي برامجنا التدريبية الميزات الأساسية والمتقدمة مع ندوات مباشرة عبر الإنترنت، وجلسات مسجلة، وورش عمل تفاعلية. تتضمن الخطط المميزة التوجيه الشخصي والاستشارات حول أفضل الممارسات لتحقيق أقصى عائد على الاستثمار للمنصة.',
      
      'Can the platform scale as our organization grows?': 'هل يمكن للمنصة التوسع مع نمو مؤسستنا؟',
      'Absolutely. Our cloud-based architecture is designed for unlimited scalability. Whether you manage 10 projects or 10,000, the platform maintains peak performance. Features like multi-tenant architecture, dynamic resource allocation, and flexible licensing ensure the system grows seamlessly with your organization without requiring infrastructure changes.': 'بالتأكيد. تم تصميم بنيتنا التحتية المستندة إلى السحابة لقابلية التوسع غير المحدودة. سواء كنت تدير 10 مشاريع أو 10,000، تحافظ المنصة على الأداء الأمثل. تضمن ميزات مثل العمارة متعددة المستأجرين، وتخصيص الموارد الديناميكي، والترخيص المرن نمو النظام بسلاسة مع مؤسستك دون الحاجة إلى تغييرات في البنية التحتية.',

      // Landing Page - Navbar Component
      'Home': 'الرئيسية',
      'Features': 'الميزات',
      'Pricing': 'الأسعار',
      'Contact': 'اتصل بنا',
      'Login': 'تسجيل الدخول',
      // 'Nexera': 'سايب',
      'Nexera': 'نكسيرا',

      // Landing Page - Footer Component
      'Register': 'إنشاء حساب',
      'Nexera': 'نكسيرا',
      'Or sign in with email': 'أو تسجيل الدخول باستخدام البريد الإلكتروني',
      'Saudi Authority for Intellectual Property': 'الهيئة السعودية للملكية الفكرية',
      'Login with Nexera SSO' : 'تسجيل الدخول باستخدام تسجيل الدخول الموحد لنكسيرا',
      'Login with Al Istedamah Holding SSO' : 'تسجيل الدخول الموحد - الاستدامة القابضة',
      // Authentication - LoginView Component
      'Sign In': 'تسجيل الدخول',
      "Please login to your account": 'يرجى تسجيل الدخول إلى حسابك',
      "Welcome to Nexera": 'مرحباً بك في نكسيرا',
      // "Welcome to Nexera": 'مرحباً بك في سايب',
      "Welcome to Nexera": 'مرحباً بك في نكسيرا',
      "Forgot your password?": 'هل نسيت كلمة المرور؟',
      'Welcome back! Please sign in to continue.': 'حياك الله! يرجى تسجيل الدخول للمتابعة.',
      'Email Address': 'عنوان البريد الإلكتروني',
      'you@example.com': 'you@example.com',
      'Please enter a valid email address': 'يرجى إدخال عنوان بريد إلكتروني صحيح',
      'Password': 'كلمة المرور',
      'Forgot Password?': 'هل نسيت كلمة المرور؟',
      'Enter your password': 'أدخل كلمة المرور',
      'Password must be at least 6 characters': 'يجب أن تتكون كلمة المرور من 6 أحرف على الأقل',
      'Remember me': 'تذكرني',
      'OR': 'أو',
      'Continue with Google': 'المتابعة مع جوجل',
      'Continue with Microsoft': 'المتابعة مع مايكروسوفت',
      "Don't have an account?": 'ليس لديك حساب؟',
      'Create one here': 'أنشئ حساباً هنا',
      
      // Login Form Validation & Messages
      'Please check your email and password': 'يرجى التحقق من بريدك الإلكتروني وكلمة المرور',
      'Incomplete Form': 'نموذج غير مكتمل',
      'Welcome back!': 'حياك الله!',
      'Login Successful': 'تم تسجيل الدخول بنجاح',
      'Please check your credentials and try again': 'يرجى التحقق من بيانات الاعتماد والمحاولة مرة أخرى',
      'Login Failed': 'فشل تسجيل الدخول',
      'Google sign-in integration coming soon': 'تكامل تسجيل الدخول عبر جوجل قريباً',
      'Google OAuth': 'مصادقة جوجل',
      'Microsoft sign-in integration coming soon': 'تكامل تسجيل الدخول عبر مايكروسوفت قريباً',
      'Microsoft OAuth': 'مصادقة مايكروسوفت',

      // Authentication - CreateNewPasswordView Component
      'Create a New Password': 'إنشاء كلمة مرور جديدة',
      'Please enter your new password below': 'يرجى إدخال كلمة المرور الجديدة أدناه',
      'This password reset link is invalid. Please ask an administrator to send a new one.': 'رابط إعادة تعيين كلمة المرور هذا غير صالح. يرجى طلب رابط جديد من المسؤول.',
      'Your password has been reset. Redirecting to login…': 'تم إعادة تعيين كلمة المرور بنجاح. جارٍ التحويل إلى صفحة تسجيل الدخول…',
      'Enter your new password': 'أدخل كلمة المرور الجديدة',
      'Password must be at least 8 characters': 'يجب أن تتكون كلمة المرور من 8 أحرف على الأقل',
      'Repeat Password': 'تأكيد كلمة المرور',
      'Repeat your new password': 'أعد إدخال كلمة المرور الجديدة',
      'Passwords do not match': 'كلمتا المرور غير متطابقتين',
      'Change Password': 'تغيير كلمة المرور',
      'Please contact your administrator if you need help.': 'يرجى التواصل مع المسؤول إذا احتجت للمساعدة.',
      'Please check your new password': 'يرجى التحقق من كلمة المرور الجديدة',
      'Your password has been reset. You can now log in.': 'تم إعادة تعيين كلمة المرور بنجاح. يمكنك الآن تسجيل الدخول.',
      'Password Reset': 'إعادة تعيين كلمة المرور',
      'This password reset link is invalid or has expired.': 'رابط إعادة تعيين كلمة المرور غير صالح أو منتهي الصلاحية.',
      'Reset Failed': 'فشلت إعادة التعيين',

      // ProjectEscalations translations
      'Escalations Management': 'إدارة التصعيدات',
      "We're building a comprehensive escalation management system for tracking critical issues.": 'نحن نبني نظاماً شاملاً لإدارة التصعيدات لتتبع المشاكل الحرجة.',
      'Multi-Level Escalation Tracking': 'تتبع التصعيدات متعددة المستويات',
      'Severity & Priority Management': 'إدارة الخطورة والأولوية',
      'Automated Notifications & Alerts': 'الإشعارات والتنبيهات الآلية',
      'Resolution Timeline Tracking': 'تتبع الجدول الزمني للحل',
      'Stakeholder Communication Hub': 'مركز التواصل مع أصحاب المصلحة',
      'This feature will be available soon. Thank you for your patience!': 'ستكون هذه الميزة متاحة قريباً. شكراً لصبركم!',
      'Track and manage project escalations': 'تتبع وإدارة تصعيدات المشروع',
      'Create Escalation': 'إنشاء تصعيد',
      'Escalation Title': 'عنوان التصعيد',
      'Severity': 'الخطورة',
      'Escalated To': 'تصعيد إلى',
      'Reported By': 'تم الإبلاغ بواسطة',
      'Date Reported': 'تاريخ الإبلاغ',
      'Resolution Date': 'تاريخ الحل',
      'Escalation Duration (Days)': 'مدة التصعيد (أيام)',
      'Enter duration in days': 'أدخل المدة بالأيام',
      'Auto-escalate to next level when duration expires': 'تصعيد تلقائي للمستوى التالي عند انتهاء المدة',
      'Escalation Duration': 'مدة التصعيد',
      'Auto Escalated': 'تصعيد تلقائي',
      'Expired': 'منتهية',
      'days remaining': 'أيام متبقية',
      'Duration': 'المدة',
      'Days': 'أيام',
      'View Details': 'عرض التفاصيل',
      'Edit Escalation': 'تعديل التصعيد',
      'Send Notification': 'إرسال إشعار',
      'Delete Escalation': 'حذف التصعيد',
      'Cannot Delete': 'لا يمكن الحذف',
      'Escalate to Next Level': 'تصعيد للمستوى التالي',
      'Comment': 'تعليق',
      'Optional': 'اختياري',
      'Explain why this needs to be escalated further...': 'اشرح لماذا يحتاج هذا إلى مزيد من التصعيد...',
      'Escalate': 'تصعيد',
      'Escalation History': 'سجل التصعيد',
      'Validation Error': 'خطأ في التحقق',
      'Comment must be at least 10 characters if provided': 'يجب أن يكون التعليق 10 أحرف على الأقل إذا تم تقديمه',
      'Escalated Successfully': 'تم التصعيد بنجاح',
      '{title} has been escalated to next level': 'تم تصعيد {title} إلى المستوى التالي',
      'Escalation Failed': 'فشل التصعيد',
      'Failed to escalate': 'فشل التصعيد',
      'Financial': 'مالي',
      'Resource': 'موارد',
      'Technical': 'تقني',
      'Timeline': 'الجدول الزمني',
      'Compliance': 'الامتثال',
      'Escalated': 'مصعد',
      'Export Successful': 'تم التصدير بنجاح',
      'Escalations data exported successfully': 'تم تصدير بيانات التصعيدات بنجاح',
      'Export Failed': 'فشل التصدير',
      'Failed to export escalations data': 'فشل تصدير بيانات التصعيدات',
      'Settings Reset': 'تم إعادة تعيين الإعدادات',
      'Table settings have been reset to default': 'تم إعادة تعيين إعدادات الجدول إلى الافتراضي',
      'Settings Applied': 'تم تطبيق الإعدادات',
      'Table settings have been applied successfully': 'تم تطبيق إعدادات الجدول بنجاح',
      'Data Refreshed': 'تم تحديث البيانات',
      'Escalations data has been refreshed': 'تم تحديث بيانات التصعيدات',
      'Escalation Created': 'تم إنشاء التصعيد',
      '{title} has been created': 'تم إنشاء {title}',
      'View Escalation': 'عرض التصعيد',
      'Viewing details for {title}': 'عرض تفاصيل {title}',
      'Editing {title}': 'تعديل {title}',
      'Updating status for {title}': 'تحديث حالة {title}',
      'Sending notification for {title}': 'إرسال إشعار لـ {title}',
      'Escalation Deleted': 'تم حذف التصعيد',
      '{title} has been deleted': 'تم حذف {title}',

      // ProjectEscalations - additional translations
      'escalations': 'تصعيدات',
      'Close Escalation': 'إغلاق التصعيد',
      'Close Escalation': 'إغلاق التصعيد',
      'Confirm Close': 'تأكيد الإغلاق',
      'Please provide justification for closing this escalation': 'يرجى تقديم مبرر لإغلاق هذا التصعيد',
      'Escalation Closed': 'تم إغلاق التصعيد',
      '{title} has been marked as closed': 'تم تمييز {title} كمغلق',
      'Failed to close escalation': 'فشل في إغلاق التصعيد',
      'Justification Required': 'المبرر مطلوب',
      'Escalation Updated': 'تم تحديث التصعيد',
      '{title} has been updated': 'تم تحديث {title}',
      'Failed to save escalation': 'فشل في حفظ التصعيد',
      'Update Escalation': 'تحديث التصعيد',
      'Update Draft': 'تحديث المسودة',
      'Draft Updated': 'تم تحديث المسودة',
      'Draft Saved': 'تم حفظ المسودة',
      '{title} has been updated as draft': 'تم تحديث {title} كمسودة',
      '{title} has been saved as draft': 'تم حفظ {title} كمسودة',
      'Failed to save draft': 'فشل في حفظ المسودة',
      'Title is required to save as draft': 'العنوان مطلوب للحفظ كمسودة',
      'Edit Draft': 'تعديل المسودة',
      'Delete Draft': 'حذف المسودة',
      'Not assigned': 'غير مُعيّن',
      'Escalation Details': 'تفاصيل التصعيد',
      'Escalation History': 'سجل التصعيد',
      'Escalated from': 'تم التصعيد من',
      'Minimum 10 characters if provided': 'الحد الأدنى 10 أحرف إذا تم تقديمه',
      'user(s) available for escalation': 'مستخدم(ون) متاح(ون) للتصعيد',
      'Select a user to escalate to': 'اختر مستخدمًا للتصعيد إليه',
      'Please select a user to escalate to': 'يرجى اختيار مستخدم للتصعيد إليه',
      'can be reassigned to any user.': 'يمكن إعادة تعيينه لأي مستخدم.',
      'will be escalated from': 'سيتم تصعيده من',
      'level.': 'مستوى.',
      'Select a user and add a comment to explain the reassignment.': 'اختر مستخدمًا وأضف تعليقًا لشرح إعادة التعيين.',
      'Add a comment to explain why this escalation needs further attention.': 'أضف تعليقًا لشرح سبب حاجة هذا التصعيد لمزيد من الاهتمام.',
      'Failed to load available handlers': 'فشل في تحميل المستخدمين المتاحين',
      'Reminder Sent': 'تم إرسال التذكير',
      'Reminder sent successfully for {title}': 'تم إرسال التذكير بنجاح لـ {title}',
      'Failed to send reminder': 'فشل في إرسال التذكير',
      'Send Reminder': 'إرسال تذكير',
      'Cannot Escalate': 'لا يمكن التصعيد',
      'You are at the highest level (Sponsor) and cannot escalate further. Please assign to a lower level if needed.': 'أنت على أعلى مستوى (الراعي) ولا يمكنك التصعيد أكثر. يرجى التعيين لمستوى أدنى إذا لزم الأمر.',
      'Escalation title is required': 'عنوان التصعيد مطلوب',
      'Escalation title must be at least 3 characters': 'يجب أن يكون عنوان التصعيد 3 أحرف على الأقل',
      'Severity is required': 'الخطورة مطلوبة',
      'Date reported is required': 'تاريخ الإبلاغ مطلوب',
      'Expected resolution date is required': 'تاريخ الحل المتوقع مطلوب',
      'Unable to determine escalation target. Please contact administrator.': 'تعذر تحديد هدف التصعيد. يرجى الاتصال بالمسؤول.',
      'Your Current Level': 'مستواك الحالي',
      'Escalating To': 'التصعيد إلى',
      'Highest Level - Cannot escalate further': 'أعلى مستوى - لا يمكن التصعيد أكثر',
      'Manager Level': 'مستوى المدير',
      'Owner Level': 'مستوى المالك',
      'Committee Level': 'مستوى اللجنة',
      'Sponsor Level': 'مستوى الراعي',
      'No issues available': 'لا توجد مشاكل متاحة',
      'Select related issue (optional)': 'اختر المشكلة ذات الصلة (اختياري)',
      'Optional: Link this escalation to an issue': 'اختياري: ربط هذا التصعيد بمشكلة',
      'No issues found. Create an issue first or skip this field.': 'لم يتم العثور على مشاكل. أنشئ مشكلة أولاً أو تخطَّ هذا الحقل.',
      'No issues found': 'لم يتم العثور على مشاكل',
      'No issues available for this project': 'لا توجد مشاكل متاحة لهذا المشروع',
      'No risks available': 'لا توجد مخاطر متاحة',
      'Select related risk (optional)': 'اختر المخاطرة ذات الصلة (اختياري)',
      'Optional: Link this escalation to a risk': 'اختياري: ربط هذا التصعيد بمخاطرة',
      'No risks found. Create a risk first or skip this field.': 'لم يتم العثور على مخاطر. أنشئ مخاطرة أولاً أو تخطَّ هذا الحقل.',
      'No risks found': 'لم يتم العثور على مخاطر',
      'No risks available for this project': 'لا توجد مخاطر متاحة لهذا المشروع',
      'Select date reported': 'اختر تاريخ الإبلاغ',
      'Note: Project start date not set. Please configure project start date.': 'ملاحظة: لم يتم تعيين تاريخ بدء المشروع. يرجى تعيين تاريخ بدء المشروع.',
      'Select expected resolution date': 'اختر تاريخ الحل المتوقع',
      'Must be after date reported': 'يجب أن يكون بعد تاريخ الإبلاغ',
      'Could not load issues. You can still create the escalation.': 'تعذر تحميل المشاكل. لا يزال بإمكانك إنشاء التصعيد.',
      'Could not load risks. You can still create the escalation.': 'تعذر تحميل المخاطر. لا يزال بإمكانك إنشاء التصعيد.',
      'Failed to load escalations': 'فشل في تحميل التصعيدات',
      'Program Escalations': 'تصعيدات البرنامج',
      'Track and manage program escalations': 'تتبع وإدارة تصعيدات البرنامج',
      'No escalations have been reported for this program': 'لم يتم الإبلاغ عن تصعيدات لهذا البرنامج',
      'This program is closed. Data is view-only.': 'هذا البرنامج مغلق. البيانات للعرض فقط.',
      'Escalate to Owner': 'تصعيد إلى المالك',
      'Escalate to Sponsor': 'تصعيد إلى الراعي',
      'Escalation duration is required': 'مدة التصعيد مطلوبة',
      'Escalation duration must be at least 1 day': 'يجب أن تكون مدة التصعيد يوم واحد على الأقل',
      'Escalation Path (2 Levels)': 'مسار التصعيد (مستويان)',
      'Escalation will be sent to': 'سيتم إرسال التصعيد إلى',
      'Level 1 - Manager': 'المستوى 1 - المدير',
      'Level 2 - Owner': 'المستوى 2 - المالك',
      'No escalation target available': 'لا يوجد هدف تصعيد متاح',
      'Sponsor (Highest Level)': 'الراعي (أعلى مستوى)',
      'Search users...': 'بحث عن مستخدمين...',
      'Select escalation target': 'اختر هدف التصعيد',
      'Unknown Level': 'مستوى غير معروف',
      'No risks available for this program': 'لا توجد مخاطر متاحة لهذا البرنامج',

      // ProjectEscalations - remaining missing translations
      'Select severity': 'اختر الخطورة',
      'Related Issue': 'المشكلة ذات الصلة',
      'Related Risk': 'المخاطرة ذات الصلة',
      'Current Level': 'المستوى الحالي',
      'Closure Justification': 'مبرر الإغلاق',
      'Assign To': 'تعيين إلى',
      'Escalation Target': 'هدف التصعيد',
      'to': 'إلى',
      'Please fill in the following required fields: ': 'يرجى ملء الحقول المطلوبة التالية: ',
      'Failed to delete escalation': 'فشل في حذف التصعيد',
      'You are at the highest level and cannot escalate further. Please assign to a lower level if needed.': 'أنت على أعلى مستوى ولا يمكنك التصعيد أكثر. يرجى التعيين لمستوى أدنى إذا لزم الأمر.',
      'This project is closed. Data is view-only.': 'هذا المشروع مغلق. البيانات للعرض فقط.',

      // ProjectLessons translations
      'Lessons Learned': 'الدروس المستفادة',
      'Document and share project lessons': 'توثيق ومشاركة دروس المشروع',
      'Add Lesson': 'إضافة درس',
      'Project Lessons': 'دروس المشروع',
      '{count} lessons': '{count} دروس',
      'No lessons found': 'لم يتم العثور على دروس',
      'Great! No lessons have been documented yet': 'رائع! لم يتم توثيق أي دروس بعد',
      'Lesson Title': 'عنوان الدرس',
      'Enter lesson title': 'أدخل عنوان الدرس',
      'Classification': 'التصنيف',
      'Select classification': 'اختر التصنيف',
      'Specify Classification': 'حدد التصنيف',
      'Enter custom classification': 'أدخل التصنيف المخصص',
      'Enter detailed description of the lesson': 'أدخل وصفًا تفصيليًا للدرس',
      'Future Recommendations': 'التوصيات المستقبلية',
      'Provide actionable recommendations for future projects': 'قدم توصيات قابلة للتنفيذ للمشاريع المستقبلية',
      'External Party': 'الطرف الخارجي',
      'External Parties': 'الأطراف الخارجية',
      'Enter external party name': 'أدخل اسم الطرف الخارجي',
      'Enter external party name (if applicable)': 'أدخل اسم الطرف الخارجي (إن وجد)',
      'No external parties added': 'لم تتم إضافة أطراف خارجية',
      'Duplicate Entry': 'إدخال مكرر',
      'This external party has already been added': 'تمت إضافة هذا الطرف الخارجي بالفعل',
      'Edit Lesson': 'تعديل الدرس',
      'Update Lesson': 'تحديث الدرس',
      'Lesson Details': 'تفاصيل الدرس',
      'Delete Lesson': 'حذف الدرس',
      'No description provided': 'لم يتم تقديم وصف',
      'No recommendations provided': 'لم يتم تقديم توصيات',
      'Time Management': 'إدارة الوقت',
      'Cost Management': 'إدارة التكاليف',
      'Quality Management': 'إدارة الجودة',
      'Risk Management': 'إدارة المخاطر',
      'Others': 'أخرى',
      'Please enter lesson title': 'يرجى إدخال عنوان الدرس',
      'Lesson title must be at least 3 characters': 'يجب أن يكون عنوان الدرس 3 أحرف على الأقل',
      'Please select lesson classification': 'يرجى اختيار تصنيف الدرس',
      'Please select recorded date': 'يرجى اختيار تاريخ التسجيل',
      'Please enter lesson description': 'يرجى إدخال وصف الدرس',
      'Please enter future recommendations': 'يرجى إدخال التوصيات المستقبلية',
      'Please specify custom classification': 'يرجى تحديد التصنيف المخصص',
      'Custom classification must be at least 3 characters': 'يجب أن يكون التصنيف المخصص 3 أحرف على الأقل',
      'Please fill all required fields correctly': 'يرجى ملء جميع الحقول المطلوبة بشكل صحيح',
      'Project ID is required': 'معرف المشروع مطلوب',
      'Lesson Added': 'تم إضافة الدرس',
      'Lesson created successfully': 'تم إنشاء الدرس بنجاح',
      'Failed to add lesson': 'فشل في إضافة الدرس',
      'Lesson Updated': 'تم تحديث الدرس',
      'Lesson updated successfully': 'تم تحديث الدرس بنجاح',
      'Failed to update lesson': 'فشل في تحديث الدرس',
      'Lesson Deleted': 'تم حذف الدرس',
      'Lesson deleted successfully': 'تم حذف الدرس بنجاح',
      'Failed to delete lesson': 'فشل في حذف الدرس',
      'Failed to load lessons': 'فشل في تحميل الدروس',
      'Lessons data exported successfully': 'تم تصدير بيانات الدروس بنجاح',
      'Failed to export lessons data': 'فشل تصدير بيانات الدروس',
      'Lessons data refreshed successfully': 'تم تحديث بيانات الدروس بنجاح',
      'Table settings reset to default': 'تم إعادة تعيين إعدادات الجدول إلى الافتراضي',
      'Table settings applied successfully': 'تم تطبيق إعدادات الجدول بنجاح',
      'Are you sure you want to delete this lesson?': 'هل أنت متأكد أنك تريد حذف هذا الدرس؟',

      // ProjectMeeting translations
      'Project Meetings': 'اجتماعات المشروع',
      'Manage and track project meetings': 'إدارة وتتبع اجتماعات المشروع',
      'Add Meeting': 'إضافة اجتماع',
      'meetings': 'اجتماعات',
      'No meetings found': 'لم يتم العثور على اجتماعات',
      'Great! No meetings have been scheduled yet': 'رائع! لم يتم جدولة أي اجتماعات بعد',
      'Meeting Purpose': 'الغرض من الاجتماع',
      'Purpose of Meeting': 'الغرض من الاجتماع',
      'Enter meeting purpose': 'أدخل الغرض من الاجتماع',
      'Purpose is required': 'الغرض مطلوب',
      'Date of Meeting': 'تاريخ الاجتماع',
      'Date is required': 'التاريخ مطلوب',
      'Minute Taker': 'كاتب المحضر',
      'Enter minute taker name': 'أدخل اسم كاتب المحضر',
      'Minute taker is required': 'كاتب المحضر مطلوب',
      'Names of Attendees': 'أسماء الحضور',
      'Select attendees': 'اختر الحضور',
      'Loading users...': 'جارٍ تحميل المستخدمين...',
      'At least one attendee is required': 'مطلوب حضور واحد على الأقل',
      'Loading departments...': 'جارٍ تحميل الأقسام...',
      'Select department': 'اختر القسم',
      'Department is required': 'القسم مطلوب',
      'Meeting Summary': 'ملخص الاجتماع',
      'Enter meeting summary': 'أدخل ملخص الاجتماع',
      'Enter tasks from the meeting': 'أدخل المهام من الاجتماع',
      'Responsible Person': 'الشخص المسؤول',
      'Enter responsible person name': 'أدخل اسم الشخص المسؤول',
      'Completion Date': 'تاريخ الإنجاز',
      'Signature of Attendees': 'توقيع الحضور',
      'Edit Meeting': 'تعديل الاجتماع',
      'Delete Meeting': 'حذف الاجتماع',
      'Meeting Details': 'تفاصيل الاجتماع',
      'Confirm Delete': 'تأكيد الحذف',
      'Are you sure you want to delete this meeting?': 'هل أنت متأكد أنك تريد حذف هذا الاجتماع؟',
      'Meeting created successfully': 'تم إنشاء الاجتماع بنجاح',
      'Meeting updated successfully': 'تم تحديث الاجتماع بنجاح',
      'Failed to create meeting': 'فشل في إنشاء الاجتماع',
      'Failed to update meeting': 'فشل في تحديث الاجتماع',
      'Meeting deleted successfully': 'تم حذف الاجتماع بنجاح',
      'Failed to delete meeting': 'فشل في حذف الاجتماع',
      'Meetings refreshed': 'تم تحديث الاجتماعات',
      'Exporting meetings to CSV...': 'جارٍ تصدير الاجتماعات إلى CSV...',
      'Failed to load meetings': 'فشل في تحميل الاجتماعات',
      'Could not load users': 'تعذر تحميل المستخدمين',
      'Could not load departments': 'تعذر تحميل الأقسام',
      'Please fill in all required fields': 'يرجى ملء جميع الحقول المطلوبة',
      'Signature': 'التوقيع',
      'No attendees': 'لا يوجد حضور',
      'attendees': 'حضور',
      'Attendees': 'الحضور',
      'Date': 'التاريخ',
      'Meeting Date': 'تاريخ الاجتماع',
      'Purpose': 'الغرض',
      'Task': 'المهمة',
      'This project is closed. Data is view-only.': 'هذا المشروع مغلق. البيانات للعرض فقط.',
      'Rows per page': 'الصفوف لكل صفحة',
      'Show row numbers': 'إظهار أرقام الصفوف',
      'Striped rows': 'صفوف مخططة',
      'Show gridlines': 'إظهار خطوط الشبكة',
      'Frozen actions column': 'تجميد عمود الإجراءات',
      'Search execution items...': 'البحث في عناصر التنفيذ...',
      'All Categories': 'جميع الفئات',
      'All File Types': 'جميع أنواع الملفات',
      'Filter:': 'فلتر:',
      'Critical Only': 'حرج فقط',
      'Clear Filter': 'مسح الفلتر',
      'Execution Phase Deliverables': 'نواتج مرحلة التنفيذ',
      'Project deliverables and execution tracking': 'نواتج المشروع وتتبع التنفيذ',
      'Toggle Filters': 'تبديل الفلاتر',
      'Send for Approval': 'إرسال للموافقة',
      'Approve all completed deliverables': 'الموافقة على جميع النواتج المكتملة',
      'Grid': 'شبكة',
      'List': 'قائمة',
      'Due:': 'الاستحقاق:',
      'Progress:': 'التقدم:',
      'Download': 'تحميل',
      'Upload': 'رفع',
      'Replace': 'استبدال',
      'Word Document': 'مستند Word',
      'Excel Spreadsheet': 'جدول Excel',
      'Text File': 'ملف نصي',
      'Not Started': 'لم يبدأ',
      'In Progress': 'قيد التنفيذ',
      'Completed': 'مكتمل',
      'Approved': 'موافق عليه',
      'Upload New Deliverable': 'رفع ناتج جديد',
      'Choose a file or drag & drop': 'اختر ملفاً أو اسحب وأفلت',
      'PDF, Word, Excel, PowerPoint, Text, or Archive files': 'ملفات PDF أو Word أو Excel أو PowerPoint أو نص أو أرشيف',
      'Browse Files': 'تصفح الملفات',
      'Deliverable Name': 'اسم الناتج',
      'Enter deliverable name': 'أدخل اسم الناتج',
      'Category': 'الفئة',
      'Select category': 'اختر الفئة',
      'Description': 'الوصف',
      'Brief description of the deliverable': 'وصف موجز للناتج',
      'Tags': 'الوسوم',
      'Enter tags separated by commas': 'أدخل الوسوم مفصولة بفواصل',
      'Priority': 'الأولوية',
      'Select priority': 'اختر الأولوية',
      'Uploading...': 'جاري الرفع...',
      'Upload Deliverable': 'رفع الناتج',
      'Deliverable Preview': 'معاينة الناتج',
      'Upload:': 'رفع:',
      'Deliverable': 'الناتج',
      'Critical': 'حرج',
      
      // Execution Deliverable Names and Descriptions
      'Progress Reports': 'تقارير التقدم',
      'Regular status reports documenting project progress, milestones achieved, and current activities': 'تقارير حالة منتظمة توثق تقدم المشروع والمعالم المحققة والأنشطة الحالية',
      'Reporting': 'إعداد التقارير',
      'Meeting Minutes': 'محاضر الاجتماعات',
      'Documentation of all project meetings including decisions, action items, and attendees': 'توثيق جميع اجتماعات المشروع بما في ذلك القرارات وبنود العمل والحضور',
      'Quality Reports': 'تقارير الجودة',
      'Quality assurance reports, testing results, and quality metrics': 'تقارير ضمان الجودة ونتائج الاختبار ومقاييس الجودة',
      'Quality Assurance': 'ضمان الجودة',
      'Training/Handover Plan': 'خطة التدريب/التسليم',
      'Training materials, handover documentation, and knowledge transfer plan': 'مواد التدريب ووثائق التسليم وخطة نقل المعرفة',
      'Training': 'التدريب',
      
      // Additional Execution labels
      'Mandatory Only': 'إلزامية فقط',
      'Add More': 'إضافة المزيد',
      'Delete': 'حذف',
      'Waiting for Approval': 'في انتظار الموافقة',
      'Uploaded Files': 'الملفات المرفوعة',
      'Due': 'الاستحقاق',
      'Technical Deliverables': 'النواتج الفنية',
      'Development': 'التطوير',
      'Documentation': 'التوثيق',
      'Security': 'الأمان',
      'Testing': 'الاختبار',
      'Deployment': 'النشر',
      'Infrastructure': 'البنية التحتية',
      'High': 'عالي',
      'Medium': 'متوسط',
      'Low': 'منخفض',
      'Execution phase submitted for approval': 'تم إرسال مرحلة التنفيذ للموافقة',
      'Please complete mandatory deliverables before submitting for approval': 'يرجى إكمال النواتج الإلزامية قبل الإرسال للموافقة',
      'PDF, Word, Excel, PowerPoint, or TXT (Max 10MB per file)': 'PDF أو Word أو Excel أو PowerPoint أو TXT (حد أقصى 10 ميجابايت لكل ملف)',
      'Choose files or drag & drop': 'اختر الملفات أو اسحبها وأفلتها',
      'file': 'ملف',
      'files': 'ملفات',
      'selected': 'محدد',

      // ProjectClosure translations
      'Search closure items...': 'البحث عن عناصر الإغلاق...',
      'Closure Phase Activities': 'أنشطة مرحلة الإغلاق',
      'Project closure activities and final documentation': 'أنشطة إغلاق المشروع والتوثيق النهائي',
      'Toggle Filters': 'تبديل المرشحات',
      'Upload New Activity': 'رفع نشاط جديد',
      'Activity Preview': 'معاينة النشاط',
      'Activity': 'النشاط',
      'Activity Name': 'اسم النشاط',
      'Enter activity name': 'أدخل اسم النشاط',
      'Brief description of the activity': 'وصف موجز للنشاط',
      'Enter tags separated by commas': 'أدخل العلامات مفصولة بفواصل',
      'Select priority': 'اختر الأولوية',
      'Uploading...': 'جاري الرفع...',
      'Upload Activity': 'رفع النشاط',
      'Browse Files': 'تصفح الملفات',
      'Choose a file or drag & drop': 'اختر ملف أو اسحب وأفلت',
      'PDF, Word, Excel, PowerPoint, or TXT files (Max 10MB)': 'ملفات PDF أو Word أو Excel أو PowerPoint أو TXT (حد أقصى 10 ميجابايت)',
      
      // Closure Activity Names and Descriptions
      'Project Closure Report': 'تقرير إغلاق المشروع',
      'Comprehensive final report documenting project outcomes, achievements, and lessons learned': 'تقرير نهائي شامل يوثق نتائج المشروع والإنجازات والدروس المستفادة',
      'Acceptance Sign-off': 'التوقيع على القبول',
      'Formal acceptance and sign-off documentation from stakeholders and sponsors': 'توثيق القبول الرسمي والتوقيع من أصحاب المصلحة والرعاة',
      'Final Financial Report': 'التقرير المالي النهائي',
      'Complete financial report including budget reconciliation and cost analysis': 'تقرير مالي كامل يتضمن تسوية الميزانية وتحليل التكاليف',
      
      // Closure Categories
      'Final Deliverables': 'النواتج النهائية',
      'Lessons Learned': 'الدروس المستفادة',
      'Project Archival': 'أرشفة المشروع',
      'Team Release': 'إطلاق الفريق',
      'Administrative Closure': 'الإغلاق الإداري',
      'Project Review': 'مراجعة المشروع',
      'Knowledge Transfer': 'نقل المعرفة',
      'Financial Closure': 'الإغلاق المالي',
      'Risk Closure': 'إغلاق المخاطر',
      'Stakeholder Sign-off': 'توقيع أصحاب المصلحة',
      'Acceptance': 'القبول',
      
      // Closure Status
      'Not Started': 'لم يبدأ',
      
      // Closure Toast Messages
      'Download Failed': 'فشل التحميل',
      'Failed to download document': 'فشل تحميل المستند',
      'Deleted': 'تم الحذف',
      'Document deleted successfully': 'تم حذف المستند بنجاح',
      'Delete Failed': 'فشل الحذف',
      'Failed to delete document': 'فشل حذف المستند',
      'uploaded successfully': 'تم الرفع بنجاح',
      'Upload Failed': 'فشل الرفع',
      'Failed to upload file': 'فشل رفع الملف',
      'Closure phase submitted for approval': 'تم إرسال مرحلة الإغلاق للموافقة',
      'Please upload mandatory documents before submitting for approval': 'يرجى رفع المستندات الإلزامية قبل الإرسال للموافقة',
      'Failed to submit phase for approval': 'فشل إرسال المرحلة للموافقة',

      // ProjectFinancial translations
      'Financial Tracking': 'التتبع المالي',
      'Monitor budget and financial performance': 'مراقبة الميزانية والأداء المالي',
      'Add Item': 'إضافة عنصر',
      'You are viewing only your own financial items': 'أنت تشاهد العناصر المالية الخاصة بك فقط',
      'Pending Items for Submission': 'العناصر المعلقة للتقديم',
      'item': 'عنصر',
      's': '',
      'Submit All for Approval': 'تقديم الكل للموافقة',
      'Clear All': 'مسح الكل',
      'Item': 'العنصر',
      'Quantity': 'الكمية',
      'Cost': 'التكلفة',
      'CoC Status': 'حالة CoC',
      'Financial Items': 'العناصر المالية',
      'items': 'عناصر',
      'Export to CSV': 'تصدير إلى CSV',
      'Table Settings': 'إعدادات الجدول',
      'Refresh': 'تحديث',
      'No financial items': 'لا توجد عناصر مالية',
      'Start adding your project financial items': 'ابدأ بإضافة العناصر المالية للمشروع',
      '#': '#',
      'Status': 'الحالة',
      'Revised Quantity': 'الكمية المعدلة',
      'CoC Date': 'تاريخ CoC',
      'Expected CoC Date': 'تاريخ CoC المتوقع',
      'CoC Documents': 'مستندات CoC',
      'CoC Document': 'مستند CoC',
      'Payment Date': 'تاريخ الدفع',
      'Payment Status': 'حالة الدفع',
      'Payment Documents': 'مستندات الدفع',
      'Payment Document': 'مستند الدفع',
      'Actions': 'الإجراءات',
      'View Details': 'عرض التفاصيل',
      'Submit for Review': 'تقديم للمراجعة',
      'Edit & Resubmit': 'تعديل وإعادة تقديم',
      'Approve': 'موافقة',
      'Reject': 'رفض',
      'Edit': 'تعديل',
      'Remove': 'إزالة',
      'Download CoC Document': 'تحميل مستند CoC',
      'Download Payment Document': 'تحميل مستند الدفع',
      'Delete Item': 'حذف العنصر',
      'Under Review': 'قيد المراجعة',
      'Approved': 'تمت الموافقة',
      'Rejected': 'مرفوض',
      'Draft': 'مسودة',
      'Scheduled': 'مجدول',
      'Completed': 'مكتمل',
      'Paid': 'مدفوع',
      'Pending': 'قيد الانتظار',
      'Overdue': 'متأخر',
      
      // Dialog headers
      'Add Financial Item': 'إضافة عنصر مالي',
      'Edit Financial Item': 'تعديل عنصر مالي',
      'View Financial Item': 'عرض عنصر مالي',
      'Approve Financial Item': 'الموافقة على عنصر مالي',
      'Reject Financial Item': 'رفض عنصر مالي',
      
      // Form labels
      'Item Description': 'وصف العنصر',
      'Item Details': 'تفاصيل العنصر',
      'Cost (SAR)': 'التكلفة (ريال)',
      'Expected CoC Status': 'حالة CoC المتوقعة',
      'Certificate of Compliance (CoC)': 'شهادة المطابقة (CoC)',
      'Payment Information': 'معلومات الدفع',
      'Approval Comment': 'تعليق الموافقة',
      'Rejection Reason': 'سبب الرفض',
      'Approval Details': 'تفاصيل الموافقة',
      'Rejection Details': 'تفاصيل الرفض',
      'Approved By': 'تمت الموافقة من قبل',
      'Approval Date': 'تاريخ الموافقة',
      'Rejected By': 'رفض من قبل',
      'Rejection Date': 'تاريخ الرفض',
      'Requested By': 'طلب من قبل',
      'Request Date': 'تاريخ الطلب',
      'Admin Direct Submission': 'تقديم مباشر من المسؤول',
      'Your {role} role allows you to directly add financial items. Items will be added to the pending list and can be submitted for approval in bulk.': 'دور {role} الخاص بك يسمح لك بإضافة العناصر المالية مباشرة. سيتم إضافة العناصر إلى القائمة المعلقة ويمكن تقديمها للموافقة بشكل جماعي.',
      'Accepted formats: PDF, DOC, DOCX (Max 10MB per file). You can upload multiple documents.': 'التنسيقات المقبولة: PDF، DOC، DOCX (بحد أقصى 10 ميجابايت لكل ملف). يمكنك رفع عدة مستندات.',
      
      // Form placeholders
      'Enter item description': 'أدخل وصف العنصر',
      'Enter item name': 'أدخل اسم العنصر',
      'Enter quantity': 'أدخل الكمية',
      'e.g., 18,750': 'مثال: 18,750',
      'Enter cost': 'أدخل التكلفة',
      'Enter revised quantity': 'أدخل الكمية المعدلة',
      'Select expected CoC date': 'اختر تاريخ CoC المتوقع',
      'Select CoC status': 'اختر حالة CoC',
      'Select payment date': 'اختر تاريخ الدفع',
      'Select payment status': 'اختر حالة الدفع',
      'Add any comments about this approval...': 'أضف أي تعليقات حول هذه الموافقة...',
      'Please provide a reason for rejection...': 'يرجى تقديم سبب للرفض...',
      
      // Button labels
      'Save & Approve': 'حفظ وموافقة',
      'Update & Approve': 'تحديث وموافقة',
      'Add to List': 'إضافة للقائمة',
      'Save as Draft': 'حفظ كمسودة',
      'Submit for Review': 'تقديم للمراجعة',
      'Cancel': 'إلغاء',
      'Submit': 'تقديم',
      'Update Item': 'تحديث العنصر',
      'Close': 'إغلاق',
      'Reset to Default': 'إعادة تعيين إلى الافتراضي',
      'Apply Settings': 'تطبيق الإعدادات',
      
      // Toast messages - Change Request
      'Draft Saved': 'تم حفظ المسودة',
      'has been saved as draft': 'تم حفظه كمسودة',
      'Change Updated & Approved': 'تم تحديث التغيير والموافقة عليه',
      'has been updated and approved': 'تم تحديثه والموافقة عليه',
      'Incomplete Data': 'بيانات غير مكتملة',
      'Title, description, and category are required': 'العنوان والوصف والفئة مطلوبة',
      'Permission Denied': 'تم رفض الإذن',
      'You do not have permission to approve changes': 'ليس لديك إذن للموافقة على التغييرات',
      'Update Failed': 'فشل التحديث',
      'Failed to update and approve change': 'فشل تحديث التغيير والموافقة عليه',
      'Failed to save draft': 'فشل حفظ المسودة',
      'Submitted for Review': 'تم التقديم للمراجعة',
      'has been submitted for review': 'تم تقديمه للمراجعة',
      'Invalid Status': 'حالة غير صالحة',
      'Only draft changes can be submitted for review': 'فقط التغييرات المسودة يمكن تقديمها للمراجعة',
      'Submission Failed': 'فشل التقديم',
      'Failed to submit for review': 'فشل التقديم للمراجعة',
      'Edit & Submit': 'تحرير وتقديم',
      'Previously Approved': 'تمت الموافقة سابقاً',
      'Previously Rejected': 'تم الرفض سابقاً',
      'Approval Comment': 'تعليق الموافقة',
      'Rejection Reason': 'سبب الرفض',
      
      // File upload labels
      'Choose CoC Documents': 'اختر مستندات CoC',
      'Add More Documents': 'إضافة المزيد من المستندات',
      'Choose Payment Documents': 'اختر مستندات الدفع',
      'Upload': 'رفع',
      'Remove File': 'إزالة الملف',
      
      // Table settings labels
      'Column Visibility': 'رؤية الأعمدة',
      'Table Behavior': 'سلوك الجدول',
      'Layout Settings': 'إعدادات التخطيط',
      'Show Row Numbers': 'إظهار أرقام الصفوف',
      'Sortable Columns': 'أعمدة قابلة للفرز',
      'Scrollable Table': 'جدول قابل للتمرير',
      'Striped Rows': 'صفوف مخططة',
      'Show Gridlines': 'إظهار خطوط الشبكة',
      'Resizable Columns': 'أعمدة قابلة لتغيير الحجم',
      'Rows per Page': 'صفوف لكل صفحة',
      'Table Size': 'حجم الجدول',
      'Selection Mode': 'وضع التحديد',
      'Small': 'صغير',
      'Normal': 'عادي',
      'Large': 'كبير',
      'None': 'لا شيء',
      'Single': 'مفرد',
      'Multiple': 'متعدد',
      'ON': 'تشغيل',
      'OFF': 'إيقاف',
      
      // Toast messages
      'Item Submitted': 'تم تقديم العنصر',
      'Financial item has been submitted for review': 'تم تقديم العنصر المالي للمراجعة',
      'Failed to submit financial item': 'فشل تقديم العنصر المالي',
      'Item Saved': 'تم حفظ العنصر',
      'Financial item has been saved as draft': 'تم حفظ العنصر المالي كمسودة',
      'Failed to save financial item': 'فشل حفظ العنصر المالي',
      'Item Approved': 'تمت الموافقة على العنصر',
      'Financial item has been approved successfully': 'تمت الموافقة على العنصر المالي بنجاح',
      'Failed to approve financial item': 'فشل الموافقة على العنصر المالي',
      'Item Rejected': 'تم رفض العنصر',
      'Financial item has been rejected': 'تم رفض العنصر المالي',
      'Failed to reject financial item': 'فشل رفض العنصر المالي',
      'Item Updated': 'تم تحديث العنصر',
      'Financial item has been updated successfully': 'تم تحديث العنصر المالي بنجاح',
      'Failed to update financial item': 'فشل تحديث العنصر المالي',
      'Item Deleted': 'تم حذف العنصر',
      'Financial item has been deleted successfully': 'تم حذف العنصر المالي بنجاح',
      'Failed to delete financial item': 'فشل حذف العنصر المالي',
      'Item Added': 'تم إضافة العنصر',
      'Financial item has been added to the list': 'تم إضافة العنصر المالي إلى القائمة',
      'All Items Submitted': 'تم تقديم جميع العناصر',
      '{count} items have been submitted for approval': 'تم تقديم {count} عنصر للموافقة',
      'Failed to submit items': 'فشل تقديم العناصر',
      'All Items Cleared': 'تم مسح جميع العناصر',
      'All pending items have been cleared': 'تم مسح جميع العناصر المعلقة',
      'Export Successful': 'نجح التصدير',
      'Financial items exported successfully': 'تم تصدير العناصر المالية بنجاح',
      'Export Failed': 'فشل التصدير',
      'Failed to export financial items': 'فشل تصدير العناصر المالية',
      'Data Refreshed': 'تم تحديث البيانات',
      'Financial items refreshed successfully': 'تم تحديث العناصر المالية بنجاح',
      'Table Settings Reset': 'تم إعادة تعيين إعدادات الجدول',
      'Table settings have been reset to default': 'تم إعادة تعيين إعدادات الجدول إلى الافتراضية',
      'Settings Applied': 'تم تطبيق الإعدادات',
      'Table settings have been applied': 'تم تطبيق إعدادات الجدول',
      'Validation Error': 'خطأ في التحقق',
      'Please fill in all required fields': 'يرجى ملء جميع الحقول المطلوبة',
      'Please provide a rejection reason': 'يرجى تقديم سبب للرفض',
      'Load Failed': 'فشل التحميل',
      'Failed to load financial items': 'فشل تحميل العناصر المالية',
      
      // Confirmation messages
      'You are about to approve the following financial item:': 'أنت على وشك الموافقة على العنصر المالي التالي:',
      'You are about to reject the following financial item:': 'أنت على وشك رفض العنصر المالي التالي:',
      'You are about to submit the following item for review:': 'أنت على وشك تقديم العنصر التالي للمراجعة:',
      'Once submitted, the item will be locked for editing until it is approved or rejected.': 'بمجرد التقديم، سيتم قفل العنصر للتعديل حتى تتم الموافقة عليه أو رفضه.',
      'Optional': 'اختياري',
      'Are you sure you want to delete this financial item?': 'هل أنت متأكد من أنك تريد حذف هذا العنصر المالي؟',
      'This action cannot be undone.': 'لا يمكن التراجع عن هذا الإجراء.',
      'Are you sure you want to clear all pending items?': 'هل أنت متأكد من أنك تريد مسح جميع العناصر المعلقة؟',
      'Are you sure you want to submit this item for review?': 'هل أنت متأكد من أنك تريد تقديم هذا العنصر للمراجعة؟',
      'Once submitted, you cannot edit until it is reviewed.': 'بمجرد التقديم، لا يمكنك التعديل حتى تتم المراجعة.',

      // ProjectGates translations
      'Project Gates & Phases': 'بوابات ومراحل المشروع',
      'We\'re building a comprehensive gate management system for tracking project milestones and phase transitions.': 'نحن نبني نظامًا شاملاً لإدارة البوابات لتتبع معالم المشروع وانتقالات المراحل.',
      'Multi-Phase Project Tracking': 'تتبع المشروع متعدد المراحل',
      'Gate Approval Workflows': 'سير عمل الموافقة على البوابة',
      'Milestone Achievement Tracking': 'تتبع إنجاز المعالم',
      'Phase Transition Criteria': 'معايير الانتقال بين المراحل',
      'Visual Progress Indicators': 'مؤشرات التقدم المرئية',
      'This feature will be available soon. Thank you for your patience!': 'ستكون هذه الميزة متاحة قريبًا. شكرًا لصبرك!',
      'Project Gates': 'بوابات المشروع',
      'Manage project phases and milestones': 'إدارة مراحل ومعالم المشروع',
      'Reset Progress': 'إعادة تعيين التقدم',
      'Planning': 'التخطيط',
      'Execution': 'التنفيذ',
      'Closure': 'الإغلاق',
      'Define project scope, objectives and charter': 'تحديد نطاق المشروع والأهداف والميثاق',
      'Create detailed project plans and strategies': 'إنشاء خطط واستراتيجيات مفصلة للمشروع',
      'Execute project deliverables and monitor progress': 'تنفيذ نواتج المشروع ومراقبة التقدم',
      'Finalize deliverables and close project': 'إنهاء النواتج وإغلاق المشروع',
      'Move to Planning': 'الانتقال إلى التخطيط',
      'Move to Execution': 'الانتقال إلى التنفيذ',
      'Move to Closure': 'الانتقال إلى الإغلاق',
      'Back to Initiation': 'العودة إلى البدء',
      'Back to Planning': 'العودة إلى التخطيط',
      'Back to Execution': 'العودة إلى التنفيذ',
      'Complete': 'إكمال',
      'Phase Completed': 'اكتملت المرحلة',
      '{phase} phase completed successfully': 'اكتملت مرحلة {phase} بنجاح',
      'Progress Reset': 'إعادة تعيين التقدم',
      'Project progress has been reset to the beginning': 'تم إعادة تعيين تقدم المشروع إلى البداية',
      'Project Completed': 'اكتمل المشروع',
      'Congratulations! The project has been completed successfully': 'تهانينا! تم إكمال المشروع بنجاح',

      // ProjectIssues translations
      'Issue Register': 'سجل المشاكل',
      'Register and resolve project issues': 'تسجيل وحل مشاكل المشروع',
      'Add Issue': 'إضافة مشكلة',
      'Project Issues': 'مشاكل المشروع',
      'issues': 'مشاكل',
      'No issues found': 'لم يتم العثور على مشاكل',
      'Great! No issues have been reported yet': 'رائع! لم يتم الإبلاغ عن أي مشاكل بعد',
      'Issue Title': 'عنوان المشكلة',
      'Linked to Risk': 'مرتبط بالمخاطر',
      'Report Date': 'تاريخ التقرير',
      'Due Date': 'تاريخ الاستحقاق',
      'View Details': 'عرض التفاصيل',
      'Edit Issue': 'تعديل المشكلة',
      'Delete Issue': 'حذف المشكلة',
      'Resource': 'المورد',
      'Regulatory': 'تنظيمي',
      'External': 'خارجي',
      'No description provided': 'لم يتم تقديم وصف',
      'Not Linked': 'غير مرتبط',
      'More Actions': 'المزيد من الإجراءات',
      'Enter issue title': 'أدخل عنوان المشكلة',
      'Select category': 'اختر الفئة',
      'Describe the issue in detail': 'وصف المشكلة بالتفصيل',
      'Select priority': 'اختر الأولوية',
      'Select impact': 'اختر التأثير',
      'Select status': 'اختر الحالة',
      'Enter owner name': 'أدخل اسم المالك',
      'Select a risk (optional)': 'اختر مخاطرة (اختياري)',
      'Select report date': 'اختر تاريخ التقرير',
      'Select due date': 'اختر تاريخ الاستحقاق',
      'Project dates not set': 'لم يتم تعيين تواريخ المشروع',
      'Must be after linked risk entry date': 'يجب أن يكون بعد تاريخ إدخال المخاطرة المرتبطة',
      'Please select report date first': 'الرجاء تحديد تاريخ التقرير أولاً',
      'Resolution Notes': 'ملاحظات الحل',
      'Enter resolution notes or comments': 'أدخل ملاحظات الحل أو التعليقات',
      'Column Visibility': 'ظهور الأعمدة',
      'Table Behavior': 'سلوك الجدول',
      'Rows per page': 'الصفوف لكل صفحة',
      'Striped Rows': 'صفوف مخططة',
      'Show Gridlines': 'إظهار خطوط الشبكة',
      'Resizable Columns': 'أعمدة قابلة لتغيير الحجم',
      'Layout Settings': 'إعدادات التخطيط',
      'Table Size': 'حجم الجدول',
      'Selection Mode': 'وضع التحديد',
      'Show Row Numbers': 'إظهار أرقام الصفوف',
      'Scrollable': 'قابل للتمرير',
      'Sortable Columns': 'أعمدة قابلة للفرز',
      'Reset to Default': 'إعادة تعيين إلى الافتراضي',
      'Apply Settings': 'تطبيق الإعدادات',
      'Issue Details': 'تفاصيل المشكلة',
      'Edit': 'تعديل',
      'Issue Added': 'تمت إضافة المشكلة',
      '{title} has been added successfully': 'تمت إضافة {title} بنجاح',
      'Validation Error': 'خطأ في التحقق',
      'Please fill in all required fields': 'الرجاء ملء جميع الحقول المطلوبة',
      'Invalid Date': 'تاريخ غير صحيح',
      'Report date must be after the linked risk entry date': 'يجب أن يكون تاريخ التقرير بعد تاريخ إدخال المخاطرة المرتبطة',
      'Report date must be on or after the project start date': 'يجب أن يكون تاريخ التقرير في أو بعد تاريخ بدء المشروع',
      'Report date must be on or before the project end date': 'يجب أن يكون تاريخ التقرير في أو قبل تاريخ انتهاء المشروع',
      'Date Reset': 'إعادة تعيين التاريخ',
      'Due date has been reset due to report date change': 'تم إعادة تعيين تاريخ الاستحقاق بسبب تغيير تاريخ التقرير',
      'Due date must be after the report date': 'يجب أن يكون تاريخ الاستحقاق بعد تاريخ التقرير',
      'Due date must be on or before the project end date': 'يجب أن يكون تاريخ الاستحقاق في أو قبل تاريخ انتهاء المشروع',
      'Missing Report Date': 'تاريخ التقرير مفقود',
      'Report date is required': 'تاريخ التقرير مطلوب',
      'Invalid Date Sequence': 'تسلسل تاريخ غير صحيح',
      'Invalid Date Range': 'نطاق تاريخ غير صحيح',
      'All dates must be within project duration': 'يجب أن تكون جميع التواريخ ضمن مدة المشروع',
      'Dates Reset': 'إعادة تعيين التواريخ',
      'Report and due dates have been reset due to linked risk change. Please select dates after the risk entry date.': 'تم إعادة تعيين تاريخ التقرير والاستحقاق بسبب تغيير المخاطرة المرتبطة. الرجاء تحديد التواريخ بعد تاريخ إدخال المخاطرة.',
      'Missing Project': 'المشروع مفقود',
      'Project information is required before creating an issue.': 'معلومات المشروع مطلوبة قبل إنشاء مشكلة.',
      'Failed to add issue': 'فشل في إضافة المشكلة',
      'Update Issue': 'تحديث المشكلة',
      'Issue Updated': 'تم تحديث المشكلة',
      '{title} has been updated successfully': 'تم تحديث {title} بنجاح',
      '{title} has been updated and linked risk status changed to "Closed"': 'تم تحديث {title} وتم تغيير حالة المخاطرة المرتبطة إلى "مغلق"',
      'Failed to update issue': 'فشل في تحديث المشكلة',
      'Project information is required before updating an issue.': 'معلومات المشروع مطلوبة قبل تحديث مشكلة.',
      'Issue Deleted': 'تم حذف المشكلة',
      '{title} has been deleted': 'تم حذف {title}',
      'Failed to delete issue': 'فشل في حذف المشكلة',
      'Export Successful': 'نجح التصدير',
      'Issues exported successfully': 'تم تصدير المشاكل بنجاح',
      'Export Failed': 'فشل التصدير',
      'Failed to export issues': 'فشل في تصدير المشاكل',
      'Settings Reset': 'إعادة تعيين الإعدادات',
      'Table settings have been reset to default': 'تم إعادة تعيين إعدادات الجدول إلى الافتراضي',
      'Settings Applied': 'تم تطبيق الإعدادات',
      'Table settings have been applied successfully': 'تم تطبيق إعدادات الجدول بنجاح',
      'Data Refreshed': 'تم تحديث البيانات',
      'Issues have been refreshed': 'تم تحديث المشاكل',
      'Failed to load issues': 'فشل في تحميل المشاكل',
      'Select linked risk': 'اختر المخاطرة المرتبطة',
      'Additional notes or comments': 'ملاحظات أو تعليقات إضافية',
      "Type is required": "النوع مطلوب",
      "Level of influence is required": "مستوى التأثير مطلوب",
      "Level of interest is required": "مستوى الاهتمام مطلوب",
      "Engagement strategy is required": "استراتيجية المشاركة مطلوبة",
      "Expectations are required": "التوقعات مطلوبة",
      "Communication method is required": "طريقة الاتصال مطلوبة",
      "Communication frequency is required": "تكرار الاتصال مطلوب",


      // ProjectLessons translations
      'projectLessons.header.title': 'الدروس المستفادة',
      'projectLessons.header.subtitle': 'التقاط ومشاركة رؤى المشروع',
      'projectLessons.buttons.addLesson': 'إضافة درس',
      'projectLessons.buttons.updateLesson': 'تحديث الدرس',
      'projectLessons.buttons.editLesson': 'تعديل الدرس',
      'projectLessons.buttons.cancel': 'إلغاء',
      'projectLessons.buttons.close': 'إغلاق',
      'projectLessons.table.title': 'دروس المشروع',
      'projectLessons.table.lessonsCount': '{count} دروس',
      'projectLessons.tooltips.exportToCsv': 'التصدير إلى CSV',
      'projectLessons.tooltips.tableSettings': 'إعدادات الجدول',
      'projectLessons.tooltips.refresh': 'تحديث',
      'projectLessons.tooltips.moreActions': 'المزيد من الإجراءات',
      'projectLessons.empty.title': 'لم يتم تسجيل أي دروس بعد',
      'projectLessons.empty.subtitle': 'ابدأ بتوثيق الرؤى والدروس من مشروعك',
      'projectLessons.columns.number': '#',
      'projectLessons.columns.lessonTitle': 'عنوان الدرس',
      'projectLessons.columns.category': 'الفئة',
      'projectLessons.columns.type': 'النوع',
      'projectLessons.columns.impactLevel': 'مستوى التأثير',
      'projectLessons.columns.projectPhase': 'مرحلة المشروع',
      'projectLessons.columns.applicability': 'قابلية التطبيق',
      'projectLessons.columns.dateRecorded': 'تاريخ التسجيل',
      'projectLessons.columns.summary': 'الملخص',
      'projectLessons.columns.recommendations': 'التوصيات',
      'projectLessons.columns.actions': 'الإجراءات',
      'projectLessons.messages.noSummary': 'لم يتم تقديم ملخص',
      'projectLessons.messages.noRecommendations': 'لم يتم تقديم توصيات',
      'projectLessons.messages.unknown': 'غير معروف',
      'projectLessons.menu.viewDetails': 'عرض التفاصيل',
      'projectLessons.menu.editLesson': 'تعديل الدرس',
      'projectLessons.menu.deleteLesson': 'حذف الدرس',
      'projectLessons.dialog.addTitle': 'إضافة درس مستفاد',
      'projectLessons.dialog.editTitle': 'تعديل درس مستفاد',
      'projectLessons.form.lessonTitle': 'عنوان الدرس',
      'projectLessons.form.lessonTitlePlaceholder': 'أدخل عنوان الدرس',
      'projectLessons.form.category': 'الفئة',
      'projectLessons.form.selectCategory': 'اختر الفئة',
      'projectLessons.form.type': 'النوع',
      'projectLessons.form.selectType': 'اختر النوع',
      'projectLessons.form.impactLevel': 'مستوى التأثير',
      'projectLessons.form.selectImpact': 'اختر التأثير',
      'projectLessons.form.projectPhase': 'مرحلة المشروع',
      'projectLessons.form.selectPhase': 'اختر المرحلة',
      'projectLessons.form.applicability': 'قابلية التطبيق',
      'projectLessons.form.selectApplicability': 'اختر قابلية التطبيق',
      'projectLessons.form.dateRecorded': 'تاريخ التسجيل',
      'projectLessons.form.selectDate': 'اختر التاريخ',
      'projectLessons.form.status': 'الحالة',
      'projectLessons.form.selectStatus': 'اختر الحالة',
      'projectLessons.form.summary': 'الملخص',
      'projectLessons.form.summaryPlaceholder': 'ملخص موجز للدرس المستفاد',
      'projectLessons.form.recommendations': 'التوصيات',
      'projectLessons.form.recommendationsPlaceholder': 'توصيات للمشاريع المستقبلية بناءً على هذا الدرس',
      'projectLessons.categories.communication': 'التواصل',
      'projectLessons.categories.technical': 'تقني',
      'projectLessons.categories.process': 'العملية',
      'projectLessons.categories.resourceManagement': 'إدارة الموارد',
      'projectLessons.categories.qualityAssurance': 'ضمان الجودة',
      'projectLessons.categories.riskManagement': 'إدارة المخاطر',
      'projectLessons.categories.budgetManagement': 'إدارة الميزانية',
      'projectLessons.categories.stakeholderManagement': 'إدارة أصحاب المصلحة',
      'projectLessons.categories.changeManagement': 'إدارة التغيير',
      'projectLessons.types.success': 'نجاح',
      'projectLessons.types.challenge': 'تحدي',
      'projectLessons.impact.low': 'منخفض',
      'projectLessons.impact.medium': 'متوسط',
      'projectLessons.impact.high': 'عالي',
      'projectLessons.phases.initiation': 'البداية',
      'projectLessons.phases.planning': 'التخطيط',
      'projectLessons.phases.execution': 'التنفيذ',
      'projectLessons.phases.monitoring': 'المراقبة',
      'projectLessons.phases.closure': 'الإغلاق',
      'projectLessons.applicability.allProjects': 'جميع المشاريع',
      'projectLessons.applicability.softwareProjects': 'مشاريع البرمجيات',
      'projectLessons.applicability.largeProjects': 'المشاريع الكبيرة',
      'projectLessons.applicability.agileProjects': 'المشاريع الرشيقة',
      'projectLessons.applicability.waterfallProjects': 'مشاريع الشلال',
      'projectLessons.applicability.remoteTeams': 'الفرق عن بُعد',
      'projectLessons.applicability.crossFunctionalTeams': 'الفرق متعددة الوظائف',
      'projectLessons.status.active': 'نشط',
      'projectLessons.status.inactive': 'غير نشط',
      'projectLessons.viewDialog.title': 'تفاصيل الدرس',
      'projectLessons.viewDialog.category': 'الفئة',
      'projectLessons.viewDialog.projectPhase': 'مرحلة المشروع',
      'projectLessons.viewDialog.applicability': 'قابلية التطبيق',
      'projectLessons.viewDialog.dateRecorded': 'تاريخ التسجيل',
      'projectLessons.viewDialog.author': 'المؤلف',
      'projectLessons.viewDialog.summary': 'الملخص',
      'projectLessons.viewDialog.recommendations': 'التوصيات',
      'projectLessons.tableSettings.title': 'إعدادات الجدول',
      'projectLessons.tableSettings.columnVisibility': 'ظهور الأعمدة',
      'projectLessons.tableSettings.tableBehavior': 'سلوك الجدول',
      'projectLessons.tableSettings.rowsPerPage': 'الصفوف لكل صفحة',
      'projectLessons.tableSettings.rows10': '10 صفوف',
      'projectLessons.tableSettings.rows25': '25 صف',
      'projectLessons.tableSettings.rows50': '50 صف',
      'projectLessons.tableSettings.rows100': '100 صف',
      'projectLessons.tableSettings.stripedRows': 'صفوف مخططة',
      'projectLessons.tableSettings.gridLines': 'خطوط الشبكة',
      'projectLessons.tableSettings.resizableColumns': 'أعمدة قابلة لتغيير الحجم',
      'projectLessons.tableSettings.layoutSettings': 'إعدادات التخطيط',
      'projectLessons.tableSettings.tableSize': 'حجم الجدول',
      'projectLessons.tableSettings.small': 'صغير',
      'projectLessons.tableSettings.normal': 'عادي',
      'projectLessons.tableSettings.large': 'كبير',
      'projectLessons.tableSettings.selectionMode': 'وضع التحديد',
      'projectLessons.tableSettings.none': 'لا شيء',
      'projectLessons.tableSettings.single': 'واحد',
      'projectLessons.tableSettings.multiple': 'متعدد',
      'projectLessons.tableSettings.showRowNumbers': 'إظهار أرقام الصفوف',
      'projectLessons.tableSettings.scrollable': 'قابل للتمرير',
      'projectLessons.tableSettings.sortableColumns': 'أعمدة قابلة للفرز',
      'projectLessons.tableSettings.on': 'تشغيل',
      'projectLessons.tableSettings.off': 'إيقاف',
      'projectLessons.tableSettings.resetToDefault': 'إعادة تعيين إلى الافتراضي',
      'projectLessons.tableSettings.applySettings': 'تطبيق الإعدادات',
      'projectLessons.toast.exportSuccessful': 'نجح التصدير',
      'projectLessons.toast.exportSuccessfulDetail': 'تم تصدير بيانات الدروس المستفادة بنجاح',
      'projectLessons.toast.exportFailed': 'فشل التصدير',
      'projectLessons.toast.exportFailedDetail': 'فشل في تصدير بيانات الدروس المستفادة',
      'projectLessons.toast.settingsReset': 'إعادة تعيين الإعدادات',
      'projectLessons.toast.settingsResetDetail': 'تم إعادة تعيين إعدادات الجدول إلى الافتراضي',
      'projectLessons.toast.settingsApplied': 'تم تطبيق الإعدادات',
      'projectLessons.toast.settingsAppliedDetail': 'تم تطبيق إعدادات الجدول بنجاح',
      'projectLessons.toast.dataRefreshed': 'تم تحديث البيانات',
      'projectLessons.toast.dataRefreshedDetail': 'تم تحديث بيانات الدروس المستفادة',
      'projectLessons.toast.error': 'خطأ',
      'projectLessons.toast.failedToLoadLessons': 'فشل في تحميل الدروس',
      'projectLessons.toast.projectIdRequired': 'معرف المشروع مطلوب',
      'projectLessons.toast.lessonAdded': 'تمت إضافة الدرس',
      'projectLessons.toast.lessonAddedDetail': 'تمت إضافة الدرس بنجاح',
      'projectLessons.toast.failedToAddLesson': 'فشل في إضافة الدرس',
      'projectLessons.toast.validationError': 'خطأ في التحقق',
      'projectLessons.toast.fillAllRequired': 'الرجاء ملء جميع الحقول المطلوبة',
      'projectLessons.toast.lessonUpdated': 'تم تحديث الدرس',
      'projectLessons.toast.lessonUpdatedDetail': 'تم تحديث الدرس بنجاح',
      'projectLessons.toast.failedToUpdateLesson': 'فشل في تحديث الدرس',
      'projectLessons.toast.lessonDeleted': 'تم حذف الدرس',
      'projectLessons.toast.lessonDeletedDetail': 'تم حذف {title}',
      'projectLessons.toast.failedToDeleteLesson': 'فشل في حذف الدرس',
      'projectLessons.confirmDelete': 'هل أنت متأكد من رغبتك في حذف "{title}"؟ لا يمكن التراجع عن هذا الإجراء.',

      // ProjectPlan translations
      'projectPlan.search.placeholder': 'البحث عن المستندات...',
      'projectPlan.filters.allCategories': 'جميع الفئات',
      'projectPlan.filters.allFileTypes': 'جميع أنواع الملفات',
      'projectPlan.filters.label': 'تصفية:',
      'projectPlan.filters.mandatoryOnly': 'إلزامية فقط',
      'projectPlan.filters.clearFilter': 'مسح التصفية',
      'projectPlan.header.title': 'مستندات مرحلة التخطيط',
      'projectPlan.header.subtitle': 'المستندات المطلوبة لتخطيط المشروع',
      'projectPlan.tooltips.toggleFilters': 'تبديل الفلاتر',
      'projectPlan.tooltips.approveAll': 'الموافقة على جميع المستندات المرفوعة',
      'projectPlan.tooltips.download': 'تنزيل',
      'projectPlan.buttons.sendForApproval': 'إرسال للموافقة',
      'projectPlan.buttons.upload': 'رفع',
      'projectPlan.buttons.replace': 'استبدال',
      'projectPlan.buttons.browseFiles': 'تصفح الملفات',
      'projectPlan.buttons.uploadPlan': 'رفع الخطة',
      'projectPlan.buttons.uploadDocument': 'رفع المستند',
      'projectPlan.buttons.cancel': 'إلغاء',
      'projectPlan.buttons.download': 'تنزيل',
      'projectPlan.buttons.close': 'إغلاق',
      'projectPlan.badges.mandatory': 'إلزامي',
      'projectPlan.labels.due': 'الاستحقاق',
      'projectPlan.labels.category': 'الفئة',
      'projectPlan.labels.fileSize': 'حجم الملف',
      'projectPlan.labels.uploadDate': 'تاريخ الرفع',
      'projectPlan.labels.downloads': 'التنزيلات',
      'projectPlan.labels.description': 'الوصف',
      'projectPlan.labels.document': 'المستند',
      'projectPlan.status.completed': 'مكتمل',
      'projectPlan.status.approved': 'موافق عليه',
      'projectPlan.status.pendingApproval': 'في انتظار الموافقة',
      'projectPlan.status.pendingUpload': 'في انتظار الرفع',
      'projectPlan.dialogs.uploadNew': 'رفع خطة جديدة',
      'projectPlan.dialogs.preview': 'معاينة الخطة',
      'projectPlan.dialogs.uploadDocument': 'رفع: {name}',
      'projectPlan.upload.chooseFile': 'اختر ملفًا أو اسحبه وأفلته',
      'projectPlan.upload.fileTypes': 'ملفات PDF أو Word أو Excel أو PowerPoint أو نصية أو أرشيف',
      'projectPlan.upload.uploading': 'جاري الرفع...',
      'projectPlan.form.planName': 'اسم الخطة',
      'projectPlan.form.planNamePlaceholder': 'أدخل اسم الخطة',
      'projectPlan.form.category': 'الفئة',
      'projectPlan.form.selectCategory': 'اختر الفئة',
      'projectPlan.form.description': 'الوصف',
      'projectPlan.form.descriptionPlaceholder': 'وصف موجز للخطة',
      'projectPlan.form.tags': 'العلامات',
      'projectPlan.form.tagsPlaceholder': 'أدخل العلامات مفصولة بفواصل',
      'projectPlan.form.accessLevel': 'مستوى الوصول',
      'projectPlan.form.selectAccessLevel': 'اختر مستوى الوصول',
      'projectPlan.documents.wbs': 'هيكل تقسيم العمل (WBS)',
      'projectPlan.documents.wbsDescription': 'تقسيم مفصل لعمل المشروع إلى مكونات يمكن إدارتها',
      'projectPlan.documents.schedule': 'جدول المشروع/الجدول الزمني',
      'projectPlan.documents.scheduleDescription': 'جدول زمني شامل للمشروع مع المعالم والتبعيات',
      'projectPlan.documents.resourceAllocation': 'خطة تخصيص الموارد',
      'projectPlan.documents.resourceAllocationDescription': 'خطة تفصل متطلبات الموارد والتوافر والمهام',
      'projectPlan.documents.communication': 'خطة التواصل',
      'projectPlan.documents.communicationDescription': 'استراتيجية وبروتوكولات التواصل مع أصحاب المصلحة',
      'projectPlan.documents.qualityManagement': 'خطة إدارة الجودة',
      'projectPlan.documents.qualityManagementDescription': 'معايير الجودة والعمليات وإجراءات المراقبة',
      'projectPlan.documents.riskManagement': 'خطة إدارة المخاطر',
      'projectPlan.documents.riskManagementDescription': 'تحديد المخاطر وتحليلها واستراتيجيات التخفيف',
      'projectPlan.categories.workStructure': 'هيكل العمل',
      'projectPlan.categories.scheduling': 'الجدولة',
      'projectPlan.categories.resourcePlanning': 'تخطيط الموارد',
      'projectPlan.categories.communication': 'التواصل',
      'projectPlan.categories.qualityManagement': 'إدارة الجودة',
      'projectPlan.categories.riskManagement': 'إدارة المخاطر',
      'projectPlan.fileTypes.pdf': 'PDF',
      'projectPlan.fileTypes.word': 'مستند Word',
      'projectPlan.fileTypes.excel': 'جدول بيانات Excel',
      'projectPlan.fileTypes.powerpoint': 'عرض PowerPoint',
      'projectPlan.fileTypes.text': 'ملف نصي',
      'projectPlan.fileTypes.archive': 'أرشيف',
      'projectPlan.view.grid': 'شبكة',
      'projectPlan.view.list': 'قائمة',
      'projectPlan.accessLevel.public': 'عام',
      'projectPlan.accessLevel.private': 'خاص',
      'projectPlan.accessLevel.restricted': 'مقيد',
      
      // Planning Document Names and Descriptions
      'Project Management Plan': 'خطة إدارة المشروع',
      'Comprehensive document defining how the project is executed, monitored and controlled': 'وثيقة شاملة تحدد كيفية تنفيذ المشروع ومراقبته والتحكم فيه',
      'Management Plans': 'خطط الإدارة',
      'Cost Baseline': 'خط الأساس للتكلفة',
      'Time-phased budget used to measure and monitor cost performance': 'ميزانية مرحلية تُستخدم لقياس ومراقبة أداء التكلفة',
      'Budget & Cost': 'الميزانية والتكلفة',
      'Risk Management Plan & Register': 'خطة وسجل إدارة المخاطر',
      'Plan for managing risks and register documenting identified risks': 'خطة لإدارة المخاطر وسجل يوثق المخاطر المحددة',
      'Risk Management': 'إدارة المخاطر',
      'Change Request Plan': 'خطة طلب التغيير',
      'Document describing how changes will be managed and controlled': 'وثيقة تصف كيفية إدارة التغييرات والتحكم فيها',
      'Change Request': 'طلب تغيير',
      'RACI Matrix': 'مصفوفة RACI',
      'Matrix defining Responsible, Accountable, Consulted, and Informed roles': 'مصفوفة تحدد الأدوار المسؤولة والمساءلة والمستشارة والمطلعة',
      'Roles & Responsibilities': 'الأدوار والمسؤوليات',
      
      // Additional ProjectPlan labels
      'projectPlan.labels.uploadedFiles': 'الملفات المرفوعة:',
      'projectPlan.tooltips.delete': 'حذف',
      'Add More': 'إضافة المزيد',
      'Delete': 'حذف',
      'Waiting for Approval': 'في انتظار الموافقة',
      'Choose files or drag & drop': 'اختر الملفات أو اسحبها وأفلتها',
      'PDF, Word, Excel, PowerPoint, or TXT (Max 10MB per file)': 'PDF أو Word أو Excel أو PowerPoint أو TXT (حد أقصى 10 ميجابايت لكل ملف)',
      'file': 'ملف',
      'files': 'ملفات',
      'selected': 'محدد',
      'Clear All': 'مسح الكل',
      'Planning phase submitted for approval': 'تم إرسال مرحلة التخطيط للموافقة',
      'files selected': 'ملفات محددة',
      'PDF, Word, Excel, PowerPoint, or TXT files (Max 10MB)': 'ملفات PDF أو Word أو Excel أو PowerPoint أو TXT (حد أقصى 10 ميجابايت)',
      
      // Project Resources
      'Resources': 'الموارد',
      'Manage project Resources and roles': 'إدارة موارد المشروع والأدوار',
      'Add Resource': 'إضافة مورد',
      'members': 'أعضاء',
      'Export to CSV': 'تصدير إلى CSV',
      'Table Settings': 'إعدادات الجدول',
      'Refresh': 'تحديث',
      'No resource found': 'لم يتم العثور على موارد',
      'Get started by adding your first resource': 'ابدأ بإضافة أول مورد',
      '#': '#',
      'Name': 'الاسم',
      'Member Name': 'اسم العضو',
      'Role': 'الدور',
      'Department': 'القسم',
      'Status': 'الحالة',
      'Join Date': 'تاريخ الانضمام',
      'Actions': 'الإجراءات',
      'Not specified': 'غير محدد',
      'More Actions': 'المزيد من الإجراءات',
      'Edit Resource': 'تعديل المورد',
      'Full Name': 'الاسم الكامل',
      'Enter full name': 'أدخل الاسم الكامل',
      'Email': 'البريد الإلكتروني',
      'Enter email address': 'أدخل عنوان البريد الإلكتروني',
      'Enter role (e.g., Developer, Designer)': 'أدخل الدور (مثل: مطور، مصمم)',
      'Select department': 'اختر القسم',
      'Select join date': 'اختر تاريخ الانضمام',
      'Select status': 'اختر الحالة',
      'Update Resource': 'تحديث المورد',
      'Cancel': 'إلغاء',
      'Resource Profile': 'ملف المورد',
      'Hours per Week': 'ساعات في الأسبوع',
      'Not set': 'غير محدد',
      'Close': 'إغلاق',
      'Member details not available.': 'تفاصيل العضو غير متوفرة.',
      'Column Visibility': 'رؤية الأعمدة',
      'ON': 'تشغيل',
      'OFF': 'إيقاف',
      'Table Behavior': 'سلوك الجدول',
      'Rows per Page': 'الصفوف لكل صفحة',
      '10 rows': '10 صفوف',
      '25 rows': '25 صفًا',
      '50 rows': '50 صفًا',
      '100 rows': '100 صف',
      'Striped Rows': 'صفوف مخططة',
      'Grid Lines': 'خطوط الشبكة',
      'Resizable Columns': 'أعمدة قابلة لتغيير الحجم',
      'Layout Settings': 'إعدادات التخطيط',
      'Table Size': 'حجم الجدول',
      'Small': 'صغير',
      'Normal': 'عادي',
      'Large': 'كبير',
      'Selection Mode': 'وضع التحديد',
      'None': 'لا شيء',
      'Single': 'فردي',
      'Multiple': 'متعدد',
      'Show Row Numbers': 'إظهار أرقام الصفوف',
      'Scrollable': 'قابل للتمرير',
      'Sortable Columns': 'أعمدة قابلة للفرز',
      'Reset to Default': 'إعادة تعيين إلى الافتراضي',
      'Apply Settings': 'تطبيق الإعدادات',
      'View Profile': 'عرض الملف الشخصي',
      'Edit Member': 'تعديل العضو',
      'Remove Member': 'إزالة العضو',
      'Active': 'نشط',
      'Inactive': 'غير نشط',
      'Name is required': 'الاسم مطلوب',
      'Email is required': 'البريد الإلكتروني مطلوب',
      'Please enter a valid email address': 'يرجى إدخال عنوان بريد إلكتروني صالح',
      'Role is required': 'الدور مطلوب',
      'Validation Error': 'خطأ في التحقق',
      'Please fill in all required fields correctly': 'يرجى ملء جميع الحقول المطلوبة بشكل صحيح',
      'Success': 'نجاح',
      'Resource updated successfully': 'تم تحديث المورد بنجاح',
      'Resource added successfully': 'تمت إضافة المورد بنجاح',
      'Error': 'خطأ',
      'Failed to save resource': 'فشل حفظ المورد',
      'Are you sure you want to remove {name}?': 'هل أنت متأكد من إزالة {name}؟',
      'Confirm Deletion': 'تأكيد الحذف',
      'Resource deleted successfully': 'تم حذف المورد بنجاح',
      'Failed to delete resource': 'فشل حذف المورد',
      'Export Successful': 'نجح التصدير',
      'Resources data exported successfully': 'تم تصدير بيانات الموارد بنجاح',
      'Export Failed': 'فشل التصدير',
      'Failed to export resources data': 'فشل تصدير بيانات الموارد',
      'Settings Reset': 'تم إعادة تعيين الإعدادات',
      'Table settings have been reset to default': 'تمت إعادة تعيين إعدادات الجدول إلى الافتراضي',
      'Settings Applied': 'تم تطبيق الإعدادات',
      'Table settings have been applied successfully': 'تم تطبيق إعدادات الجدول بنجاح',
      'Data Refreshed': 'تم تحديث البيانات',
      'Resources data has been refreshed': 'تم تحديث بيانات الموارد',
      'Failed to load departments. Please try again.': 'فشل تحميل الأقسام. يرجى المحاولة مرة أخرى.',
      'Failed to load resources': 'فشل تحميل الموارد',
      
      // Project Risks
      'Risk Management': 'إدارة المخاطر',
      'Identify, assess, and manage project risks': 'تحديد وتقييم وإدارة مخاطر المشروع',
      'Add Risk': 'إضافة مخاطرة',
      'Project Risks': 'مخاطر المشروع',
      'risks': 'مخاطر',
      'No risks identified': 'لم يتم تحديد أي مخاطر',
      'Get started by identifying your first project risk': 'ابدأ بتحديد أول مخاطرة للمشروع',
      'Risk Description': 'وصف المخاطرة',
      'Risk Owner': 'مالك المخاطرة',
      'Expected Closing Date': 'تاريخ الإغلاق المتوقع',
      'Actual Closing Date': 'تاريخ الإغلاق الفعلي',
      'Not Closed': 'غير مغلقة',
      'Risk Details': 'تفاصيل المخاطرة',
      'Edit Risk': 'تعديل المخاطرة',
      'Remove Risk': 'إزالة المخاطرة',
      'Risk Title': 'عنوان المخاطرة',
      'Enter risk title': 'أدخل عنوان المخاطرة',
      'Select category': 'اختر الفئة',
      'Description': 'الوصف',
      'Describe the risk in detail': 'قم بوصف المخاطرة بالتفصيل',
      'Cause of Risk': 'سبب المخاطرة',
      'Describe the cause or source of this risk': 'قم بوصف سبب أو مصدر هذه المخاطرة',
      'Expected Impact': 'التأثير المتوقع',
      'Describe the expected impact if this risk occurs': 'قم بوصف التأثير المتوقع إذا حدثت هذه المخاطرة',
      'Select probability': 'اختر الاحتمالية',
      'Select impact': 'اختر التأثير',
      'Select impact rating': 'اختر تقييم التأثير',
      'Impact Rating': 'تقييم التأثير',
      'Impact rating is required': 'تقييم التأثير مطلوب',
      'Select status': 'اختر الحالة',
      'Enter risk owner name': 'أدخل اسم مالك المخاطرة',
      'Project dates not set. Please set project start and end dates first.': 'لم يتم تعيين تواريخ المشروع. يرجى تعيين تواريخ بداية ونهاية المشروع أولاً.',
      'Please select entry date first': 'يرجى اختيار تاريخ الإدخال أولاً',
      'Select expected closing date': 'اختر تاريخ الإغلاق المتوقع',
      'Please select review date first': 'يرجى اختيار تاريخ المراجعة أولاً',
      'Select actual closing date': 'اختر تاريخ الإغلاق الفعلي',
      'Set this date when the risk is actually closed/resolved': 'قم بتعيين هذا التاريخ عندما يتم إغلاق/حل المخاطرة فعلياً',
      'Mitigation Plan': 'خطة التخفيف',
      'Describe the mitigation strategies': 'قم بوصف استراتيجيات التخفيف',
      'Update Risk': 'تحديث المخاطرة',
      'Technical': 'تقني',
      'Financial': 'مالي',
      'Resource': 'موارد',
      'Schedule': 'جدول',
      'Quality': 'جودة',
      'Security': 'أمان',
      'Regulatory': 'تنظيمي',
      'External': 'خارجي',
      'High': 'عالي',
      'Medium': 'متوسط',
      'Low': 'منخفض',
      'Open': 'مفتوحة',
      'In Progress': 'قيد التنفيذ',
      'Resolved': 'محلولة',
      'Closed': 'مغلقة',
      'Failed to load risks': 'فشل تحميل المخاطر',
      'Risk added successfully': 'تمت إضافة المخاطرة بنجاح',
      'Failed to add risk': 'فشل في إضافة المخاطرة',
      'Risk updated successfully': 'تم تحديث المخاطرة بنجاح',
      'Failed to update risk': 'فشل في تحديث المخاطرة',
      'Are you sure you want to remove this risk?': 'هل أنت متأكد من إزالة هذه المخاطرة؟',
      'Confirm Risk Deletion': 'تأكيد حذف المخاطرة',
      'Risk removed successfully': 'تمت إزالة المخاطرة بنجاح',
      'Failed to remove risk': 'فشل في إزالة المخاطرة',
      'Risks data exported successfully': 'تم تصدير بيانات المخاطر بنجاح',
      'Failed to export risks data': 'فشل في تصدير بيانات المخاطر',
      'Risks data has been refreshed': 'تم تحديث بيانات المخاطر',
      'Entry date must be before review date': 'يجب أن يكون تاريخ الإدخال قبل تاريخ المراجعة',
      'Review date must be after entry date': 'يجب أن يكون تاريخ المراجعة بعد تاريخ الإدخال',
      'Expected closing date must be after review date': 'يجب أن يكون تاريخ الإغلاق المتوقع بعد تاريخ المراجعة',

      // ProjectSchedule translations
      'schedule.filters.title': 'تصفية الجدول',
      'schedule.filters.taskName': 'اسم المهمة',
      'schedule.filters.taskNamePlaceholder': 'تصفية حسب اسم المهمة',
      'schedule.filters.status': 'الحالة',
      'schedule.filters.allStatuses': 'كل الحالات',
      'schedule.filters.statusPlaceholder': 'تصفية حسب الحالة',
      'schedule.filters.assignedTo': 'مسند إلى',
      'schedule.filters.assignedToPlaceholder': 'تصفية حسب المسند إليه',
      'schedule.filters.progress': 'التقدم',
      'schedule.filters.allProgress': 'كل التقدم',
      'schedule.filters.notStarted': 'لم تبدأ (0%)',
      'schedule.filters.inProgress': 'قيد التقدم (1-99%)',
      'schedule.filters.completed': 'مكتمل (100%)',
      'schedule.filters.progressPlaceholder': 'تصفية حسب التقدم',
      'schedule.filters.clearFilters': 'مسح المرشحات',
      'schedule.filters.tasksShown': '{filtered} من {total} مهمة معروضة',

      'schedule.status.notStarted': 'لم تبدأ',
      'schedule.status.inProgress': 'قيد التنفيذ',
      'schedule.status.completed': 'مكتمل',
      'schedule.status.onHold': 'معلق',
      'schedule.status.cancelled': 'ملغى',

      'schedule.rowType.parent': 'صف أساسي',
      'schedule.rowType.child': 'صف فرعي',
      'schedule.rowType.task': 'مهمة',

      'schedule.dateFormat.mmddyyyy': 'شهر/يوم/سنة',
      'schedule.dateFormat.ddmmyyyy': 'يوم/شهر/سنة',
      'schedule.dateFormat.yyyymmdd': 'سنة-شهر-يوم',

      'schedule.days.monday': 'الاثنين',
      'schedule.days.tuesday': 'الثلاثاء',
      'schedule.days.wednesday': 'الأربعاء',
      'schedule.days.thursday': 'الخميس',
      'schedule.days.friday': 'الجمعة',
      'schedule.days.saturday': 'السبت',
      'schedule.days.sunday': 'الأحد',

      'schedule.columnType.text': 'نص',
      'schedule.columnType.textDesc': 'إدخال نص من سطر واحد',
      'schedule.columnType.dropdown': 'قائمة منسدلة',
      'schedule.columnType.dropdownDesc': 'اختر من خيارات محددة مسبقاً',
      'schedule.columnType.date': 'تاريخ',
      'schedule.columnType.dateDesc': 'منتقي التاريخ',
      'schedule.columnType.longText': 'نص طويل',
      'schedule.columnType.longTextDesc': 'منطقة نص متعددة الأسطر',
      'schedule.columnType.number': 'رقم',
      'schedule.columnType.numberDesc': 'إدخال رقمي',
      'schedule.columnType.labels': 'تسميات',
      'schedule.columnType.labelsDesc': 'تسميات/علامات متعددة',
      'schedule.columnType.checkbox': 'مربع اختيار',
      'schedule.columnType.checkboxDesc': 'مربع اختيار صحيح/خطأ',
      'schedule.columnType.files': 'ملفات',
      'schedule.columnType.filesDesc': 'رفع الملفات',
      'schedule.columnType.email': 'بريد إلكتروني',
      'schedule.columnType.emailDesc': 'إدخال عنوان البريد الإلكتروني',
      'schedule.columnType.phone': 'هاتف',
      'schedule.columnType.phoneDesc': 'إدخال رقم الهاتف',
      'schedule.columnType.location': 'موقع',
      'schedule.columnType.locationDesc': 'إدخال العنوان/الموقع',
      'schedule.columnType.progress': 'شريط التقدم',
      'schedule.columnType.progressDesc': 'حساب التقدم التلقائي',
      'schedule.columnType.summary': 'ملخص',
      'schedule.columnType.summaryDesc': 'ملخص للبيانات ذات الصلة',
      'schedule.columnType.custom': 'نص مخصص',
      'schedule.columnType.customDesc': 'نص مُنسق حسب الطلب',
      'schedule.columnType.rating': 'تقييم',
      'schedule.columnType.ratingDesc': 'نظام التقييم بالنجوم',
      'schedule.columnType.button': 'زر',
      'schedule.columnType.buttonDesc': 'زر إجراء',
      'schedule.columnType.currency': 'عملة',
      'schedule.columnType.currencyDesc': 'مبلغ العملة',

      'schedule.alignment.left': 'يسار',
      'schedule.alignment.center': 'وسط',
      'schedule.alignment.right': 'يمين',

      'schedule.position.atEnd': 'في النهاية',
      'schedule.position.afterTaskName': 'بعد اسم المهمة',
      'schedule.position.beforeStatus': 'قبل الحالة',
      'schedule.position.beforeComments': 'قبل التعليقات',

      'schedule.export.asXML': 'تصدير كـ XML (.xml)',
      'schedule.export.asMPP': 'تصدير كـ MPP (.mpp)',

      'schedule.dialog.addNewRow': 'إضافة صف جديد',
      'schedule.dialog.rowType': 'نوع الصف',
      'schedule.dialog.selectType': 'اختر النوع',
      'schedule.dialog.parent': 'الأصل',
      'schedule.dialog.selectParent': 'اختر الأصل (اختياري)',
      'schedule.dialog.taskName': 'اسم المهمة',
      'schedule.dialog.enterTaskName': 'أدخل اسم المهمة',
      'schedule.dialog.duration': 'المدة',
      'schedule.dialog.durationPlaceholder': 'مثال: 5 أيام، 2 أسابيع',
      'schedule.dialog.startDate': 'تاريخ البدء',
      'schedule.dialog.assignedTo': 'مسند إلى',
      'schedule.dialog.selectAssignee': 'اختر المسند إليه',
      'schedule.dialog.status': 'الحالة',
      'schedule.dialog.selectStatus': 'اختر الحالة',
      'schedule.dialog.addRow': 'إضافة صف',
      'schedule.dialog.comments': 'التعليقات',
      'schedule.dialog.addCommentPlaceholder': 'إضافة تعليق...',
      'schedule.dialog.addComment': 'إضافة تعليق',
      'schedule.dialog.settings': 'إعدادات الجدول الذكي',
      'schedule.dialog.displayOptions': 'خيارات العرض',
      'schedule.dialog.showRowNumbers': 'إظهار أرقام الصفوف',
      'schedule.dialog.showGridLines': 'إظهار خطوط الشبكة',
      'schedule.dialog.freezeColumns': 'تجميد الأعمدة',
      'schedule.dialog.autoSave': 'الحفظ التلقائي',
      'schedule.dialog.dateTime': 'التاريخ والوقت',
      'schedule.dialog.dateFormat': 'تنسيق التاريخ',
      'schedule.dialog.workingDays': 'أيام العمل',

      'schedule.toast.resourcesLoading': 'تحميل الموارد',
      'schedule.toast.resourcesLoadingDetail': 'استخدام قائمة الموارد الاحتياطية. الموارد النظامية غير متاحة.',
      'schedule.toast.baselineError': 'خطأ في عرض خط الأساس',
      'schedule.toast.baselineErrorDetail': 'فشل في عرض خطوط الأساس للمشروع',

      'schedule.menu.taskInformation': 'معلومات المهمة...',
      'schedule.menu.insertRowAbove': 'إدراج صف أعلى',
      'schedule.menu.insertRowBelow': 'إدراج صف أسفل',
      'schedule.menu.schedule': 'الجدولة',
      'schedule.menu.manualSchedule': 'جدولة يدوية',
      'schedule.menu.autoSchedule': 'جدولة تلقائية',
      'schedule.menu.respectLinks': 'احترام الروابط',
      'schedule.menu.activateTask': 'تنشيط المهمة',
      'schedule.menu.inactivateTask': 'تعطيل المهمة',
      'schedule.menu.markOnTrack': 'وضع علامة في المسار',
      'schedule.menu.splitTask': 'تقسيم المهمة...',
      'schedule.menu.addColumn': 'إضافة عمود',
      'schedule.menu.duplicate': 'تكرار',
      'schedule.menu.insertTask': 'إدراج مهمة',
      'schedule.menu.deleteTask': 'حذف المهمة',
      'schedule.menu.cutCell': 'قص الخلية',
      'schedule.menu.copyCell': 'نسخ الخلية',
      'schedule.menu.scrollToTask': 'التمرير إلى المهمة',
      'schedule.menu.manuallySchedule': 'جدولة يدوية',

      'common.cut': 'قص',
      'common.copy': 'نسخ',
      'common.paste': 'لصق',
      'common.delete': 'حذف',
      'common.edit': 'تعديل',
      'common.cancel': 'إلغاء',

      // TabPlaceholder translations
      'tabPlaceholder.comingSoon': 'قريباً',
      'tabPlaceholder.underDevelopment': 'هذا القسم قيد التطوير حالياً.',
      'tabPlaceholder.requestFeature': 'طلب ميزة',
      'tabPlaceholder.learnMore': 'تعلم المزيد',

      // ProjectStakeholders translations
      'Stakeholders': 'أصحاب المصلحة',
      'Manage stakeholder engagement and influence': 'إدارة مشاركة وتأثير أصحاب المصلحة',
      'Add Stakeholder': 'إضافة صاحب مصلحة',
      '{count} Stakeholders': '{count} أصحاب مصلحة',
      'Export to CSV': 'التصدير إلى CSV',
      'Table Settings': 'إعدادات الجدول',
      'Refresh': 'تحديث',
      'No stakeholders found': 'لم يتم العثور على أصحاب مصلحة',
      'Get started by adding your first stakeholder': 'ابدأ بإضافة صاحب مصلحة الأول',
      'Not set': 'غير محدد',

      // Column headers
      '#': '#',
      'Name': 'الاسم',
      'Role': 'الدور',
      'Type': 'النوع',
      'Internal': 'داخلي',
      'External': 'خارجي',
      'Level of Influence': 'مستوى التأثير',
      'Level of Interest': 'مستوى الاهتمام',
      'High': 'عالي',
      'Medium': 'متوسط',
      'Low': 'منخفض',
      'Engagement Strategy': 'استراتيجية المشاركة',
      'Expectations': 'التوقعات',
      'Communication Method': 'طريقة التواصل',
      'Communication Frequency': 'تكرار التواصل',
      'Email': 'البريد الإلكتروني',
      'Phone Number': 'رقم الهاتف',
      'Comment': 'تعليق',
      'Actions': 'الإجراءات',
      
      // Dialog section headers
      'Basic Information': 'المعلومات الأساسية',
      'Influence & Interest': 'التأثير والاهتمام',
      'Engagement & Expectations': 'المشاركة والتوقعات',
      'Communication': 'الاتصال',
      'Additional Information': 'معلومات إضافية',
      'Contact Information': 'معلومات الاتصال',
      
      // Form labels and placeholders
      'Enter full name': 'أدخل الاسم الكامل',
      'Enter stakeholder role': 'أدخل دور صاحب المصلحة',
      'Select type': 'اختر النوع',
      'Select influence level': 'اختر مستوى التأثير',
      'Select interest level': 'اختر مستوى الاهتمام',
      'Describe how to engage with this stakeholder': 'صف كيفية التعامل مع صاحب المصلحة هذا',
      'What does this stakeholder expect from the project?': 'ماذا يتوقع صاحب المصلحة هذا من المشروع؟',
      'Select communication method': 'اختر طريقة التواصل',
      'Select frequency': 'اختر التكرار',
      'Enter email address': 'أدخل عنوان البريد الإلكتروني',
      'Only numbers are allowed (format: 05XXXXXXXX)': 'الأرقام فقط مسموحة (التنسيق: 05XXXXXXXX)',
      'Phone number must be exactly 10 digits': 'يجب أن يكون رقم الهاتف 10 أرقام بالضبط',
      'Enter any additional comments about stakeholder': 'أدخل أي تعليقات إضافية حول صاحب المصلحة',
      
      // Validation messages
      'Invalid Character': 'حرف غير صالح',
      'Maximum Length': 'الحد الأقصى للطول',
      'Please fix the errors in the form': 'يرجى إصلاح الأخطاء في النموذج',
      
      // Actions
      'Edit Stakeholder': 'تعديل صاحب المصلحة',
      'More Actions': 'المزيد من الإجراءات',
      'common.view': 'عرض',
      'common.edit': 'تعديل',
      'common.remove': 'إزالة',

      // Communication methods
      'Meeting': 'اجتماع',
      'Mobile': 'موبايل',
      'Message': 'رسالة',

      // Communication frequency
      'Daily': 'يومي',
      'Weekly': 'أسبوعي',
      'Biweekly': 'نصف شهري',
      'Monthly': 'شهري',
      'Quarterly': 'ربع سنوي',
      'As Needed': 'حسب الحاجة',

      // Toast messages
      'Settings Reset': 'إعادة تعيين الإعدادات',
      'Table settings reset to default': 'تم إعادة تعيين إعدادات الجدول إلى الافتراضي',
      'Settings Applied': 'تم تطبيق الإعدادات',
      'Table settings applied successfully': 'تم تطبيق إعدادات الجدول بنجاح',
      'Failed to load stakeholders': 'فشل تحميل أصحاب المصلحة',
      'Stakeholder Added': 'تمت إضافة صاحب المصلحة',
      '{name} added successfully': 'تمت إضافة {name} بنجاح',
      'Failed to add stakeholder': 'فشل في إضافة صاحب المصلحة',
      'Stakeholder Updated': 'تم تحديث صاحب المصلحة',
      '{name} updated successfully': 'تم تحديث {name} بنجاح',
      'Failed to update stakeholder': 'فشل في تحديث صاحب المصلحة',
      'Stakeholder Removed': 'تم إزالة صاحب المصلحة',
      '{name} removed successfully': 'تم إزالة {name} بنجاح',
      'Failed to remove stakeholder': 'فشل في إزالة صاحب المصلحة',
      'Export Successful': 'نجح التصدير',
      'Stakeholders exported successfully': 'تم تصدير أصحاب المصلحة بنجاح',
      'Export Failed': 'فشل التصدير',
      'Failed to export stakeholders': 'فشل تصدير أصحاب المصلحة',
      'Data Refreshed': 'تم تحديث البيانات',
      'Stakeholder data refreshed': 'تم تحديث بيانات أصحاب المصلحة',

      // ProjectCreate translations
      'Back to Projects': 'العودة إلى المشاريع',
      'Create New Project': 'إنشاء مشروع جديد',
      'Define your project scope and execution plan': 'حدد نطاق مشروعك وخطة التنفيذ',
      'Edit Project': 'تعديل المشروع',
      'Update your project details': 'تحديث تفاصيل مشروعك',
      'Edit Phase 2 - Execution Planning': 'تعديل المرحلة 2 - تخطيط التنفيذ',
      'Complete the execution planning for your project': 'أكمل تخطيط التنفيذ لمشروعك',
      'Save as Draft': 'حفظ كمسودة',
      
      'Project Definition': 'تعريف المشروع',
      'Define the core project parameters and objectives': 'حدد معايير المشروع الأساسية والأهداف',
      'Execution Planning': 'تخطيط التنفيذ',
      'Plan project execution and resources': 'خطط تنفيذ المشروع والموارد',

      'Project Name': 'اسم المشروع',
      'Select Department': 'القسم',
      'Project Manager': 'مدير المشروع',
      'Start Date': 'تاريخ البدء',
      'End Date': 'تاريخ الانتهاء',
      'Total Budget': 'إجمالي الميزانية',
      'Currency': 'العملة',
      'Description': 'الوصف',
      'Objectives': 'الأهداف',
      'Scope': 'النطاق',

      'Enter project name': 'أدخل اسم المشروع',
      'Choose Department': 'اختر القسم',
      'Choose Project Manager': 'اختر مدير المشروع',
      'Select start date': 'اختر تاريخ البدء',
      'Select end date': 'اختر تاريخ الانتهاء',
      'Enter total budget': 'أدخل إجمالي الميزانية',
      'Select currency': 'اختر العملة',
      'Enter project description': 'أدخل وصف المشروع',
      'Enter project objectives': 'أدخل أهداف المشروع',
      'Enter project scope': 'أدخل نطاق المشروع',

      'IT Department': 'قسم تقنية المعلومات',
      'Marketing Department': 'قسم التسويق',
      'Operations Department': 'قسم العمليات',
      'Finance Department': 'قسم المالية',
      'HR Department': 'قسم الموارد البشرية',

      'Saudi Riyal (SAR)': 'الريال السعودي (SAR)',

      'Development Team': 'فريق التطوير',
      'Design Team': 'فريق التصميم',
      'QA Team': 'فريق ضمان الجودة',
      'DevOps Team': 'فريق DevOps',
      'Product Team': 'فريق المنتج',

      'Cost Reduction': 'تقليل التكلفة',
      'Revenue Increase': 'زيادة الإيرادات',
      'Process Improvement': 'تحسين العمليات',
      'Customer Satisfaction': 'رضا العملاء',

      // Additional Phase 1 fields
      'Project Code': 'كود المشروع',
      'Will be auto-generated (e.g., P-1, P-2, P-3)': 'سيتم إنشاؤه تلقائياً (مثال: P-1, P-2, P-3)',
      'Sequential code will be auto-generated (P-1, P-2, P-3, etc.)': 'سيتم إنشاء الكود التسلسلي تلقائياً (P-1, P-2, P-3، إلخ.)',
      'Strategic Objectives': 'الأهداف الاستراتيجية',
      'Contribution Description': 'وصف المساهمة',
      'Describe how this project contributes to organizational goals': 'اصف كيف يساهم هذا المشروع في الأهداف التنظيمية',
      'Describe the strategic objectives for this project': 'اصف الأهداف الاستراتيجية لهذا المشروع',
      'Budget Information': 'معلومات الميزانية',

      // Phase 2 header
      'Project Execution Planning': 'تخطيط تنفيذ المشروع',
      'Plan milestones, deliverables, and budget allocation': 'خطط المعالم والمخرجات وتوزيع الميزانية',

      // Budget summary
      'Budget Summary': 'ملخص الميزانية',
      'Allocated': 'المخصص',
      'Remaining': 'المتبقي',
      'No budget was set in Phase 1. You can still add milestones without budget allocation.': 'لم يتم تحديد ميزانية في المرحلة 1. لا يزال بإمكانك إضافة معالم دون تخصيص ميزانية.',

      // Deliverable display labels
      'Quantity': 'الكمية',
      'Due': 'الاستحقاق',
      'Owner': 'المسؤول',

      // Loading messages
      'Creating your project...': 'جاري إنشاء مشروعك...',
      'Please wait while we process your request': 'يرجى الانتظار أثناء معالجة طلبك',

      // Stakeholder section
      'Stakeholder Registry': 'سجل أصحاب المصلحة',
      'Add Stakeholder': 'إضافة صاحب مصلحة',
      'Edit Stakeholder': 'تعديل صاحب المصلحة',
      'Name': 'الاسم',
      'Role': 'الدور',
      'Type': 'النوع',
      'Power': 'القوة',
      'Interest': 'الاهتمام',
      'Communication Method': 'طريقة التواصل',
      'Communication Frequency': 'تكرار التواصل',
      'Enter stakeholder name': 'أدخل اسم صاحب المصلحة',
      'Enter stakeholder role': 'أدخل دور صاحب المصلحة',
      'Select type': 'اختر النوع',
      'Select power level': 'اختر مستوى القوة',
      'Select interest level': 'اختر مستوى الاهتمام',
      'Enter communication method': 'أدخل طريقة التواصل',
      'Enter communication frequency': 'أدخل تكرار التواصل',
      'No stakeholders added yet': 'لم تتم إضافة أصحاب مصلحة بعد',

      // KPI section
      'Success Indicators': 'مؤشرات النجاح',
      'Quantitative KPIs': 'مؤشرات الأداء الكمية',
      'Add Indicator': 'إضافة مؤشر',
      'KPI Name': 'اسم المؤشر',
      'Target Value': 'القيمة المستهدفة',
      'Unit': 'الوحدة',
      'Enter KPI name': 'أدخل اسم المؤشر',
      'Enter target value': 'أدخل القيمة المستهدفة',
      'Enter unit': 'أدخل الوحدة',
      'Qualitative Outcomes': 'النتائج النوعية',
      'Describe the expected qualitative outcomes': 'اصف النتائج النوعية المتوقعة',

      // Milestone section
      'Milestones and Deliverables': 'المعالم والمخرجات',
      'Add Milestone': 'إضافة معلم',
      'Edit Milestone': 'تعديل المعلم',
      'Milestone Name': 'اسم المعلم',
      'Planned Date': 'التاريخ المخطط',
      'Allocated Value': 'القيمة المخصصة',
      'Enter milestone name': 'أدخل اسم المعلم',
      'Select planned date': 'اختر التاريخ المخطط',
      'Enter allocated value': 'أدخل القيمة المخصصة',
      'Available': 'متاح',
      'Warning: No budget was set in Phase 1. You can still add milestones.': 'تحذير: لم يتم تحديد ميزانية في المرحلة 1. لا يزال بإمكانك إضافة معالم.',
      'No milestones added yet': 'لم تتم إضافة معالم بعد',

      // Deliverable section
      'Deliverables': 'المخرجات',
      'Add Deliverable': 'إضافة مخرج',
      'Edit Deliverable': 'تعديل المخرج',
      'Deliverable Name': 'اسم المخرج',
      'Planned Delivery Date': 'تاريخ التسليم المخطط',
      'Team': 'الفريق',
      'Linked Benefit': 'المنفعة المرتبطة',
      'Enter deliverable name': 'أدخل اسم المخرج',
      'Enter deliverable description': 'أدخل وصف المخرج',
      'Enter quantity': 'أدخل الكمية',
      'Enter unit (e.g., pieces, hours, documents)': 'أدخل الوحدة (مثال: قطع، ساعات، مستندات)',
      'Select delivery date': 'اختر تاريخ التسليم',
      'Select owner team': 'اختر الفريق المسؤول',
      'Select linked benefit': 'اختر المنفعة المرتبطة',
      'No deliverables added yet': 'لم تتم إضافة مخرجات بعد',

      // Button labels
      'Submit for Approval': 'إرسال للموافقة',
      'Next': 'التالي',
      'Previous': 'السابق',
      'Save': 'حفظ',
      'Cancel': 'إلغاء',
      'Actions': 'الإجراءات',
      'View': 'عرض',
      'Remove': 'إزالة',
      'Meetings'  : 'الاجتماعات',
      'Date': 'التاريخ',

      // Validation errors
      'Project name is required': 'اسم المشروع مطلوب',
      'Department is required': 'القسم مطلوب',
      'Project manager is required': 'مدير المشروع مطلوب',
      'Start date is required': 'تاريخ البدء مطلوب',
      'End date is required': 'تاريخ الانتهاء مطلوب',
      'Budget is required and must be greater than 0': 'الميزانية مطلوبة ويجب أن تكون أكبر من 0',
      'End date must be greater than or equal to start date': 'تاريخ الانتهاء يجب أن يكون أكبر من أو يساوي تاريخ البدء',
      'At least one milestone is required': 'مطلوب معلم واحد على الأقل',
      'Consider adding deliverables to your milestones for better project tracking': 'فكر في إضافة مخرجات إلى معالمك لتتبع أفضل للمشروع',
      'Total allocated budget exceeds project budget': 'إجمالي الميزانية المخصصة يتجاوز ميزانية المشروع',
      'Total allocated budget exceeds the project budget': 'إجمالي الميزانية المخصصة يتجاوز ميزانية المشروع',
      'Error': 'خطأ',
      'Warning': 'تحذير',
      'Success': 'نجح',
      'Stakeholder saved successfully': 'تم حفظ صاحب المصلحة بنجاح',
      'Stakeholder deleted successfully': 'تم حذف صاحب المصلحة بنجاح',
      'Milestone saved successfully': 'تم حفظ المعلم بنجاح',
      'Milestone deleted successfully': 'تم حذف المعلم بنجاح',
      'Deliverable saved successfully': 'تم حفظ المخرج بنجاح',
      'Deliverable deleted successfully': 'تم حذف المخرج بنجاح',
      'Phase 1 validation successful': 'نجح التحقق من صحة المرحلة 1',
      'Phase 2 validation successful': 'نجح التحقق من صحة المرحلة 2',
      'Please fix errors before proceeding': 'يرجى إصلاح الأخطاء قبل المتابعة',
      
      // ProjectRisks translations
      'Risk Management': 'إدارة المخاطر',
      'Identify, assess, and manage project risks': 'تحديد وتقييم وإدارة مخاطر المشروع',
      'Manage project risks and mitigation strategies': 'إدارة مخاطر المشروع واستراتيجيات التخفيف',
      'Add Risk': 'إضافة خطر',
      'Project Risks': 'مخاطر المشروع',
      'Risks': 'المخاطر',
      'risks': 'المخاطر',
      'No risks identified': 'لم يتم تحديد مخاطر',
      'Get started by identifying your first project risk': 'ابدأ بتحديد أول خطر للمشروع',
      'Risk Description': 'وصف الخطر',
      'More Actions': 'المزيد من الإجراءات',
      'Risk Details': 'تفاصيل الخطر',
      'Edit Risk': 'تعديل الخطر',
      'Remove Risk': 'إزالة الخطر',
      'Risk Title': 'عنوان الخطر',
      'Risk Name': 'اسم الخطر',
      'Name': 'الاسم',
      'Enter risk name': 'أدخل اسم الخطر',
      'Enter risk title': 'أدخل عنوان الخطر',
      'Select category': 'اختر الفئة',
      'Describe the risk in detail': 'اصف الخطر بالتفصيل',
      'Cause of Risk': 'سبب المخاطرة',
      'Describe the cause or source of this risk': 'قم بوصف سبب أو مصدر هذه المخاطرة',
      'Expected Impact': 'التأثير المتوقع',
      'Describe the expected impact if this risk occurs': 'قم بوصف التأثير المتوقع إذا حدثت هذه المخاطرة',
      'Select probability': 'اختر الاحتمال',
      'Select impact': 'اختر التأثير',
      'Select impact rating': 'اختر تقييم التأثير',
      'Impact Rating': 'تقييم التأثير',
      'Impact rating is required': 'تقييم التأثير مطلوب',
      'Select status': 'اختر الحالة',
      'Risk Owner': 'مسؤول الخطر',
      'Enter risk owner name': 'أدخل اسم مسؤول الخطر',
      'Select entry date': 'اختر تاريخ الإدخال',
      'Project dates not set. Please set project start and end dates first.': 'لم يتم تعيين تواريخ المشروع. يرجى تعيين تاريخ البدء والانتهاء أولاً.',
      'Project start date not set': 'لم يتم تعيين تاريخ بدء المشروع',
      'Please select entry date first': 'يرجى اختيار تاريخ الإدخال أولاً',
      'Select review date': 'اختر تاريخ المراجعة',
      'Select expected closing date': 'اختر تاريخ الإغلاق المتوقع',
      'Please select review date first': 'يرجى اختيار تاريخ المراجعة أولاً',
      'Select actual closing date': 'اختر تاريخ الإغلاق الفعلي',
      'Set this date when the risk is actually closed/resolved': 'حدد هذا التاريخ عندما يتم إغلاق/حل الخطر فعلياً',
      'Risk Treatment Date': 'تاريخ معالجة الخطر',
      'Select risk treatment date': 'اختر تاريخ معالجة الخطر',
      'Mitigation Plan': 'خطة التخفيف',
      'Enter mitigation strategy': 'أدخل استراتيجية التخفيف',
      'Response Plan': 'خطة الاستجابة',
      'Enter response plan strategy': 'أدخل استراتيجية خطة الاستجابة',
      'Required Support': 'الدعم المطلوب',
      'Enter required support': 'أدخل الدعم المطلوب',
      'Update Risk': 'تحديث الخطر',
      'Delete': 'حذف',
      'No description provided': 'لم يتم تقديم وصف',
      'This project is closed. Data is view-only.': 'هذا المشروع مغلق. البيانات للعرض فقط.',
      'Please fix the errors in the form': 'يرجى إصلاح الأخطاء في النموذج',
      '#': '#',
      'a': 'أ',
      'T': 'ت',
      
      // Risk categories
      'Technical': 'تقني',
      'Financial': 'مالي',
      'Resource': 'الموارد',
      'Schedule': 'الجدول الزمني',
      'Quality': 'الجودة',
      'Security': 'الأمن',
      'Regulatory': 'تنظيمي',
      'External': 'خارجي',
      
      // Risk status options (already have High, Medium, Low from other sections)
      'Identified': 'تم تحديده',
      'Mitigated': 'تم التخفيف',
      'Monitoring': 'المراقبة',
      'Resolved': 'تم حله',
      
      // Table settings
      'Column Visibility': 'رؤية الأعمدة',
      'Table Behavior': 'سلوك الجدول',
      'Layout Settings': 'إعدادات التخطيط',
      'ON': 'تشغيل',
      'OFF': 'إيقاف',
      'Rows per Page': 'الصفوف لكل صفحة',
      '10 rows': '10 صفوف',
      '25 rows': '25 صفاً',
      '50 rows': '50 صفاً',
      '100 rows': '100 صف',
      'Striped Rows': 'الصفوف المخططة',
      'Grid Lines': 'خطوط الشبكة',
      'Resizable Columns': 'أعمدة قابلة لتغيير الحجم',
      'Table Size': 'حجم الجدول',
      'Small': 'صغير',
      'Normal': 'عادي',
      'Large': 'كبير',
      'Selection Mode': 'وضع التحديد',
      'None': 'لا شيء',
      'Single': 'واحد',
      'Multiple': 'متعدد',
      'Show Row Numbers': 'إظهار أرقام الصفوف',
      'Scrollable': 'قابل للتمرير',
      'Sortable Columns': 'أعمدة قابلة للفرز',
      'Reset to Default': 'إعادة تعيين إلى الافتراضي',
      'Apply Settings': 'تطبيق الإعدادات',
      
      // Toast messages for risks
      'Failed to load risks': 'فشل تحميل المخاطر',
      'Export Successful': 'نجح التصدير',
      'Risks exported successfully': 'تم تصدير المخاطر بنجاح',
      'Export Failed': 'فشل التصدير',
      'Failed to export risks': 'فشل تصدير المخاطر',
      'Settings Reset': 'تمت إعادة تعيين الإعدادات',
      'Table settings have been reset to default': 'تمت إعادة تعيين إعدادات الجدول إلى الافتراضي',
      'Settings Applied': 'تم تطبيق الإعدادات',
      'Table settings have been applied successfully': 'تم تطبيق إعدادات الجدول بنجاح',
      'Data Refreshed': 'تم تحديث البيانات',
      'Risks have been refreshed': 'تم تحديث المخاطر',
      'Invalid Date': 'تاريخ غير صالح',
      'Entry date must be on or after the project start date': 'يجب أن يكون تاريخ الإدخال في أو بعد تاريخ بدء المشروع',
      'Entry date must be on or before the project end date': 'يجب أن يكون تاريخ الإدخال في أو قبل تاريخ انتهاء المشروع',
      'Dates Reset': 'تمت إعادة تعيين التواريخ',
      'Review and expected closing dates have been reset due to entry date change': 'تمت إعادة تعيين تواريخ المراجعة والإغلاق المتوقع بسبب تغيير تاريخ الإدخال',
      'Review date must be after the entry date': 'يجب أن يكون تاريخ المراجعة بعد تاريخ الإدخال',
      'Review date must be on or before the project end date': 'يجب أن يكون تاريخ المراجعة في أو قبل تاريخ انتهاء المشروع',
      'Date Reset': 'تمت إعادة تعيين التاريخ',
      'Expected closing date has been reset due to review date change': 'تمت إعادة تعيين تاريخ الإغلاق المتوقع بسبب تغيير تاريخ المراجعة',
      'Expected closing date must be after the review date': 'يجب أن يكون تاريخ الإغلاق المتوقع بعد تاريخ المراجعة',
      'Expected closing date must be on or before the project end date': 'يجب أن يكون تاريخ الإغلاق المتوقع في أو قبل تاريخ انتهاء المشروع',
      'Missing Entry Date': 'تاريخ الإدخال مفقود',
      'Entry date is required': 'تاريخ الإدخال مطلوب',
      'Missing Review Date': 'تاريخ المراجعة مفقود',
      'Review date is required': 'تاريخ المراجعة مطلوب',
      'Invalid Date Sequence': 'تسلسل تاريخ غير صالح',
      'Review date must be after entry date': 'يجب أن يكون تاريخ المراجعة بعد تاريخ الإدخال',
      'Expected closing date must be after review date': 'يجب أن يكون تاريخ الإغلاق المتوقع بعد تاريخ المراجعة',
      'Invalid Date Range': 'نطاق تاريخ غير صالح',
      'Entry date must be within project duration': 'يجب أن يكون تاريخ الإدخال ضمن مدة المشروع',
      'All dates must be within project duration': 'يجب أن تكون جميع التواريخ ضمن مدة المشروع',
      'Risk Added': 'تمت إضافة الخطر',
      '{name} has been added successfully': 'تمت إضافة {name} بنجاح',
      'Failed to create risk': 'فشل إنشاء الخطر',
      'Validation Error': 'خطأ في التحقق',
      'Please fill in all required fields': 'يرجى ملء جميع الحقول المطلوبة',
      'Risk Updated': 'تم تحديث الخطر',
      '{name} has been updated successfully': 'تم تحديث {name} بنجاح',
      'Failed to update risk': 'فشل تحديث الخطر',
      'Are you sure you want to delete "{title}"?': 'هل أنت متأكد أنك تريد حذف "{title}"؟',
      'Risk Deleted': 'تم حذف الخطر',
      '{name} has been deleted successfully': 'تم حذف {name} بنجاح',
      'Failed to delete risk': 'فشل حذف الخطر',
      'Title': 'العنوان',
      'Owner': 'المسؤول',
      
      // ---------- PROJECT ISSUES TAB ----------
      // Header and General
      'Issue Register': 'سجل التحديات',
      'Register and resolve project issues': 'سجل وحل مشاكل المشروع',
      'Add Issue': 'إضافة تحدي',
      'Project Issues': 'مشاكل المشروع',
      'issues': 'مشاكل',
      'No issues found': 'لم يتم العثور على مشاكل',
      'Great! No issues have been reported yet': 'رائع! لم يتم الإبلاغ عن أي مشاكل بعد',
      
      // Issue form labels and placeholders
      'Issue Title': 'عنوان التحدي',
      'Enter issue title': 'أدخل عنوان التحدي',
      'Describe the issue in detail': 'صف التحدي بالتفصيل',
      'Select category': 'اختر الفئة',
      'Select priority': 'اختر الأولوية',
      'Select impact': 'اختر التأثير',
      'Select status': 'اختر الحالة',
      'Enter owner name': 'أدخل اسم المسؤول',
      'Linked to Risk': 'مرتبط بالخطر',
      'Select a risk (optional)': 'اختر خطراً (اختياري)',
      'Not Linked': 'غير مرتبط',
      'Report Date': 'تاريخ الإبلاغ',
      'Select report date': 'اختر تاريخ الإبلاغ',
      'Due Date': 'تاريخ الاستحقاق',
      'Select due date': 'اختر تاريخ الاستحقاق',
      'Please select report date first': 'يرجى اختيار تاريخ الإبلاغ أولاً',
      'Project dates not set': 'لم يتم تعيين تواريخ المشروع',
      'Resolution Notes': 'ملاحظات الحل',
      'Enter resolution notes or comments': 'أدخل ملاحظات الحل أو التعليقات',
      
      // Issue status options
      'Open': 'مفتوح',
      'In Progress': 'قيد التنفيذ',
      'Resolved': 'تم حله',
      'Closed': 'مغلق',
      
      // Actions and dialogs
      'Edit Issue': 'تعديل التحدي',
      'Delete Issue': 'حذف التحدي',
      'View Details': 'عرض التفاصيل',
      'More Actions': 'المزيد من الإجراءات',
      'Issue Details': 'تفاصيل التحدي',
      'Edit': 'تعديل',
      'Update Issue': 'تحديث التحدي',

      "Risk title is required": "عنوان الخطر مطلوب",
      "Category is required": "الفئة مطلوبة",
      "Description is required": "الوصف مطلوب",
      "Probability is required": "احتمالية الخطر مطلوبة",
      "Impact is required": "التأثير مطلوب",
      "Risk owner is required": "المسؤول عن الخطر مطلوب",
      "Entry date is required": "تاريخ الإدخال مطلوب",
      "Review date is required": "تاريخ المراجعة مطلوب",
      "Please fix all errors before submitting": "يرجى إصلاح جميع الأخطاء قبل الإرسال",
      "Please select at least one file to upload": "يرجى اختيار ملف واحد على الأقل للتحميل",
      "You can select multiple files": "يمكنك اختيار ملفات متعددة للتحميل",
      "Document name is required": "اسم المستند مطلوب",
      "Custom category is required when Other is selected": "الفئة المخصصة مطلوبة عند اختيار أخرى",
      "Escalation title is required": "عنوان التصعيد مطلوب",
      "Select severity": "اختر شدة التصعيد",
      "Select team member": "اختر عضو الفريق",
      "Select expected resolution date": "اختر تاريخ الحل المتوقع",
      "Select date reported": "اختر تاريخ الإبلاغ",
      "Date reported is required": "تاريخ الإبلاغ مطلوب",
      "Date reported is required": "تاريخ الإبلاغ مطلوب",
      "Closure Justification": "مبرر الإغلاق",
      "Target realization date is required": "تاريخ تحقيق الهدف مطلوب",
      "Target value is required": "القيمة المستهدفة مطلوبة",
      "Benefit title is required": "عنوان الفائدة مطلوب",

      "Issue title is required": "عنوان التحدي مطلوب",
      "Category is required": "الفئة مطلوبة",
      "Description is required": "الوصف مطلوب",
      "Priority is required": "الأولوية مطلوبة",
      "Impact is required": "التأثير مطلوب",
      "Report date is required": "تاريخ الإبلاغ مطلوب",
      "Owner is required": "المسؤول مطلوب",

      // Toast messages for issues
      'Failed to load issues': 'فشل تحميل التحديات',
      'Issues exported successfully': 'تم تصدير التحديات بنجاح',
      'Failed to export issues': 'فشل تصدير التحديات',
      'Issues have been refreshed': 'تم تحديث التحديات',
      'Report date must be after the linked risk entry date': 'يجب أن يكون تاريخ الإبلاغ بعد تاريخ إدخال الخطر المرتبط',
      'Report date must be after project start date': 'يجب أن يكون تاريخ الإبلاغ بعد تاريخ بدء المشروع',
      'Report date must be before project end date': 'يجب أن يكون تاريخ الإبلاغ قبل تاريخ انتهاء المشروع',
      'Due date must be after report date': 'يجب أن يكون تاريخ الاستحقاق بعد تاريخ الإبلاغ',
      'Due date must be before project end date': 'يجب أن يكون تاريخ الاستحقاق قبل تاريخ انتهاء المشروع',
      'Must be after linked risk entry date ({date})': 'يجب أن يكون بعد تاريخ إدخال الخطر المرتبط ({date})',
      'Report date has been reset due to date change': 'تمت إعادة تعيين تاريخ الإبلاغ بسبب تغيير التاريخ',
      'Due date has been reset due to report date change': 'تمت إعادة تعيين تاريخ الاستحقاق بسبب تغيير تاريخ الإبلاغ',
      'Report and due dates have been reset due to linked risk change. Please select dates after the risk entry date.': 'تمت إعادة تعيين تواريخ الإبلاغ والاستحقاق بسبب تغيير الخطر المرتبط. يرجى اختيار تواريخ بعد تاريخ إدخال الخطر.',
      'Dates Reset': 'تمت إعادة تعيين التواريخ',
      'Missing Report Date': 'تاريخ الإبلاغ مفقود',
      'Report date is required': 'تاريخ الإبلاغ مطلوب',
      'Invalid Date Sequence': 'تسلسل تاريخ غير صالح',
      'Report date must be within project duration': 'يجب أن يكون تاريخ الإبلاغ ضمن مدة المشروع',
      'Invalid Date Range': 'نطاق تاريخ غير صالح',
      'All dates must be within project duration': 'يجب أن تكون جميع التواريخ ضمن مدة المشروع',
      'Missing Project': 'المشروع مفقود',
      'Project information is required before creating an issue.': 'معلومات المشروع مطلوبة قبل إنشاء التحدي.',
      'Project information is required before updating an issue.': 'معلومات المشروع مطلوبة قبل تحديث التحدي.',
      'Project information is required before deleting an issue.': 'معلومات المشروع مطلوبة قبل حذف التحدي.',
      'Issue Added': 'تمت إضافة التحدي',
      '{name} issue added successfully': 'تمت إضافة تحدي {name} بنجاح',
      'Failed to create issue': 'فشل إنشاء التحدي',
      'Issue Updated': 'تم تحديث التحدي',
      '{name} issue updated successfully': 'تم تحديث تحدي {name} بنجاح',
      '{name} issue has been closed and the linked risk has been automatically closed': 'تم إغلاق تحدي {name} وتم إغلاق الخطر المرتبط تلقائياً',
      'Failed to update issue': 'فشل تحديث التحدي',
      'Are you sure you want to delete "{title}" issue?': 'هل أنت متأكد أنك تريد حذف تحدي "{title}"؟',
      'Issue Deleted': 'تم حذف التحدي',
      '{name} issue deleted successfully': 'تم حذف تحدي {name} بنجاح',
      'Failed to delete issue': 'فشل حذف التحدي',

      // ProjectIssues form fields and placeholders
      'Responsible Person': 'الشخص المسؤول',
      'Enter responsible person name': 'أدخل اسم الشخص المسؤول',
      'Enter responsible person': 'أدخل الشخص المسؤول',
      'Responsible person is required': 'الشخص المسؤول مطلوب',
      'Responsible person must be at least 2 characters': 'يجب أن يتكون اسم الشخص المسؤول من حرفين على الأقل',
      'Issue Resolution Method': 'طريقة حل التحدي',
      'Resolution Method': 'طريقة الحل',
      'Enter resolution method': 'أدخل طريقة الحل',
      'Required Support': 'الدعم المطلوب',
      'Enter required support': 'أدخل الدعم المطلوب',
      'Enter required support details': 'أدخل تفاصيل الدعم المطلوب',
      'Not specified': 'غير محدد',
      'No linked risk': 'لا يوجد خطر مرتبط',
      'No risks available': 'لا توجد مخاطر متاحة',
      'Related Risk': 'المخاطرة ذات الصلة',
      'Select related risk (optional)': 'اختر المخاطرة ذات الصلة (اختياري)',
      'Start Date': 'تاريخ البدء',
      'Select start date': 'اختر تاريخ البدء',
      'Start date is required': 'تاريخ البدء مطلوب',
      'Project start date not set': 'لم يتم تعيين تاريخ بدء المشروع',
      'Issue Status': 'حالة التحدي',
      'Issue status is required': 'حالة التحدي مطلوبة',
      'Select issue status': 'اختر حالة التحدي',
      'Issue title must be at least 3 characters': 'يجب أن يتكون عنوان التحدي من 3 أحرف على الأقل',
      'Issue title must not exceed 255 characters': 'يجب ألا يتجاوز عنوان التحدي 255 حرفاً',
      'Issue created successfully': 'تم إنشاء التحدي بنجاح',
      'This project is closed. Data is view-only.': 'هذا المشروع مغلق. البيانات للعرض فقط.',
      'Please fill in all required fields': 'يرجى ملء جميع الحقول المطلوبة',
      'Please fix the errors in the form': 'يرجى إصلاح الأخطاء في النموذج',
      'Incomplete Form': 'نموذج غير مكتمل',
      'Creation Failed': 'فشل الإنشاء',
      'Update Failed': 'فشل التحديث',
      'Delete Confirmation': 'تأكيد الحذف',
      'Export Successful': 'نجح التصدير',
      'Export Failed': 'فشل التصدير',
      'Data Refreshed': 'تم تحديث البيانات',
      'Settings Reset': 'تمت إعادة تعيين الإعدادات',
      'Settings Applied': 'تم تطبيق الإعدادات',
      '/': '/',
      'a': 'أ',
      'T': 'ت',
      'Required Support': 'الدعم المطلوب',
      'Enter required support details': 'أدخل تفاصيل الدعم المطلوب',
      'Enter required support': 'أدخل الدعم المطلوب',
      'Select issue status': 'اختر حالة التحدي',
      'Project start date not set': 'لم يتم تعيين تاريخ بدء المشروع',
      'Describe the issue in detail': 'صف التحدي بالتفصيل',
      'Issue created successfully': 'تم إنشاء التحدي بنجاح',
      'Creation Failed': 'فشل الإنشاء',
      'Please fix the errors in the form': 'يرجى إصلاح الأخطاء في النموذج',

      // ProjectIssues validation messages
      'Issue title must be at least 3 characters': 'يجب أن يكون عنوان التحدي 3 أحرف على الأقل',
      'Issue title must not exceed 255 characters': 'يجب ألا يتجاوز عنوان التحدي 255 حرفاً',
      'Responsible person is required': 'الشخص المسؤول مطلوب',
      'Responsible person must be at least 2 characters': 'يجب أن يكون اسم الشخص المسؤول حرفين على الأقل',
      'Issue status is required': 'حالة التحدي مطلوبة',
      'Not Set': 'غير محدد',

      // Dashboard & Home Page
      'Loading dashboard...': 'جارٍ تحميل لوحة المعلومات...',
      'Failed to load dashboard data': 'فشل تحميل بيانات لوحة المعلومات',
      'Retry': 'إعادة المحاولة',
      'User': 'مستخدم',
      'Loading...': 'جارٍ التحميل...',
      'Team Member': 'عضو فريق',
      'N/A': 'غير متوفر',
      'Projects': 'المشاريع',
      'Active': 'نشط',
      'Joined': 'تاريخ الانضمام',
      'Email': 'البريد الإلكتروني',
      'Phone': 'الهاتف',
      'Total Projects': 'إجمالي المشاريع',
      'Active Projects': 'المشاريع النشطة',
      'Planning': 'التخطيط',
      'Completed': 'مكتمل',
      'Complete': 'مكتمل',
      'Pending': 'قيد الانتظار',
      'None': 'لا يوجد',
      'Tasks Left': 'المهام المتبقية',
      'Loading profile...': 'جارٍ تحميل الملف الشخصي...',
      'Online': 'متصل',
      'vs last period': 'مقارنة بالفترة السابقة',
      'Recent Activities & Tasks': 'الأنشطة والمهام الأخيرة',
      "Today's Tasks": 'مهام اليوم',
      'Notifications': 'الإشعارات',
      'Latest News': 'آخر الأخبار',
      'View Task': 'عرض المهمة',
      'Update Now': 'تحديث الآن',
      'Prepare': 'تحضير',
      'Schedule': 'جدولة',
      'View Details': 'عرض التفاصيل',
      'View All Activities': 'عرض جميع الأنشطة',
      'Overdue by': 'متأخر بمقدار',
      'Due in': 'مستحق خلال',
      '2 days': 'يومين',
      '1 day': 'يوم واحد',
      '3 days': '3 أيام',
      '1 week': 'أسبوع واحد',
      'Welcome back': 'مرحباً بعودتك',
      'Great! No tasks remaining today': 'رائع! لا توجد مهام متبقية اليوم',
      'You have 1 task remaining today': 'لديك مهمة واحدة متبقية اليوم',
      'You have {count} tasks remaining today': 'لديك {count} مهام متبقية اليوم',

      // Dashboard Tasks
      'Review Monthly Project Report': 'مراجعة التقرير الشهري للمشروع',
      'Review and analyze progress of all projects': 'مراجعة وتحليل تقدم جميع المشاريع',
      'Development Team Meeting': 'اجتماع فريق التطوير',
      'Discuss new system updates': 'مناقشة تحديثات النظام الجديدة',
      'Review Job Applications': 'مراجعة طلبات التوظيف',
      'Examine CVs of new candidates': 'فحص السير الذاتية للمرشحين الجدد',
      'Prepare Presentation': 'تحضير العرض التقديمي',
      'Prepare project presentation for new client': 'تحضير عرض المشروع للعميل الجديد',
      'Follow Up Project Progress': 'متابعة تقدم المشروع',
      'Check status of all ongoing projects': 'التحقق من حالة جميع المشاريع الجارية',

      // Dashboard Notifications
      'Project Completed Successfully': 'تم إنجاز المشروع بنجاح',
      'New mobile app development project': 'مشروع تطوير تطبيق جوال جديد',
      'Deadline Approaching': 'اقتراب الموعد النهائي',
      'Website project - 1 day remaining': 'مشروع الموقع - متبقي يوم واحد',
      'New Team Meeting': 'اجتماع فريق جديد',
      'Project review meeting - tomorrow at 2:00 PM': 'اجتماع مراجعة المشروع - غداً الساعة 2:00 مساءً',
      'Task Delayed': 'تأخر المهمة',
      'UI design task - 2 days behind schedule': 'مهمة تصميم واجهة المستخدم - متأخرة يومين',
      'New Team Member': 'عضو فريق جديد',
      'Ahmed Mohamed joined the development team': 'انضم أحمد محمد إلى فريق التطوير',
      '5 minutes ago': 'منذ 5 دقائق',
      '30 minutes ago': 'منذ 30 دقيقة',
      '1 hour ago': 'منذ ساعة',
      '2 hours ago': 'منذ ساعتين',
      '3 hours ago': 'منذ 3 ساعات',
      '5 hours ago': 'منذ 5 ساعات',
      '1 day ago': 'منذ يوم',
      '2 days ago': 'منذ يومين',

      // Dashboard Latest News
      'New Project Management Tool Released': 'إصدار أداة جديدة لإدارة المشاريع',
      'Enhanced collaboration features now available for all teams': 'ميزات التعاون المحسّنة متاحة الآن لجميع الفرق',
      'Q3 Performance Review Complete': 'اكتمال مراجعة أداء الربع الثالث',
      'All departments exceeded their quarterly targets': 'تجاوزت جميع الأقسام أهدافها الفصلية',
      'Remote Work Policy Update': 'تحديث سياسة العمل عن بُعد',
      'New hybrid work guidelines effective next month': 'إرشادات العمل المختلط الجديدة سارية الشهر المقبل',
      'Team Building Event Scheduled': 'تم جدولة فعالية بناء الفريق',
      'Annual company retreat planned for November': 'مخطط لملتقى الشركة السنوي في نوفمبر',
      'Product Update': 'تحديث المنتج',
      'Company News': 'أخبار الشركة',
      'Policy Update': 'تحديث السياسة',
      'Events': 'الفعاليات',

      // Calendar Widget
      'Calendar': 'التقويم',
      'Greg': 'ميلادي',
      'Hijri': 'هجري',
      'Showing Gregorian calendar': 'عرض التقويم الميلادي',
      'Showing Hijri calendar': 'عرض التقويم الهجري',
      'Today': 'اليوم',

      // ========== PROJECT CREATE FORM - Missing Translations ==========
      // Project Sponsor
      'Project Sponsor': 'راعي المشروع',
      'Enter project sponsor name': 'أدخل اسم راعي المشروع',

      // Project Code
      'Auto-generated': 'يتم إنشاؤه تلقائياً',

      // Define your project scope
      'Define your project scope': 'حدد نطاق مشروعك',

      // Strategic Objectives Component
      'Project Objectives & Scope': 'أهداف ونطاق المشروع',
      'Project Objectives': 'أهداف المشروع',
      'Project Scope': 'نطاق المشروع',
      'Define the specific objectives and goals this project aims to achieve': 'حدد الأهداف المحددة التي يسعى هذا المشروع لتحقيقها',
      'Describe what the project aims to accomplish': 'اصف ما يهدف المشروع لتحقيقه',
      'Define the boundaries, deliverables, and key activities included in this project': 'حدد الحدود والمخرجات والأنشطة الرئيسية المشمولة في هذا المشروع',
      'Specify what is included and what is out of scope': 'حدد ما هو مشمول وما هو خارج النطاق',

      // Additional Information Section
      'Additional Information': 'معلومات إضافية',
      'Supplementary project details and classifications': 'تفاصيل وتصنيفات المشروع التكميلية',

      // Project Classification
      'Project Classification': 'تصنيف المشروع',
      'Select Project Classification': 'اختر تصنيف المشروع',
      'Complex, large-scale, ambiguous project': 'مشروع معقد، واسع النطاق، غامض',
      'Project is complex and medium in size': 'مشروع معقد ومتوسط الحجم',
      'Simple, small, clear project': 'مشروع بسيط، صغير، واضح',

      // Owning Administration
      'Owning Administration': 'الإدارة المالكة',
      'Enter owning administration': 'أدخل الإدارة المالكة',

      // Project Justifications
      'Project Justifications': 'مبررات المشروع',
      'Enter project justifications and rationale...': 'أدخل مبررات المشروع والأسباب...',

      // Budget
      'Budget': 'الميزانية',
      '0.00': '0.00',

      // Contracts
      'Contracts': 'العقود',
      'Enter contract details...': 'أدخل تفاصيل العقد...',

      // Project Deliverables
      'Project Deliverables': 'مخرجات المشروع',
      'No project deliverables added yet': 'لم تتم إضافة مخرجات للمشروع بعد',
      'Click "Add Deliverable" to get started': 'انقر على "إضافة مخرج" للبدء',
      'Project Deliverable Name': 'اسم مخرج المشروع',
      'Contract Number': 'رقم العقد',
      'Completion Date': 'تاريخ الإنجاز',
      'Enter contract number': 'أدخل رقم العقد',
      'Contract Name': 'اسم العقد',
      'Enter contract name': 'أدخل اسم العقد',
      'Select date': 'اختر التاريخ',
      'Deliverable name is required': 'اسم المخرج مطلوب',
      'Deliverable updated successfully': 'تم تحديث المخرج بنجاح',
      'Deliverable added successfully': 'تمت إضافة المخرج بنجاح',
      'Failed to save deliverable': 'فشل حفظ المخرج',
      'Deliverable removed successfully': 'تمت إزالة المخرج بنجاح',
      'Failed to delete deliverable': 'فشل حذف المخرج',

      // Expected Risks
      'Expected Risks': 'المخاطر المتوقعة',
      'No risks identified yet': 'لم يتم تحديد مخاطر بعد',
      'Click "Add Risk" to identify potential risks': 'انقر على "إضافة خطر" لتحديد المخاطر المحتملة',
      'Probability': 'الاحتمالية',
      'Impact': 'التأثير',
      'High': 'عالي',
      'Medium': 'متوسط',
      'Low': 'منخفض',
      'Calculated Risk Rating': 'تقييم المخاطر المحسوب',
      'Based on Probability × Impact': 'بناءً على الاحتمالية × التأثير',
      'Likelihood': 'احتمالية الحدوث',
      'Critical Risk': 'خطر حرج',
      'High Risk': 'خطر عالي',
      'Medium Risk': 'خطر متوسط',
      'Low Risk': 'خطر منخفض',
      'Very Low Risk': 'خطر منخفض جداً',
      'Unknown': 'غير معروف',
      'Risk description is required': 'وصف الخطر مطلوب',
      'Probability is required': 'الاحتمالية مطلوبة',
      'Impact rating is required': 'تقييم التأثير مطلوب',
      'Risk updated successfully': 'تم تحديث الخطر بنجاح',
      'Risk added successfully': 'تمت إضافة الخطر بنجاح',
      'Failed to save risk. Please try again.': 'فشل حفظ الخطر. يرجى المحاولة مرة أخرى.',
      'Risk removed successfully': 'تمت إزالة الخطر بنجاح',
      'Failed to delete risk. Please try again.': 'فشل حذف الخطر. يرجى المحاولة مرة أخرى.',
      'Probability > 50%': 'الاحتمالية > 50%',
      'Probability 20% - 50%': 'الاحتمالية 20% - 50%',
      'Probability < 20%': 'الاحتمالية < 20%',
      'Update Risk': 'تحديث الخطر',

      // Program Risk Management
      'Program Risks': 'مخاطر البرنامج',
      'Identify, assess, and manage program risks': 'تحديد وتقييم وإدارة مخاطر البرنامج',
      'Get started by identifying your first program risk': 'ابدأ بتحديد أول مخاطرة للبرنامج',
      'Risk Matrix Info': 'معلومات مصفوفة المخاطر',
      'Risk Matrix Information': 'معلومات مصفوفة المخاطر',
      'Risk Matrix': 'مصفوفة المخاطر',
      'Risk Scores': 'درجات المخاطر',
      '(Combination of Likelihood × Impact)': '(مزيج الاحتمالية × التأثير)',
      'Likelihood Levels': 'مستويات الاحتمالية',
      'Risk title must be at least 3 characters': 'يجب أن يتكون عنوان الخطر من 3 أحرف على الأقل',
      'High Likelihood / Low Impact: Score 3': 'احتمالية عالية / تأثير منخفض: الدرجة 3',
      'High Likelihood / Medium Impact: Score 6': 'احتمالية عالية / تأثير متوسط: الدرجة 6',
      'High Likelihood / High Impact: Score 9': 'احتمالية عالية / تأثير عالي: الدرجة 9',
      'Medium Likelihood / Low Impact: Score 2': 'احتمالية متوسطة / تأثير منخفض: الدرجة 2',
      'Medium Likelihood / Medium Impact: Score 4': 'احتمالية متوسطة / تأثير متوسط: الدرجة 4',
      'Medium Likelihood / High Impact: Score 6': 'احتمالية متوسطة / تأثير عالي: الدرجة 6',
      'Low Likelihood / Low Impact: Score 1': 'احتمالية منخفضة / تأثير منخفض: الدرجة 1',
      'Low Likelihood / Medium Impact: Score 2': 'احتمالية منخفضة / تأثير متوسط: الدرجة 2',
      'Low Likelihood / High Impact: Score 3': 'احتمالية منخفضة / تأثير عالي: الدرجة 3',
      'High Likelihood / High Impact': 'احتمالية عالية / تأثير عالي',
      'High Likelihood / Medium Impact': 'احتمالية عالية / تأثير متوسط',
      'High Likelihood / Low Impact': 'احتمالية عالية / تأثير منخفض',
      'Medium Likelihood / High Impact': 'احتمالية متوسطة / تأثير عالي',
      'Medium Likelihood / Medium Impact': 'احتمالية متوسطة / تأثير متوسط',
      'Medium Likelihood / Low Impact': 'احتمالية متوسطة / تأثير منخفض',
      'Low Likelihood / High Impact': 'احتمالية منخفضة / تأثير عالي',
      'Low Likelihood / Medium Impact': 'احتمالية منخفضة / تأثير متوسط',
      'Low Likelihood / Low Impact': 'احتمالية منخفضة / تأثير منخفض',
      'Probability of occurrence greater than 50%': 'احتمالية الحدوث أكبر من 50%',
      'Probability of occurrence between 20% – 50%': 'احتمالية الحدوث بين 20% – 50%',
      'Probability of occurrence less than 20%': 'احتمالية الحدوث أقل من 20%',
      'Enter response plan / mitigation strategy': 'أدخل خطة الاستجابة / استراتيجية التخفيف',
      'Enter support details': 'أدخل تفاصيل الدعم',
      'Risk created successfully': 'تم إنشاء الخطر بنجاح',
      'Risk deleted successfully': 'تم حذف الخطر بنجاح',
      'Are you sure you want to delete this risk?': 'هل أنت متأكد من حذف هذا الخطر؟',
      'Delete Confirmation': 'تأكيد الحذف',
      'Risks exported successfully': 'تم تصدير المخاطر بنجاح',
      'Failed to export risks': 'فشل تصدير المخاطر',
      'No description provided': 'لم يتم تقديم وصف',
      'Describe the risk in detail': 'صف الخطر بالتفصيل',
      'Enter risk title': 'أدخل عنوان الخطر',
      'Select probability': 'اختر الاحتمالية',
      'Select impact': 'اختر التأثير',
      'Risk Rating': 'تقييم الخطر',
      'Treatment Date': 'تاريخ المعالجة',
      'Date of Risk Treatment': 'تاريخ معالجة الخطر',
      'Response Plan': 'خطة الاستجابة',
      'Rating': 'التقييم',
      'Risk title is required': 'عنوان الخطر مطلوب',
      'Risk Details': 'تفاصيل الخطر',
      'Select date': 'اختر التاريخ',
      'Calculated Risk Rating': 'تقييم الخطر المحسوب',
      'Based on Probability × Impact': 'بناءً على الاحتمالية × التأثير',
      'Very Low Risk': 'خطر منخفض جداً',
      'Low Risk': 'خطر منخفض',
      'Medium Risk': 'خطر متوسط',
      'High Risk': 'خطر عالي',
      'Critical Risk': 'خطر حرج',
      'No risks identified': 'لم يتم تحديد مخاطر',
      'Risk updated successfully': 'تم تحديث الخطر بنجاح',
      'Remove Risk': 'إزالة الخطر',
      'View Details': 'عرض التفاصيل',
      'More Actions': 'المزيد من الإجراءات',
      'Export to CSV': 'تصدير إلى CSV',
      'Please fill all required fields correctly': 'يرجى ملء جميع الحقول المطلوبة بشكل صحيح',
      'risks': 'مخاطر',
      'Not Set': 'غير محدد',
      'Unknown': 'غير معروف',
      'Likelihood': 'الاحتمالية',

      // Project Board Members
      'Project Board Members': 'أعضاء مجلس المشروع',
      'Add Board Member': 'إضافة عضو مجلس',
      'Edit Board Member': 'تعديل عضو المجلس',
      'Board Member Name': 'اسم عضو المجلس',
      'Enter board member name': 'أدخل اسم عضو المجلس',
      'No board members added yet': 'لم تتم إضافة أعضاء مجلس بعد',
      'Click "Add Board Member" to add members': 'انقر على "إضافة عضو مجلس" لإضافة الأعضاء',
      'Board member name is required': 'اسم عضو المجلس مطلوب',
      'Board member updated successfully': 'تم تحديث عضو المجلس بنجاح',
      'Board member added successfully': 'تمت إضافة عضو المجلس بنجاح',
      'Failed to save board member. Please try again.': 'فشل حفظ عضو المجلس. يرجى المحاولة مرة أخرى.',
      'Board member removed successfully': 'تمت إزالة عضو المجلس بنجاح',
      'Failed to delete board member. Please try again.': 'فشل حذف عضو المجلس. يرجى المحاولة مرة أخرى.',
      'Project Budget' : 'ميزانية المشروع',

      // Notes
      'Notes': 'الملاحظات',
      'Enter any additional notes, comments, or special considerations for this project...': 'أدخل أي ملاحظات إضافية أو تعليقات أو اعتبارات خاصة لهذا المشروع...',

      // Form Actions
      'Update Project': 'تحديث المشروع',
      'Submit for Approval': 'إرسال للموافقة',
      'Create Project': 'إنشاء المشروع',

      // Validation Messages
      'Please enter project name': 'يرجى إدخال اسم المشروع',
      'Project name must be at least 3 characters': 'يجب أن يكون اسم المشروع 3 أحرف على الأقل',
      'Please select department': 'يرجى اختيار القسم',
      'Please select project manager': 'يرجى اختيار مدير المشروع',
      'Please enter project sponsor': 'يرجى إدخال راعي المشروع',
      'Sponsor name must be at least 2 characters': 'يجب أن يكون اسم الراعي حرفين على الأقل',
      'Please select start date': 'يرجى اختيار تاريخ البدء',
      'Please select end date': 'يرجى اختيار تاريخ الانتهاء',
      'End date must be greater than or equal to start date': 'تاريخ الانتهاء يجب أن يكون أكبر من أو يساوي تاريخ البدء',
      'Please enter total budget': 'يرجى إدخال إجمالي الميزانية',
      'Budget must be greater than 0': 'يجب أن تكون الميزانية أكبر من 0',
      'Validation Error': 'خطأ في التحقق',
      'Please fill all required fields correctly': 'يرجى ملء جميع الحقول المطلوبة بشكل صحيح',
      'Project validation successful': 'نجح التحقق من المشروع',
      'Please fix errors before proceeding': 'يرجى إصلاح الأخطاء قبل المتابعة',

      // Toast Messages
      'Project created successfully!': 'تم إنشاء المشروع بنجاح!',
      'Project updated successfully!': 'تم تحديث المشروع بنجاح!',
      'Project updated and auto-approved successfully!': 'تم تحديث المشروع والموافقة عليه تلقائياً بنجاح!',
      'Project resubmitted successfully! Waiting for admin approval.': 'تمت إعادة إرسال المشروع بنجاح! في انتظار موافقة المسؤول.',
      'Project changes submitted for approval! Waiting for admin review.': 'تم إرسال تغييرات المشروع للموافقة! في انتظار مراجعة المسؤول.',
      'Error creating project. Please try again.': 'خطأ في إنشاء المشروع. يرجى المحاولة مرة أخرى.',
      'Error submitting project. Please try again.': 'خطأ في إرسال المشروع. يرجى المحاولة مرة أخرى.',
      'Failed to load managers. Please refresh the page.': 'فشل تحميل المديرين. يرجى تحديث الصفحة.',
      'Failed to load departments. Please refresh the page.': 'فشل تحميل الأقسام. يرجى تحديث الصفحة.',
      'Project Submitted': 'تم إرسال المشروع',
      'Project has been submitted and is waiting for approval.': 'تم إرسال المشروع وهو في انتظار الموافقة.',
      'Project has been created and approved successfully.': 'تم إنشاء المشروع والموافقة عليه بنجاح.',
      'Project Resubmitted': 'تمت إعادة إرسال المشروع',
      'Project has been resubmitted and is waiting for approval.': 'تمت إعادة إرسال المشروع وهو في انتظار الموافقة.',
      'Draft saved successfully': 'تم حفظ المسودة بنجاح',
      'Project resubmitted successfully! Waiting for approval.': 'تمت إعادة إرسال المشروع بنجاح! في انتظار الموافقة.',
      'Error saving draft. Please try again.': 'خطأ في حفظ المسودة. يرجى المحاولة مرة أخرى.',

      // Stakeholder Registry
      'Stakeholder Registry': 'سجل أصحاب المصلحة',
      '{count} Stakeholders': '{count} أصحاب مصلحة',
      'Register a new stakeholder': 'تسجيل صاحب مصلحة جديد',
      'No stakeholders yet': 'لا يوجد أصحاب مصلحة بعد',
      'Add your first stakeholder to start tracking influence, expectations, and communication plans.': 'أضف أول صاحب مصلحة لبدء تتبع التأثير والتوقعات وخطط التواصل.',
      'Add stakeholder information including name, role, and department.': 'أضف معلومات صاحب المصلحة بما في ذلك الاسم والدور والقسم.',
      'Save Stakeholder': 'حفظ صاحب المصلحة',
      'Remove Stakeholder': 'إزالة صاحب المصلحة',
      'Search departments...': 'البحث في الأقسام...',
      'Department': 'القسم',

      // Stakeholder validation
      'Name is required': 'الاسم مطلوب',
      'Name must be at least 2 characters': 'يجب أن يكون الاسم حرفين على الأقل',
      'Name must not exceed 255 characters': 'يجب ألا يتجاوز الاسم 255 حرفاً',
      'Role is required': 'الدور مطلوب',
      'Role must be at least 2 characters': 'يجب أن يكون الدور حرفين على الأقل',
      'Role must not exceed 100 characters': 'يجب ألا يتجاوز الدور 100 حرف',
      'Type is required': 'النوع مطلوب',
      'Level of influence is required': 'مستوى التأثير مطلوب',
      'Level of interest is required': 'مستوى الاهتمام مطلوب',
      'Engagement strategy is required': 'استراتيجية المشاركة مطلوبة',
      'Expectations are required': 'التوقعات مطلوبة',
      'Communication method is required': 'طريقة التواصل مطلوبة',
      'Communication frequency is required': 'تكرار التواصل مطلوب',
      'Email is required': 'البريد الإلكتروني مطلوب',
      'Please enter a valid email address': 'يرجى إدخال عنوان بريد إلكتروني صحيح',
      'Email must not exceed 255 characters': 'يجب ألا يتجاوز البريد الإلكتروني 255 حرفاً',
      'Phone number is required': 'رقم الهاتف مطلوب',
      'Phone number must be exactly 10 digits and start with 05': 'يجب أن يكون رقم الهاتف 10 أرقام بالضبط ويبدأ بـ 05',
      'Maximum Length': 'الحد الأقصى للطول',
      'Phone number must be exactly 10 digits': 'يجب أن يكون رقم الهاتف 10 أرقام بالضبط',
      'Invalid Character': 'حرف غير صالح',
      'Only numbers are allowed (format: 05XXXXXXXX)': 'الأرقام فقط مسموح بها (الصيغة: 05XXXXXXXX)',
      'Internal': 'داخلي',
      'External': 'خارجي',
      'Meeting': 'اجتماع',
      'Mobile': 'جوال',
      'Message': 'رسالة',
      'Daily': 'يومي',
      'Weekly': 'أسبوعي',
      'Biweekly': 'نصف شهري',
      'Monthly': 'شهري',
      'Quarterly': 'ربع سنوي',
      'As Needed': 'حسب الحاجة',

      // Strategy Management translations
      // Strategy map
      'Strategy Map': 'الخريطة الاستراتيجية',
      'Visualize strategic objectives and their relationships across all perspectives': 'استعرض الأهداف الاستراتيجية وعلاقاتها عبر جميع المنظورات',
      'Total Objectives': 'إجمالي الأهداف',
      'Active objectives': 'الأهداف النشطة',
      'Connections': 'الارتباطات',
      'Strategic links': 'الروابط الاستراتيجية',
      'Perspectives': 'المنظورات',
      'Balanced scorecard': 'بطاقة الأداء المتوازن',
      'Avg Progress': 'متوسط التقدم',
      'Overall completion': 'الإنجاز الكلي',
      'Search objectives...': 'ابحث في الأهداف...',
      'Strategic Connection': 'ارتباط استراتيجي',
      'No objectives match your search': 'لا توجد أهداف تطابق بحثك',
      'Add objectives to this perspective': 'أضف أهدافًا إلى هذا المنظور',
      'Exporting...': 'جارٍ التصدير...',
      'objectives': 'أهداف',
      'Financial Perspective': 'المنظور المالي',
      'Customer Perspective': 'منظور العملاء',
      'Internal Process': 'العمليات الداخلية',
      'Learning & Growth': 'التعلم والنمو',
      'Focus on financial performance and shareholder value': 'التركيز على الأداء المالي وقيمة أصحاب المصلحة',
      'Deliver value to customers and stakeholders': 'تقديم قيمة للعملاء وأصحاب المصلحة',
      'Optimize internal operations and processes': 'تحسين العمليات والإجراءات الداخلية',
      'Build organizational capabilities and culture': 'بناء القدرات والثقافة المؤسسية',
      'Reduce Operating Costs': 'خفض تكاليف التشغيل',
      'Increase Profit Margins': 'زيادة هوامش الربح',
      'Expand Revenue Streams': 'تنويع مصادر الإيرادات',
      'Enhance Customer Experience': 'تحسين تجربة العميل',
      'Build Brand Leadership': 'تعزيز ريادة العلامة التجارية',
      'Strengthen Partnerships': 'تعزيز الشراكات',
      'Optimize Operations': 'تحسين العمليات',
      'Knowledge Management': 'إدارة المعرفة',
      'Develop Talent': 'تطوير المواهب',
      'Technology Innovation': 'الابتكار التقني',
      'in-progress': 'قيد التنفيذ',
      'at-risk': 'معرّض للخطر',

      // Department assignment
      'Department Assignment': 'تعيين الإدارات',
      'Unassigned Projects': 'المشاريع غير المسندة',
      'Try adjusting your search': 'حاول تعديل عبارة البحث',
      'Assign to {name}': 'تعيين إلى {name}',
      'Already Assigned': 'المعيّن حاليًا',
      'Initiative project': 'مشروع مبادرة',
      'Initiative Projects to assign': 'مشاريع المبادرات المطلوب تعيينها',
      'Every initiative project already has a department': 'جميع مشاريع المبادرات معيّنة لإدارات',
      'Failed to assign selected items': 'فشل تعيين العناصر المحددة',
      'Sent': 'تم الإرسال',
      '1 project': 'مشروع واحد',
      '{n} items sent to {name}': 'تم إرسال {n} عناصر إلى {name}',

      // strategic-plans.vue
      'Plans Overview': 'نظرة عامة على الخطط',
      'Institutional': 'مؤسسية',
      'Sectoral': 'قطاعية',
      'Total Plans': 'إجمالي الخطط',
      'No plans yet': 'لا توجد خطط بعد',
      'KPI Summary': 'ملخص مؤشرات الأداء',
      'Total KPIs': 'إجمالي مؤشرات الأداء',
      'Achieved KPIs': 'مؤشرات الأداء المحققة',
      'Create Strategic Plan': 'إنشاء خطة استراتيجية',
      'Create KPI': 'إنشاء مؤشر أداء',
      'Save All': 'حفظ الكل',
      'Revise': 'مراجعة وتعديل',
      'Review Details': 'تفاصيل المراجعة',
      'Reject Measurement': 'رفض القياس',
      'Measurement approved successfully': 'تمت الموافقة على القياس بنجاح',
      'Measurement rejected': 'تم رفض القياس',
      'Failed to approve measurement': 'فشل في الموافقة على القياس',
      'Failed to reject measurement': 'فشل في رفض القياس',
      'No pending measurement found for this KPI': 'لا يوجد قياس معلق لمؤشر الأداء هذا',
      'No pending measurement found': 'لا يوجد قياس معلق',
      'Please provide a reason for rejecting this measurement submission.': 'يرجى تقديم سبب لرفض هذا القياس المقدم.',
      'Enter rejection reason...': 'أدخل سبب الرفض...',
      'Rejection reason is required': 'سبب الرفض مطلوب',
      'period ready to submit': 'فترة جاهزة للتقديم',
      'periods ready to submit': 'فترات جاهزة للتقديم',
      'can be saved as draft': 'يمكن حفظها كمسودة',
      'Previous Achieved': 'المحقق السابق',
      'New Achieved': 'المحقق الجديد',
      'No strategic plans yet': 'لا توجد خطط استراتيجية بعد',
      'Get started by creating your first strategic plan': 'ابدأ بإنشاء خطتك الاستراتيجية الأولى',
      'No plans found': 'لم يتم العثور على خطط',
      'Try adjusting your search or filter criteria': 'حاول تعديل معايير البحث أو التصفية',
      'Failed to load strategic plans. Please refresh the page.': 'فشل تحميل الخطط الاستراتيجية. يرجى تحديث الصفحة.',
      'Are you sure you want to delete': 'هل أنت متأكد من الحذف',
      'This action cannot be undone. All associated KPIs will also be removed.': 'لا يمكن التراجع عن هذا الإجراء. سيتم أيضاً إزالة جميع مؤشرات الأداء المرتبطة.',
      'Confirm Delete': 'تأكيد الحذف',
      'Strategic plan deleted successfully': 'تم حذف الخطة الاستراتيجية بنجاح',
      'Failed to delete strategic plan': 'فشل حذف الخطة الاستراتيجية',

      // strategic-plan/create.vue
      'Create New Strategic Plan': 'إنشاء خطة استراتيجية جديدة',
      'Edit Strategic Plan': 'تعديل الخطة الاستراتيجية',
      'Define your strategic plan scope and KPIs': 'حدد نطاق خطتك الاستراتيجية ومؤشرات الأداء',
      'Update your strategic plan details': 'تحديث تفاصيل خطتك الاستراتيجية',
      'Back to Strategic Plans': 'العودة إلى الخطط الاستراتيجية',
      'Save Plan': 'حفظ الخطة',
      'Update Plan': 'تحديث الخطة',
      'Update Strategic Plan': 'تحديث الخطة الاستراتيجية',
      'Plan Definition': 'تعريف الخطة',
      'Define the core strategic plan parameters': 'حدد معايير الخطة الاستراتيجية الأساسية',
      'Strategy Name': 'اسم الاستراتيجية',
      'Enter strategy name': 'أدخل اسم الاستراتيجية',
      'Strategy Type': 'نوع الاستراتيجية',
      'Select strategy type': 'اختر نوع الاستراتيجية',
      'Vision': 'الرؤية',
      'Enter the vision statement for this strategic plan': 'أدخل بيان الرؤية لهذه الخطة الاستراتيجية',
      'Mission': 'الرسالة',
      'Enter the mission statement for this strategic plan': 'أدخل بيان الرسالة لهذه الخطة الاستراتيجية',
      'Pillars': 'الركائز',
      'Enter the strategic pillars': 'أدخل الركائز الاستراتيجية',
      'Strategic Objectives': 'الأهداف الاستراتيجية',
      'Enter the strategic objectives': 'أدخل الأهداف الاستراتيجية',
      'Key Performance Indicators': 'مؤشرات الأداء الرئيسية',
      'Define measurable KPIs for this strategic plan': 'حدد مؤشرات الأداء القابلة للقياس لهذه الخطة الاستراتيجية',
      'No KPIs Added': 'لم تتم إضافة مؤشرات أداء',
      'Add key performance indicators to track progress': 'أضف مؤشرات الأداء الرئيسية لتتبع التقدم',
      'Add First KPI': 'إضافة أول مؤشر أداء',
      'Add KPI': 'إضافة مؤشر أداء',
      'Edit KPI': 'تعديل مؤشر الأداء',
      'Update KPI': 'تحديث مؤشر الأداء',
      'KPI Name': 'اسم مؤشر الأداء',
      'Enter KPI name': 'أدخل اسم مؤشر الأداء',
      'Target': 'الهدف',
      'Previous Period': 'الفترة السابقة',
      'Previous Period Achieved': 'الإنجاز في الفترة السابقة',
      'Actual Achieved': 'الإنجاز الفعلي',
      '% Achievement': '% الإنجاز',
      '% of Achievement': '% من الإنجاز',
      'Status': 'الحالة',
      'Select status': 'اختر الحالة',
      'Actions': 'الإجراءات',
      'Edit': 'تعديل',
      'Delete': 'حذف',
      '0.00': '0.00',
      'KPI name is required': 'اسم مؤشر الأداء مطلوب',
      'Strategy name is required': 'اسم الاستراتيجية مطلوب',
      'Strategy type is required': 'نوع الاستراتيجية مطلوب',
      'All KPIs must have a name': 'يجب أن يكون لجميع مؤشرات الأداء اسم',
      'Please fill in all required fields': 'يرجى ملء جميع الحقول المطلوبة',
      'Validation Error': 'خطأ في التحقق',
      'Strategic plan created successfully': 'تم إنشاء الخطة الاستراتيجية بنجاح',
      'Strategic plan updated successfully': 'تم تحديث الخطة الاستراتيجية بنجاح',
      'Failed to save strategic plan': 'فشل حفظ الخطة الاستراتيجية',
      'Failed to load strategic plan': 'فشل تحميل الخطة الاستراتيجية',

      // strategic-plan/[id].vue
      'Strategic Plan Details': 'تفاصيل الخطة الاستراتيجية',
      'Edit Plan': 'تعديل الخطة',
      'KPI Achievement': 'إنجاز مؤشرات الأداء',
      'Overall Achievement': 'الإنجاز الإجمالي',
      'Plan Info': 'معلومات الخطة',
      'Created By': 'تم الإنشاء بواسطة',
      'Created On': 'تاريخ الإنشاء',
      'N/A': 'غير متوفر',
      'KPI Status Distribution': 'توزيع حالة مؤشرات الأداء',
      'On Track': 'على المسار الصحيح',
      'At Risk': 'معرض للخطر',
      'Achieved': 'محقق',
      'KPIs defined': 'مؤشرات الأداء المحددة',
      'No vision defined': 'لم يتم تحديد رؤية',
      'No mission defined': 'لم يتم تحديد رسالة',
      'No pillars defined': 'لم يتم تحديد ركائز',
      'No strategic objectives defined': 'لم يتم تحديد أهداف استراتيجية',
      'No KPIs Defined': 'لم يتم تحديد مؤشرات أداء',
      'This strategic plan has no key performance indicators yet': 'لا تحتوي هذه الخطة الاستراتيجية على مؤشرات أداء رئيسية بعد',
      'Add KPIs': 'إضافة مؤشرات الأداء',
      'Edit KPIs': 'تعديل مؤشرات الأداء',
      'Strategic Plan Not Found': 'لم يتم العثور على الخطة الاستراتيجية',

      // Strategy Components (StrategyManagementHeader.vue, StrategyManagementFilters.vue)
      'Strategy Management': 'إدارة الاستراتيجية',
      'Manage and track all strategic plans efficiently': 'إدارة وتتبع جميع الخطط الاستراتيجية بكفاءة',
      'Search strategic plans by name, vision, or mission...': 'البحث في الخطط الاستراتيجية بالاسم أو الرؤية أو الرسالة...',
      'Filter by Type': 'تصفية حسب النوع',
      'Sort by': 'ترتيب حسب',
      'Reset Filters': 'إعادة تعيين التصفية',
      'Showing': 'عرض',
      'of': 'من',
      'strategic plans': 'الخطط الاستراتيجية',
      'Grid View': 'عرض الشبكة',
      'List View': 'عرض القائمة',
      'All Types': 'كل الأنواع',
      'Name (A-Z)': 'الاسم (أ-ي)',
      'Name (Z-A)': 'الاسم (ي-أ)',
      'Date Created': 'تاريخ الإنشاء',
      'Type': 'النوع',

      // Breadcrumb & Navigation
      'Strategy': 'الاستراتيجية',
      'Strategic Plans': 'الخطط الاستراتيجية',
      'Strategy Details': 'تفاصيل الاستراتيجية',
      'Create Strategic Plan': 'إنشاء خطة استراتيجية',
      'Edit Strategic Plan': 'تعديل خطة استراتيجية',
      'Review Strategic Plan': 'مراجعة خطة استراتيجية',
      'Approved Plans': 'الخطط المعتمدة',
      'Approved Initiatives': 'المبادرات المعتمدة',
      'Create': 'إنشاء',
      'Details': 'التفاصيل',
      'Search': 'بحث',

      // Additional Project & Program Translations
      'Draft Projects': 'المشاريع المسودة',
      'Closed Projects': 'المشاريع المغلقة',
      'Draft Programs': 'البرامج المسودة',
      'Active Programs': 'البرامج النشطة',
      'Closed Programs': 'البرامج المغلقة',
      'Programs Overview': 'نظرة عامة على البرامج',
      'No programs yet': 'لا توجد برامج بعد',
      'No programs found': 'لم يتم العثور على برامج',
      'Get started by creating your first program': 'ابدأ بإنشاء برنامجك الأول',
      'Create Program': 'إنشاء برنامج',
      'Program Management': 'إدارة البرامج',
      'Manage and track all programs efficiently': 'إدارة وتتبع جميع البرامج بكفاءة',
      'Approve or reject the project submission': 'الموافقة أو رفض تقديم المشروع',
      'active :': 'نشط :',
      'closed :': 'مغلق :',
      'draft :': 'مسودة :',
      'Hijri Calendar is a Base Calendar. :': 'التقويم الهجري هو تقويم أساسي. :',
      'Hijri Calendar :': 'التقويم الهجري :',
      'Super Admin': 'المشرف العام',
      'Admin': 'المسؤول',
      'Project Owner': 'مالك المشروع',
      'Manage Program': 'إدارة البرنامج',

      // ── Project Review Page ──────────────────────────────────
      'Back to Projects': 'العودة إلى المشاريع',
      'Project Review': 'مراجعة المشروع',
      'Review and approve project submission': 'مراجعة وقبول طلب المشروع',
      'Approval Progress': 'تقدم الاعتماد',
      'Approval History': 'سجل الموافقات',
      'Planning Dept': 'إدارة التخطيط',
      'PMO': 'مكتب إدارة المشاريع',
      'Project Board': 'مجلس المشروع',
      'Project Information': 'معلومات المشروع',
      'Project Name': 'اسم المشروع',
      'Project Code': 'رمز المشروع',
      'Project Manager': 'مدير المشروع',
      'Project Sponsor': 'راعي المشروع',
      'Project Classification': 'تصنيف المشروع',
      'Start Date': 'تاريخ البدء',
      'End Date': 'تاريخ الانتهاء',
      'Owning Administration': 'الإدارة المالكة',
      'Created By': 'أنشئ بواسطة',
      'Unknown User': 'مستخدم غير معروف',
      'Project Justifications': 'مبررات المشروع',
      'Notes': 'الملاحظات',
      'Stakeholder Registry': 'سجل أصحاب المصلحة',
      'No stakeholders added yet': 'لم يُضف أصحاب مصلحة بعد',
      'Stakeholders will appear here once added': 'سيظهر أصحاب المصلحة هنا بعد إضافتهم',
      'Strategic Objectives': 'الأهداف الاستراتيجية',
      'Project Objectives': 'أهداف المشروع',
      'Project Scope': 'نطاق المشروع',
      'Budget & Contracts': 'الميزانية والعقود',
      'Currency': 'العملة',
      'Contracts': 'العقود',
      'No contract details provided': 'لم تُقدَّم تفاصيل العقد',
      'Main Outputs': 'المخرجات الرئيسية',
      'Deliverable Name': 'اسم المخرج',
      'Contract Number': 'رقم العقد',
      'Contract Name': 'اسم العقد',
      'Completion Date': 'تاريخ الاكتمال',
      'No main outputs defined': 'لم تُحدَّد مخرجات رئيسية',
      'Main outputs will be listed here': 'ستُعرض المخرجات الرئيسية هنا',
      'Expected Risks': 'المخاطر المتوقعة',
      'Risk Description': 'وصف المخاطرة',
      'Cause of Risk': 'سبب المخاطرة',
      'Expected Impact': 'التأثير المتوقع',
      'Probability': 'الاحتمالية',
      'Impact': 'التأثير',
      'Risk Rating': 'تقييم المخاطرة',
      'No risks identified': 'لم تُحدَّد مخاطر',
      'Risk assessment will appear here': 'سيظهر تقييم المخاطر هنا',
      'Project Board Members': 'أعضاء مجلس المشروع',
      'Review Status': 'حالة المراجعة',
      'Submitted': 'تاريخ التقديم',
      'Submitted by': 'قُدِّم بواسطة',
      'Manager': 'المدير',
      'Your Decision': 'قرارك',
      'Select Project Classification': 'اختر تصنيف المشروع',
      'Approval Comments': 'تعليقات الاعتماد',
      'Rejection Reason': 'سبب الرفض',
      'Add any comments or feedback (optional)...': 'أضف أي تعليقات أو ملاحظات (اختياري)...',
      'Please provide a reason for rejection (required)...': 'يرجى ذكر سبب الرفض (مطلوب)...',
      'Approve': 'اعتماد',
      'Reject': 'رفض',
      'Project Not Found': 'المشروع غير موجود',
      "The project you're trying to review doesn't exist or has been deleted.": 'المشروع الذي تحاول مراجعته غير موجود أو تم حذفه.',
      'N/A': 'غير متاح',

      // Stage labels
      'Owner Review': 'مراجعة المالك',
      'Project Owner Review': 'مراجعة مالك المشروع',
      'Planning Department Review': 'مراجعة إدارة التخطيط',
      'PMO Review': 'مراجعة مكتب إدارة المشاريع',
      'Project Board Review': 'مراجعة مجلس المشروع',
      'Awaiting Review': 'في انتظار المراجعة',
      'Review Completed': 'اكتملت المراجعة',
      'Under Review': 'قيد المراجعة',
      'Pending Approval': 'في انتظار الاعتماد',
      'Approved': 'معتمد',
      'Rejected': 'مرفوض',
      'Active': 'نشط',
      'Awaiting': 'في الانتظار',

      // Status labels
      'Completed': 'مكتمل',
      'On Hold': 'معلق',
      'Cancelled': 'ملغى',
      'In Progress': 'قيد التنفيذ',

      // Review title / description
      'Project Approved': 'تم اعتماد المشروع',
      'Project Rejected': 'تم رفض المشروع',
      'Review Project': 'مراجعة المشروع',
      'Review': 'مراجعة',
      'This project has been fully approved through all stages': 'تمت الموافقة على المشروع عبر جميع المراحل',
      'This project has been rejected': 'تم رفض هذا المشروع',
      'Planning Department: Review alignment with strategic goals and resource availability': 'إدارة التخطيط: مراجعة التوافق مع الأهداف الاستراتيجية وتوفر الموارد',
      'PMO: Review project feasibility, classify the project, and verify compliance': 'مكتب إدارة المشاريع: مراجعة جدوى المشروع وتصنيفه والتحقق من الامتثال',
      'Project Board: Final approval before project activation': 'مجلس المشروع: الاعتماد النهائي قبل تفعيل المشروع',
      'Project Owner: Review and approve the initial project submission': 'مالك المشروع: مراجعة واعتماد طلب المشروع الأولي',

      // Confirm dialogs & toasts
      'Are you sure you want to approve this project?': 'هل أنت متأكد من اعتماد هذا المشروع؟',
      'Confirm Approval': 'تأكيد الاعتماد',
      'Yes, Approve': 'نعم، اعتمد',
      'Are you sure you want to reject this project at': 'هل أنت متأكد من رفض هذا المشروع عند مرحلة',
      'The project will be sent back for revision.': 'سيُعاد المشروع للمراجعة.',
      'Confirm Rejection': 'تأكيد الرفض',
      'Yes, Reject': 'نعم، ارفض',
      'Validation Error': 'خطأ في التحقق',
      'Please select a Project Classification before approving': 'يرجى اختيار تصنيف المشروع قبل الاعتماد',
      'Please provide a reason for rejection': 'يرجى تقديم سبب الرفض',
      'Project has been approved at': 'تم اعتماد المشروع عند مرحلة',
      'Project has been rejected': 'تم رفض المشروع',
      'Failed to approve project': 'فشل اعتماد المشروع',
      'Failed to reject project': 'فشل رفض المشروع',
      'Failed to load project details': 'فشل تحميل تفاصيل المشروع',
      'Failed to load project': 'فشل تحميل المشروع',

      // Classification options
      '(a) Complex, large-scale, ambiguous project': '(أ) مشروع معقد وكبير الحجم وغير واضح',
      '(b) Project is complex and medium in size': '(ب) مشروع معقد ومتوسط الحجم',
      '(c) Simple, small, clear project': '(ج) مشروع بسيط وصغير وواضح',

      // Department names
      'Budget Planning': 'تخطيط الميزانية',
      'Information Technology': 'تقنية المعلومات',
      'Human Resources': 'الموارد البشرية',
      'Finance': 'المالية',
      'Operations': 'العمليات',
      'Marketing': 'التسويق',
      'Legal': 'الشؤون القانونية',
      'Procurement': 'المشتريات',
      'Engineering': 'الهندسة',
      'Research and Development': 'البحث والتطوير',
      'Customer Service': 'خدمة العملاء',
      'Sales': 'المبيعات',
      'Administration': 'الإدارة',
      'General Administration': 'الإدارة العامة',
      'Strategic Planning': 'التخطيط الاستراتيجي',
      'Project Management': 'إدارة المشاريع',

      // Project card waiting messages
      'Waiting for Project Owner Approval...': 'في انتظار اعتماد مالك المشروع...',
      'Waiting for Planning Department Approval...': 'في انتظار اعتماد إدارة التخطيط...',
      'Waiting for PMO Approval...': 'في انتظار اعتماد مكتب إدارة المشاريع...',
      'Waiting for Project Board Approval...': 'في انتظار اعتماد مجلس المشروع...',
      'Waiting for Approval...': 'في انتظار الاعتماد...',
      'Waiting for manager to resubmit': 'في انتظار إعادة تقديم المدير',
      'View Rejection Details': 'عرض تفاصيل الرفض',
      'View Approval Details': 'عرض تفاصيل الاعتماد',
      'Manage Project': 'إدارة المشروع',
      'View Approval Progress': 'عرض تفاصيل الموافقة',
      'Deliverables': 'المخرجات',
      'Stakeholders': 'أصحاب المصلحة',
      'Progress': 'التقدم',
      'Start': 'البدء',
      'End': 'الانتهاء',
      'Unknown': 'غير معروف',
      'Draft': 'مسودة',
      'Planning Review': 'مراجعة التخطيط',
      'PMO Review': 'مراجعة مكتب إدارة المشاريع',
      'Board Review': 'مراجعة المجلس',

      // 20042026 - Additional translations
      'Owner Review': 'مراجعة المالك',
      'Content Creation': 'إنشاء المحتوى',
      'Create your first project to see statistics': 'أنشئ مشروعك الأول لرؤية الإحصائيات',
      'Planning Dept': 'قسم التخطيط',
      'PMO': 'مكتب إدارة المشاريع',
      'Project Board': 'مجلس المشروع',
      'Project Owner Review': 'مراجعة مالك المشروع',
      'Project Owner: Review and approve the initial project submission.': 'مالك المشروع: مراجعة والموافقة على تقديم المشروع الأولي.',
      'Approval Progress': 'تقدم الموافقة',
      'Track your project through the approval pipeline': 'تتبع مشروعك من خلال خط الموافقة',
      'Planning Department Review': 'مراجعة قسم التخطيط',
      'Planning Department: Review alignment with strategic goals and resource availability.': 'قسم التخطيط: مراجعة التوافق مع الأهداف الاستراتيجية وتوفر الموارد.',
      'PMO: Review project feasibility, classify the project, and verify compliance': 'مكتب إدارة المشاريع: مراجعة جدوى المشروع، تصنيف المشروع، والتحقق من الامتثال',
      'Project Board Review': 'مراجعة مجلس المشروع',
      'Project Board: Final approval before project activation.': 'مجلس المشروع: الموافقة النهائية قبل تفعيل المشروع.',
      'Manage Phases': 'إدارة المراحل',
      'Project Phases': 'مراحل المشروع',
      'Manage phases, weights & assignments': 'إدارة المراحل، الأوزان والتعيينات',
      'Add Phase': 'إضافة مرحلة',
      'No Phases Yet': 'لا توجد مراحل بعد',
      'Organize your project into phases with weighted milestones to track progress more effectively.': 'نظم مشروعك إلى مراحل مع معالم مرجحة لتتبع التقدم بشكل أكثر فعالية.',
      'Create First Phase': 'إنشاء المرحلة الأولى',
      'Phase Name': 'اسم المرحلة',
      'Create Phase': 'إنشاء المرحلة',
      'Weight Allocation': 'تخصيص الوزن',
      'Balanced': 'متوازن',
      'Schedule Hierarchy': 'تسلسل زمني',
      'Unlinked Deliverables': 'مخرجات غير مرتبطة',
      'Attachments': 'المرفقات',
      'Attendee Approval': 'موافقة الحضور',
      'After the meeting is created, each attendee will be able to Approve or Reject the meeting record.': 'بعد إنشاء الاجتماع، سيتمكن كل حاضر من الموافقة أو رفض سجل الاجتماع.',
      'No risks found. Create a risk first.': 'لم يتم العثور على مخاطر. أنشئ مخاطرة أولاً.',
      'No issues found. Create an issue first.': 'لم يتم العثور على مشاكل. أنشئ مشكلة أولاً.',
      'Future Recommendations': 'التوصيات المستقبلية',
      'Expand All': 'توسيع الكل',
      'Collapse All': 'طي الكل',

      // Portfolio Create / Review
      'Portfolio Management': 'إدارة المحافظ',
      'Manage and track all portfolios efficiently': 'إدارة جميع المحافظ وتتبعها بكفاءة',
      'Create Portfolio': 'إنشاء محفظة',
      'Total Portfolios': 'إجمالي المحافظ',
      'Active Portfolios': 'المحافظ النشطة',
      'Completed Portfolios': 'المحافظ المكتملة',
      'Portfolios Overview': 'نظرة عامة على المحافظ',
      'Portfolio by Phases': 'المحافظ حسب المراحل',
      'Manage Portfolio': 'إدارة المحفظة',
      'Search portfolios by name, description, or owner...': 'ابحث في المحافظ بالاسم أو الوصف أو المالك...',
      'No portfolios found': 'لم يتم العثور على محافظ',
      'No portfolios yet': 'لا توجد محافظ بعد',
      'Get started by creating your first portfolio': 'ابدأ بإنشاء محفظتك الأولى',
      'View Portfolio': 'عرض المحفظة',
      'Delete Portfolio': 'حذف المحفظة',
      'Dept': 'القسم',
      'Action not allowed': 'الإجراء غير مسموح',
      'You can only delete drafts that you created.': 'يمكنك فقط حذف المسودات التي أنشأتها.',
      'Portfolio Deleted': 'تم حذف المحفظة',
      'Portfolio "{name}" has been permanently deleted': 'تم حذف المحفظة "{name}" نهائياً',
      'Failed to delete portfolio': 'فشل حذف المحفظة',
      'Portfolio': 'المحفظة',
      'Portfolios Loaded': 'تم تحميل المحافظ',
      '{count} portfolios loaded successfully': 'تم تحميل {count} محفظة بنجاح',
      'Failed to load portfolios. Please refresh the page.': 'فشل تحميل المحافظ. يرجى تحديث الصفحة.',
      'Approve Portfolio Phase': 'اعتماد مرحلة المحفظة',
      'Reject Portfolio Phase': 'رفض مرحلة المحفظة',
      'You are approving Phase {phase} for portfolio: {name}': 'أنت تعتمد المرحلة {phase} للمحفظة: {name}',
      'You are rejecting Phase {phase} for portfolio: {name}': 'أنت ترفض المرحلة {phase} للمحفظة: {name}',
      'Phase {phase} - Rejection Details': 'المرحلة {phase} - تفاصيل الرفض',
      'Phase 1 (Portfolio Definition)': 'المرحلة الأولى (تعريف المحفظة)',
      'Your portfolio was rejected': 'تم رفض محفظتك',
      'Failed to approve portfolio': 'فشل اعتماد المحفظة',
      'Failed to reject portfolio': 'فشل رفض المحفظة',
      'Exporting portfolios as {type}...': 'جارٍ تصدير المحافظ بصيغة {type}...',

      // Complete Portfolio module
      'Actual Budget': 'الميزانية الفعلية',
      'Add Dependency': 'إضافة تبعية',
      'Add Portfolio Benefit': 'إضافة منفعة للمحفظة',
      'Add Program': 'إضافة برنامج',
      'Add any final remarks': 'أضف أي ملاحظات ختامية',
      'Agile Programs': 'البرامج الرشيقة',
      'All Phases': 'جميع المراحل',
      'All Portfolios': 'جميع المحافظ',
      'Allocated to Milestones': 'المخصص للمعالم',
      'Allocated value is required': 'القيمة المخصصة مطلوبة',
      'Allocated value must be positive': 'يجب أن تكون القيمة المخصصة موجبة',
      'Back to Pre-Initiation': 'العودة إلى ما قبل البدء',
      'Before Closure': 'قبل الإغلاق',
      'Before Execution': 'قبل التنفيذ',
      'Before Initiation': 'قبل البدء',
      'Before Planning': 'قبل التخطيط',
      'Benefit title must be at least 3 characters': 'يجب ألا يقل عنوان المنفعة عن 3 أحرف',
      'Budget Exceeded': 'تم تجاوز الميزانية',
      'Budget Variance': 'انحراف الميزانية',
      'Certificate of Completion (CoC)': 'شهادة الإنجاز',
      'Challenges Faced': 'التحديات التي تمت مواجهتها',
      'Change Management': 'إدارة التغيير',
      'Charter Saved': 'تم حفظ الميثاق',
      'Closing this issue will automatically close {riskTitle}': 'سيؤدي إغلاق هذه المشكلة إلى إغلاق {riskTitle} تلقائياً',
      'Closure Phase Documents': 'مستندات مرحلة الإغلاق',
      'Closure Report Saved': 'تم حفظ تقرير الإغلاق',
      'Closure phase must be approved before completing program': 'يجب اعتماد مرحلة الإغلاق قبل إكمال البرنامج',
      'Communication Plan': 'خطة التواصل',
      'Complete Portfolio': 'إكمال المحفظة',
      'Comprehensive view of portfolio details and metrics': 'عرض شامل لتفاصيل المحفظة ومؤشراتها',
      'Confirm Issue Closure': 'تأكيد إغلاق المشكلة',
      'Creating a closed issue will automatically close {riskTitle}': 'سيؤدي إنشاء مشكلة مغلقة إلى إغلاق {riskTitle} تلقائياً',
      'Define success criteria': 'حدد معايير النجاح',
      'Define the program scope': 'حدد نطاق البرنامج',
      'Delete Deliverable': 'حذف المخرج',
      'Delete Milestone': 'حذف المعلم',
      'Deliverable name must be at least 3 characters': 'يجب ألا يقل اسم المخرج عن 3 أحرف',
      'Dependencies': 'التبعيات',
      'Describe major challenges faced': 'صف أبرز التحديات التي تمت مواجهتها',
      'Describe the objectives achieved': 'صف الأهداف المحققة',
      'Describe the program objective': 'صف هدف البرنامج',
      'Document Deleted': 'تم حذف المستند',
      'Document lessons learned': 'وثّق الدروس المستفادة',
      'Document removed successfully': 'تمت إزالة المستند بنجاح',
      'Downloading': 'جارٍ التنزيل',
      'Edit Portfolio Benefit': 'تعديل منفعة المحفظة',
      'Enter actual budget spent': 'أدخل الميزانية الفعلية المنفقة',
      'Enter allocated budget': 'أدخل الميزانية المخصصة',
      'Enter budget amount': 'أدخل مبلغ الميزانية',
      'Enter budget variance (over/under budget)': 'أدخل انحراف الميزانية (زيادة/نقص)',
      'Entry Date': 'تاريخ الإدخال',
      'Entry date must be on or after project start date': 'يجب أن يكون تاريخ الإدخال في تاريخ بدء المشروع أو بعده',
      'Escalate to is required': 'جهة التصعيد مطلوبة',
      'Execution Phase Documents': 'مستندات مرحلة التنفيذ',
      'Failed to approve phase': 'فشل اعتماد المرحلة',
      'Failed to create portfolio': 'فشل إنشاء المحفظة',
      'Failed to refresh portfolio data': 'فشل تحديث بيانات المحفظة',
      'Failed to reject phase': 'فشل رفض المرحلة',
      'Failed to resubmit phase': 'فشلت إعادة تقديم المرحلة',
      'Fill Form': 'تعبئة النموذج',
      'Fill out the charter form or upload a document': 'عبّئ نموذج الميثاق أو ارفع مستنداً',
      'Fill out the closure report form or upload a document': 'عبّئ نموذج تقرير الإغلاق أو ارفع مستنداً',
      'Filter Portfolios': 'تصفية المحافظ',
      'Final Remarks': 'الملاحظات الختامية',
      'Gather Project Closure Reports': 'جمع تقارير إغلاق المشاريع',
      'Health': 'الحالة الصحية',
      'Identify, assess, and manage portfolio risks': 'تحديد مخاطر المحفظة وتقييمها وإدارتها',
      'If you close this issue, the linked risk will be automatically closed. Do you want to continue?': 'إذا أغلقت هذه المشكلة فسيتم إغلاق الخطر المرتبط تلقائياً. هل تريد المتابعة؟',
      'Influence': 'التأثير',
      'Issue update cancelled': 'تم إلغاء تحديث المشكلة',
      'Justification & Planning': 'المبررات والتخطيط',
      'Justification is required': 'المبرر مطلوب',
      'Justification must not exceed 1000 characters': 'يجب ألا يتجاوز المبرر 1000 حرف',
      'Key Deliverables': 'المخرجات الرئيسية',
      'Key Stakeholders': 'أصحاب المصلحة الرئيسيون',
      'Kickoff Presentation': 'عرض الاجتماع الافتتاحي',
      'Large Programs': 'البرامج الكبيرة',
      'Linked Portfolio': 'المحفظة المرتبطة',
      'List key deliverables completed': 'اذكر المخرجات الرئيسية المكتملة',
      'List key stakeholders': 'اذكر أصحاب المصلحة الرئيسيين',
      'Manage all programs under this portfolio': 'إدارة جميع البرامج ضمن هذه المحفظة',
      'Manage and monitor all portfolio activities': 'إدارة جميع أنشطة المحفظة ومراقبتها',
      'Manage dependencies and interdependencies': 'إدارة التبعيات والاعتماديات المتبادلة',
      'Manage program phases and milestones': 'إدارة مراحل البرنامج ومعالمه',
      'Milestone': 'المعلم',
      'Milestone not selected': 'لم يتم تحديد معلم',
      'Move to Initiation': 'الانتقال إلى البدء',
      'New Portfolio': 'محفظة جديدة',
      'No certificate of completion uploaded yet': 'لم يتم رفع شهادة إنجاز بعد',
      'No charter document uploaded yet': 'لم يتم رفع مستند الميثاق بعد',
      'No communication plan uploaded yet': 'لم يتم رفع خطة التواصل بعد',
      'No deliverables tracking document uploaded yet': 'لم يتم رفع مستند تتبع المخرجات بعد',
      'No implementation plan uploaded yet': 'لم يتم رفع خطة التنفيذ بعد',
      'No kickoff presentation uploaded yet': 'لم يتم رفع العرض الافتتاحي بعد',
      'No program closure report uploaded yet': 'لم يتم رفع تقرير إغلاق البرنامج بعد',
      'No project closure reports uploaded yet': 'لم يتم رفع تقارير إغلاق المشاريع بعد',
      'Note: You must fully allocate the total budget across all milestones before submitting for review.': 'ملاحظة: يجب تخصيص إجمالي الميزانية بالكامل على جميع المعالم قبل التقديم للمراجعة.',
      'Objectives Achieved': 'الأهداف المحققة',
      'Off Track': 'خارج المسار',
      'Owner must be at least 2 characters': 'يجب ألا يقل اسم المالك عن حرفين',
      'Owner must not exceed 100 characters': 'يجب ألا يتجاوز اسم المالك 100 حرف',
      'Phase 1: Project Definition': 'المرحلة الأولى: تعريف المشروع',
      'Planned Delivery': 'التسليم المخطط',
      'Planned delivery date is required': 'تاريخ التسليم المخطط مطلوب',
      'Planning Phase Documents': 'مستندات مرحلة التخطيط',
      'Please fix all errors before saving': 'يرجى تصحيح جميع الأخطاء قبل الحفظ',
      'Please select program phase': 'يرجى اختيار مرحلة البرنامج',
      'Please upload all mandatory documents (RFP, Proposal, Contract) first': 'يرجى رفع جميع المستندات الإلزامية أولاً (طلب العروض والمقترح والعقد)',
      'Please upload all mandatory documents first': 'يرجى رفع جميع المستندات الإلزامية أولاً',
      'Portfolio Benefits': 'منافع المحفظة',
      'Portfolio Charter': 'ميثاق المحفظة',
      'Portfolio Charter Form': 'نموذج ميثاق المحفظة',
      'Portfolio Closure Report': 'تقرير إغلاق المحفظة',
      'Portfolio Closure Report Form': 'نموذج تقرير إغلاق المحفظة',
      'Portfolio Completed': 'اكتملت المحفظة',
      'Portfolio Completion': 'إكمال المحفظة',
      'Portfolio Dependencies': 'تبعيات المحفظة',
      'Portfolio Details': 'تفاصيل المحفظة',
      'Portfolio Document': 'مستند المحفظة',
      'Portfolio Gates': 'بوابات المحفظة',
      'Portfolio ID is required': 'معرف المحفظة مطلوب',
      'Portfolio Implementation Plan': 'خطة تنفيذ المحفظة',
      'Portfolio Lessons': 'دروس المحفظة',
      'Portfolio Navigation': 'التنقل في المحفظة',
      'Portfolio Objective': 'هدف المحفظة',
      'Portfolio Phase': 'مرحلة المحفظة',
      'Portfolio Risks': 'مخاطر المحفظة',
      'Portfolio Scope': 'نطاق المحفظة',
      'Portfolio Summary': 'ملخص المحفظة',
      'Portfolio change management': 'إدارة تغييرات المحفظة',
      'Portfolio charter has been created successfully': 'تم إنشاء ميثاق المحفظة بنجاح',
      'Portfolio closure report has been created successfully': 'تم إنشاء تقرير إغلاق المحفظة بنجاح',
      'Portfolio completed successfully!': 'تم إكمال المحفظة بنجاح!',
      'Portfolio dependencies': 'تبعيات المحفظة',
      'Portfolio escalations': 'تصعيدات المحفظة',
      'Portfolio information is required before creating an issue.': 'معلومات المحفظة مطلوبة قبل إنشاء مشكلة.',
      'Portfolio information is required before deleting an issue.': 'معلومات المحفظة مطلوبة قبل حذف مشكلة.',
      'Portfolio information is required before updating an issue.': 'معلومات المحفظة مطلوبة قبل تحديث مشكلة.',
      'Portfolio issues tracker': 'متتبع مشكلات المحفظة',
      'Portfolio lessons learned': 'الدروس المستفادة من المحفظة',
      'Portfolio phases and stage gates': 'مراحل المحفظة وبواباتها',
      'Portfolio risk register': 'سجل مخاطر المحفظة',
      'Portfolio summary and key metrics': 'ملخص المحفظة ومؤشراتها الرئيسية',
      'Pre-Initiation': 'ما قبل البدء',
      'Pre-Initiation Phase Documents': 'مستندات مرحلة ما قبل البدء',
      'Program Manager': 'مدير البرنامج',
      'Programs within this portfolio': 'البرامج ضمن هذه المحفظة',
      'Project start date not set. Please set project start date first.': 'لم يتم تحديد تاريخ بدء المشروع. يرجى تحديده أولاً.',
      'Provide recommendations for future programs': 'قدم توصيات للبرامج المستقبلية',
      'Quantity must be at least 1': 'يجب ألا تقل الكمية عن 1',
      'Report date must be on or after project start date': 'يجب أن يكون تاريخ التقرير في تاريخ بدء المشروع أو بعده',
      'Request for Proposal (RFP)': 'طلب تقديم العروض',
      'Required documents for program closure': 'المستندات المطلوبة لإغلاق البرنامج',
      'Required documents for program execution': 'المستندات المطلوبة لتنفيذ البرنامج',
      'Required documents for program initiation': 'المستندات المطلوبة لبدء البرنامج',
      'Required documents for program planning': 'المستندات المطلوبة لتخطيط البرنامج',
      'Required documents for program pre-initiation': 'المستندات المطلوبة لما قبل بدء البرنامج',
      'Review and approve portfolio submission': 'مراجعة تقديم المحفظة واعتماده',
      'Save Charter': 'حفظ الميثاق',
      'Save Closure Report': 'حفظ تقرير الإغلاق',
      'Search programs...': 'البحث في البرامج...',
      'Select completion date': 'اختر تاريخ الإكمال',
      'Select planned delivery date': 'اختر تاريخ التسليم المخطط',
      'Software Programs': 'برامج البرمجيات',
      'Stakeholder Feedback': 'ملاحظات أصحاب المصلحة',
      'Start by adding your first portfolio benefit': 'ابدأ بإضافة أول منفعة للمحفظة',
      'Status is required': 'الحالة مطلوبة',
      'Success Criteria': 'معايير النجاح',
      'Summarize stakeholder feedback': 'لخّص ملاحظات أصحاب المصلحة',
      'Target value must be a valid number': 'يجب أن تكون القيمة المستهدفة رقماً صالحاً',
      'Timeline & Budget': 'الجدول الزمني والميزانية',
      'Total milestone allocated value ({total}) exceeds the total budget ({budget}). Available budget: {remaining}': 'إجمالي القيمة المخصصة للمعالم ({total}) يتجاوز إجمالي الميزانية ({budget}). الميزانية المتاحة: {remaining}',
      'Track Deliverables': 'تتبع المخرجات',
      'Track and manage portfolio escalations': 'تتبع تصعيدات المحفظة وإدارتها',
      'Upload deliverables tracking document or reports': 'ارفع مستند أو تقارير تتبع المخرجات',
      'Upload project closure reports from all constituent projects': 'ارفع تقارير إغلاق جميع المشاريع التابعة',
      'Upload the certificate of completion document': 'ارفع مستند شهادة الإنجاز',
      'Upload the detailed program implementation plan': 'ارفع خطة تنفيذ البرنامج التفصيلية',
      'Upload the official RFP document': 'ارفع وثيقة طلب العروض الرسمية',
      'Upload the portfolio proposal document': 'ارفع مستند مقترح المحفظة',
      'Upload the program communication plan': 'ارفع خطة تواصل البرنامج',
      'Upload the program kickoff presentation': 'ارفع عرض الاجتماع الافتتاحي للبرنامج',
      'Upload the signed contract document': 'ارفع مستند العقد الموقّع',
      'Warning: Total allocated value exceeds the budget!': 'تحذير: إجمالي القيمة المخصصة يتجاوز الميزانية!',
      'Waterfall Programs': 'برامج المنهجية التسلسلية',
      'Yes, Close Issue': 'نعم، أغلق المشكلة',
      'e.g., pieces, kg, m2': 'مثال: قطعة، كجم، م²',
      'the linked risk': 'الخطر المرتبط',
      'Create New Portfolio': 'إنشاء محفظة جديدة',
      'Edit Portfolio': 'تعديل المحفظة',
      'Define your portfolio details and upload required documents': 'حدد تفاصيل المحفظة وارفع المستندات المطلوبة',
      'Portfolio Definition': 'تعريف المحفظة',
      'Define the core program parameters and details': 'حدد المعايير والتفاصيل الأساسية للبرنامج',
      'Save as Draft': 'حفظ كمسودة',
      'Submit Portfolio': 'تقديم المحفظة',
      'Portfolio has been saved as draft': 'تم حفظ المحفظة كمسودة',
      'Portfolio created successfully': 'تم إنشاء المحفظة بنجاح',
      'Please fill all required fields correctly': 'يرجى ملء جميع الحقول المطلوبة بشكل صحيح',
      'Failed to save draft': 'فشل حفظ المسودة',
      'Failed to load dropdown data': 'فشل تحميل بيانات القوائم المنسدلة',
      'Failed to load portfolio data': 'فشل تحميل بيانات المحفظة',
      'Portfolio name is required': 'اسم المحفظة مطلوب',
      'Department is required': 'القسم مطلوب',
      'Portfolio manager is required': 'مدير المحفظة مطلوب',
      'Portfolio sponsor is required': 'راعي المحفظة مطلوب',
      'Establishment date is required': 'تاريخ التأسيس مطلوب',
      'Actual start date is required': 'تاريخ البدء الفعلي مطلوب',
      'Enter portfolio name': 'أدخل اسم المحفظة',
      'Select establishment date': 'اختر تاريخ التأسيس',
      'Enter portfolio manager name': 'أدخل اسم مدير المحفظة',
      'Enter portfolio sponsor name': 'أدخل اسم راعي المحفظة',
      'Select actual start date': 'اختر تاريخ البدء الفعلي',
      'Enter budget': 'أدخل الميزانية',
      'Establishment Date': 'تاريخ التأسيس',
      'Actual Start Date': 'تاريخ البدء الفعلي',
      'Budget': 'الميزانية',
      '(Optional)': '(اختياري)',
      // Portfolio Review
      'Portfolio Review': 'مراجعة المحفظة',
      'Review and approve program submission': 'مراجعة واعتماد تقديم البرنامج',
      'Portfolio Information': 'معلومات المحفظة',
      'Portfolio Name': 'اسم المحفظة',
      'Portfolio Code': 'رمز المحفظة',
      'Portfolio Manager': 'مدير المحفظة',
      'Portfolio Sponsor': 'راعي المحفظة',
      'Estimated Budget': 'الميزانية التقديرية',
      'Portfolio Description': 'وصف المحفظة',
      'Required Documents': 'المستندات المطلوبة',
      'No files uploaded': 'لم يتم رفع ملفات',
      'RFP Document': 'وثيقة طلب العروض',
      'Portfolio Proposal': 'مقترح المحفظة',
      'Signed Contract': 'العقد الموقّع',
      'Review Actions': 'إجراءات المراجعة',
      'Review Comments': 'تعليقات المراجعة',
      'Add your review comments here...': 'أضف تعليقات مراجعتك هنا...',
      'Approve Portfolio': 'اعتماد المحفظة',
      'Request Changes': 'طلب تعديلات',
      'Reject Portfolio': 'رفض المحفظة',
      'Timeline': 'الجدول الزمني',
      'Created': 'تم الإنشاء',
      'Submitted': 'تم التقديم',
      'Last Reviewed': 'آخر مراجعة',
      'Portfolio Not Found': 'المحفظة غير موجودة',
      "The portfolio you're looking for doesn't exist.": 'المحفظة التي تبحث عنها غير موجودة.',
      'Back to Portfolios': 'العودة إلى المحافظ',
      'Download file': 'تحميل الملف',
      'file(s)': 'ملف/ملفات',
      'Approve or reject the project execution plan and deliverables': 'الموافقة أو الرفض لخطة تنفيذ المشروع والمخرجات',
      'Phase 2 - Execution Planning': 'المرحلة الثانية - تخطيط التنفيذ',
      'Select Program Classification': 'اختر تصنيف البرنامج',
      'Add any comments or feedback (optional)...': 'أضف أي تعليقات أو ملاحظات (اختياري)...',
      'Please provide a reason for rejection (required)...': 'يرجى تقديم سبب الرفض (مطلوب)...',
      '1 Month': 'شهر واحد',
      '1 Year': 'سنة واحدة',
      'A closure request has been submitted and is awaiting approval.': 'تم تقديم طلب إغلاق وهو بانتظار الموافقة.',
      'Accept': 'قبول',
      'Accept risks': 'قبول المخاطر',
      'Access Denied': 'تم رفض الوصول',
      'Achieved Result': 'النتيجة المحققة',
      'Achieved Score': 'الدرجة المحققة',
      'Achieved Score = Achieved ÷ Target': 'الدرجة المحققة = المحقق ÷ الهدف',
      'Achieved Score thresholds for each level': 'عتبات الدرجة المحققة لكل مستوى',
      'Achieved Value': 'القيمة المحققة',
      'Achieved Weight': 'الوزن المحقق',
      'Achieved value cannot exceed the target': 'لا يمكن أن تتجاوز القيمة المحققة الهدف',
      'Achieved value cannot exceed the target value': 'لا يمكن أن تتجاوز القيمة المحققة قيمة الهدف',
      'Achieved value is required': 'القيمة المحققة مطلوبة',
      'Achievement': 'الإنجاز',
      'Actual': 'الفعلي',
      'Actual Impact': 'الأثر الفعلي',
      'Actual Impact Measurement from Initiative Implementation': 'قياس الأثر الفعلي من تنفيذ المبادرة',
      'Add Challenge': 'إضافة تحدٍّ',
      'Add Child Indicator': 'إضافة مؤشر فرعي',
      'Add Corrective Plan': 'إضافة خطة تصحيحية',
      'Add First Objective': 'إضافة أول هدف',
      'Add First Output': 'إضافة أول مخرج',
      'Add First Pillar': 'إضافة أول ركيزة',
      'Add Initiative': 'إضافة مبادرة',
      'Add KPIs to this plan first before updating measurements': 'أضف مؤشرات الأداء إلى هذه الخطة أولاً قبل تحديث القياسات',
      'Add Objective': 'إضافة هدف',
      'Add Output': 'إضافة مخرج',
      'Add Pillar': 'إضافة ركيزة',
      'Add a comment...': 'أضف تعليقاً...',
      'Add an approval comment...': 'أضف تعليق موافقة...',
      'Add an optional comment before approving.': 'أضف تعليقاً اختيارياً قبل الموافقة.',
      'Add an optional comment...': 'أضف تعليقاً اختيارياً...',
      'Add any additional notes...': 'أضف أي ملاحظات إضافية...',
      'Add approval comment...': 'أضف تعليق الموافقة...',
      'Add initiative outputs to define deliverables and targets': 'أضف مخرجات المبادرة لتحديد المنجزات والأهداف',
      'Add strategic objectives to define your plan targets': 'أضف الأهداف الاستراتيجية لتحديد أهداف خطتك',
      'Add strategic pillars to define the core areas of your plan': 'أضف الركائز الاستراتيجية لتحديد المحاور الأساسية لخطتك',
      'Additional Details': 'تفاصيل إضافية',
      'All': 'الكل',
      'All Challenge Statuses': 'جميع حالات التحديات',
      'All Classifications': 'جميع التصنيفات',
      'All Funding Sources': 'جميع مصادر التمويل',
      'All Impacts': 'جميع الأثار',
      'All Initiatives': 'جميع المبادرات',
      'All KPI Measurements': 'جميع قياسات المؤشرات',
      'All KPIs': 'جميع المؤشرات',
      'All Performance Statuses': 'جميع حالات الأداء',
      'All Probabilities': 'جميع الاحتماليات',
      'All approved KPIs with measurement data and pending approvals': 'جميع المؤشرات المعتمدة مع بيانات القياس والموافقات المعلقة',
      'All change requests': 'جميع طلبات التغيير',
      'All initiatives available for output updates': 'جميع المبادرات المتاحة لتحديثات المخرجات',
      'All initiatives — approved and those with closure activity': 'جميع المبادرات — المعتمدة وتلك التي لها نشاط إغلاق',
      'All measurements for this KPI have been reviewed.': 'تمت مراجعة جميع قياسات هذا المؤشر.',
      'Analysis': 'التحليل',
      'Analyze the performance of initiatives under this plan': 'تحليل أداء المبادرات ضمن هذه الخطة',
      'Annual': 'سنوي',
      'Annual final target value': 'القيمة المستهدفة النهائية السنوية',
      'Any additional information...': 'أي معلومات إضافية...',
      'Approval Decision': 'قرار الموافقة',
      'Approval comment (optional)': 'تعليق الموافقة (اختياري)',
      'Approve & Close': 'موافقة وإغلاق',
      'Approve All': 'الموافقة على الكل',
      'Approve Challenge': 'الموافقة على التحدي',
      'Approve Change Request': 'الموافقة على طلب التغيير',
      'Approve Corrective Plan': 'الموافقة على الخطة التصحيحية',
      'Approve Initiative': 'الموافقة على المبادرة',
      'Approve KPI': 'الموافقة على المؤشر',
      'Approve KPI Change Request': 'الموافقة على طلب تغيير المؤشر',
      'Approve KPI Measurement': 'الموافقة على قياس المؤشر',
      'Approve Plan': 'الموافقة على الخطة',
      'Approve Risk': 'الموافقة على المخاطرة',
      'Approve Strategic Plan': 'الموافقة على الخطة الاستراتيجية',
      'Approve challenge': 'الموافقة على التحدي',
      'Approve change request': 'الموافقة على طلب التغيير',
      'Approve closure for': 'الموافقة على إغلاق',
      'Approve corrective plan': 'الموافقة على الخطة التصحيحية',
      'Approve or reject measurement submissions': 'الموافقة على تقديمات القياس أو رفضها',
      'Approve risk': 'الموافقة على المخاطرة',
      'Approve this change request?': 'هل توافق على طلب التغيير هذا؟',
      'Approved On': 'تاريخ الموافقة',
      'Approved change requests': 'طلبات التغيير المعتمدة',
      'Approved initiatives ready for output updates': 'المبادرات المعتمدة الجاهزة لتحديثات المخرجات',
      'Approved initiatives ready to be closed': 'المبادرات المعتمدة الجاهزة للإغلاق',
      'Approvers cannot edit change requests': 'لا يمكن للمعتمدين تعديل طلبات التغيير',
      'Are you sure you want to approve': 'هل أنت متأكد من الموافقة على',
      'Are you sure you want to approve this initiative?': 'هل أنت متأكد من الموافقة على هذه المبادرة؟',
      'Are you sure you want to approve this strategic plan?': 'هل أنت متأكد من الموافقة على هذه الخطة الاستراتيجية؟',
      'Are you sure you want to delete change request': 'هل أنت متأكد من حذف طلب التغيير',
      'Are you sure you want to delete this KPI change request? This action cannot be undone.': 'هل أنت متأكد من حذف طلب تغيير المؤشر هذا؟ لا يمكن التراجع عن هذا الإجراء.',
      'Are you sure you want to delete this KPI?': 'هل أنت متأكد من حذف هذا المؤشر؟',
      'Are you sure you want to delete this change request?': 'هل أنت متأكد من حذف طلب التغيير هذا؟',
      'Are you sure you want to reject': 'هل أنت متأكد من رفض',
      'Are you sure you want to reject this initiative? The initiative will be sent back for revision.': 'هل أنت متأكد من رفض هذه المبادرة؟ سيتم إعادة المبادرة للمراجعة.',
      'Are you sure you want to reject this strategic plan? The plan will be sent back for revision.': 'هل أنت متأكد من رفض هذه الخطة الاستراتيجية؟ سيتم إعادة الخطة للمراجعة.',
      'At least 2 child indicators are required for a composite KPI': 'يجب أن يحتوي المؤشر المركب على مؤشرين فرعيين على الأقل',
      'At least one attachment is required to submit': 'يجب إرفاق مستند واحد على الأقل للتقديم',
      'At least one attachment is required to submit this measurement': 'يجب إرفاق مستند واحد على الأقل لتقديم هذا القياس',
      'At least one output must have a completion percentage to submit': 'يجب أن يكون لمخرج واحد على الأقل نسبة اكتمال للتقديم',
      'At least one strategic objective is required': 'يجب تحديد هدف استراتيجي واحد على الأقل',
      'At least one strategic pillar is required': 'يجب تحديد ركيزة استراتيجية واحدة على الأقل',
      'At least one supporting document is required': 'يجب إرفاق وثيقة داعمة واحدة على الأقل',
      'Attach relevant files to support this change request': 'أرفق الملفات ذات الصلة لدعم طلب التغيير هذا',
      'Attachment removed': 'تمت إزالة المرفق',
      'Attachment uploaded': 'تم رفع المرفق',
      'Attachments Required': 'المرفقات مطلوبة',
      'Attachments are required when submitting measurements for this indicator.': 'المرفقات مطلوبة عند تقديم قياسات هذا المؤشر.',
      'Auto': 'تلقائي',
      'Auto-filled from KPI': 'تم ملؤه تلقائياً من المؤشر',
      'Auto-generated based on order': 'يُنشأ تلقائياً بناءً على الترتيب',
      'Auto-generated by the system': 'يُنشأ تلقائياً من النظام',
      'Auto-generated on save': 'يُنشأ تلقائياً عند الحفظ',
      'Auto-generated from the perspective, its pillar number and the next sequence.': 'يُنشأ تلقائيًا من المنظور ورقم الركيزة والتسلسل التالي.',
      'Average Actual Completion': 'متوسط الاكتمال الفعلي',
      'Average Deviation': 'متوسط الانحراف',
      'Average Planned Completion': 'متوسط الاكتمال المخطط',
      'Avg Completion': 'متوسط الاكتمال',
      'Avg Score': 'متوسط الدرجة',
      'Avg. Progress': 'متوسط التقدم',
      'Avoid risks': 'تجنب المخاطر',
      'Awaiting Approval': 'بانتظار الموافقة',
      'Back to Change Requests': 'العودة إلى طلبات التغيير',
      'Back to Initiatives': 'العودة إلى المبادرات',
      'Back to Plans': 'العودة إلى الخطط',
      'Back to Strategic KPIs': 'العودة إلى المؤشرات الاستراتيجية',
      'Baseline': 'خط الأساس',
      'Baseline & Targets': 'خط الأساس والأهداف',
      'Baseline is required': 'خط الأساس مطلوب',
      'Benefits & Activities': 'الفوائد والأنشطة',
      'Benefits and Activities Completed': 'الفوائد والأنشطة المكتملة',
      'Bulk Actions': 'الإجراءات الجماعية',
      'By': 'بواسطة',
      'Calendar Days': 'أيام التقويم',
      'Catastrophic': 'كارثي',
      'Categorise the KPI by classification, type and polarity': 'صنّف المؤشر حسب التصنيف والنوع والاتجاه',
      'Challenge Approved': 'تمت الموافقة على التحدي',
      'Challenge Code': 'رمز التحدي',
      'Challenge Details': 'تفاصيل التحدي',
      'Challenge Information': 'معلومات التحدي',
      'Challenge Name': 'اسم التحدي',
      'Challenge Rejected': 'تم رفض التحدي',
      'Challenge Status': 'حالة التحدي',
      'Challenge approved': 'تمت الموافقة على التحدي',
      'Challenge auto-approved': 'تمت الموافقة التلقائية على التحدي',
      'Challenge created successfully': 'تم إنشاء التحدي بنجاح',
      'Challenge deleted': 'تم حذف التحدي',
      'Challenge deleted successfully': 'تم حذف التحدي بنجاح',
      'Challenge rejected': 'تم رفض التحدي',
      'Challenge saved but submission failed': 'تم حفظ التحدي لكن فشل التقديم',
      'Challenge submitted for approval': 'تم تقديم التحدي للموافقة',
      'Challenge updated successfully': 'تم تحديث التحدي بنجاح',
      'Challenges': 'التحديات',
      'Challenges Encountered': 'التحديات التي واجهتنا',
      'Challenges Register': 'سجل التحديات',
      'Challenges encountered in this strategic plan': 'التحديات التي واجهت هذه الخطة الاستراتيجية',
      'Change Description': 'وصف التغيير',
      'Change Justification': 'مبرر التغيير',
      'Change Request Information': 'معلومات طلب التغيير',
      'Change Request Not Found': 'طلب التغيير غير موجود',
      'Change request approved': 'تمت الموافقة على طلب التغيير',
      'Change request created': 'تم إنشاء طلب التغيير',
      'Change request deleted': 'تم حذف طلب التغيير',
      'Change request rejected': 'تم رفض طلب التغيير',
      'Change request submitted for approval': 'تم تقديم طلب التغيير للموافقة',
      'Change request updated': 'تم تحديث طلب التغيير',
      'Change requests awaiting approval': 'طلبات التغيير بانتظار الموافقة',
      'Classification & Type': 'التصنيف والنوع',
      'Classification is required': 'التصنيف مطلوب',
      'Click "Upload File" above to attach supporting documents': 'انقر على "رفع ملف" أعلاه لإرفاق الوثائق الداعمة',
      'Click or drop files — PDF, WORD, EXCEL, PPT': 'انقر أو أسقط الملفات — PDF، WORD، EXCEL، PPT',
      'Click or drop files — PDF, WORD, EXCEL, PPT, Images': 'انقر أو أسقط الملفات — PDF، WORD، EXCEL، PPT، صور',
      'Close Initiative': 'إغلاق المبادرة',
      'Close completed or cancelled initiatives': 'إغلاق المبادرات المكتملة أو الملغاة',
      'Closed initiatives cannot be updated': 'لا يمكن تحديث المبادرات المغلقة',
      'Closure Information': 'معلومات الإغلاق',
      'Closure Pending Approval': 'الإغلاق بانتظار الموافقة',
      'Closure Rejected': 'تم رفض الإغلاق',
      'Closure Status': 'حالة الإغلاق',
      'Closure drafts not yet submitted': 'مسودات الإغلاق غير المقدمة بعد',
      'Closure submitted successfully': 'تم تقديم طلب الإغلاق بنجاح',
      'Closures awaiting approval review': 'طلبات الإغلاق بانتظار مراجعة الموافقة',
      'Comment (optional)': 'تعليق (اختياري)',
      'Completion': 'الاكتمال',
      'Completion %': 'نسبة الاكتمال %',
      'Completion Percentage': 'نسبة الاكتمال',
      'Composite': 'مركب',
      'Composite Indicator = Σ ( Indicator × Weight ) ÷ Number of Indicators': 'المؤشر المركب = Σ (المؤشر × الوزن) ÷ عدد المؤشرات',
      'Composite Indicators': 'المؤشرات المركبة',
      'Composite Sub-Indicators': 'المؤشرات الفرعية المركبة',
      'Composite indicator weights must sum to exactly 100%': 'يجب أن تساوي أوزان المؤشر المركب 100% بالضبط',
      'Computed Relative Scale': 'المقياس النسبي المحسوب',
      'Confirm Approve': 'تأكيد الموافقة',
      'Confirm Reject': 'تأكيد الرفض',
      'Contribution Details': 'تفاصيل المساهمة',
      'Core KPI identification and ownership': 'تحديد المؤشر الرئيسي وملكيته',
      'Core initiative identification and details': 'تحديد المبادرة الرئيسية وتفاصيلها',
      'Corrective Plan': 'الخطة التصحيحية',
      'Corrective Plan Details': 'تفاصيل الخطة التصحيحية',
      'Corrective Plans': 'الخطط التصحيحية',
      'Corrective plan created successfully': 'تم إنشاء الخطة التصحيحية بنجاح',
      'Corrective plan deleted successfully': 'تم حذف الخطة التصحيحية بنجاح',
      'Corrective plan updated successfully': 'تم تحديث الخطة التصحيحية بنجاح',
      'Corrective plans can only be added to approved strategic plans': 'يمكن إضافة الخطط التصحيحية فقط إلى الخطط الاستراتيجية المعتمدة',
      'Could not upload': 'تعذّر الرفع',
      'Count': 'العدد',
      'Create Challenge': 'إنشاء تحدٍّ',
      'Create Change Request': 'إنشاء طلب تغيير',
      'Create Corrective Plan': 'إنشاء خطة تصحيحية',
      'Create Initiative': 'إنشاء مبادرة',
      'Create New Initiative': 'إنشاء مبادرة جديدة',
      'Create Risk': 'إنشاء مخاطرة',
      'Create Strategic KPI': 'إنشاء مؤشر أداء استراتيجي',
      'Create a corrective plan for a delayed indicator': 'إنشاء خطة تصحيحية لمؤشر متأخر',
      'Create a new KPI change request': 'إنشاء طلب تغيير جديد للمؤشر',
      'Create a new change request for an approved initiative': 'إنشاء طلب تغيير جديد لمبادرة معتمدة',
      'Create a new initiative change request': 'إنشاء طلب تغيير جديد للمبادرة',
      'Create a new strategic challenge entry': 'إنشاء إدخال تحدٍّ استراتيجي جديد',
      'Create a new strategic performance indicator': 'إنشاء مؤشر أداء استراتيجي جديد',
      'Create a new strategic risk entry': 'إنشاء إدخال مخاطرة استراتيجية جديدة',
      'Create corrective plans to address underperforming indicators': 'إنشاء خطط تصحيحية لمعالجة المؤشرات دون المستوى المطلوب',
      'Create initiatives first to see performance analysis': 'أنشئ المبادرات أولاً لعرض تحليل الأداء',
      'Create initiatives to execute this strategic plan': 'أنشئ مبادرات لتنفيذ هذه الخطة الاستراتيجية',
      'Creator': 'المنشئ',
      'Current baseline value': 'قيمة خط الأساس الحالية',
      'Data Source': 'مصدر البيانات',
      'Dates & Timeline': 'التواريخ والجدول الزمني',
      'Decision Made': 'القرار المتخذ',
      'Define Strategic KPI': 'تحديد المؤشر الاستراتيجي',
      'Define a new strategic initiative': 'تحديد مبادرة استراتيجية جديدة',
      'Define and plan strategic initiatives': 'تحديد المبادرات الاستراتيجية وتخطيطها',
      'Define how this KPI is measured and tracked': 'تحديد كيفية قياس هذا المؤشر وتتبعه',
      'Define measurable strategic objectives': 'تحديد أهداف استراتيجية قابلة للقياس',
      'Define the core strategic pillars for this plan': 'تحديد الركائز الاستراتيجية الأساسية لهذه الخطة',
      'Define the initiative outputs and deliverables': 'تحديد مخرجات المبادرة ومنجزاتها',
      'Define your strategic plan scope': 'تحديد نطاق خطتك الاستراتيجية',
      'Delay Justification': 'مبرر التأخير',
      'Delayed': 'متأخر',
      'Delete Challenge': 'حذف التحدي',
      'Delete Change Request': 'حذف طلب التغيير',
      'Delete Corrective Plan': 'حذف الخطة التصحيحية',
      'Delete KPI Change Request': 'حذف طلب تغيير المؤشر',
      'Delete Plan': 'حذف الخطة',
      'Delete Risk': 'حذف المخاطرة',
      'Delete challenge': 'حذف التحدي',
      'Delete corrective plan': 'حذف الخطة التصحيحية',
      'Delete risk': 'حذف المخاطرة',
      'Dependency': 'الاعتمادية',
      'Describe any challenges encountered...': 'صف أي تحديات تمت مواجهتها...',
      'Describe contribution details...': 'صف تفاصيل المساهمة...',
      'Describe the actual impact measurement...': 'صف قياس الأثر الفعلي...',
      'Describe the benefits and activities completed...': 'صف الفوائد والأنشطة المكتملة...',
      'Describe the change in detail...': 'صف التغيير بالتفصيل...',
      'Describe the corrective actions to be taken...': 'صف الإجراءات التصحيحية التي يجب اتخاذها...',
      'Describe the impact if this change is not accepted...': 'صف الأثر إذا لم يتم قبول هذا التغيير...',
      'Describe the key achievements of this initiative...': 'صف الإنجازات الرئيسية لهذه المبادرة...',
      'Describe the lessons learned...': 'صف الدروس المستفادة...',
      'Describe the purpose and context of this KPI': 'صف الغرض وسياق هذا المؤشر',
      'Describe the purpose and context of this indicator': 'صف الغرض وسياق هذا المؤشر',
      'Describe the reason for requesting this change...': 'صف سبب طلب هذا التغيير...',
      'Describe the required change in detail...': 'صف التغيير المطلوب بالتفصيل...',
      'Describe the support needed to implement the plan...': 'صف الدعم المطلوب لتنفيذ الخطة...',
      'Description of Required Change': 'وصف التغيير المطلوب',
      'Deviation': 'الانحراف',
      'Document challenges to track obstacles in your strategic plan': 'وثّق التحديات لتتبع العقبات في خطتك الاستراتيجية',
      'Document removed': 'تمت إزالة الوثيقة',
      'Document risks to proactively manage strategic plan challenges': 'وثّق المخاطر لإدارة تحديات الخطة الاستراتيجية بشكل استباقي',
      'Document uploaded': 'تم رفع الوثيقة',
      'Does accepting the change require a retroactive amendment?': 'هل يستلزم قبول التغيير تعديلاً بأثر رجعي؟',
      'Draft change requests not yet submitted': 'مسودات طلبات التغيير غير المقدمة بعد',
      'Draft initiatives not yet submitted': 'مسودات المبادرات غير المقدمة بعد',
      'Drag & drop files here or': 'اسحب وأسقط الملفات هنا أو',
      'Drop files here or click to browse': 'أسقط الملفات هنا أو انقر للتصفح',
      'Edit Challenge': 'تعديل التحدي',
      'Edit Corrective Plan': 'تعديل الخطة التصحيحية',
      'Edit Initiative': 'تعديل المبادرة',
      'Edit KPI Change Request': 'تعديل طلب تغيير المؤشر',
      'Edit Objective': 'تعديل الهدف',
      'Edit Output': 'تعديل المخرج',
      'Edit Pillar': 'تعديل الركيزة',
      'Edit Strategic KPI': 'تعديل المؤشر الاستراتيجي',
      'Endorsement': 'التأييد',
      'Enter achieved value': 'أدخل القيمة المحققة',
      'Enter achieved values for each measurement period. Only Achieved, Notes, and Attachments can be updated.': 'أدخل القيم المحققة لكل فترة قياس. يمكن تحديث المحقق والملاحظات والمرفقات فقط.',
      'Enter baseline value': 'أدخل قيمة خط الأساس',
      'Enter challenge description...': 'أدخل وصف التحدي...',
      'Enter challenge name...': 'أدخل اسم التحدي...',
      'Enter completion %': 'أدخل نسبة الاكتمال %',
      'Enter data source': 'أدخل مصدر البيانات',
      'Enter endorsement...': 'أدخل التأييد...',
      'Enter final target value': 'أدخل قيمة الهدف النهائي',
      'Enter indicator name': 'أدخل اسم المؤشر',
      'Enter initiative description': 'أدخل وصف المبادرة',
      'Enter initiative name': 'أدخل اسم المبادرة',
      'Enter mitigation actions...': 'أدخل إجراءات التخفيف...',
      'Enter notes if any': 'أدخل الملاحظات إن وجدت',
      'Enter objective description': 'أدخل وصف الهدف',
      'Enter objective name': 'أدخل اسم الهدف',
      'Enter output name': 'أدخل اسم المخرج',
      'Enter output source': 'أدخل مصدر المخرج',
      'Enter output type': 'أدخل نوع المخرج',
      'Enter owning sector': 'أدخل القطاع المالك',
      'Enter pillar description': 'أدخل وصف الركيزة',
      'Enter pillar name': 'أدخل اسم الركيزة',
      'Enter reason for rejection...': 'أدخل سبب الرفض...',
      'Enter requester name...': 'أدخل اسم مقدم الطلب...',
      'Enter review comments (optional for approval, required for rejection)': 'أدخل تعليقات المراجعة (اختياري للموافقة، إلزامي للرفض)',
      'Enter risk description...': 'أدخل وصف المخاطرة...',
      'Enter risk name...': 'أدخل اسم المخاطرة...',
      'Enter risk owner...': 'أدخل مالك المخاطرة...',
      'Enter sector name': 'أدخل اسم القطاع',
      'Enter weight': 'أدخل الوزن',
      'Escalate risks': 'تصعيد المخاطر',
      'Evaluate internal and external factors affecting your strategic plan': 'تقييم العوامل الداخلية والخارجية المؤثرة على خطتك الاستراتيجية',
      'Expected End': 'النهاية المتوقعة',
      'Expected End Date': 'تاريخ النهاية المتوقعة',
      'Expected end date is required': 'تاريخ النهاية المتوقعة مطلوب',
      'Explain the reasons for the delay...': 'اشرح أسباب التأخير...',
      'Failed to add item': 'فشل إضافة العنصر',
      'Failed to approve': 'فشلت الموافقة',
      'Failed to approve KPI': 'فشلت الموافقة على المؤشر',
      'Failed to approve challenge': 'فشلت الموافقة على التحدي',
      'Failed to approve initiative': 'فشلت الموافقة على المبادرة',
      'Failed to approve plan': 'فشلت الموافقة على الخطة',
      'Failed to approve risk': 'فشلت الموافقة على المخاطرة',
      'Failed to approve strategic plan': 'فشلت الموافقة على الخطة الاستراتيجية',
      'Failed to create measurement record for upload': 'فشل إنشاء سجل القياس للرفع',
      'Failed to delete': 'فشل الحذف',
      'Failed to delete KPI': 'فشل حذف المؤشر',
      'Failed to delete challenge': 'فشل حذف التحدي',
      'Failed to delete corrective plan': 'فشل حذف الخطة التصحيحية',
      'Failed to delete initiative': 'فشل حذف المبادرة',
      'Failed to delete item': 'فشل حذف العنصر',
      'Failed to delete plan': 'فشل حذف الخطة',
      'Failed to load KPI': 'فشل تحميل المؤشر',
      'Failed to load KPI change request': 'فشل تحميل طلب تغيير المؤشر',
      'Failed to load KPI change requests': 'فشل تحميل طلبات تغيير المؤشر',
      'Failed to load KPIs': 'فشل تحميل المؤشرات',
      'Failed to load challenge': 'فشل تحميل التحدي',
      'Failed to load challenges': 'فشل تحميل التحديات',
      'Failed to load change request': 'فشل تحميل طلب التغيير',
      'Failed to load corrective plan': 'فشل تحميل الخطة التصحيحية',
      'Failed to load corrective plans': 'فشل تحميل الخطط التصحيحية',
      'Failed to load initiative': 'فشل تحميل المبادرة',
      'Failed to load initiative details': 'فشل تحميل تفاصيل المبادرة',
      'Failed to load initiative update form': 'فشل تحميل نموذج تحديث المبادرة',
      'Failed to load initiatives': 'فشل تحميل المبادرات',
      'Failed to load measurement form': 'فشل تحميل نموذج القياس',
      'Failed to load measurements': 'فشل تحميل القياسات',
      'Failed to load performance analysis': 'فشل تحميل تحليل الأداء',
      'Failed to load plan data': 'فشل تحميل بيانات الخطة',
      'Failed to load risk': 'فشل تحميل المخاطرة',
      'Failed to load strategic plan details': 'فشل تحميل تفاصيل الخطة الاستراتيجية',
      'Failed to refresh strategy data': 'فشل تحديث بيانات الاستراتيجية',
      'Failed to reject': 'فشل الرفض',
      'Failed to reject KPI': 'فشل رفض المؤشر',
      'Failed to reject challenge': 'فشل رفض التحدي',
      'Failed to reject initiative': 'فشل رفض المبادرة',
      'Failed to reject plan': 'فشل رفض الخطة',
      'Failed to reject risk': 'فشل رفض المخاطرة',
      'Failed to reject strategic plan': 'فشل رفض الخطة الاستراتيجية',
      'Failed to remove attachment': 'فشل إزالة المرفق',
      'Failed to remove document': 'فشل إزالة الوثيقة',
      'Failed to resubmit strategic plan': 'فشل إعادة تقديم الخطة الاستراتيجية',
      'Failed to save KPI': 'فشل حفظ المؤشر',
      'Failed to save KPI change request': 'فشل حفظ طلب تغيير المؤشر',
      'Failed to save challenge': 'فشل حفظ التحدي',
      'Failed to save change request': 'فشل حفظ طلب التغيير',
      'Failed to save corrective plan': 'فشل حفظ الخطة التصحيحية',
      'Failed to save initiative': 'فشل حفظ المبادرة',
      'Failed to save measurement': 'فشل حفظ القياس',
      'Failed to save measurements': 'فشل حفظ القياسات',
      'Failed to save risk': 'فشل حفظ المخاطرة',
      'Failed to save updates': 'فشل حفظ التحديثات',
      'Failed to submit': 'فشل التقديم',
      'Failed to submit KPI': 'فشل تقديم المؤشر',
      'Failed to submit KPI for approval': 'فشل تقديم المؤشر للموافقة',
      'Failed to submit challenge': 'فشل تقديم التحدي',
      'Failed to submit closure': 'فشل تقديم طلب الإغلاق',
      'Failed to submit initiative for approval': 'فشل تقديم المبادرة للموافقة',
      'Failed to submit plan': 'فشل تقديم الخطة',
      'Failed to submit plan for approval': 'فشل تقديم الخطة للموافقة',
      'Failed to submit risk': 'فشل تقديم المخاطرة',
      'Failed to update item': 'فشل تحديث العنصر',
      'Failed to upload attachment': 'فشل رفع المرفق',
      'Fields highlighted in amber reflect the proposed changes.': 'الحقول المظللة بالعنبري تعكس التغييرات المقترحة.',
      'Final Result': 'النتيجة النهائية',
      'Final Target': 'الهدف النهائي',
      'Final target is required': 'الهدف النهائي مطلوب',
      'Formula': 'الصيغة',
      'Formula: Composite = Σ(Indicator × Weight). Weights must sum to 100%.': 'الصيغة: المركب = Σ(المؤشر × الوزن). يجب أن تساوي الأوزان 100%.',
      'Full Analysis': 'التحليل الكامل',
      'Full initiative duration': 'مدة المبادرة الكاملة',
      'Funding Source': 'مصدر التمويل',
      'Funding source is required': 'مصدر التمويل مطلوب',
      'Get started by creating your first initiative': 'ابدأ بإنشاء مبادرتك الأولى',
      'Get started by creating your first strategic KPI': 'ابدأ بإنشاء مؤشر أدائك الاستراتيجي الأول',
      'Go Back': 'العودة',
      'Grid view': 'عرض شبكي',
      'Hours': 'ساعات',
      'Identify the responsible party...': 'حدد الجهة المسؤولة...',
      'Impact if Change is Not Accepted': 'الأثر إذا لم يتم قبول التغيير',
      'Impact if Change is Rejected': 'الأثر إذا تم رفض التغيير',
      'Index': 'الفهرس',
      'Indicator': 'المؤشر',
      'Indicator Classification': 'تصنيف المؤشر',
      'Indicator Code': 'رمز المؤشر',
      'Indicator Description': 'وصف المؤشر',
      'Indicator Information': 'معلومات المؤشر',
      'Indicator Measurement Update Form': 'نموذج تحديث قياس المؤشر',
      'Indicator Name': 'اسم المؤشر',
      'Indicator Number': 'رقم المؤشر',
      'Indicator Owner': 'مالك المؤشر',
      'Indicator Owning Department': 'الإدارة المالكة للمؤشر',
      'Indicator Owning Sector': 'القطاع المالك للمؤشر',
      'Indicator Polarity': 'اتجاه المؤشر',
      'Indicator Type': 'نوع المؤشر',
      'Indicator Weight': 'وزن المؤشر',
      'Indicator corrective plans for this strategy': 'الخطط التصحيحية للمؤشرات في هذه الاستراتيجية',
      'Indicator name is required': 'اسم المؤشر مطلوب',
      'Initiative': 'المبادرة',
      'Initiative Card': 'بطاقة المبادرة',
      'Initiative Change Requests': 'طلبات تغيير المبادرات',
      'Initiative Closed': 'تم إغلاق المبادرة',
      'Initiative Closure': 'إغلاق المبادرة',
      'Initiative Closure Form': 'نموذج إغلاق المبادرة',
      'Initiative Closures': 'إغلاق المبادرات',
      'Initiative Code': 'رمز المبادرة',
      'Initiative Description': 'وصف المبادرة',
      'Initiative Details': 'تفاصيل المبادرة',
      'Initiative Delay Impact': 'أثر تأخر المبادرات',
      'Initiative End Date': 'تاريخ انتهاء المبادرة',
      'Initiative Information': 'معلومات المبادرة',
      'Initiative Name': 'اسم المبادرة',
      'Initiative Not Found': 'المبادرة غير موجودة',
      'Initiative Outputs': 'مخرجات المبادرة',
      'Initiative Owner': 'مالك المبادرة',
      'Initiative Performance Analysis': 'تحليل أداء المبادرة',
      'Initiative Planning Form': 'نموذج تخطيط المبادرة',
      'Initiative Progress Breakdown': 'تفصيل تقدم المبادرة',
      'Initiative Rejected': 'تم رفض المبادرة',
      'Initiative Review': 'مراجعة المبادرة',
      'Initiative Start Date': 'تاريخ بدء المبادرة',
      'Initiative Status': 'حالة المبادرة',
      'Initiative Summary': 'ملخص المبادرة',
      'Initiative Update': 'تحديث المبادرة',
      'Initiative Update Form': 'نموذج تحديث المبادرة',
      'Initiative Updates': 'تحديثات المبادرة',
      'Initiative Weight': 'وزن المبادرة',
      'Initiative approved automatically': 'تمت الموافقة التلقائية على المبادرة',
      'Initiative approved successfully': 'تمت الموافقة على المبادرة بنجاح',
      'Initiative closure approved': 'تمت الموافقة على إغلاق المبادرة',
      'Initiative closure approved — initiative is now closed': 'تمت الموافقة على إغلاق المبادرة — المبادرة مغلقة الآن',
      'Initiative closure rejected': 'تم رفض إغلاق المبادرة',
      'Initiative created successfully': 'تم إنشاء المبادرة بنجاح',
      'Initiative deleted successfully': 'تم حذف المبادرة بنجاح',
      'Initiative has been approved successfully': 'تمت الموافقة على المبادرة بنجاح',
      'Initiative has been rejected': 'تم رفض المبادرة',
      'Initiative name is required': 'اسم المبادرة مطلوب',
      'Initiative rejected': 'تم رفض المبادرة',
      'Initiative saved as draft': 'تم حفظ المبادرة كمسودة',
      'Initiative submitted for approval': 'تم تقديم المبادرة للموافقة',
      'Initiative updated successfully': 'تم تحديث المبادرة بنجاح',
      'Initiatives': 'المبادرات',
      'Initiatives awaiting approval review': 'المبادرات بانتظار مراجعة الموافقة',
      'Initiatives can only be deleted when in draft or rejected status': 'يمكن حذف المبادرات فقط عندما تكون في حالة مسودة أو مرفوضة',
      'Initiatives can only be edited when in draft or rejected status': 'يمكن تعديل المبادرات فقط عندما تكون في حالة مسودة أو مرفوضة',
      'Justify the reason for this change...': 'اشرح مبرر هذا التغيير...',
      'KPI': 'مؤشر الأداء',
      'KPI Approved': 'تمت الموافقة على المؤشر',
      'KPI Change Request': 'طلب تغيير المؤشر',
      'KPI Change Request Details': 'تفاصيل طلب تغيير المؤشر',
      'KPI Change Requests': 'طلبات تغيير المؤشر',
      'KPI Classification': 'تصنيف المؤشر',
      'KPI Code': 'رمز المؤشر',
      'KPI Details': 'تفاصيل المؤشر',
      'KPI Information': 'معلومات المؤشر',
      'KPI Measurements': 'قياسات المؤشر',
      'KPI Not Found': 'المؤشر غير موجود',
      'KPI approved automatically': 'تمت الموافقة التلقائية على المؤشر',
      'KPI change request approved': 'تمت الموافقة على طلب تغيير المؤشر',
      'KPI change request approved successfully': 'تمت الموافقة على طلب تغيير المؤشر بنجاح',
      'KPI change request created': 'تم إنشاء طلب تغيير المؤشر',
      'KPI change request deleted': 'تم حذف طلب تغيير المؤشر',
      'KPI change request rejected': 'تم رفض طلب تغيير المؤشر',
      'KPI change request submitted for approval': 'تم تقديم طلب تغيير المؤشر للموافقة',
      'KPI change request updated': 'تم تحديث طلب تغيير المؤشر',
      'KPI created successfully': 'تم إنشاء المؤشر بنجاح',
      'KPI deleted successfully': 'تم حذف المؤشر بنجاح',
      'KPI not found': 'المؤشر غير موجود',
      'KPI submitted for approval': 'تم تقديم المؤشر للموافقة',
      'KPI updated successfully': 'تم تحديث المؤشر بنجاح',
      'KPI was rejected': 'تم رفض المؤشر',
      'KPI — Proposed Changes': 'المؤشر — التغييرات المقترحة',
      'KPIs': 'المؤشرات',
      'KPIs can only be deleted when in draft or rejected status': 'يمكن حذف المؤشرات فقط عندما تكون في حالة مسودة أو مرفوضة',
      'KPIs can only be edited when in draft or rejected status': 'يمكن تعديل المؤشرات فقط عندما تكون في حالة مسودة أو مرفوضة',
      'KPIs ready for measurement entry or update': 'المؤشرات الجاهزة لإدخال القياسات أو تحديثها',
      'KPIs with approved measurements': 'المؤشرات ذات القياسات المعتمدة',
      'KPIs with draft measurements': 'المؤشرات ذات القياسات المسودة',
      'KPIs with rejected measurements that need revision': 'المؤشرات ذات القياسات المرفوضة التي تحتاج إلى مراجعة',
      'Key Achievements': 'الإنجازات الرئيسية',
      'Key Performance Indicators for this strategic plan': 'مؤشرات الأداء الرئيسية لهذه الخطة الاستراتيجية',
      'Less than 1 month': 'أقل من شهر',
      'Level': 'المستوى',
      'Likely': 'محتمل',
      'List view': 'عرض قائمة',
      'Major change requests': 'طلبات التغيير الكبرى',
      'Manage Plan': 'إدارة الخطة',
      'Manage and track strategic key performance indicators': 'إدارة وتتبع مؤشرات الأداء الاستراتيجية',
      'Manage change requests for KPIs in this plan': 'إدارة طلبات التغيير لمؤشرات هذه الخطة',
      'Manage corrective plans for delayed indicators': 'إدارة الخطط التصحيحية للمؤشرات المتأخرة',
      'Manage indicator measurement updates and approvals': 'إدارة تحديثات وموافقات قياسات المؤشرات',
      'Manage initiative change requests and approvals': 'إدارة طلبات تغيير المبادرات والموافقات',
      'Manage initiative change requests for this plan': 'إدارة طلبات تغيير المبادرات لهذه الخطة',
      'Manage initiative closure requests and approvals': 'إدارة طلبات إغلاق المبادرات والموافقات',
      'Manage initiative output updates and approvals': 'إدارة تحديثات وموافقات مخرجات المبادرات',
      'Manage strategic KPI amendment requests': 'إدارة طلبات تعديل المؤشرات الاستراتيجية',
      'Manage strategic challenges and mitigation actions': 'إدارة التحديات الاستراتيجية وإجراءات التخفيف',
      'Manage strategic risks and mitigation actions': 'إدارة المخاطر الاستراتيجية وإجراءات التخفيف',
      'Managers can only access approved strategic plans': 'يمكن للمديرين الوصول فقط إلى الخطط الاستراتيجية المعتمدة',
      'Managers can only add challenges to approved strategic plans': 'يمكن للمديرين إضافة التحديات فقط إلى الخطط الاستراتيجية المعتمدة',
      'Managers can only add initiatives to approved strategic plans': 'يمكن للمديرين إضافة المبادرات فقط إلى الخطط الاستراتيجية المعتمدة',
      'Managers can only add risks to approved strategic plans': 'يمكن للمديرين إضافة المخاطر فقط إلى الخطط الاستراتيجية المعتمدة',
      'Managers can only close initiatives in approved strategic plans': 'يمكن للمديرين إغلاق المبادرات فقط في الخطط الاستراتيجية المعتمدة',
      'Managers can only create change requests for approved strategic plans': 'يمكن للمديرين إنشاء طلبات التغيير فقط للخطط الاستراتيجية المعتمدة',
      'Managers can only update initiatives in approved strategic plans': 'يمكن للمديرين تحديث المبادرات فقط في الخطط الاستراتيجية المعتمدة',
      'Managers cannot close approved initiatives': 'لا يمكن للمديرين إغلاق المبادرات المعتمدة',
      'Managers cannot delete approved strategic plans': 'لا يمكن للمديرين حذف الخطط الاستراتيجية المعتمدة',
      'Managers cannot edit approved strategic plans': 'لا يمكن للمديرين تعديل الخطط الاستراتيجية المعتمدة',
      'Measurement': 'القياس',
      'Measurement Frequency': 'تكرار القياس',
      'Measurement Method': 'طريقة القياس',
      'Measurement Not Found': 'القياس غير موجود',
      'Measurement Period': 'فترة القياس',
      'Measurement Periods': 'فترات القياس',
      'Measurement Results': 'نتائج القياس',
      'Measurement Update': 'تحديث القياس',
      'Measurement frequency is required': 'تكرار القياس مطلوب',
      'Measurement method is required': 'طريقة القياس مطلوبة',
      'Measurement saved': 'تم حفظ القياس',
      'Measurement submissions awaiting your review': 'تقديمات القياس بانتظار مراجعتك',
      'Minor change requests': 'طلبات التغيير البسيطة',
      'Mission is required': 'الرسالة مطلوبة',
      'Mitigate risks': 'تخفيف المخاطر',
      'Mitigation Actions Taken': 'إجراءات التخفيف المتخذة',
      'Monitor initiative progress and performance': 'رصد تقدم وأداء المبادرات',
      'Delayed initiatives and the KPIs affected by each delay': 'المبادرات المتأخرة ومؤشرات الأداء المتأثرة بكل تأخير',
      'Affected KPIs': 'مؤشرات الأداء المتأثرة',
      'No linked KPIs': 'لا توجد مؤشرات أداء مرتبطة',
      'No delayed initiatives': 'لا توجد مبادرات متأخرة',
      'National Strategy': 'الاستراتيجية الوطنية',
      'Negative': 'سلبي',
      'Negative (Lower is Better)': 'سلبي (الأقل أفضل)',
      'Negligible': 'ضئيل',
      'New Challenge': 'تحدٍّ جديد',
      'New Change Request': 'طلب تغيير جديد',
      'New Corrective Plan': 'خطة تصحيحية جديدة',
      'KPI Change Request': 'طلب تغيير مؤشر جديد',
      'New Request': 'طلب جديد',
      'New Risk': 'مخاطرة جديدة',
      'No Challenges Recorded': 'لم يتم تسجيل أي تحديات',
      'No Corrective Plans': 'لا توجد خطط تصحيحية',
      'No Initiatives Yet': 'لا توجد مبادرات بعد',
      'No Initiatives for Closure': 'لا توجد مبادرات للإغلاق',
      'No Initiatives to Update': 'لا توجد مبادرات للتحديث',
      'No KPI Change Requests': 'لا توجد طلبات تغيير للمؤشر',
      'No KPI change requests have been created for this plan.': 'لم يتم إنشاء أي طلبات تغيير للمؤشر لهذه الخطة.',
      'No KPI change requests match your search': 'لا توجد طلبات تغيير للمؤشر تطابق بحثك',
      'No KPIs Yet': 'لا توجد مؤشرات بعد',
      'No KPIs available for the selected filter.': 'لا توجد مؤشرات متاحة للفلتر المحدد.',
      'No KPIs defined': 'لم يتم تحديد أي مؤشرات',
      'No KPIs found': 'لم يتم العثور على أي مؤشرات',
      'No KPIs match your filters': 'لا توجد مؤشرات تطابق فلاترك',
      'No KPIs match your search': 'لا توجد مؤشرات تطابق بحثك',
      'No KPIs match your search criteria. Try a different search term.': 'لا توجد مؤشرات تطابق معايير بحثك. جرب مصطلح بحث مختلف.',
      'No KPIs to Update': 'لا توجد مؤشرات للتحديث',
      'No Objectives Added': 'لم تتم إضافة أي أهداف',
      'No Outputs Added': 'لم تتم إضافة أي مخرجات',
      'No Performance Data': 'لا توجد بيانات أداء',
      'No Pillars Added': 'لم تتم إضافة أي ركائز',
      'No Risks Identified': 'لم يتم تحديد أي مخاطر',
      'No approved initiatives are available for performance analysis yet': 'لا توجد مبادرات معتمدة متاحة لتحليل الأداء بعد',
      'No approved initiatives available for progress updates': 'لا توجد مبادرات معتمدة متاحة لتحديثات التقدم',
      'No challenges found': 'لم يتم العثور على أي تحديات',
      'No challenges match your search': 'لا توجد تحديات تطابق بحثك',
      'No change requests match your search': 'لا توجد طلبات تغيير تطابق بحثك',
      'No child indicators added yet': 'لم تتم إضافة أي مؤشرات فرعية بعد',
      'No corrective plans found': 'لم يتم العثور على أي خطط تصحيحية',
      'No corrective plans match your search': 'لا توجد خطط تصحيحية تطابق بحثك',
      'No initiative change requests have been created for this plan.': 'لم يتم إنشاء أي طلبات تغيير للمبادرة لهذه الخطة.',
      'No initiative closures available for the selected filter.': 'لا توجد طلبات إغلاق للمبادرة متاحة للفلتر المحدد.',
      'No initiatives available for the selected filter.': 'لا توجد مبادرات متاحة للفلتر المحدد.',
      'No initiatives found': 'لم يتم العثور على أي مبادرات',
      'No initiatives match your search': 'لا توجد مبادرات تطابق بحثك',
      'No initiatives match your search criteria.': 'لا توجد مبادرات تطابق معايير بحثك.',
      'No initiatives match your search criteria. Try a different search term.': 'لا توجد مبادرات تطابق معايير بحثك. جرب مصطلح بحث مختلف.',
      'No initiatives yet': 'لا توجد مبادرات بعد',
      'No items yet': 'لا توجد عناصر بعد',
      'No measurement periods available': 'لا توجد فترات قياس متاحة',
      'No mitigation actions recorded': 'لم يتم تسجيل أي إجراءات تخفيف',
      'No objectives defined': 'لم يتم تحديد أي أهداف',
      'No outputs defined for this initiative': 'لم يتم تحديد أي مخرجات لهذه المبادرة',
      'No results found': 'لم يتم العثور على نتائج',
      'No risks match your search': 'لا توجد مخاطر تطابق بحثك',
      'No strategic KPIs yet': 'لا توجد مؤشرات أداء استراتيجية بعد',
      'No supporting documents': 'لا توجد وثائق داعمة',
      'Not Allowed': 'غير مسموح',
      'Not Entered': 'لم يُدخل',
      'Not linked': 'غير مرتبط',
      'Numerical & Relative Scale': 'المقياس الرقمي والنسبي',
      'Numerical Scale': 'المقياس الرقمي',
      'Numerical Scale: 5 points. Relative Scale = Numerical Scale ÷ Final Target': 'المقياس الرقمي: 5 نقاط. المقياس النسبي = المقياس الرقمي ÷ الهدف النهائي',
      'Numerical scale is required': 'المقياس الرقمي مطلوب',
      'Objective': 'الهدف',
      'Objective Name': 'اسم الهدف',
      'Objective is required': 'الهدف مطلوب',
      'Objective name is required': 'اسم الهدف مطلوب',
      'Only Managers can create change requests': 'يمكن للمديرين فقط إنشاء طلبات التغيير',
      'Only draft or rejected KPIs can be edited': 'يمكن تعديل المؤشرات المسودة أو المرفوضة فقط',
      'Only draft or rejected challenges can be edited': 'يمكن تعديل التحديات المسودة أو المرفوضة فقط',
      'Only draft or rejected corrective plans can be edited': 'يمكن تعديل الخطط التصحيحية المسودة أو المرفوضة فقط',
      'Only draft or rejected initiatives can be edited': 'يمكن تعديل المبادرات المسودة أو المرفوضة فقط',
      'Only draft or rejected risks can be edited': 'يمكن تعديل المخاطر المسودة أو المرفوضة فقط',
      'Only managers can create challenges': 'يمكن للمديرين فقط إنشاء التحديات',
      'Only managers can create risks': 'يمكن للمديرين فقط إنشاء المخاطر',
      'Operational': 'تشغيلي',
      'Output': 'المخرج',
      'Output Number': 'رقم المخرج',
      'Output Source': 'مصدر المخرج',
      'Output Updates': 'تحديثات المخرجات',
      'Output Weight (%)': 'وزن المخرج (%)',
      'Output Weight Sum': 'مجموع وزن المخرجات',
      'Output name is required': 'اسم المخرج مطلوب',
      'Output source is required': 'مصدر المخرج مطلوب',
      'Output weight is required': 'وزن المخرج مطلوب',
      'Outputs': 'المخرجات',
      'Overall Achieved Result': 'النتيجة المحققة الإجمالية',
      'Overall Progress Overview': 'نظرة عامة على التقدم الإجمالي',
      'Owning Department': 'الإدارة المالكة',
      'Owning Sector': 'القطاع المالك',
      'Owning department is required': 'الإدارة المالكة مطلوبة',
      'Owning sector is required': 'القطاع المالك مطلوب',
      'PDF, WORD, EXCEL, PPT': 'PDF، WORD، EXCEL، PPT',
      'PDF, Word, Excel, PowerPoint · Max 10MB per file': 'PDF، Word، Excel، PowerPoint · الحد الأقصى 10 ميجابايت للملف',
      'PESTEL': 'PESTEL',
      'Page': 'الصفحة',
      'Percentage (%)': 'نسبة مئوية (%)',
      'Performance Analysis': 'تحليل الأداء',
      'On Track': 'على المسار',
      'Delayed': 'متأخر',
      'Overall Completion': 'الإنجاز الكلي',
      'Completion %': 'نسبة الإنجاز',
      'Due Date': 'تاريخ الاستحقاق',
      'Period': 'الفترة',
      'Period Targets': 'أهداف الفترة',
      'Periods Filled': 'الفترات المكتملة',
      'Pillar Name': 'اسم الركيزة',
      'Pillar name is required': 'اسم الركيزة مطلوب',
      'Plan Approved': 'تمت الموافقة على الخطة',
      'Plan Code': 'رمز الخطة',
      'Plan Information': 'معلومات الخطة',
      'Plan Rejected': 'تم رفض الخطة',
      'Plan approved': 'تمت الموافقة على الخطة',
      'Plan auto-approved': 'تمت الموافقة التلقائية على الخطة',
      'Plan deleted': 'تم حذف الخطة',
      'Plan rejected': 'تم رفض الخطة',
      'Plan saved but submission failed': 'تم حفظ الخطة لكن فشل التقديم',
      'Plan submitted for approval': 'تم تقديم الخطة للموافقة',
      'Please enter a rejection reason': 'يرجى إدخال سبب الرفض',
      'Please fill all required fields': 'يرجى ملء جميع الحقول المطلوبة',
      'Please fill in the required fields': 'يرجى ملء الحقول المطلوبة',
      'Please provide a reason for rejecting the measurement for': 'يرجى تقديم سبب لرفض قياس',
      'Please provide a reason for rejection.': 'يرجى تقديم سبب الرفض.',
      'Please select a start date first': 'يرجى تحديد تاريخ البداية أولاً',
      'Polarity': 'الاتجاه',
      'Polarity is required': 'الاتجاه مطلوب',
      'Positive': 'إيجابي',
      'Positive (Higher is Better)': 'إيجابي (الأعلى أفضل)',
      'Postponed': 'مؤجل',
      'Previously Submitted Request': 'طلب مقدم سابقاً',
      'Previously uploaded': 'مرفوع مسبقاً',
      'Probable': 'محتمل جداً',
      'Provide a reason for rejecting the closure...': 'قدّم سبباً لرفض الإغلاق...',
      'Provide a reason for rejection...': 'قدّم سبباً للرفض...',
      'Provide a reason...': 'قدّم سبباً...',
      'Provide recommendations for the future...': 'قدّم توصيات للمستقبل...',
      'Rare': 'نادر',
      'Ratio': 'نسبة',
      'Ready to Close': 'جاهز للإغلاق',
      'Reason': 'السبب',
      'Reason for Change': 'سبب التغيير',
      'Recommendations for Applying Lessons Learned in the Future': 'توصيات لتطبيق الدروس المستفادة في المستقبل',
      'Reject All': 'رفض الكل',
      'Reject Challenge': 'رفض التحدي',
      'Reject Corrective Plan': 'رفض الخطة التصحيحية',
      'Reject Initiative': 'رفض المبادرة',
      'Reject Initiative Update': 'رفض تحديث المبادرة',
      'Reject KPI': 'رفض المؤشر',
      'Reject KPI Change Request': 'رفض طلب تغيير المؤشر',
      'Reject KPI Measurement': 'رفض قياس المؤشر',
      'Reject Plan': 'رفض الخطة',
      'Reject Risk': 'رفض المخاطرة',
      'Reject Strategic Plan': 'رفض الخطة الاستراتيجية',
      'Reject challenge': 'رفض التحدي',
      'Reject change request': 'رفض طلب التغيير',
      'Reject closure for': 'رفض إغلاق',
      'Reject corrective plan': 'رفض الخطة التصحيحية',
      'Reject risk': 'رفض المخاطرة',
      'Reject this change request?': 'هل تريد رفض طلب التغيير هذا؟',
      'Rejected change requests': 'طلبات التغيير المرفوضة',
      'Rejected closures that need revision': 'طلبات الإغلاق المرفوضة التي تحتاج إلى مراجعة',
      'Rejected initiatives that need revision': 'المبادرات المرفوضة التي تحتاج إلى مراجعة',
      'Rejection reason': 'سبب الرفض',
      'Average approved KPI score': 'متوسط درجة مؤشرات الأداء المعتمدة',
      'Plan initiatives': 'مبادرات الخطة',
      'KPIs requiring attention': 'مؤشرات الأداء التي تتطلب الاهتمام',
      'Strategy Structure': 'هيكل الاستراتيجية',
      'Direction': 'التوجه الاستراتيجي',
      'View Feedback': 'عرض الملاحظات',
      'Review Plan': 'مراجعة الخطة',
      'Related Projects': 'المشاريع المرتبطة',
      'Relative': 'نسبي',
      'Relative Scale = Numerical Scale Value ÷ Target': 'المقياس النسبي = قيمة المقياس الرقمي ÷ الهدف',
      'Relative Scale = Value ÷ Final Target. These thresholds determine Achieved Weight (1–5).': 'المقياس النسبي = القيمة ÷ الهدف النهائي. تحدد هذه العتبات الوزن المحقق (1-5).',
      'Relative Scale Results': 'نتائج المقياس النسبي',
      'Request Code': 'رمز الطلب',
      'Request Status': 'حالة الطلب',
      'Requester': 'مقدم الطلب',
      'Required': 'مطلوب',
      'Response': 'الاستجابة',
      'Response Type': 'نوع الاستجابة',
      'Responsible Party': 'الجهة المسؤولة',
      'Resubmit': 'إعادة التقديم',
      'Resubmit Closure': 'إعادة تقديم الإغلاق',
      'Result': 'النتيجة',
      'Results': 'النتائج',
      'Retroactive Amendment': 'تعديل بأثر رجعي',
      'Review Action': 'إجراء المراجعة',
      'Review Change Request': 'مراجعة طلب التغيير',
      'Review Closure': 'مراجعة الإغلاق',
      'Review KPI Measurements': 'مراجعة قياسات المؤشر',
      'Review and approve initiative change request': 'مراجعة طلب تغيير المبادرة والموافقة عليه',
      'Review and approve or reject this KPI': 'مراجعة هذا المؤشر والموافقة عليه أو رفضه',
      'Review and approve strategic initiative submission': 'مراجعة تقديم المبادرة الاستراتيجية والموافقة عليه',
      'Review and approve strategic plan submission': 'مراجعة تقديم الخطة الاستراتيجية والموافقة عليه',
      'Revise & Resubmit': 'مراجعة وإعادة التقديم',
      'Risk Approved': 'تمت الموافقة على المخاطرة',
      'Risk Code': 'رمز المخاطرة',
      'Risk Information': 'معلومات المخاطرة',
      'Risk Register': 'سجل المخاطر',
      'Risk Rejected': 'تم رفض المخاطرة',
      'Risk approved': 'تمت الموافقة على المخاطرة',
      'Risk auto-approved': 'تمت الموافقة التلقائية على المخاطرة',
      'Risk deleted': 'تم حذف المخاطرة',
      'Risk rejected': 'تم رفض المخاطرة',
      'Risk saved but submission failed': 'تم حفظ المخاطرة لكن فشل التقديم',
      'Risk submitted for approval': 'تم تقديم المخاطرة للموافقة',
      'SWOT': 'SWOT',
      'Save All Drafts': 'حفظ جميع المسودات',
      'Scale & Thresholds': 'المقياس والعتبات',
      'Score': 'الدرجة',
      'Search KPI change requests...': 'البحث في طلبات تغيير المؤشر...',
      'Search KPIs by name...': 'البحث في المؤشرات بالاسم...',
      'Search KPIs...': 'البحث في المؤشرات...',
      'Search and select an indicator...': 'البحث واختيار مؤشر...',
      'Search and select indicator...': 'البحث واختيار مؤشر...',
      'Search by KPI name or code...': 'البحث باسم المؤشر أو رمزه...',
      'Search by KPI or request code...': 'البحث برمز المؤشر أو الطلب...',
      'Search by code or name...': 'البحث بالرمز أو الاسم...',
      'Search by code, requester, or description...': 'البحث بالرمز أو مقدم الطلب أو الوصف...',
      'Search by initiative name or code...': 'البحث باسم المبادرة أو رمزها...',
      'Search challenges...': 'البحث في التحديات...',
      'Search change requests...': 'البحث في طلبات التغيير...',
      'Search corrective plans...': 'البحث في الخطط التصحيحية...',
      'Search initiatives by name...': 'البحث في المبادرات بالاسم...',
      'Search initiatives...': 'البحث في المبادرات...',
      'Search risks...': 'البحث في المخاطر...',
      'Select Indicator (KPI)': 'اختر المؤشر (KPI)',
      'Select Initiative': 'اختر المبادرة',
      'Select KPI': 'اختر المؤشر',
      'Select a KPI...': 'اختر مؤشراً...',
      'Select an approved initiative...': 'اختر مبادرة معتمدة...',
      'Select classification...': 'اختر التصنيف...',
      'Select dependency initiative': 'اختر مبادرة الاعتمادية',
      'Select funding source': 'اختر مصدر التمويل',
      'Select impact...': 'اختر الأثر...',
      'Select numerical scale': 'اختر المقياس الرقمي',
      'Select objective': 'اختر الهدف',
      'Select owning department': 'اختر الإدارة المالكة',
      'Select polarity': 'اختر الاتجاه',
      'Select priority...': 'اختر الأولوية...',
      'Select probability...': 'اختر الاحتمالية...',
      'Select related projects': 'اختر المشاريع المرتبطة',
      'Select request date...': 'اختر تاريخ الطلب...',
      'Select request status...': 'اختر حالة الطلب...',
      'Select response type...': 'اختر نوع الاستجابة...',
      'Select start date...': 'اختر تاريخ البداية...',
      'Select status...': 'اختر الحالة...',
      'Select strategic pillar': 'اختر الركيزة الاستراتيجية',
      'Select supporting department': 'اختر الإدارة الداعمة',
      'Select target year': 'اختر السنة المستهدفة',
      'Select unit': 'اختر الوحدة',
      'Select year': 'اختر السنة',
      'Select...': 'اختر...',
      'Selected Initiative': 'المبادرة المختارة',
      'Semi-Annual': 'نصف سنوي',
      'Set baseline and target values per measurement period': 'حدد قيم خط الأساس والأهداف لكل فترة قياس',
      'Set baseline values and per-year targets': 'حدد قيم خط الأساس والأهداف السنوية',
      'Set the KPI validity period and measurement years': 'حدد فترة صلاحية المؤشر وسنوات القياس',
      'Share risks': 'مشاركة المخاطر',
      'Simple': 'بسيط',
      'Source': 'المصدر',
      'Specify the initiative measurement period': 'حدد فترة قياس المبادرة',
      'Start adding KPIs to track performance of this strategic plan': 'ابدأ بإضافة المؤشرات لتتبع أداء هذه الخطة الاستراتيجية',
      'Start by creating a new challenge entry': 'ابدأ بإنشاء إدخال تحدٍّ جديد',
      'Start by creating a new corrective plan for a delayed indicator': 'ابدأ بإنشاء خطة تصحيحية جديدة لمؤشر متأخر',
      'Start by creating a new risk entry': 'ابدأ بإنشاء إدخال مخاطرة جديد',
      'Strategic Analysis': 'التحليل الاستراتيجي',
      'Strategic KPI Details': 'تفاصيل المؤشر الاستراتيجي',
      'Strategic KPI Review': 'مراجعة المؤشر الاستراتيجي',
      'Strategic KPIs': 'المؤشرات الاستراتيجية',
      'Strategic Objective': 'الهدف الاستراتيجي',
      'Strategic Pillar': 'الركيزة الاستراتيجية',
      'Strategic Pillars': 'الركائز الاستراتيجية',
      'Strategic Plan Review': 'مراجعة الخطة الاستراتيجية',
      'Strategic initiatives linked to this plan': 'المبادرات الاستراتيجية المرتبطة بهذه الخطة',
      'Strategic objective is required': 'الهدف الاستراتيجي مطلوب',
      'Strategic pillar is required': 'الركيزة الاستراتيجية مطلوبة',
      'Strategic plan approved successfully': 'تمت الموافقة على الخطة الاستراتيجية بنجاح',
      'Strategic plan has been approved successfully': 'تمت الموافقة على الخطة الاستراتيجية بنجاح',
      'Strategic plan has been rejected': 'تم رفض الخطة الاستراتيجية',
      'Strategic plan rejected': 'تم رفض الخطة الاستراتيجية',
      'Strategic plan resubmitted for approval': 'تمت إعادة تقديم الخطة الاستراتيجية للموافقة',
      'Strategic plan saved as draft': 'تم حفظ الخطة الاستراتيجية كمسودة',
      'Strategic plan submitted for approval': 'تم تقديم الخطة الاستراتيجية للموافقة',
      'Strategic risks associated with this plan': 'المخاطر الاستراتيجية المرتبطة بهذه الخطة',
      'Strategy Navigation': 'التنقل في الاستراتيجية',
      'The department will be formally assigned from Department Assignment.': 'سيتم تعيين الإدارة رسميًا من خلال صفحة تعيين الإدارات.',
      'Pending assignment': 'بانتظار التعيين',
      'Manage the plan, performance and approvals': 'إدارة الخطة والأداء والاعتمادات',
      'Structure': 'الهيكل',
      'Submit & Approve': 'تقديم وموافقة',
      'Submit Updates': 'تقديم التحديثات',
      'Submit initiative closure': 'تقديم إغلاق المبادرة',
      'Submitted By': 'مقدم من قِبل',
      'Submitted On': 'تاريخ التقديم',
      'Successfully closed initiatives': 'تم إغلاق المبادرات بنجاح',
      'Sum of targets': 'مجموع الأهداف',
      'Supporting Department': 'الإدارة الداعمة',
      'Supporting Documents': 'الوثائق الداعمة',
      'Supporting department is required': 'الإدارة الداعمة مطلوبة',
      'Switch to Approve': 'التبديل إلى الموافقة',
      'Switch to Reject': 'التبديل إلى الرفض',
      'Tactical': 'تكتيكي',
      'Target Year': 'السنة المستهدفة',
      'Target Years': 'السنوات المستهدفة',
      'Target Years for Measurement': 'سنوات القياس المستهدفة',
      'Target year is required': 'السنة المستهدفة مطلوبة',
      'Targets': 'الأهداف',
      'Targets & Baseline': 'الأهداف وخط الأساس',
      'The Achieved Result reflects the final indicator result for the relevant period on the indicator scale': 'تعكس النتيجة المحققة النتيجة النهائية للمؤشر للفترة ذات الصلة على مقياس المؤشر',
      'The measurement you are looking for does not exist.': 'القياس الذي تبحث عنه غير موجود.',
      'There are no initiatives pending closure at this time.': 'لا توجد مبادرات معلقة للإغلاق في الوقت الحالي.',
      'This KPI is in draft. Submit when ready.': 'هذا المؤشر في حالة مسودة. قدّمه عندما تكون مستعداً.',
      'This KPI was rejected. Edit and resubmit.': 'تم رفض هذا المؤشر. عدّله وأعد تقديمه.',
      'This change request cannot be edited': 'لا يمكن تعديل طلب التغيير هذا',
      'This initiative has been approved.': 'تمت الموافقة على هذه المبادرة.',
      'This initiative has been rejected.': 'تم رفض هذه المبادرة.',
      'This initiative has not been submitted for approval yet.': 'لم يتم تقديم هذه المبادرة للموافقة بعد.',
      'This initiative is a draft and has not been submitted for approval yet.': 'هذه المبادرة مسودة ولم يتم تقديمها للموافقة بعد.',
      'This initiative is awaiting approval from an authorized reviewer.': 'هذه المبادرة بانتظار الموافقة من مراجع مخوّل.',
      'This plan has been approved.': 'تمت الموافقة على هذه الخطة.',
      'This plan has been rejected.': 'تم رفض هذه الخطة.',
      'This plan has not been submitted for approval yet.': 'لم يتم تقديم هذه الخطة للموافقة بعد.',
      'This plan is a draft and has not been submitted for approval yet.': 'هذه الخطة مسودة ولم يتم تقديمها للموافقة بعد.',
      'This plan is awaiting approval from an authorized reviewer.': 'هذه الخطة بانتظار الموافقة من مراجع مخوّل.',
      'This will close the initiative permanently.': 'سيؤدي هذا إلى إغلاق المبادرة نهائياً.',
      'This will close the initiative.': 'سيؤدي هذا إلى إغلاق المبادرة.',
      'To Update': 'للتحديث',
      'Total Achieved': 'الإجمالي المحقق',
      'Total Initiative Weight (%)': 'الوزن الإجمالي للمبادرة (%)',
      'Total Initiatives': 'إجمالي المبادرات',

      // ── Initiatives Roadmap page ──────────────────────────────────────────
      'Initiatives Roadmap': 'خارطة طريق المبادرات',
      'Master timeline of strategic initiatives across all pillars': 'الجدول الزمني الرئيسي للمبادرات الاستراتيجية عبر جميع الركائز',
      'Master Timeline of Initiatives': 'الجدول الزمني الرئيسي للمبادرات',
      'Filter by Pillar:': 'تصفية حسب الركيزة:',
      'Show All': 'عرض الكل',
      'TOTAL INITIATIVES': 'إجمالي المبادرات',
      'COMPLETED MILESTONES': 'المعالم المكتملة',
      'ACTIVE INITIATIVES': 'المبادرات النشطة',
      'AVERAGE COMPLETION': 'متوسط الإنجاز',
      'Across all roadmap initiatives': 'عبر جميع مبادرات خارطة الطريق',
      'Timeline range': 'نطاق الجدول الزمني',
      'initiatives across 5 strategic pillars': 'مبادرة عبر ٥ ركائز استراتيجية',
      'by end of': 'بنهاية',
      'current year': 'السنة الحالية',
      'running during': 'جارية خلال',
      'active in': 'نشطة في',
      'Completion milestone': 'معلم الإنجاز',
      'Milestones': 'المعالم',
      'No initiatives found': 'لا توجد مبادرات',
      'No initiatives match the selected pillar filter.': 'لا توجد مبادرات تطابق فلتر الركيزة المحددة.',
      'No initiative data is available yet. Data will appear once loaded from the API.': 'لا تتوفر بيانات المبادرات بعد. ستظهر البيانات فور تحميلها من الواجهة البرمجية.',
      'Failed to load roadmap data.': 'فشل تحميل بيانات خارطة الطريق.',
      'INITIATIVE': 'المبادرة',
      'Start': 'البداية',
      'End': 'النهاية',

      // Pillar labels
      'Excellence in IP Operations':                   'التميز في عمليات الملكية الفكرية',
      'Improve IP Respect':                            'تعزيز احترام الملكية الفكرية',
      'Maximize IP Value':                             'تعظيم قيمة الملكية الفكرية',
      'Strengthen Global Influence':                   'تعزيز التأثير العالمي',
      'Lead the Ecosystem & Institutional Excellence': 'قيادة المنظومة والتميز المؤسسي',

      // ── Strategic House page (Arabic additions) ───────────────────────────
      'Strategic House': 'البيت الاستراتيجي',
      'Visual representation of the organizational strategic framework': 'تمثيل مرئي للإطار الاستراتيجي للمؤسسة',
      'VISION':       'الرؤية',
      'MISSION':      'الرسالة',
      'PILLARS':      'الركائز',
      'OBJECTIVES &': 'الأهداف &',
      'Loading…': 'جارٍ التحميل…',
      'objectives': 'أهداف',
      'initiatives': 'مبادرات',
      'pillars': 'ركائز',
      'Pillar': 'الركيزة',
      'Plan:': 'الخطة:',
      'Select plan': 'اختر الخطة',
      'Pillar:': 'الركيزة:',
      'All': 'الكل',
      'Vision not set for this plan': 'لم يتم تحديد الرؤية لهذه الخطة',
      'Mission not set for this plan': 'لم يتم تحديد الرسالة لهذه الخطة',
      'No pillars found for this plan.': 'لا توجد ركائز لهذه الخطة.',
      'No objectives': 'لا توجد أهداف',
      'Strategic Pillars': 'الركائز الاستراتيجية',
      'Initiatives': 'المبادرات',
      'Failed to load strategic plans.': 'فشل تحميل الخطط الاستراتيجية.',
      'Failed to load strategic house data.': 'فشل تحميل بيانات البيت الاستراتيجي.',
      'Approved': 'معتمد',
      'Pending': 'قيد الانتظار',
      'Draft': 'مسودة',
      'Rejected': 'مرفوض',

      // ── SMO Dashboard ──────────────────────────────────────────────────────
      'SMO Dashboard':                   'لوحة متابعة الأداء الاستراتيجي',
      'Strategic performance overview':  'نظرة عامة على الأداء الاستراتيجي',
      'Strategic performance overview across all pillars and KPIs': 'نظرة عامة على الأداء الاستراتيجي عبر جميع الركائز والمؤشرات',
      'Select strategic plan':           'اختر الخطة الاستراتيجية',
      'Filter:':                         'تصفية:',
      'Filter by status:':               'تصفية حسب الحالة:',
      'pillars':                         'ركائز',
      'objectives':                      'أهداف',
      'KPIs':                            'مؤشرات',
      'initiatives':                     'مبادرات',
      'deliverables':                    'مخرجات',
      'projects':                        'مشاريع',
      'approved':                        'معتمد',
      'strategic objectives':            'أهداف استراتيجية',
      'Select a Pillar to review its Strategic Objectives': 'اختر ركيزة لمراجعة أهدافها الاستراتيجية',
      'Click any card to enter':         'انقر على أي بطاقة للدخول',
      'Strategic Objectives of the Selected Pillar': 'الأهداف الاستراتيجية للركيزة المختارة',
      'ACHIEVED OBJECTIVES':             'الأهداف المحققة',
      'Achieved Objectives':             'الأهداف المحققة',
      'APPROVED KPIS':                   'المؤشرات المعتمدة',
      'PENDING KPIS':                    'المؤشرات المعلقة',
      'DRAFT KPIS':                      'مؤشرات المسودة',
      'REJECTED KPIS':                   'المؤشرات المرفوضة',
      'status from database':            'الحالة من قاعدة البيانات',
      'exceeded target':                 'تجاوز الهدف',
      'ON TRACK':                        'على المسار',
      'On Track':                        'على المسار',
      'Minor Delay':                     'تأخر بسيط',
      'Critical Delay':                  'تأخر عالي',
      'KPIs on schedule':                'مؤشرات في الموعد',
      'per plan':                        'وفق الخطة',
      'UNDER WATCH':                     'تحت المراقبة',
      'Under Watch':                     'تحت المراقبة',
      'minor deviation':                 'انحراف طفيف',
      'BELOW TARGET':                    'دون الهدف',
      'Below Target':                    'دون الهدف',
      'needs intervention':              'يحتاج تدخلاً',
      'View at level:':                  'عرض على مستوى:',
      'All Pillars':                     'جميع الركائز',
      'Select a Pillar':                 'اختر ركيزة',
      'Select a perspective and objective': 'اختر منظورًا وهدفًا',
      'Vision':                          'الرؤية',
      'Mission':                         'الرسالة',
      'Failed to load dashboard':        'فشل تحميل لوحة المعلومات',
      'Failed to load dashboard.':       'فشل تحميل لوحة المعلومات.',
      'Failed to load dashboard data.':  'فشل تحميل بيانات لوحة المعلومات.',
      'Failed to load pillar data.':     'فشل تحميل بيانات الركيزة.',
      'Failed to load plans.':           'فشل تحميل الخطط.',
      'Failed to load pillar.':          'فشل تحميل الركيزة.',
      'Retry':                           'إعادة المحاولة',
      'Achieved':                        'محقق',
      'Watch':                           'مراقبة',
      'Below':                           'دون الهدف',
      'No Data':                         'لا بيانات',
      'All':                             'الكل',
      'AVG':                             'المتوسط',
      'avg achievement':                 'متوسط الإنجاز',
      'KPI CODE':                        'رمز المؤشر',
      'KPI NAME':                        'اسم المؤشر',
      'TARGET':                          'الهدف',
      'ACTUAL':                          'الفعلي',
      'ACHIEVEMENT %':                   'نسبة الإنجاز',
      'STATUS':                          'الحالة',
      'APPROVAL':                        'الاعتماد',
      'UNIT':                            'الوحدة',
      'KPI':                             'المؤشر',
      'Strategic Objective':             'الهدف الاستراتيجي',
      'Initiative':                      'المبادرة',
      'Deliverable':                     'المخرج',
      'Project':                         'المشروع',
      'Target':                          'الهدف',
      'Actual':                          'الفعلي',
      'Unit':                            'الوحدة',
      'Weight':                          'الوزن',
      'Dates':                           'التواريخ',
      'Due':                             'مستحق في',
      'Achievement':                     'الإنجاز',
      'avg':                             'متوسط',
      'of':                              'من',
      'total':                           'الإجمالي',
      'No linked KPI, initiative, deliverable, or project data': 'لا توجد مؤشرات أو مبادرات أو مخرجات أو مشاريع مرتبطة',
      'Untitled deliverable':            'مخرج بدون عنوان',
      'No KPIs defined for this objective': 'لا توجد مؤشرات محددة لهذا الهدف',
      'No objectives match this filter': 'لا توجد أهداف تطابق هذا الفلتر',
      'No dashboard data available':     'لا تتوفر بيانات لوحة المعلومات',
      'No pillars found for this plan':  'لا توجد ركائز لهذه الخطة',
      'Select a strategic plan to view performance.': 'اختر خطة استراتيجية لعرض بيانات الأداء.',
      'Select a strategic plan to view its performance dashboard.': 'اختر خطة استراتيجية لعرض لوحة أدائها.',
      'Total:':                          'الإجمالي:',
      // KPI approval breakdown
      'KPI Approval':                    'اعتماد المؤشرات',
      'Approved':                        'معتمد',
      'Pending':                         'معلق',
      'Draft':                           'مسودة',
      'Rejected':                        'مرفوض',
      'Achievement % calculated for approved KPIs only': 'نسبة الإنجاز تُحسب للمؤشرات المعتمدة فقط',
      'Not included in achievement calculations — KPI is not yet approved': 'غير مدرج في حسابات الإنجاز — المؤشر لم يُعتمد بعد',
      'No actual value recorded yet':    'لم يُسجَّل قيمة فعلية بعد',
      'Show All':                        'عرض الكل',

      'Total Output Weight': 'الوزن الإجمالي للمخرجات',
      'Total Outputs': 'إجمالي المخرجات',
      'Total Weight': 'الوزن الإجمالي',
      'Total initiative weight is required': 'الوزن الإجمالي للمبادرة مطلوب',
      'Total of targets': 'مجموع الأهداف',
      'Try a different search term': 'جرب مصطلح بحث مختلف',
      'Try adjusting your search or filter': 'جرب تعديل بحثك أو الفلتر',
      'Type to search KPIs...': 'اكتب للبحث في المؤشرات...',
      'Unit of Measurement': 'وحدة القياس',
      'Unit of measurement is required': 'وحدة القياس مطلوبة',
      'Update Initiative': 'تحديث المبادرة',
      'Update Initiatives': 'تحديث المبادرات',
      'Update KPI Measurements': 'تحديث قياسات المؤشر',
      'Update KPIs': 'تحديث المؤشرات',
      'Update Measurement': 'تحديث القياس',
      'Update Objective': 'تحديث الهدف',
      'Update Output': 'تحديث المخرج',
      'Update Pillar': 'تحديث الركيزة',
      'Update Strategic KPI': 'تحديث المؤشر الاستراتيجي',
      'Update initiative details and outputs': 'تحديث تفاصيل المبادرة ومخرجاتها',
      'Update measurement data for KPIs in this plan': 'تحديث بيانات قياسات المؤشرات في هذه الخطة',
      'Update outputs completion': 'تحديث اكتمال المخرجات',
      'Update progress and outputs for your initiatives': 'تحديث التقدم والمخرجات لمبادراتك',
      'Update the completion percentage and supporting documents for each output.': 'قم بتحديث نسبة الاكتمال والوثائق الداعمة لكل مخرج.',
      'Updated': 'تم التحديث',
      'Updates saved and approved': 'تم حفظ التحديثات والموافقة عليها',
      'Updates saved as draft': 'تم حفظ التحديثات كمسودة',
      'Updates submitted for approval': 'تم تقديم التحديثات للموافقة',
      'Upload File': 'رفع ملف',
      'Upload supporting documents (PDF, WORD, EXCEL, PPT)': 'رفع الوثائق الداعمة (PDF، WORD، EXCEL، PPT)',
      'Validation': 'التحقق',
      'Value': 'القيمة',
      'Very High': 'مرتفع جداً',
      'View All Closures': 'عرض جميع طلبات الإغلاق',
      'View All Updates': 'عرض جميع التحديثات',
      'View Initiative': 'عرض المبادرة',
      'View KPI change request details': 'عرض تفاصيل طلب تغيير المؤشر',
      'View Plan': 'عرض الخطة',
      'View initiative change request details': 'عرض تفاصيل طلب تغيير المبادرة',
      'Vision & Mission': 'الرؤية والرسالة',
      'Vision is required': 'الرؤية مطلوبة',
      'Waiting for SMO Approval': 'بانتظار موافقة مكتب إدارة الاستراتيجية',
      'Weight %': 'الوزن %',
      'Weight (1-5)': 'الوزن (1-5)',
      'Weight is required': 'الوزن مطلوب',
      'Weighted Progress': 'التقدم الموزون',
      'With Measurement': 'مع القياس',
      'Working Days': 'أيام العمل',
      'Working Week': 'أسبوع العمل',
      'You are about to approve the measurement for': 'أنت على وشك الموافقة على قياس',
      'You are about to reject': 'أنت على وشك رفض',
      'You do not have permission to review this initiative.': 'ليس لديك صلاحية مراجعة هذه المبادرة.',
      'You do not have permission to review this plan.': 'ليس لديك صلاحية مراجعة هذه الخطة.',
      'approved': 'معتمد',
      'at 100%': 'بنسبة 100%',
      'browse': 'تصفح',
      'by': 'بواسطة',
      'by Period': 'حسب الفترة',
      'e.g. 25.00': 'مثال: 25.00',
      'e.g. Monthly survey, ERP report...': 'مثال: مسح شهري، تقرير ERP...',
      'editable': 'قابل للتعديل',
      'exceeds 10MB limit': 'يتجاوز حد 10 ميجابايت',
      'failed': 'فشل',
      'has been approved': 'تمت الموافقة عليه',
      'measurement approved': 'تمت الموافقة على القياس',
      'measurement rejected': 'تم رفض القياس',
      'measurement(s) saved as draft': 'تم حفظ القياس/القياسات كمسودة',
      'measurement(s) submitted for approval': 'تم تقديم القياس/القياسات للموافقة',
      'measurements approved': 'تمت الموافقة على القياسات',
      'measurements rejected': 'تم رفض القياسات',
      'on scale of': 'على مقياس',
      'optional': 'اختياري',
      'output(s) have no completion percentage': 'المخرج/المخرجات لا تحمل نسبة اكتمال',
      'outputs': 'المخرجات',
      'outputs have completion values': 'المخرجات لديها قيم اكتمال',
      'pending': 'معلق',
      'period(s) have no achieved value': 'الفترة/الفترات لا تحتوي على قيمة محققة',
      'period(s) missing required attachments': 'الفترة/الفترات تفتقر إلى المرفقات المطلوبة',
      'periods': 'الفترات',
      'rejected': 'مرفوض',
      'required if rejecting': 'مطلوب عند الرفض',
      'should equal 100%': 'يجب أن يساوي 100%',
      'submit': 'تقديم',
      'submitted for approval': 'مقدم للموافقة',
      'succeeded': 'نجح',
      'target': 'الهدف',
      'visible': 'مرئي',
      'Σ (Completion × Weight)': 'Σ (الاكتمال × الوزن)',
      'draft': 'مسودة',

      // ── PMO Dashboard ──────────────────────────────────────────────────────
      'PMO Dashboard': 'لوحة تحكم مكتب إدارة المشاريع',
      'Project Tracker': 'متتبع المشاريع',
      'Monitor individual project performance': 'مراقبة أداء المشاريع الفردية',
      'Projects overview and performance analytics': 'نظرة عامة على المشاريع وتحليلات الأداء',
      'Select a project to view its tracker': 'اختر مشروعاً لعرض متتبعه',
      'Select project': 'اختر المشروع',
      'Select program': 'اختر البرنامج',
      'Export': 'تصدير',
      'Export PDF': 'تصدير PDF',
      'Export Excel': 'تصدير Excel',

      // Stat cards
      'DELIVERABLES': 'المُنجَزات',
      'SCHEDULE (SPI)': 'الجدول الزمني (SPI)',
      'BUDGET SPENT': 'الميزانية المُنفَقة',
      'OVERALL COMPLETION': 'الإنجاز الإجمالي',
      'On schedule': 'في الموعد المحدد',
      'days late': 'أيام متأخرة',
      'days overdue': 'أيام تجاوز الموعد',
      'Overall Completion': 'الإنجاز الإجمالي',
      'SAR': 'ريال سعودي',
      'spent': 'منفق',
      'Spent': 'المنفق',
      'remaining': 'المتبقي',
      'Remaining': 'المتبقي',
      'of projects': 'من المشاريع',
      'All projects': 'جميع المشاريع',
      'Total Planned': 'إجمالي المخطط',

      // Timeline section
      'Timeline Compliance Rate': 'معدل الالتزام بالجدول الزمني',
      'Average compliance': 'متوسط الالتزام',
      'At Risk': 'في خطر',
      'AT RISK': 'في خطر',
      'Deviating': 'منحرف',
      'Planning': 'تخطيط',
      'Completed': 'مكتمل',
      'COMPLETED': 'مكتمل',
      'In mitigation': 'في المعالجة',
      'Under Review': 'قيد المراجعة',
      'in planning': 'في التخطيط',
      'overdue': 'متأخر',
      'OVERDUE': 'متأخر',
      'NEEDS ATTENTION': 'يحتاج اهتماماً',

      // Project KPIs
      'Risk Level Overview': 'نظرة عامة على مستوى المخاطر',
      'TOTAL PROJECTS': 'إجمالي المشاريع',
      'Projects by Status': 'المشاريع حسب الحالة',
      'Active projects': 'المشاريع النشطة',
      'Avg progress': 'متوسط التقدم',
      'Schedule compliance': 'الالتزام بالجدول',
      'Critical overdue': 'متأخر حرج',
      'Milestones met': 'المعالم المنجزة',
      'Key Milestones': 'المعالم الرئيسية',

      // Charts
      'Progress Curve (S-Curve)': 'منحنى التقدم (S-Curve)',
      'Actual vs planned progress': 'التقدم الفعلي مقابل المخطط',
      'Exposure map': 'خريطة التعرّض',
      'Recorded delivery progress': 'تقدم التسليم المسجل',
      'Approved baseline': 'خط الأساس المعتمد',
      'SPI': 'مؤشر أداء الجدول',
      'Actual': 'الفعلي',
      'Planned': 'المخطط',
      'Budget Breakdown': 'تفاصيل الميزانية',
      'Breakdown by status': 'التوزيع حسب الحالة',
      'Challenge Distribution by Category': 'توزيع التحديات حسب الفئة',
      'All Project Challenges': 'جميع تحديات المشروع',

      // Risk
      'Risk Matrix': 'مصفوفة المخاطر',
      'Impact vs likelihood': 'التأثير مقابل الاحتمالية',
      'Likelihood': 'الاحتمالية',
      'Critical': 'حرج',
      'Very High': 'عالٍ جداً',
      'High': 'عالٍ',
      'Medium': 'متوسط',
      'Low': 'منخفض',
      'Very Low': 'منخفض جداً',
      'In mitigation': 'في المعالجة',
      'in_mitigation': 'في المعالجة',
      'unmitigated': 'غير معالج',
      'Unmitigated': 'غير معالج',
      'committed': 'ملتزم',
      'Committed': 'ملتزم',
      'closed': 'مغلق',
      'Closed': 'مغلق',
      'under_review': 'قيد المراجعة',

      // Change Requests
      'Change Requests': 'طلبات التغيير',
      'change_request': 'طلب تغيير',
      'Total this quarter': 'الإجمالي هذا الربع',
      'Approved': 'معتمد',
      'Rejected': 'مرفوض',
      'completed': 'مكتمل',
      'rejected': 'مرفوض',
      'on_track': 'في المسار',
      'deviating': 'منحرف',

      // Deliverables
      'Deliverables': 'المُنجَزات',
      'Deliverables Status': 'حالة المُنجَزات',
      'No deliverables found': 'لا توجد مُنجَزات',
      'project-deliverables': 'مُنجَزات المشروع',
      'STATUS': 'الحالة',
      'DUE': 'تاريخ الاستحقاق',
      'OWNER': 'المسؤول',
      'OPEN': 'مفتوح',

      // Phases / Milestones
      'No phases defined': 'لا توجد مراحل محددة',
      'financials': 'الماليات',
      'risks': 'المخاطر',
      'projects': 'مشاريع',
      'Projects': 'المشاريع',
      'total': 'الإجمالي',

      // Council / Governance
      'Council Boards': 'مجالس البرامج',
      'Program councils, meetings and governance': 'مجالس البرامج والاجتماعات والحوكمة',
      'Council': 'المجلس',
      'No programs found': 'لا توجد برامج',
      'NEXT SESSION': 'الجلسة القادمة',
      'LAST SESSION': 'آخر جلسة',
      'UPCOMING SESSION': 'الجلسة القادمة',
      'Upcoming Sessions': 'الجلسات القادمة',
      'All Sessions': 'جميع الجلسات',
      'No sessions scheduled': 'لا توجد جلسات مجدولة',
      'No upcoming sessions scheduled': 'لا توجد جلسات قادمة مجدولة',
      'Next session': 'الجلسة القادمة',
      'Session Agenda': 'جدول أعمال الجلسة',
      'Chaired by': 'برئاسة',
      'Council Members': 'أعضاء المجلس',
      'Members': 'الأعضاء',
      'MEMBERS': 'الأعضاء',
      'Total decisions': 'إجمالي القرارات',
      'Council members': 'أعضاء المجلس',
      'No members listed': 'لا يوجد أعضاء مدرجون',
      'ATTENDANCE': 'الحضور',
      'ATTENDEES': 'الحاضرون',
      'ATTENDANCE CONFIRMED': 'تأكيد الحضور',
      'Confirmed': 'مؤكد',
      'All on track': 'كل شيء في موعده',
      'Physical': 'حضوري',
      'Virtual': 'افتراضي',
      'Hybrid': 'هجين',
      'Scheduled': 'مجدولة',
      'Cancelled': 'ملغاة',

      // Agenda / Actions / Decisions
      'AGENDA ITEMS': 'بنود جدول الأعمال',
      'ITEMS': 'البنود',
      'No agenda items': 'لا توجد بنود في جدول الأعمال',
      'ACTION': 'الإجراء',
      'Action Items': 'بنود الإجراءات',
      'action-items': 'بنود الإجراءات',
      'ACTION ITEMS': 'بنود الإجراءات',
      'OVERDUE ACTIONS': 'الإجراءات المتأخرة',
      'OVERDUE TASKS': 'المهام المتأخرة',
      'Follow-up Action Items': 'بنود المتابعة',
      'No action items': 'لا توجد بنود إجراءات',
      'Require follow-up': 'يستلزم المتابعة',
      'TASK': 'المهمة',
      'PRIORITY': 'الأولوية',
      'high': 'عالي', 'medium': 'متوسط', 'low': 'منخفض',
      'not_started': 'لم يبدأ', 'in_progress': 'جاري', 'on_hold': 'معلق', 'completed': 'مكتمل',
      'DECISIONS': 'القرارات',
      'Decisions': 'القرارات',
      'Decisions Tabled to the Council': 'القرارات المعروضة على المجلس',
      'PREVIOUS DECISIONS': 'القرارات السابقة',
      'Previous decisions': 'القرارات السابقة',
      'No decisions recorded': 'لا توجد قرارات مسجلة',
      'Pending': 'معلق', 'Approved': 'معتمد', 'Rejected': 'مرفوض',
      'ID': 'الرقم',
      'CHALLENGE': 'التحدي',
      'SEVERITY': 'الخطورة',
      'AGE': 'العمر الزمني',
      'PROJECT': 'المشروع',

      // ─── Program Councils Tab (ProgramCouncils.vue) ─────────────────────
      'Track and manage program councils':                 'تتبع وإدارة مجالس البرنامج',
      'This program is closed. Councils are in read-only mode.': 'هذا البرنامج مغلق. المجالس في وضع القراءة فقط.',
      'Search councils...':                                'بحث في المجالس...',
      'All Statuses':                                      'جميع الحالات',
      'Refresh':                                           'تحديث',
      'Export':                                            'تصدير',
      'councils':                                          'مجالس',
      'No Chairperson':                                    'لا يوجد رئيس',
      'Manage Council':                                    'إدارة المجلس',
      'No councils found':                                 'لم يتم العثور على مجالس',
      'Try adjusting your filters':                        'جرّب تعديل المرشحات',
      'No councils have been created for this program':    'لم يتم إنشاء أي مجالس لهذا البرنامج',
      'Council Name':                                      'اسم المجلس',
      'Not assigned':                                      'غير معيّن',
      'Councils':                                          'المجالس',
      'Edit Council':                                      'تعديل المجلس',
      'Council Overview':                                  'نظرة عامة على المجلس',
      'Council details and membership':                    'تفاصيل المجلس والعضوية',
      'Council Information':                               'معلومات المجلس',
      'Created By':                                        'أنشأه',
      'Created At':                                        'تاريخ الإنشاء',
      'System':                                            'النظام',
      'Leadership':                                        'القيادة',
      'No members assigned':                               'لم يتم تعيين أعضاء',
      'Members will appear here once added':               'سيظهر الأعضاء هنا بعد إضافتهم',
      'members':                                           'أعضاء',
      'Schedule and track council meetings':               'جدولة ومتابعة اجتماعات المجلس',
      'Add Meeting':                                       'إضافة اجتماع',
      'No meetings yet':                                   'لا توجد اجتماعات بعد',
      'Schedule the first meeting for this council':       'جدول الاجتماع الأول لهذا المجلس',
      'Location/Link':                                     'الموقع / الرابط',
      'Track council decisions and resolutions':           'تتبع قرارات المجلس وقراراته',
      'Add Decision':                                      'إضافة قرار',
      'No decisions yet':                                  'لا توجد قرارات بعد',
      'Record the first council decision':                 'سجّل أول قرار للمجلس',
      'Decision Maker':                                    'صاحب القرار',
      'Manage council tasks and action items':             'إدارة مهام المجلس وبنود الإجراءات',
      'Add Task':                                          'إضافة مهمة',
      'No tasks yet':                                      'لا توجد مهام بعد',
      'Add action items and tasks for this council':       'أضف بنود إجراءات ومهام لهذا المجلس',
      'Unassigned':                                        'غير مُسنَد',
      'Assigned To':                                       'مُسنَد إلى',

      // Council dialog — create/edit
      'Enter council name':                                'أدخل اسم المجلس',
      'Name is required':                                  'الاسم مطلوب',
      'Enter council description':                         'أدخل وصف المجلس',
      'Description is required':                           'الوصف مطلوب',
      'Select start date':                                 'اختر تاريخ البدء',
      'Start date is required':                            'تاريخ البدء مطلوب',
      'Select end date':                                   'اختر تاريخ الانتهاء',
      'End date is required':                              'تاريخ الانتهاء مطلوب',
      'Program Council · Chairperson Role':                'مجلس البرنامج · دور رئيس المجلس',
      'Select chairperson':                                'اختر رئيس المجلس',
      'Program Council · Secretary Role':                  'مجلس البرنامج · دور الأمين',
      'Select secretary':                                  'اختر الأمين',
      'Program Council Role':                              'دور مجلس البرنامج',
      'Select members':                                    'اختر الأعضاء',
      'At least one member is required':                   'يجب تعيين عضو واحد على الأقل',
      'Chairperson':                                       'رئيس المجلس',
      'Secretary':                                         'الأمين',
      'Update':                                            'تحديث',

      // Meeting dialog
      'Meeting Title':                                     'عنوان الاجتماع',
      'Enter meeting title':                               'أدخل عنوان الاجتماع',
      'Meeting Type':                                      'نوع الاجتماع',
      'Select type':                                       'اختر النوع',
      'Required':                                          'مطلوب',
      'Select category':                                   'اختر الفئة',
      'One-time':                                          'مرة واحدة',
      'Automatically determined by category':              'يُحدَّد تلقائياً حسب الفئة',
      'Meeting Day':                                       'يوم الاجتماع',
      'Select day':                                        'اختر اليوم',
      'Enter location or meeting link':                    'أدخل الموقع أو رابط الاجتماع',
      'Enter meeting description':                         'أدخل وصف الاجتماع',
      'Agenda':                                            'جدول الأعمال',
      'Enter agenda items':                                'أدخل بنود جدول الأعمال',
      'List the main topics and items to be covered in the meeting': 'أدرج المحاور الرئيسية والبنود التي سيتم تناولها في الاجتماع',
      'Select attendees':                                  'اختر الحاضرين',
      'Start Time':                                        'وقت البدء',
      'End Time':                                          'وقت الانتهاء',
      'Select start time':                                 'اختر وقت البدء',
      'Select end time':                                   'اختر وقت الانتهاء',
      'Time':                                              'الوقت',
      'Edit Meeting':                                      'تعديل الاجتماع',
      'Add New Meeting':                                   'إضافة اجتماع جديد',
      'Update Meeting':                                    'تحديث الاجتماع',

      // Meeting categories
      'Board Meeting':                                     'اجتماع مجلس الإدارة',
      'National Strategy Committee Meeting':               'اجتماع لجنة الاستراتيجية الوطنية',
      'National Entities Meeting':                         'اجتماع الجهات الوطنية',
      'Periodic Update Meeting':                           'اجتماع التحديث الدوري',
      'Quarterly':                                         'ربع سنوي',
      'Monthly':                                           'شهري',
      'Bi-weekly':                                         'كل أسبوعين',

      // Decision dialog
      'Enter decision title':                              'أدخل عنوان القرار',
      'Enter decision details':                            'أدخل تفاصيل القرار',
      'Select Date':                                       'اختر التاريخ',
      'e.g., Budget, Policy':                              'مثال: الميزانية، السياسة',
      'Who made this decision?':                           'من اتخذ هذا القرار؟',
      'Edit Decision':                                     'تعديل القرار',

      // Task dialog
      'Enter task title':                                  'أدخل عنوان المهمة',
      'Enter task description':                            'أدخل وصف المهمة',
      'Select due date':                                   'اختر تاريخ الاستحقاق',
      'Select assignee':                                   'اختر المُكلَّف',
      'Edit Task':                                         'تعديل المهمة',
      'Title is required':                                 'العنوان مطلوب',

      // Toast / confirm messages
      'Failed to load councils':                           'فشل في تحميل المجالس',
      'Council updated successfully':                      'تم تحديث المجلس بنجاح',
      'Council created successfully':                      'تم إنشاء المجلس بنجاح',
      'Failed to save council':                            'فشل في حفظ المجلس',
      'Are you sure you want to delete this council?':     'هل أنت متأكد من حذف هذا المجلس؟',
      'Confirm Delete':                                    'تأكيد الحذف',
      'Council deleted':                                   'تم حذف المجلس',
      'Failed to delete council':                          'فشل في حذف المجلس',
      'Meeting updated':                                   'تم تحديث الاجتماع',
      'Meeting added successfully':                        'تمت إضافة الاجتماع بنجاح',
      'Failed to save meeting':                            'فشل في حفظ الاجتماع',
      'Delete this meeting?':                              'هل تريد حذف هذا الاجتماع؟',
      'Meeting deleted':                                   'تم حذف الاجتماع',
      'Failed to delete meeting':                          'فشل في حذف الاجتماع',
      'Decision updated':                                  'تم تحديث القرار',
      'Decision added':                                    'تمت إضافة القرار',
      'Failed to save decision':                           'فشل في حفظ القرار',
      'Delete this decision?':                             'هل تريد حذف هذا القرار؟',
      'Decision deleted':                                  'تم حذف القرار',
      'Failed to delete decision':                         'فشل في حذف القرار',
      'Task updated':                                      'تم تحديث المهمة',
      'Task added':                                        'تمت إضافة المهمة',
      'Failed to save task':                               'فشل في حفظ المهمة',
      'Delete this task?':                                 'هل تريد حذف هذه المهمة؟',
      'Task deleted':                                      'تم حذف المهمة',
      'Failed to delete task':                             'فشل في حذف المهمة',
      'Your vote has been recorded':                       'تم تسجيل تصويتك',
      'Failed to record vote':                             'فشل في تسجيل التصويت',
      'Voted':                                             'تم التصويت',
      'Confirm':                                           'تأكيد',


      'KPI Report':                                'تقرير المؤشرات',
      'Board-ready KPI performance report':        'تقرير أداء المؤشرات جاهز للعرض على مجلس الإدارة',
      'Board-ready executive report for leadership presentation.': 'تقرير تنفيذي جاهز لعرضه على القيادة.',
      'Report Controls':                           'إعدادات التقرير',
      'Configure & Generate Report':               'اضبط الإعدادات وقم بإنشاء التقرير',
      'Select your period, scope, and status filters above, then click Generate Report to view results.':
        'اختر الفترة والنطاق والحالة من الأعلى ثم اضغط "إنشاء التقرير" لعرض النتائج.',
      'Generate Report': 'إنشاء التقرير',
      'Recent Notifications': 'الإشعارات الأخيرة',
      'No notifications': 'لا توجد إشعارات',
      'View All Notifications': 'عرض جميع الإشعارات',
      'You are all caught up!': 'لقد اطلعت على كل شيء!',
      'LOADING': 'جار التحميل',
      'All caught up' : 'اطلعت على كل شيء',
      'Mark All as Read' : 'وضع علامة مقروء للجميع',
      'New Report':                                'تقرير جديد',
      'Preview':                                   'معاينة',
      'Print':                                     'طباعة',
      'Download PDF': 'تحميل PDF',
      'Download Word': 'تحميل Word',
      'Download Excel': 'تحميل Excel',
      'Close':                                     'إغلاق',
      'KPI Report Preview':                        'معاينة تقرير المؤشرات',

      // Period / scope / status / pillar labels
      'Quarter':                                   'ربع سنة',
      'Month':                                     'شهر',
      'Year':                                      'سنة',
      'Custom Range':                              'فترة مخصصة',
      'Start date':                                'تاريخ البدء',
      'End date':                                  'تاريخ الانتهاء',
      'Scope':                                     'النطاق',
      'Objective':                                 'الهدف',
      'All Objectives':                            'جميع الأهداف',
      'Pillar':                                    'الركيزة',
      'All Pillars':                               'جميع الركائز',
      'Q1 (Jan – Mar)':                            'الربع الأول (يناير – مارس)',
      'Q2 (Apr – Jun)':                            'الربع الثاني (أبريل – يونيو)',
      'Q3 (Jul – Sep)':                            'الربع الثالث (يوليو – سبتمبر)',
      'Q4 (Oct – Dec)':                            'الربع الرابع (أكتوبر – ديسمبر)',
      'January':                                   'يناير',
      'February':                                  'فبراير',
      'March':                                     'مارس',
      'April':                                     'أبريل',
      'May':                                       'مايو',
      'June':                                      'يونيو',
      'July':                                      'يوليو',
      'August':                                    'أغسطس',
      'September':                                 'سبتمبر',
      'October':                                   'أكتوبر',
      'November':                                  'نوفمبر',
      'December':                                  'ديسمبر',
      'Wk':                                        'أسبوع',

      // Status bands
      'Partially Achieved':                        'محقق جزئياً',
      'Delayed':                                   'متأخر',
      'Not Achieved':                              'غير محقق',
      'Not Measured':                              'غير مُقاس',

      // Cover page meta
      'REPORT CENTER':                             'مركز التقارير',
      'REPORT NO.':                                'رقم التقرير',
      'REPORTING PERIOD':                          'فترة التقرير',
      'ISSUE DATE':                                'تاريخ الإصدار',
      'VERSION':                                   'الإصدار',
      'PREPARED FOR':                              'مُعدّ من أجل',
      'Board of Directors & Executive Management': 'مجلس الإدارة والإدارة التنفيذية',
      'APPLIED FILTERS':                           'المرشحات المطبقة',
      '{n} of {total} statuses':                   '{n} من {total} حالات',

      // Sections
      'Executive Summary':                         'الملخص التنفيذي',
      'KPI Status Overview':                       'نظرة عامة على حالة المؤشرات',
      'KPI Distribution':                          'توزيع المؤشرات',
      'KPI Details':                               'تفاصيل المؤشرات',
      'Trend Analysis':                            'تحليل الاتجاه',
      'Key Achievements':                          'أبرز الإنجازات',
      'Challenges':                                'التحديات',
      'Recommendations':                           'التوصيات',
      'Performance Direction':                     'اتجاه الأداء',

      // Table headers
      'Code':                                      'الرمز',
      'Owner':                                     'الجهة المسؤولة',
      'Achievement %':                             'نسبة الإنجاز',
      'KPIs':                                      'المؤشرات',

      // Empty / status
      'No KPI data available for the selected filters.': 'لا توجد بيانات مؤشرات للمعايير المختارة.',
      'No KPI data available for the selected period.':  'لا توجد بيانات مؤشرات للفترة المختارة.',
      'No notable achievements yet.':              'لا توجد إنجازات بارزة حتى الآن.',
      'No critical challenges detected.':          'لم يتم رصد تحديات حرجة.',
      'No data':                                   'لا توجد بيانات',
      'No KPIs match the selected filters.':       'لا توجد مؤشرات مطابقة للمعايير المختارة.',
      'Failed to generate report':                 'فشل إنشاء التقرير',
      'Q2 (Apr – Jun)':                            'الربع الثاني (أبريل – يونيو)',
      'Q3 (Jul – Sep)':                            'الربع الثالث (يوليو – سبتمبر)',
      'Q4 (Oct – Dec)':                            'الربع الرابع (أكتوبر – ديسمبر)',
      'January':                                   'يناير',
      'February':                                  'فبراير',
      'March':                                     'مارس',
      'April':                                     'أبريل',
      'May':                                       'مايو',
      'June':                                      'يونيو',
      'July':                                      'يوليو',
      'August':                                    'أغسطس',
      'September':                                 'سبتمبر',
      'October':                                   'أكتوبر',
      'November':                                  'نوفمبر',
      'December':                                  'ديسمبر',
      'Wk':                                        'أسبوع',

      // Status bands
      'Partially Achieved':                        'محقق جزئياً',
      'Delayed':                                   'متأخر',
      'Not Achieved':                              'غير محقق',
      'Not Measured':                              'غير مُقاس',

      // Cover page meta
      'REPORT CENTER':                             'مركز التقارير',
      'REPORT NO.':                                'رقم التقرير',
      'REPORTING PERIOD':                          'فترة التقرير',
      'ISSUE DATE':                                'تاريخ الإصدار',
      'VERSION':                                   'الإصدار',
      'PREPARED FOR':                              'مُعدّ من أجل',
      'Board of Directors & Executive Management': 'مجلس الإدارة والإدارة التنفيذية',

      // Sections
      'Executive Summary':                         'الملخص التنفيذي',
      'KPI Status Overview':                       'نظرة عامة على حالة المؤشرات',
      'KPI Distribution':                          'توزيع المؤشرات',
      'KPI Details':                               'تفاصيل المؤشرات',
      'Trend Analysis':                            'تحليل الاتجاه',
      'Key Achievements':                          'أبرز الإنجازات',
      'Challenges':                                'التحديات',
      'Recommendations':                           'التوصيات',
      'Performance Direction':                     'اتجاه الأداء',

      // Table headers
      'Code':                                      'الرمز',
      'Owner':                                     'الجهة المسؤولة',
      'Achievement %':                             'نسبة الإنجاز',
      'KPIs':                                      'المؤشرات',

      // Empty / status
      'No KPI data available for the selected filters.': 'لا توجد بيانات مؤشرات للمعايير المختارة.',
      'No KPI data available for the selected period.':  'لا توجد بيانات مؤشرات للفترة المختارة.',
      'No notable achievements yet.':              'لا توجد إنجازات بارزة حتى الآن.',
      'No critical challenges detected.':          'لم يتم رصد تحديات حرجة.',
      'No data':                                   'لا توجد بيانات',
      'No KPIs match the selected filters.':       'لا توجد مؤشرات مطابقة للمعايير المختارة.',
      'Failed to generate report':                 'فشل إنشاء التقرير',

      // Executive summary (template parts)
      'During this period, {total} KPIs were tracked across strategic pillars. {achieved} achieved, {partial} partially achieved, while {delayed} require immediate corrective measures.':
        'خلال هذه الفترة، تمت متابعة {total} مؤشراً عبر الركائز الاستراتيجية، تحقق منها {achieved} وتحقق جزئياً {partial} بينما يتطلب {delayed} إجراءات تصحيحية فورية.',

      // Insights phrasing
      '{name} reached {pct}% completion':                          '{name} بلغ {pct}% من الإنجاز',
      '{name} shows lagging performance at {pct}%':                '{name} يُظهر أداءً متأخراً بنسبة {pct}%',
      'Launch corrective action plan for {n} critical KPIs':       'إطلاق خطة إجراءات تصحيحية لـ {n} مؤشراً حرجاً',
      'Maintain current execution trajectory':                     'الحفاظ على مسار التنفيذ الحالي',
      'Re-allocate resources to {n} delayed indicators':           'إعادة توزيع الموارد على {n} مؤشراً متأخراً',
      'Continue cadence and reporting discipline':                 'الاستمرار في الالتزام بدورة التقارير',
      'Establish measurement baseline for {n} un-measured KPIs':   'إنشاء خط أساس للقياس لعدد {n} مؤشراً غير مُقاس',
      'Sustain measurement discipline':                            'الحفاظ على انضباط القياس',
      'Positive direction with {pct}% achievement rate':           'اتجاه إيجابي بمعدل إنجاز {pct}%',
      'Steady direction — momentum building':                      'اتجاه ثابت — يتسارع الزخم',
      'Average achievement: {pct}%':                               'متوسط الإنجاز: {pct}%',
      'Decline in {n} indicators requires intervention':           'تراجع في {n} مؤشراً يتطلب التدخل',
      'Trajectory under control':                                  'المسار تحت السيطرة',

      // ─── Initiative Report (Strategy → tab #15) ──────────────────────────
      'Initiative Report':                          'تقرير المبادرات',
      'Board-ready initiative performance report':  'تقرير أداء المبادرات جاهز للعرض على مجلس الإدارة',
      'Initiative Report Preview':                  'معاينة تقرير المبادرات',
      'Initiative Status Overview':                 'نظرة عامة على حالة المبادرات',
      'Initiative Distribution':                    'توزيع المبادرات',
      'Initiative Details':                         'تفاصيل المبادرات',
      'Initiatives':                                'المبادرات',
      'Weight %':                                   'الوزن %',
      'Progress %':                                 'نسبة التقدم',
      'Average progress: {pct}%':                   'متوسط التقدم: {pct}%',
      'Approved':                                   'معتمد',
      'Draft':                                      'مسودة',
      'Rejected':                                   'مرفوض',
      'No initiative data available for the selected filters.': 'لا توجد بيانات مبادرات للمعايير المختارة.',
      'No initiative data available for the selected period.':  'لا توجد بيانات مبادرات للفترة المختارة.',
      'No initiatives match the selected filters.': 'لا توجد مبادرات مطابقة للمعايير المختارة.',
      'During this period, {total} initiatives were tracked across strategic pillars. {approved} approved, {pending} pending review, while {rejected} were rejected and require attention.':
        'خلال هذه الفترة، تمت متابعة {total} مبادرة عبر الركائز الاستراتيجية. {approved} معتمدة، {pending} قيد المراجعة، بينما {rejected} مرفوضة وتحتاج إلى اهتمام.',
      'Launch corrective action plan for {n} rejected initiatives': 'إطلاق خطة تصحيحية لـ {n} مبادرات مرفوضة',
      'Expedite approval for {n} pending initiatives':              'تسريع اعتماد {n} مبادرات معلقة',
      'Establish execution baseline for {n} draft initiatives':     'وضع خط أساس تنفيذي لـ {n} مبادرات مسودة',
      'Positive direction with {pct}% approval rate':              'اتجاه إيجابي بنسبة اعتماد {pct}%',
      'Decline in {n} initiatives requires intervention':           'تراجع في {n} مبادرات يستلزم التدخل',

      // ── Data Import ────────────────────────────────────────────────────────
      'Data Import':                        'استيراد البيانات',
      'Import bulk data into the system using the official Excel template. Only Super Admin can perform imports.': 'استيراد البيانات بالجملة إلى النظام باستخدام قالب Excel الرسمي. يُسمح فقط للمشرف الأعلى بإجراء الاستيراد.',
      'Download Template':                  'تحميل القالب',
      'Download Arabic Template':           'تحميل القالب بالعربية',
      'Download English Template':          'تحميل القالب بالإنجليزية',
      'Export Data Arabic':      'تصدير البيانات بالعربية',
      'Export Data English':     'تصدير البيانات بالإنجليزية',
      'Step 1: Upload Excel File':          'الخطوة 1: رفع ملف Excel',
      'Step 2: Select Sheets to Import':    'الخطوة 2: اختر الجداول للاستيراد',
      'Drag & drop your Excel file here, or click to browse': 'اسحب وأسقط ملف Excel هنا، أو انقر للتصفح',
      'Supported formats: .xlsx, .xls — max 50 MB':           'الصيغ المدعومة: xlsx. ,xls. — الحد الأقصى 50 ميجابايت',
      'Analysing file…':                    'جارٍ تحليل الملف…',
      'Select All':                         'تحديد الكل',
      'Deselect All':                       'إلغاء تحديد الكل',
      'rows':                               'صف',
      'Preview File':                       'معاينة الملف',
      'Run Import':                         'تشغيل الاستيراد',
      'Uploading and processing…':          'جارٍ الرفع والمعالجة…',
      'Upload complete. Server is processing the import…': 'اكتمل الرفع. الخادم يعالج عملية الاستيراد الآن…',
      'Import Results':                     'نتائج الاستيراد',
      'Total Imported':                     'إجمالي المستورد',
      'Skipped': 'تم التخطي',
      'Upload complete. Server is processing the import…': 'اكتمل الرفع. الخادم يعالج عملية الاستيراد الآن…',
      'Sheet':                              'الجدول',
      'Imported':                           'مستورد',
      'Warnings':                           'تحذيرات',
      'Errors':                             'أخطاء',
      'Import Complete':                    'اكتمل الاستيراد',
      'records imported':                   'سجل مستورد',
      'skipped':                            'تم تخطيه',
      'Import failed. Check the results below.': 'فشل الاستيراد. راجع النتائج أدناه.',
      'Preview Ready':                      'المعاينة جاهزة',
      'Select the sheets you want to import': 'اختر الجداول التي تريد استيرادها',
      'Preview Failed':                     'فشلت المعاينة',
      'Could not read the file':            'تعذّر قراءة الملف',
      'Downloaded':                         'تم التحميل',
      'Template downloaded successfully':   'تم تحميل القالب بنجاح',
      'Failed to download template':        'فشل تحميل القالب',
      'Database data downloaded successfully': 'تم تحميل بيانات قاعدة البيانات بنجاح',
      'Failed to download database data':   'فشل تحميل بيانات قاعدة البيانات',

      // ─── Project Report ────────────────────────────────────────────────────
      'Project Report':                            'تقرير المشروع',
      'Board-ready project performance report':    'تقرير أداء المشروع جاهز للعرض على مجلس الإدارة',
      'Project Report Preview':                    'معاينة تقرير المشروع',
      'Include Sections':                          'الأقسام المدرجة',
      'Report Language':                           'لغة التقرير',
      'PROJECT DETAILS':                           'تفاصيل المشروع',
      'Project Progress Overview':                 'نظرة عامة على تقدم المشروع',
      'Completed':                                 'مكتمل',
      'Days Overdue':                              'أيام التأخير',
      'Budget Utilised':                           'نسبة استخدام الميزانية',
      'Progress Detail':                           'تفاصيل التقدم',
      'Project Progress':                          'تقدم المشروع',
      'Budget Item':                               'بند الميزانية',
      'Amount':                                    'المبلغ',
      '% of Total':                                '% من الإجمالي',
      'Amount Spent':                              'المبلغ المنفق',
      'Remaining Budget':                          'الميزانية المتبقية',
      'Schedule & Phases':                         'الجدول الزمني والمراحل',
      'Risk Register Summary':                     'ملخص سجل المخاطر',
      'Risk':                                      'المخاطرة',
      'No risks recorded for this project.':       'لا توجد مخاطر مسجلة لهذا المشروع.',
      'Issues Summary':                            'ملخص المشكلات',
      'Issue':                                     'المشكلة',
      'Issues':                                    'المشكلات',
      'No issues recorded for this project.':      'لا توجد مشكلات مسجلة لهذا المشروع.',
      'Change':                                    'التغيير',
      'Changes':                                   'التغييرات',
      'No change requests recorded for this period.': 'لا توجد طلبات تغيير مسجلة لهذه الفترة.',
      'Insights & Recommendations':               'الرؤى والتوصيات',
      'No project':                                'لا يوجد مشروع',
      'Project data not available':                'بيانات المشروع غير متوفرة',
      // Executive summary template
      'This report covers project "{name}" for the period {period}. Overall progress stands at {progress}%, with {done} of {total} deliverables completed. Budget utilisation is at {budget}%. The project is {status}.':
        'يغطي هذا التقرير مشروع "{name}" للفترة {period}. يبلغ التقدم الإجمالي {progress}%، مع إنجاز {done} من أصل {total} مخرجات. نسبة استخدام الميزانية {budget}%. المشروع {status}.',
      'currently overdue':                         'متأخر حالياً',
      '{n} days remaining':                        'متبقٍ {n} أيام',
      // Insight phrases for Project Report
      'Project is {pct}% complete — strong execution momentum':       'المشروع مكتمل بنسبة {pct}% — زخم تنفيذ قوي',
      '{n} of {total} deliverables successfully completed':            'تم إنجاز {n} من أصل {total} مخرجات بنجاح',
      'Budget utilisation at {pct}% — within healthy range':          'استخدام الميزانية {pct}% — ضمن النطاق الصحي',
      'Project is {n} days past its planned end date':                 'تجاوز المشروع تاريخ انتهائه بـ {n} أيام',
      '{n} high-priority risks require immediate attention':           '{n} مخاطر عالية الأولوية تستدعي اهتماماً فورياً',
      '{n} open issues need resolution':                               '{n} مشكلات مفتوحة تحتاج إلى معالجة',
      'Budget utilisation at {pct}% — close to exhaustion':           'استخدام الميزانية {pct}% — قريب من الاستنفاد',
      'Accelerate execution cadence to close progress gap':            'تسريع وتيرة التنفيذ لسد فجوة التقدم',
      'Escalate high-priority risks to steering committee':            'تصعيد المخاطر عالية الأولوية إلى لجنة التوجيه',
      'Continue proactive risk monitoring':                            'الاستمرار في المراقبة الاستباقية للمخاطر',
      'Assign resolution owners and deadlines to open issues':         'تعيين أصحاب المسؤولية وجداول لإغلاق المشكلات المفتوحة',
      'Sustain issue management discipline':                           'الحفاظ على انضباط إدارة المشكلات',
      'Positive direction — project on track for completion':          'اتجاه إيجابي — المشروع في مسار الإتمام',
      'Moderate progress — close monitoring recommended':              'تقدم معتدل — يُوصى بالمتابعة الدقيقة',
      'Progress below expectations — corrective action needed':        'التقدم دون التوقعات — مطلوب إجراء تصحيحي',
      'Average KPI achievement: {pct}%':                              'متوسط إنجاز المؤشرات: {pct}%',
      'Schedule overrun — recovery plan required':                     'تجاوز الجدول الزمني — مطلوب خطة استرداد',
      'Schedule on track':                                             'الجدول الزمني على المسار الصحيح',

      // Program Management — remaining workflow and form translations
      'Program submitted for approval successfully': 'تم تقديم البرنامج للاعتماد بنجاح',
      'Failed to submit program': 'فشل تقديم البرنامج',
      'e.g. PROG-001': 'مثال: PROG-001',
      'Choose program sponsor': 'اختر راعي البرنامج',
      'Search...': 'بحث...',
      'No users with Program Sponsor role found': 'لم يتم العثور على مستخدمين بدور راعي البرنامج',
      'Choose program director': 'اختر مدير البرنامج',
      'No users with Program Director role found': 'لم يتم العثور على مستخدمين بدور مدير البرنامج',
      'Choose program owner': 'اختر مالك البرنامج',
      'No users with Program Owner role found': 'لم يتم العثور على مستخدمين بدور مالك البرنامج',
      'Select projects to add...': 'اختر المشاريع لإضافتها...',
      'Weight (%)': 'الوزن (%)',
      'Project weights must sum to exactly 100%. Current total: {n}%': 'يجب أن يساوي مجموع أوزان المشاريع 100% تماماً. المجموع الحالي: {n}%',
      'Total weight: {n}% — {remaining}% remaining': 'إجمالي الوزن: {n}% — المتبقي {remaining}%',
      'Distribute Equally': 'توزيع بالتساوي',
      'Create your first program to see statistics': 'أنشئ برنامجك الأول لعرض الإحصائيات',
      'Approve Program Phase': 'اعتماد مرحلة البرنامج',
      'for program': 'للبرنامج',
      'Reject Program Phase': 'رفض مرحلة البرنامج',
      'Your program was rejected': 'تم رفض برنامجك',
      'Your program was rejected by the administrator. Please review the feedback below and make the necessary changes.': 'تم رفض برنامجك من قبل المسؤول. يرجى مراجعة الملاحظات أدناه وإجراء التعديلات اللازمة.',
      'Please click "Edit & Resubmit" to make the required changes and resubmit your program for approval.': 'يرجى النقر على «تعديل وإعادة التقديم» لإجراء التعديلات المطلوبة وإعادة تقديم البرنامج للاعتماد.',
      'Program Approved': 'تم اعتماد البرنامج',
      'This program has been reviewed and approved by the PMO/Administrator.': 'تمت مراجعة هذا البرنامج واعتماده من مكتب إدارة المشاريع أو المسؤول.',
      'Failed to load programs. Please refresh the page.': 'فشل تحميل البرامج. يرجى تحديث الصفحة.',
      'Exporting programs as': 'تصدير البرامج بصيغة',
      'Are you sure you want to delete the program': 'هل أنت متأكد من حذف البرنامج',
      'Delete Program': 'حذف البرنامج',
      'Program Deleted': 'تم حذف البرنامج',
      'Program has been permanently deleted': 'تم حذف البرنامج نهائياً',
      'Failed to delete program': 'فشل حذف البرنامج',
      'No comments provided': 'لم يتم تقديم تعليقات',
      'Phase has been approved successfully': 'تم اعتماد المرحلة بنجاح',
      'Phase has been rejected': 'تم رفض المرحلة',
      'The program you are trying to review does not exist or has been deleted.': 'البرنامج الذي تحاول مراجعته غير موجود أو تم حذفه.',
      'View Program': 'عرض البرنامج',
      'Manage and monitor all program activities': 'إدارة جميع أنشطة البرنامج ومتابعتها',
      'Filter Programs': 'تصفية البرامج',
      'New Program': 'برنامج جديد',
      'Newest First': 'الأحدث أولاً',
      'Program': 'البرنامج',
      'Program councils management': 'إدارة مجالس البرنامج',
      'This program is closed. Benefits are in read-only mode.': 'هذا البرنامج مغلق. المنافع متاحة للقراءة فقط.',
      'Project is required': 'المشروع مطلوب',
      "Supporting the Authority's culture and values": 'دعم ثقافة الجهة وقيمها',
      "Supporting the Authority's strategies": 'دعم استراتيجيات الجهة',
      'Archive Program Files': 'أرشفة ملفات البرنامج',
      'Differences in Program Cost': 'فروقات تكلفة البرنامج',
      'Expected Program Cost': 'التكلفة المتوقعة للبرنامج',
      'Enter expected program cost': 'أدخل التكلفة المتوقعة للبرنامج',
      'Actual Program Cost': 'التكلفة الفعلية للبرنامج',
      'Enter actual program cost': 'أدخل التكلفة الفعلية للبرنامج',
      'Program Cost Difference': 'فرق تكلفة البرنامج',
      'Enter program cost difference': 'أدخل فرق تكلفة البرنامج',
      'No project deliverables': 'لا توجد مخرجات للمشروع',
      'Updated At': 'تاريخ التحديث',
      'Program files archived': 'تمت أرشفة ملفات البرنامج',
      'Program files have not been archived': 'لم تتم أرشفة ملفات البرنامج',
      'Failed to load project deliverables': 'فشل تحميل مخرجات المشروع',
      'Meetings': 'الاجتماعات',
      'Chairperson is required': 'رئيس المجلس مطلوب',
      'Secretary is required': 'أمين المجلس مطلوب',
      'Update Council': 'تحديث المجلس',
      'Added': 'تمت الإضافة',
      'This program is closed. Escalations are in read-only mode.': 'هذا البرنامج مغلق. التصعيدات متاحة للقراءة فقط.',
      'At least one of the following must be selected: Risk or Issue or both': 'يجب اختيار واحد على الأقل: خطر أو مشكلة أو كلاهما',
      'Select related risk': 'اختر الخطر المرتبط',
      'Select at least one: Risk or Issue': 'اختر واحداً على الأقل: خطر أو مشكلة',
      'Select related issue': 'اختر المشكلة المرتبطة',
      'No issues available for this program': 'لا توجد مشكلات متاحة لهذا البرنامج',
      'Please select at least one: Risk or Issue': 'يرجى اختيار واحد على الأقل: خطر أو مشكلة',
      'Risk or Issue': 'خطر أو مشكلة',
      'Report date must be before the project end date': 'يجب أن يكون تاريخ التقرير قبل تاريخ انتهاء المشروع',
      'Missing Program': 'البرنامج غير متوفر',
      'Program information is required before creating an issue.': 'معلومات البرنامج مطلوبة قبل إنشاء مشكلة.',
      'Program information is required before updating an issue.': 'معلومات البرنامج مطلوبة قبل تحديث مشكلة.',
      'Program information is required before deleting an issue.': 'معلومات البرنامج مطلوبة قبل حذف مشكلة.',
      'This program is closed. Lessons are in read-only mode.': 'هذا البرنامج مغلق. الدروس المستفادة متاحة للقراءة فقط.',
      'Please specify': 'يرجى التحديد',
      'Enter classification type': 'أدخل نوع التصنيف',
      'Future Recommendation': 'التوصية المستقبلية',
      'Enter future recommendation': 'أدخل التوصية المستقبلية',
      'Please specify the classification type': 'يرجى تحديد نوع التصنيف',
      'Please select classification': 'يرجى اختيار التصنيف',
      'Time management': 'إدارة الوقت',
      'Risk management': 'إدارة المخاطر',
      'Manage Weights': 'إدارة الأوزان',
      'Program Weight': 'وزن البرنامج',
      'Equal': 'متساوٍ',
      'Manage Project Weights': 'إدارة أوزان المشاريع',
      'Assign a contribution weight (%) to each project. Weights must total exactly 100% for accurate program progress calculation.': 'عيّن وزن مساهمة (%) لكل مشروع. يجب أن يساوي مجموع الأوزان 100% تماماً لحساب تقدم البرنامج بدقة.',
      'Equal Distribution': 'توزيع متساوٍ',
      'Reset to Saved': 'استعادة القيم المحفوظة',
      'Save Weights': 'حفظ الأوزان',
      'Saved': 'تم الحفظ',
      'Project weights updated successfully.': 'تم تحديث أوزان المشاريع بنجاح.',
      'Failed to save weights.': 'فشل حفظ الأوزان.',
      'This program is closed. Risks are in read-only mode.': 'هذا البرنامج مغلق. المخاطر متاحة للقراءة فقط.',
      'Response plan is required': 'خطة الاستجابة مطلوبة',
      'Date of risk treatment is required': 'تاريخ معالجة الخطر مطلوب',
      'Support is required': 'الدعم مطلوب',

      // Project Management — listing and create/edit translations
      'Project submitted for approval successfully': 'تم تقديم المشروع للاعتماد بنجاح',
      'Failed to submit project': 'فشل تقديم المشروع',
      'e.g. PROJ-001': 'مثال: PROJ-001',
      'Select Project Owner': 'اختر مالك المشروع',
      'Choose project sponsor': 'اختر راعي المشروع',
      'Search sponsors...': 'البحث عن الرعاة...',
      'No sponsors found. Assign the Project Sponsor role to users first.': 'لم يتم العثور على رعاة. عيّن دور راعي المشروع للمستخدمين أولاً.',
      'Select Classification': 'اختر التصنيف',
      'Owning administration is required': 'الإدارة المالكة مطلوبة',
      'Select board members': 'اختر أعضاء المجلس',
      'Search board members...': 'البحث عن أعضاء المجلس...',
      'Project justifications are required': 'مبررات المشروع مطلوبة',
      'Define all contracts associated with this project': 'حدد جميع العقود المرتبطة بهذا المشروع',
      'Add Contract': 'إضافة عقد',
      'At least one contract is required': 'مطلوب عقد واحد على الأقل',
      'Contracts Required!': 'العقود مطلوبة!',
      'No contracts added yet': 'لم تتم إضافة عقود بعد',
      'Click "Add Contract" to get started': 'انقر على «إضافة عقد» للبدء',
      'List the key outputs and deliverables for this project': 'أدرج المخرجات والتسليمات الرئيسية لهذا المشروع',
      'At least one deliverable is required': 'مطلوب مخرج واحد على الأقل',
      'Deliverables Required!': 'المخرجات مطلوبة!',
      'Add First Deliverable': 'إضافة المخرج الأول',
      'Attach the business case document. Accepted formats: PDF, DOCX, XLSX.': 'أرفق مستند دراسة الجدوى. الصيغ المقبولة: PDF وDOCX وXLSX.',
      'Click to upload or drag & drop': 'انقر للرفع أو اسحب الملف وأفلته',
      'No contracts defined yet. Please add contracts in the Contracts section first.': 'لم يتم تعريف عقود بعد. يرجى إضافة العقود في قسم العقود أولاً.',
      'No contracts available': 'لا توجد عقود متاحة',
      'Select a contract': 'اختر عقداً',
      'Contract #': 'رقم العقد',
      'Deliverable End Date': 'تاريخ انتهاء المخرج',
      'Edit Contract': 'تعديل العقد',
      'Please select a project sponsor': 'يرجى اختيار راعي المشروع',
      'Only PDF, DOCX, and XLSX files are accepted.': 'تُقبل ملفات PDF وDOCX وXLSX فقط.',
      'File must be smaller than 20 MB.': 'يجب أن يكون حجم الملف أقل من 20 ميجابايت.',
      'Failed to load sponsors. Please refresh the page.': 'فشل تحميل الرعاة. يرجى تحديث الصفحة.',
      'Failed to load users. Please refresh the page.': 'فشل تحميل المستخدمين. يرجى تحديث الصفحة.',
      'Contract name is required': 'اسم العقد مطلوب',
      'End date cannot be earlier than start date': 'لا يمكن أن يكون تاريخ الانتهاء قبل تاريخ البدء',
      'errors': 'أخطاء',
      'You are editing the project': 'أنت تقوم بتعديل المشروع',
      'Failed to load project data': 'فشل تحميل بيانات المشروع',
      'Some data may not be available': 'قد لا تتوفر بعض البيانات',
      'Choose stakeholder role': 'اختر دور صاحب المصلحة',
      'Specify Role': 'تحديد الدور',
      'Enter custom role name': 'أدخل اسم الدور المخصص',
      'This role will be saved to the system for future use.': 'سيتم حفظ هذا الدور في النظام للاستخدام مستقبلاً.',
      'Saving Role...': 'جارٍ حفظ الدور...',
      'Business Owner': 'مالك الأعمال',
      'Department Head': 'رئيس القسم',
      'End User': 'المستخدم النهائي',
      'Subject Matter Expert': 'خبير المجال',
      'Vendor / Supplier': 'المورد',
      'Consultant': 'استشاري',
      'Auditor': 'مدقق',
      'Regulator': 'جهة تنظيمية',
      'Change Manager': 'مدير التغيير',
      'Quality Manager': 'مدير الجودة',
      'Risk Manager': 'مدير المخاطر',
      'PMO Member': 'عضو مكتب إدارة المشاريع',

      // Error pages
      'Error':                                                         'خطأ',
      'Oops!':                                                         'عذراً!',
      'Something gone wrong!':                                         'حدث خطأ ما!',
      'There is nothing here':                                         'لا يوجد شيء هنا',
      'You don’t have the permissions to access this page':             'ليست لديك الصلاحيات للوصول إلى هذه الصفحة',
      'You don\'t have the permissions to access this page.':           'ليست لديك الصلاحيات للوصول إلى هذه الصفحة.',
      'Go to Home':                                                    'الذهاب إلى الرئيسية',
      'Go to Dashboard':                                               'الذهاب إلى لوحة المعلومات',
      'Your role:':                                                    'دورك:',
      'Critical Delay':                                                'تأخر عالي',
      'Completed':                                                     'مكتمل',
      'Minor Delay':                                                   'تأخر بسيط',
      'On Track':                                                      'على المسار',
      'Annual Plan':                                                   'الخطة السنوية',
      'Master Plan':                                                   'الخطة الرئيسية',
      'KPI Value Source':                                              'مصدر قيمة مؤشر الأداء',
      'KPI Result Source':                                             'مصدر نتيجة مؤشر الأداء',
      'Entered manually':                                              'إدخال يدوي',
      'Read from Operational KPI results':                             'قراءة من نتائج مؤشرات الأداء التشغيلية',
      'Direct Manual Entry':                                           'إدخال مباشر يدوي',
      'Calculated from Linked Operational KPIs':                       'يُحتسب من مؤشرات الأداء التشغيلية المرتبطة',
      'Authorized users enter and maintain the KPI result directly.':  'يقوم المستخدمون المخولون بإدخال نتيجة المؤشر وتحديثها مباشرة.',
      'The result is calculated automatically from linked Operational KPIs.': 'تُحتسب النتيجة تلقائيًا من مؤشرات الأداء التشغيلية المرتبطة.',
      'Specify whether this strategic KPI is updated directly or calculated from linked Operational KPI results.': 'حدد ما إذا كان مؤشر الأداء الاستراتيجي يُحدّث مباشرة أو يُحتسب من نتائج مؤشرات الأداء التشغيلية المرتبطة.',
      'Approved strategic plans':                                      'الخطط الاستراتيجية المعتمدة',
      'Plans awaiting approval':                                       'الخطط بانتظار الاعتماد',
      'Department Weights':                                            'أوزان الإدارات',
      'Division Weights':                                              'أوزان القطاعات',
      'All Divisions':                                                 'جميع القطاعات',
      'Used Weight':                                                   'الوزن المستخدم',
      'Remaining Weight':                                              'الوزن المتبقي',
      'Review and adjust OPI weights across departments without opening each OPI.': 'راجع وعدّل أوزان مؤشرات الأداء التشغيلية عبر الإدارات دون فتح كل مؤشر.',
      'Review and adjust OPI weights across divisions without opening each OPI.': 'راجع وعدّل أوزان مؤشرات الأداء التشغيلية عبر القطاعات دون فتح كل مؤشر.',
      'OPI weights updated':                                           'تم تحديث أوزان مؤشرات الأداء التشغيلية',
      'Unable to save weights':                                        'تعذر حفظ الأوزان',
      'Already used':                                                  'المستخدم سابقًا',
      'Remaining':                                                     'المتبقي',
      'Owning Division':                                               'القطاع المالك',
      'Remaining division weight: {left}%':                            'الوزن المتبقي للقطاع: {left}٪',
      'The division groups the responsible and contributing departments; the remaining weight is the capacity available after existing OPI allocations.': 'يجمع القطاع الإدارة المسؤولة والإدارات المساهمة؛ ويمثل الوزن المتبقي السعة المتاحة بعد تخصيص أوزان مؤشرات الأداء التشغيلية الحالية.',
      'Responsible Department':                                        'الإدارة المسؤولة',
      'Select responsible department':                                 'اختر الإدارة المسؤولة',
      'Responsible Division':                                          'القطاع المسؤول',
      'Select responsible division':                                   'اختر القطاع المسؤول',
      'Division Weight Availability':                                  'الوزن المتاح للقطاع',
      'Champion':                                                       'الراعي',
      'Select a champion':                                              'اختر الراعي',
      'Responsible Champion':                                          'الراعي المسؤول',
      'Responsible':                                                   'المسؤولة',
      'Select responsible champion':                                   'اختر الراعي المسؤول',
      'This department enters the consolidated result; other departments review it.': 'تُدخل هذه الإدارة النتيجة المجمعة، وتراجعها الإدارات الأخرى.',
      'Link to a Strategic KPI':                                        'ربط بمؤشر أداء استراتيجي',
      'Contributing Departments & Weights':                            'الإدارات المساهمة وأوزانها',
      'Select contributing departments':                               'اختر الإدارات المساهمة',
      'Contributing Divisions & Weights':                              'القطاعات المساهمة وأوزانها',
      'Select contributing divisions':                                 'اختر القطاعات المساهمة',
      'Select a responsible division first':                           'اختر القطاع المسؤول أولاً',
      'Select a responsible division first.':                          'اختر القطاع المسؤول أولاً.',
      'No divisions are available':                                    'لا توجد قطاعات متاحة',
      'No contributing divisions are assigned.':                       'لم يتم تعيين قطاعات مساهمة.',
      'Select a responsible champion first':                           'اختر الراعي المسؤول أولاً',
      'Select a responsible champion to load its departments.':        'اختر الراعي المسؤول لعرض إدارات قطاعه.',
      'No departments are available in this division':                 'لا توجد إدارات متاحة في هذا القطاع',
      'OPI Code':                                                       'رمز مؤشر الأداء التشغيلي',
      'OPI Name':                                                       'اسم مؤشر الأداء التشغيلي',
      'Enter OPI name':                                                 'أدخل اسم مؤشر الأداء التشغيلي',
      'Operational KPI Card':                                           'بطاقة مؤشر الأداء التشغيلي',
      'Strategy Period':                                                'فترة الاستراتيجية',
      'Annual Targets':                                                'المستهدفات السنوية',
      'Annual Target':                                                 'المستهدف السنوي',
      'To be determined':                                              'يحدد لاحقًا',
      'Set by the responsible department':                             'تُحدد حسب الإدارة المسؤولة',
      'Annual targets appear once the strategy period is known.':      'تظهر المستهدفات السنوية بعد تحديد فترة الاستراتيجية.',
      // OKPI → OPI rename (client terminology)
      // Initiatives (INI-01 … INI-15)
      'Used by other OPIs {used}% · This OPI {mine}% · Remaining {left}%': 'مستخدم من مؤشرات أخرى {used}% · هذا المؤشر {mine}% · المتبقي {left}%',
      'Almost full: only {left}% of this department is left.': 'اقتربت من الامتلاء: تبقّى {left}% فقط من هذه الإدارة.',
      'Almost full: only {left}% of this division is left.': 'اقترب من الامتلاء: تبقّى {left}% فقط من هذا القطاع.',
      'Five-Year Strategy Roadmap': 'خارطة طريق الاستراتيجية الخمسية',
      'Strategic linkage & initiative completion': 'الارتباط الاستراتيجي وإنجاز المبادرات',
      'Show projects': 'عرض المشاريع',
      'Obj': 'هدف',
      'Outcome': 'النتائج',
      'Enablers': 'الممكنات',
      'Add Project': 'إضافة مشروع',
      'All projects are closed, so this initiative is closed automatically.': 'أُغلقت جميع المشاريع، لذا أُغلقت هذه المبادرة تلقائيًا.',
      'Applied': 'طُبّق',
      'Approver': 'المعتمِد',
      'Assigned when saved': 'يُعيَّن عند الحفظ',
      'Available: {n}%': 'المتاح: {n}%',
      'Budget & Risks': 'الميزانية والمخاطر',
      'Cancel Initiative': 'إلغاء المبادرة',
      'Confirm Cancellation': 'تأكيد الإلغاء',
      'Confirm Merge': 'تأكيد الدمج',
      'Cancelled / merged': 'ملغاة / مدمجة',
      'Change Type': 'نوع التغيير',
      'Change request saved as draft': 'حُفظ طلب التغيير كمسودة',
      'Current Result': 'النتيجة الحالية',
      'Current results saved': 'حُفظت النتائج الحالية',
      'Describe the initiative and the results it should deliver.': 'صِف المبادرة والنتائج التي يجب أن تحققها.',
      'Describe the intended results of the initiative': 'صِف النتائج المستهدفة للمبادرة',
      'Description & Expected Outcomes': 'الوصف والنتائج المتوقعة',
      'Enabler': 'الممكِّن',
      'Expected Budget': 'الميزانية المتوقعة',
      'Expected Budget — Maximum': 'الميزانية المتوقعة — الحد الأعلى',
      'Expected Budget — Minimum': 'الميزانية المتوقعة — الحد الأدنى',
      'Expected Outcome': 'النتيجة المتوقعة',
      'Expected Outcomes': 'النتائج المتوقعة',
      'Expected budget range, risks and supporting context.': 'نطاق الميزانية المتوقعة والمخاطر والسياق الداعم.',
      'Five-Year Roadmap': 'خارطة الطريق الخمسية',
      'Generated automatically as I-[Objective Number].[Sequence].': 'يُولَّد تلقائيًا بالصيغة I-[رقم الهدف].[التسلسل].',
      'Identity & Alignment': 'الهوية والمواءمة',
      'Impact if Not Accepted': 'الأثر في حال عدم القبول',
      'Initiative KPIs': 'مؤشرات أداء المبادرة',
      'Strategic KPI Linkage': 'ربط مؤشرات الأداء الاستراتيجية',
      'Linked to Strategic KPI': 'مرتبط بمؤشر أداء استراتيجي',
      'Standalone OPI': 'مؤشر أداء تشغيلي مستقل',
      'This OPI is not linked to a Strategic KPI': 'مؤشر الأداء التشغيلي هذا غير مرتبط بمؤشر أداء استراتيجي',
      'Select the Strategic KPIs linked to this initiative.': 'اختر مؤشرات الأداء الاستراتيجية المرتبطة بهذه المبادرة.',
      'Define the operational KPIs owned and updated by this initiative.': 'عرّف مؤشرات الأداء التشغيلية الخاصة بهذه المبادرة والتي يتم تحديثها من خلالها.',
      'Update the fixed operational KPIs for this initiative.': 'حدّث مؤشرات الأداء التشغيلية الثابتة لهذه المبادرة.',
      'Add Initiative KPI': 'إضافة مؤشر أداء للمبادرة',
      'KPI Name': 'اسم مؤشر الأداء',
      'Enter KPI name': 'أدخل اسم مؤشر الأداء',
      'Enabled': 'مفعّل',
      'Disabled': 'معطّل',
      'No initiative KPIs added yet.': 'لم تتم إضافة مؤشرات أداء للمبادرة بعد.',
      'Each initiative KPI requires a name and unit.': 'يتطلب كل مؤشر أداء للمبادرة اسمًا ووحدة قياس.',
      'Schedule Variance vs. Plan': 'انحراف الجدول الزمني عن الخطة',
      'Cost Variance vs. Approved Budget': 'انحراف التكلفة عن الميزانية المعتمدة',
      'Approved Deliverables Completed': 'المخرجات المعتمدة المكتملة',
      'Deliverables Accepted Without Rework': 'المخرجات المقبولة دون إعادة عمل',
      'Initiative List': 'قائمة المبادرات',
      'Initiative Performance': 'أداء المبادرة',
      'Initiative Views': 'عروض المبادرات',
      'Initiative–KPI Linkage': 'ربط المبادرات بالمؤشرات',
      'Select the Strategic KPIs linked to this initiative. Results are updated later on the initiative page.': 'اختر مؤشرات الأداء الاستراتيجية المرتبطة بهذه المبادرة. تُحدَّث النتائج لاحقًا من صفحة المبادرة.',
      'Linked KPIs': 'المؤشرات المرتبطة',
      'Merge Initiative': 'دمج المبادرة',
      'Merge into': 'الدمج في',
      'Request Merge': 'طلب دمج',
      'Request Cancellation': 'طلب إلغاء',
      'Raise a merge request for review': 'رفع طلب دمج للمراجعة',
      'Raise a cancellation request for review': 'رفع طلب إلغاء للمراجعة',
      'Select the initiative to merge into.': 'اختر المبادرة المراد الدمج فيها.',
      'This initiative has projects. Select the initiative they should move to before cancelling.': 'تحتوي هذه المبادرة على مشاريع. اختر المبادرة التي ستنتقل إليها قبل الإلغاء.',
      'This initiative has no projects, so a target is optional.': 'لا تحتوي هذه المبادرة على مشاريع، لذا فإن تحديد مبادرة مستهدفة اختياري.',
      'Nothing changes until the request is approved. On approval the initiative is merged and all of its projects move to the selected initiative.': 'لا يتغير شيء حتى تتم الموافقة على الطلب. عند الموافقة تُدمج المبادرة وتنتقل جميع مشاريعها إلى المبادرة المحددة.',
      'Nothing changes until the request is approved. Projects are never deleted — on approval they move to the initiative selected here.': 'لا يتغير شيء حتى تتم الموافقة على الطلب. لا تُحذف المشاريع أبدًا — عند الموافقة تنتقل إلى المبادرة المحددة هنا.',
      'The target initiative must belong to the same strategic plan.': 'يجب أن تنتمي المبادرة المستهدفة إلى الخطة الاستراتيجية نفسها.',
      'Select an active, approved initiative.': 'اختر مبادرة نشطة ومعتمدة.',
      'Merged': 'مدمجة',
      'Merged into': 'مدمجة في',
      'Milestone name': 'اسم المعلم',
      'Milestones Achieved': 'المعالم المتحققة',
      'Milestones after the change': 'المعالم بعد التغيير',
      'Move projects to': 'نقل المشاريع إلى',
      'Name the initiative and connect it to the strategy.': 'سمِّ المبادرة واربطها بالاستراتيجية.',
      'New Description': 'الوصف الجديد',
      'New End': 'النهاية الجديدة',
      'New Expected Outcomes': 'النتائج المتوقعة الجديدة',
      'New Initiative Name': 'الاسم الجديد للمبادرة',
      'New Start': 'البداية الجديدة',
      'No KPIs are linked to this initiative. Select Strategic KPIs in the initiative form (Initiative Measurement).': 'لا توجد مؤشرات مرتبطة بهذه المبادرة. اختر مؤشرات الأداء الاستراتيجية في نموذج المبادرة (قياس المبادرة).',
      'No KPIs linked yet.': 'لا توجد مؤشرات مرتبطة بعد.',
      'No change requests for this initiative yet.': 'لا توجد طلبات تغيير لهذه المبادرة بعد.',
      'No initiatives yet.': 'لا توجد مبادرات بعد.',
      'No projects yet. Add the projects that deliver this initiative; their weights must add up to 100%.': 'لا توجد مشاريع بعد. أضف المشاريع التي تنفذ هذه المبادرة؛ يجب أن يكون مجموع أوزانها 100%.',
      'Objective / Initiative': 'الهدف / المبادرة',
      'On approval, this initiative is merged and all its projects move to the initiative you select.': 'عند الاعتماد تُدمج هذه المبادرة وتنتقل جميع مشاريعها إلى المبادرة التي تختارها.',
      'All projects will move automatically to the selected initiative when the merge is confirmed.': 'ستنتقل جميع المشاريع تلقائيًا إلى المبادرة المحددة عند تأكيد الدمج.',
      'Projects are never deleted. Select the initiative they will move to before confirming cancellation.': 'لا يتم حذف المشاريع مطلقًا. اختر المبادرة التي ستنتقل إليها قبل تأكيد الإلغاء.',
      'Initiative merged successfully': 'تم دمج المبادرة بنجاح',
      'Initiative cancelled successfully': 'تم إلغاء المبادرة بنجاح',
      'Enter reason': 'أدخل السبب',
      'Unable to load initiatives': 'تعذر تحميل المبادرات',
      'Other initiatives': 'مبادرات أخرى',
      'Owner(s)': 'المالك (المالكون)',
      'Ownership & Timeline': 'الملكية والجدول الزمني',
      'Pillar / Objective / Initiative / Project': 'الركيزة / الهدف / المبادرة / المشروع',
      'Project Weight within the Initiative': 'وزن المشروع ضمن المبادرة',
      'Project Weights': 'أوزان المشاريع',
      'Project updated': 'تم تحديث المشروع',
      'Project weights in an initiative cannot exceed 100%.': 'لا يمكن أن يتجاوز مجموع أوزان المشاريع في المبادرة 100%.',
      'Reason is required': 'السبب مطلوب',
      'Request': 'الطلب',
      'Request a change to the initiative. Once approved, the change is applied automatically.': 'اطلب تغييرًا على المبادرة. بعد الاعتماد يُطبَّق التغيير تلقائيًا.',
      'Responsible Division': 'القطاع المسؤول',
      'Roadmap, list and KPI linkage of a strategy\'s initiatives.': 'خارطة الطريق والقائمة وربط المؤشرات لمبادرات الاستراتيجية.',
      'Save Results': 'حفظ النتائج',
      'Select a strategy to see its initiatives.': 'اختر استراتيجية لعرض مبادراتها.',
      'Select enabler': 'اختر الممكِّن',
      'Select initiative': 'اختر مبادرة',
      'Select strategy': 'اختر الاستراتيجية',
      'Select the end quarter and year': 'اختر ربع وسنة النهاية',
      'Select the responsible division': 'اختر القطاع المسؤول',
      'Select the start quarter and year': 'اختر ربع وسنة البداية',
      'Set the initiative start and end quarters to open its reporting periods.': 'حدد ربعي بداية ونهاية المبادرة لفتح فترات التقرير.',
      'Start / End Dates': 'تاريخا البداية والنهاية',
      'Stopped': 'متوقفة',
      'Strategic Linkage': 'الارتباط الاستراتيجي',
      'The end quarter must be on or after the start quarter.': 'يجب أن يكون ربع النهاية في ربع البداية أو بعده.',
      'The first objective sets the initiative code.': 'الهدف الأول يحدد رمز المبادرة.',
      'The maximum budget must be greater than or equal to the minimum budget.': 'يجب أن يكون الحد الأعلى للميزانية أكبر من الحد الأدنى أو مساويًا له.',
      'The strategy has no start and end dates, so no roadmap years can be shown.': 'لا تحتوي الاستراتيجية على تاريخي بداية ونهاية، لذا لا يمكن عرض سنوات خارطة الطريق.',
      'This initiative has no projects, so nothing needs to move.': 'لا توجد مشاريع في هذه المبادرة، لذا لا حاجة لنقل شيء.',
      'This initiative has {n} project(s). Select the initiative they will move to before cancelling.': 'تحتوي هذه المبادرة على {n} مشروع. اختر المبادرة التي ستنتقل إليها قبل الإلغاء.',
      'This initiative has been cancelled. Its projects were retained and transferred to the selected initiative.': 'أُلغيت هذه المبادرة. تم الاحتفاظ بمشاريعها ونقلها إلى المبادرة المحددة.',
      'This initiative is {status}; its projects were moved to another initiative.': 'هذه المبادرة {status}؛ نُقلت مشاريعها إلى مبادرة أخرى.',
      'This pillar has no enablers.': 'لا توجد ممكِّنات لهذه الركيزة.',
      'Update the Current Result for each reporting period. Periods follow each KPI\'s reporting frequency within the initiative\'s start and end quarters.': 'حدّث النتيجة الحالية لكل فترة تقرير. تتبع الفترات دورية كل مؤشر ضمن ربعي بداية ونهاية المبادرة.',
      'Updated by': 'حدّثه',
      'Weights are complete': 'اكتملت الأوزان',
      'What do you want to change?': 'ما الذي تريد تغييره؟',
      'Who owns the initiative and when it runs, by quarter.': 'مالك المبادرة وفترة تنفيذها بالأرباع.',
      'Years come from the strategy\'s start and end dates.': 'تأتي السنوات من تاريخي بداية ونهاية الاستراتيجية.',
      'e.g. 1,000,000': 'مثال: 1,000,000',
      'e.g. 500,000': 'مثال: 500,000',
      '{n} initiatives': '{n} مبادرات',
      '{n} milestone(s)': '{n} معالم',
      '{n}% still to assign': 'متبقٍ {n}% للتوزيع',
      'Enter a clear, outcome-focused initiative title': 'أدخل عنوانًا واضحًا للمبادرة يركز على النتائج',
      'Enter approver name or role': 'أدخل اسم المعتمِد أو دوره',
      'Enter owner names or roles, separated by commas': 'أدخل أسماء المالكين أو أدوارهم مفصولة بفواصل',
      'Describe each risk or constraint and the planned mitigation': 'صِف كل خطر أو قيد وخطة التخفيف منه',
      'Add any supporting details from the initiative card': 'أضف أي تفاصيل داعمة من بطاقة المبادرة',
      'Select KPIs measured by this initiative': 'اختر المؤشرات التي تقيسها هذه المبادرة',
      'Select a pillar first': 'اختر ركيزة أولًا',
      'Select linked objectives': 'اختر الأهداف المرتبطة',
      'Select related initiatives': 'اختر المبادرات المرتبطة',
      'Select effective date': 'اختر تاريخ السريان',
      'Linked KPI(s)': 'المؤشرات المرتبطة',
      'Linked Objective(s)': 'الأهداف المرتبطة',
      'Interdependencies': 'الترابطات',
      'Initiative Title': 'عنوان المبادرة',
      'Initiative Owner(s)': 'مالك (مالكو) المبادرة',
      'Initiative Measurement': 'قياس المبادرة',
      'Risk & Constraints and Mitigation': 'المخاطر والقيود وخطط التخفيف',
      'This strategy has no start or end date yet. Set them on the strategy so the KPI can inherit its period and target years.': 'لا تحتوي هذه الاستراتيجية على تاريخ بداية أو نهاية بعد. حدّدهما في الاستراتيجية ليرث المؤشر فترته وسنوات مستهدفاته.',
      'Start date, end date and target years are inherited automatically from this strategy.': 'تاريخ البداية وتاريخ النهاية وسنوات المستهدفات تُورث تلقائيًا من هذه الاستراتيجية.',
      'Select an objective: the KPI inherits its strategy, dates and target years from it.': 'اختر هدفًا: يرث المؤشر منه الاستراتيجية والتواريخ وسنوات المستهدفات.',
      '{n} OPI(s) are linked to this KPI. Unlink them before switching to direct manual entry.': 'يرتبط {n} من مؤشرات الأداء التشغيلية بهذا المؤشر. ألغِ ربطها قبل التحويل إلى الإدخال اليدوي المباشر.',
      'Results come from linked OPIs': 'النتائج تأتي من مؤشرات الأداء التشغيلية المرتبطة',
      'Each period\'s result is built from the approved results of the linked OPIs: amounts (Bln SAR) are added up, other units are averaged. Manual result entry is disabled for this KPI.': 'تُبنى نتيجة كل فترة من النتائج المعتمدة لمؤشرات الأداء التشغيلية المرتبطة: تُجمع المبالغ (مليار ريال) ويُحسب متوسط الوحدات الأخرى. الإدخال اليدوي للنتائج معطل لهذا المؤشر.',
      'Link OPIs from the OPIs tab. A linked OPI must use this KPI\'s unit and reporting frequency.': 'اربط مؤشرات الأداء التشغيلية من تبويب المؤشرات التشغيلية. يجب أن يستخدم المؤشر المرتبط نفس وحدة ودورية تقرير هذا المؤشر.',
      'No OPIs are linked yet.': 'لا توجد مؤشرات أداء تشغيلية مرتبطة بعد.',
      'Results are entered directly': 'تُدخل النتائج مباشرة',
      'Authorized users enter each period\'s result on the Update KPIs page, and it goes through the normal review.': 'يُدخل المستخدمون المخولون نتيجة كل فترة من صفحة تحديث المؤشرات، وتمر بالمراجعة المعتادة.',
      'Linked OPIs report at this frequency, so it is locked while they are linked.': 'تُرفع مؤشرات الأداء التشغيلية المرتبطة بهذه الدورية، لذا تبقى مقفلة طوال ارتباطها.',
      'From OPIs': 'من المؤشرات التشغيلية',
      'Calculated from linked OPIs': 'محتسب من مؤشرات الأداء التشغيلية المرتبطة',
      'Results for this KPI are built from the approved results of its linked OPIs, so they cannot be entered or edited here. Update the OPIs instead.': 'تُبنى نتائج هذا المؤشر من النتائج المعتمدة لمؤشرات الأداء التشغيلية المرتبطة، لذا لا يمكن إدخالها أو تعديلها هنا. حدّث المؤشرات التشغيلية بدلًا من ذلك.',
      'Set by the linked Strategic KPI.': 'تُحدد من مؤشر الأداء الاستراتيجي المرتبط.',
      'Linked OPIs': 'مؤشرات الأداء التشغيلية المرتبطة',
      'Each period\'s result is built from the approved results of these OPIs: amounts (Bln SAR) are added up, other units are averaged.': 'تُبنى نتيجة كل فترة من النتائج المعتمدة لهذه المؤشرات التشغيلية: تُجمع المبالغ (مليار ريال) ويُحسب متوسط الوحدات الأخرى.',
      'This KPI is calculated from its linked OPIs, so results cannot be entered or reviewed manually.': 'يُحتسب هذا المؤشر من مؤشرات الأداء التشغيلية المرتبطة، لذا لا يمكن إدخال النتائج أو مراجعتها يدويًا.',
      'OPI Scoring Settings': 'إعدادات احتساب مؤشرات الأداء التشغيلية',
      'OPI Scoring': 'احتساب مؤشرات الأداء التشغيلية',
      'Scoring Settings': 'إعدادات الاحتساب',
      'Achieved Threshold': 'حد التحقق',
      'Partially Achieved Threshold': 'حد التحقق الجزئي',
      'Maximum Score': 'الحد الأقصى للدرجة',
      'Current setting': 'الإعداد الحالي',
      'System cap': 'الحد الأعلى للنظام',
      'Scoring Thresholds': 'حدود الاحتساب',
      'Set the score at which each OPI status begins.': 'حدد الدرجة التي تبدأ عندها كل حالة لمؤشر الأداء التشغيلي.',
      'Live preview of the statuses produced by the current thresholds.': 'معاينة مباشرة للحالات الناتجة عن الحدود الحالية.',
      'Thresholds that turn a period score into its status. Changes apply to every OPI and restate saved periods.': 'الحدود التي تحوّل درجة الفترة إلى حالتها. تُطبق التغييرات على جميع المؤشرات التشغيلية وتُعيد احتساب الفترات المحفوظة.',
      'Only SMO or an administrator can change these thresholds.': 'يمكن لمكتب إدارة الاستراتيجية أو المسؤول فقط تغيير هذه الحدود.',
      'Achieved from': 'متحقق من',
      'Partially Achieved from': 'متحقق جزئيًا من',
      'Scores at or above this are Achieved.': 'الدرجات المساوية لهذا الحد أو أعلى تُعد متحققة.',
      'Must be lower than the Achieved threshold.': 'يجب أن يكون أقل من حد التحقق.',
      'Status bands': 'نطاقات الحالة',
      'below': 'أقل من',
      'Active period with no Actual yet': 'فترة نشطة بدون قيمة فعلية بعد',
      'Scores are capped at {cap}%. Inactive periods show their state instead of a status.': 'الحد الأقصى للدرجات {cap}%. تعرض الفترات غير النشطة حالتها بدلًا من حالة الأداء.',
      'Save Thresholds': 'حفظ الحدود',
      'Enter the Achieved threshold': 'أدخل حد التحقق',
      'Enter the Partially Achieved threshold': 'أدخل حد التحقق الجزئي',
      'The Partially Achieved threshold must be lower than the Achieved threshold.': 'يجب أن يكون حد التحقق الجزئي أقل من حد التحقق.',
      'OPI thresholds updated': 'تم تحديث حدود مؤشرات الأداء التشغيلية',
      'Unable to load OPI thresholds': 'تعذر تحميل حدود مؤشرات الأداء التشغيلية',
      'Unable to save OPI thresholds': 'تعذر حفظ حدود مؤشرات الأداء التشغيلية',
      'Year to date from approved periods. Scores are capped at 120%; Achieved from {achieved}%, Partially Achieved from {partial}%.': 'من بداية السنة حتى تاريخه بناءً على الفترات المعتمدة. الحد الأقصى للدرجة 120%؛ متحقق من {achieved}%، ومتحقق جزئيًا من {partial}%.',
      'Annual Summary': 'الملخص السنوي',
      'Annual Adj Wt': 'الوزن المعدل السنوي',
      'Annual Wtd Score': 'الدرجة الموزونة السنوية',
      'Adj Wtd': 'الوزن المعدل',
      'Wtd Score': 'الدرجة الموزونة',
      'Waiting for SMO target': 'بانتظار مستهدف مكتب إدارة الاستراتيجية',
      'Analysis & Files': 'التحليل والملفات',
      'OPI Update Details': 'تفاصيل تحديث مؤشر الأداء التشغيلي',
      'Division indicators': 'مؤشرات القطاع',
      'Scoring': 'الاحتساب',
      'Weights': 'الأوزان',
      'One consolidated OPI value': 'قيمة موحدة واحدة لمؤشر الأداء التشغيلي',
      "Department weights allocate the OPI within each department's scorecard. They are not used to sum or average the reported value.": 'توزع أوزان الإدارات المؤشر ضمن بطاقة أداء كل إدارة، ولا تُستخدم لجمع القيمة المُبلغ عنها أو حساب متوسطها.',
      "Division weights allocate the OPI within each division's scorecard. They are not used to sum or average the reported value.": 'توزع أوزان القطاعات المؤشر ضمن بطاقة أداء كل قطاع، ولا تُستخدم لجمع القيمة المُبلغ عنها أو حساب متوسطها.',
      'Consolidated value entry': 'إدخال القيمة الموحدة',
      'Review-only access': 'صلاحية المراجعة فقط',
      'Enter one result for all contributing departments. Do not enter department-level values.': 'أدخل نتيجة موحدة واحدة لجميع الإدارات المساهمة، ولا تدخل قيماً منفصلة لكل إدارة.',
      'Enter one result for all contributing divisions. Do not enter division-level values.': 'أدخل نتيجة موحدة واحدة لجميع القطاعات المساهمة، ولا تدخل قيماً منفصلة لكل قطاع.',
      'The Performance Ambassador for {department} enters the single consolidated result. It then goes to the Head of Division and Performance Management for approval.': 'يدخل سفير الأداء في {department} النتيجة الموحدة، ثم تُرسل إلى رئيس القطاع وإدارة الأداء للاعتماد.',
      'A Performance Ambassador in {division} enters the single consolidated result. It then goes to the Head of Division and Performance Management for approval.': 'يدخل سفير أداء في {division} النتيجة الموحدة، ثم تُرسل إلى رئيس القطاع وإدارة الأداء للاعتماد.',
      'Actual result, analysis, supporting files, and future forecast are required before submission.': 'النتيجة الفعلية والتحليل والملفات الداعمة والتوقع المستقبلي مطلوبة قبل الإرسال.',
      'Consolidated Actual': 'النتيجة الفعلية الموحدة',
      'Returned for correction': 'أُعيد للتصحيح',
      'Contributing department review': 'مراجعة الإدارة المساهمة',
      'Head of Division review': 'مراجعة رئيس القطاع',
      'Performance Management review': 'مراجعة إدارة الأداء',
      'Not started': 'لم يبدأ',
      'Only Strategic KPIs whose value source is OPI are available.': 'تظهر فقط مؤشرات الأداء الاستراتيجية التي مصدر قيمتها مؤشر أداء تشغيلي.',
      'OPI update saved': 'تم حفظ تحديث مؤشر الأداء التشغيلي',
      'Unassigned OPIs': 'مؤشرات الأداء التشغيلية غير المسندة',
      'Assign initiative projects and OPIs to the department that will run them. Once sent, the department sees them in its own Annual Business Plan.': 'أسند مشاريع المبادرات ومؤشرات الأداء التشغيلية إلى الإدارة التي ستنفذها. بعد الإرسال، تظهر للإدارة في خطة أعمالها السنوية.',
      'No unassigned OPIs': 'لا توجد مؤشرات أداء تشغيلية غير مسندة',
      'OPIs': 'مؤشرات الأداء التشغيلية',
      'Business Demand': 'طلب الأعمال',
      'Approved OPI': 'مؤشر أداء تشغيلي معتمد',
      'Select an approved OPI': 'اختر مؤشر أداء تشغيلي معتمد',
      'No approved OPIs are available': 'لا توجد مؤشرات أداء تشغيلية معتمدة متاحة',
      'Approved OPI is required': 'يجب اختيار مؤشر أداء تشغيلي معتمد',
      'The logged-in Division Head is assigned automatically.': 'يتم تعيين رئيس القطاع المسجل دخوله تلقائيًا.',
      'Add OPI': 'إضافة مؤشر تشغيلي',
      'Total OPIs': 'إجمالي المؤشرات التشغيلية',
      'Search OPIs...': 'ابحث في المؤشرات التشغيلية...',
      'No OPIs match your filters': 'لا توجد مؤشرات تشغيلية مطابقة للتصفية',
      'No OPIs Yet': 'لا توجد مؤشرات أداء تشغيلية بعد',
      'No OPIs Awaiting Approval': 'لا توجد مؤشرات أداء تشغيلية بانتظار الموافقة',
      'There are no OPIs assigned to your division for review.': 'لا توجد مؤشرات أداء تشغيلية مسندة إلى قطاعك للمراجعة.',
      'Create OPI': 'إنشاء مؤشر تشغيلي',
      'OPI': 'مؤشر الأداء التشغيلي',
      'Edit OPI': 'تعديل مؤشر الأداء التشغيلي',
      'OPI Card': 'بطاقة مؤشر الأداء التشغيلي',
      'Enter OPI Result': 'إدخال نتيجة المؤشر التشغيلي',
      'Review OPI Update': 'مراجعة تحديث المؤشر التشغيلي',
      'Review OPI': 'مراجعة مؤشر الأداء التشغيلي',
      'Are you sure you want to delete this OPI?': 'هل أنت متأكد من حذف هذا المؤشر التشغيلي؟',
      'OPI updated': 'تم تحديث مؤشر الأداء التشغيلي',
      'OPI created': 'تم إنشاء مؤشر الأداء التشغيلي',
      'OPI deleted': 'تم حذف مؤشر الأداء التشغيلي',
      '1 OPI in this department': 'مؤشر تشغيلي واحد في هذه الإدارة',
      '{n} OPIs in this department': '{n} مؤشرات تشغيلية في هذه الإدارة',
      'OPIs to assign': 'مؤشرات الأداء التشغيلية للتعيين',
      'Specify whether this strategic KPI is updated directly or calculated from linked OPI results.': 'حدد ما إذا كان مؤشر الأداء الاستراتيجي يُحدّث مباشرة أو يُحتسب من نتائج مؤشرات الأداء التشغيلية المرتبطة.',
      'Calculated from Linked OPIs': 'يُحتسب من مؤشرات الأداء التشغيلية المرتبطة',
      'The result is calculated automatically from linked OPIs.': 'تُحتسب النتيجة تلقائيًا من مؤشرات الأداء التشغيلية المرتبطة.',
      'Projects and OPIs sent to your department will appear here': 'ستظهر هنا المشاريع والمؤشرات التشغيلية المُرسلة إلى إدارتك',
      'Select an OPI': 'اختر مؤشر أداء تشغيلي',
      'No OPIs in this department yet': 'لا توجد مؤشرات تشغيلية في هذه الإدارة بعد',
      '{projects} projects and {kpis} OPIs sent to the SMO': 'تم إرسال {projects} مشاريع و{kpis} مؤشرات تشغيلية إلى مكتب إدارة الاستراتيجية',
      'Latest Target': 'آخر مستهدف',
      'Latest Actual': 'آخر قيمة فعلية',
      'Targets and results are limited to the range of the selected unit.': 'تقتصر المستهدفات والنتائج على نطاق الوحدة المحددة.',
      'Calculation methodology is required': 'منهجية الاحتساب مطلوبة',
      'This year is TBD. Enter the annual target to replace it.': 'مستهدف هذه السنة يحدد لاحقًا. أدخل المستهدف السنوي لاستبداله.',
      'Select a unit': 'اختر وحدة',
      'One or more values do not match the selected unit range.': 'قيمة واحدة أو أكثر لا تطابق نطاق الوحدة المحددة.',
      'TBD':                                                           'يحدد لاحقًا',
      'Period Targets':                                                'مستهدفات الفترات',
      'Reporting Period Targets':                                      'مستهدفات فترات التقرير',
      'Save Targets':                                                  'حفظ المستهدفات',
      'Reporting-period targets saved':                                'تم حفظ مستهدفات فترات التقرير',
      'OPI Updates':                                                   'تحديثات المؤشر التشغيلي',
      'KPI Updates':                                                   'تحديثات مؤشر الأداء',
      'Unable to save targets':                                        'تعذر حفظ المستهدفات',
      'Set the measurement frequency on the KPI before entering period targets.': 'حدد دورية القياس للمؤشر قبل إدخال مستهدفات الفترات.',
      'Provide exactly one target for each reporting period.':         'أدخل مستهدفًا واحدًا لكل فترة تقرير.',
      'The year must be within the strategy period.':                  'يجب أن تكون السنة ضمن فترة الاستراتيجية.',
      'Every period target must be a number.':                         'يجب أن يكون كل مستهدف فترة رقمًا.',
      'Only Strategy Management Office can set reporting-period targets': 'مكتب إدارة الاستراتيجية فقط يمكنه تحديد مستهدفات فترات التقرير',
      'Approval Tracking':                                             'متابعة الاعتمادات',
      'OPI Approval Workflow Tracking':                                'متابعة مسار اعتماد مؤشرات الأداء التشغيلية',
      'Review the current approval stage of every OPI reporting period on one screen.': 'راجع مرحلة الاعتماد الحالية لكل فترة تقرير لجميع مؤشرات الأداء التشغيلية في شاشة واحدة.',
      'All Approval Stages':                                           'جميع مراحل الاعتماد',
      'Current Approval Stage':                                        'مرحلة الاعتماد الحالية',
      'Waiting On':                                                    'بانتظار',
      'Contributor Reviews':                                           'مراجعات الإدارات المساهمة',
      'Submitted At':                                                  'تاريخ الإرسال',
      'Contributing Departments':                                      'الإدارات المساهمة',
      'Performance Ambassador':                                        'سفير الأداء',
      'Head of Division':                                              'رئيس القطاع',
      'Performance Management':                                        'إدارة الأداء',
      'Complete':                                                      'مكتمل',
      'Not started':                                                   'لم يبدأ',
      'No OPI reporting periods match the current filters.':           'لا توجد فترات تقرير لمؤشرات الأداء التشغيلية تطابق عوامل التصفية الحالية.',
      'Operational KPI Updates':                                       'تحديثات مؤشر الأداء التشغيلي',
      'Enter Operational KPI Result':                                  'إدخال نتيجة المؤشر التشغيلي',
      'Actual Result':                                                 'النتيجة الفعلية',
      'Relative Analysis':                                             'التحليل النسبي',
      'Future Forecast':                                               'التوقعات المستقبلية',
      'Annual Score':                                                  'الدرجة السنوية',
      'Annual Adjusted Weight':                                        'الوزن المعدل السنوي',
      'Annual Weighted Score':                                         'الدرجة المرجحة السنوية',
      'Annual Status':                                                 'الحالة السنوية',
      'Weighted Score':                                                'الدرجة المرجحة',
      'Adjusted Weight':                                               'الوزن المعدل',
      'Reported':                                                      'المبلغ عنه',
      'State':                                                         'الحالة',
      'Workflow':                                                      'سير الاعتماد',
      'Enter Update':                                                  'إدخال التحديث',
      'Save Draft':                                                    'حفظ المسودة',
      'Review Operational KPI Update':                                 'مراجعة تحديث المؤشر التشغيلي',
      'Review comment':                                                'تعليق المراجعة',
      'Supporting Files':                                              'الملفات الداعمة',
      'Up to 10 MB per file':                                          'حتى ١٠ ميغابايت لكل ملف',
      'Active':                                                        'نشط',
      'Inactive':                                                      'غير نشط',
      'division_review':                                               'مراجعة رئيس القطاع',
      'department_review':                                             'مراجعة الإدارات المساهمة',
      'performance_review':                                            'مراجعة إدارة الأداء',
      'returned':                                                      'معاد للتصحيح',
      'Select a strategy':                                             'اختر استراتيجية',
      'Select the linked Strategic KPI':                               'اختر مؤشر الأداء الاستراتيجي المرتبط',
      'Include the responsible department':                            'أدرج الإدارة المسؤولة',
      'Department weights cannot exceed 100%':                         'لا يمكن أن تتجاوز أوزان الإدارة ١٠٠٪',
      'Include the responsible division':                              'أدرج القطاع المسؤول',
      'Division weights cannot exceed 100%':                           'لا يمكن أن تتجاوز أوزان القطاع ١٠٠٪',
      'Enter an annual target or TBD for each OPI year':                'أدخل مستهدفًا سنويًا أو حدد لاحقًا لكل سنة من فترة المؤشر التشغيلي',
      'Generated automatically when saved':                            'يُنشأ تلقائيًا عند الحفظ',
      'The system generates this code automatically.':                 'يُنشئ النظام هذا الرمز تلقائيًا.',
      'Calculation Methodology':                                       'منهجية الاحتساب',
      'Reporting Frequency':                                           'دورية التقرير',
      'Not Due Yet':                                                   'لم يحن موعده بعد',
      'OPI Level':                                                     'مستوى مؤشر الأداء التشغيلي',
      'OPI Level & Type':                                              'مستوى ونوع مؤشر الأداء التشغيلي',
      'OPI Type':                                                      'نوع مؤشر الأداء التشغيلي',
      'Data Collection Method':                                        'طريقة جمع البيانات',
      'Data Source and Collection':                                    'مصدر البيانات وجمعها',
      'Billion SAR':                                                   'مليار ريال سعودي',
      'Bln SAR':                                                       'مليار ريال سعودي',
      'Index Score':                                                   'درجة المؤشر',
      'Ratio':                                                         'نسبة',
      'Linked Strategic KPI':                                          'مؤشر الأداء الاستراتيجي المرتبط',
      'Strategy Start Date':                                           'تاريخ بدء الاستراتيجية',
      'Strategy End Date':                                             'تاريخ انتهاء الاستراتيجية',
      'Automatically inherited from the selected strategy.':          'يُورث تلقائيًا من الاستراتيجية المحددة.',
      'Approved Items':                                                'العناصر المعتمدة',
      'Approved Annual Plan Items':                                    'عناصر الخطة السنوية المعتمدة',
      'Automatically moved here after CEO approval':                   'تُنقل إلى هنا تلقائيًا بعد اعتماد الرئيس التنفيذي',
      'Department Activities':                                        'أنشطة الإدارات',
      'No approved items yet':                                        'لا توجد عناصر معتمدة بعد',
      'Failed to load approved annual plan items':                     'فشل تحميل عناصر الخطة السنوية المعتمدة',
      'Division Plans':                                                'خطط القطاعات',
      'Annual Business Plan':                                          'خطة الأعمال السنوية',
      'User Type':                                                     'نوع المستخدم',
      'Filter by type':                                                'تصفية حسب النوع',
      'Execution Mode': 'طريقة التنفيذ',
      'Priority & Budget': 'الأولوية والميزانية',
      'The project is created for your department as a draft; the rest of its details are filled in on the project itself.': 'يُنشأ المشروع لإدارتك كمسودة، وتُستكمل بقية التفاصيل داخل المشروع نفسه.',
      'Sent to Departments': 'المُرسل إلى الإدارات',
      'My Department': 'إدارتي',
      'No projects sent to this department yet': 'لم تُرسل أي مشاريع إلى هذه الإدارة بعد',
      'No OKPIs sent to this department yet': 'لم تُرسل أي مؤشرات تشغيلية إلى هذه الإدارة بعد',
      'Sent to My Department': 'المُرسل إلى إدارتي',
      'No projects sent to your department yet': 'لم تُرسل أي مشاريع إلى إدارتك بعد',
      'No OKPIs sent to your department yet': 'لم تُرسل أي مؤشرات تشغيلية إلى إدارتك بعد',
      'Send to Department': 'إرسال إلى الإدارة',
      'Sent to Department': 'تم الإرسال إلى الإدارة',
      'Items sent to the department': 'تم إرسال العناصر إلى الإدارة',
      'Failed to send the items to the department': 'فشل إرسال العناصر إلى الإدارة',
      'Department items': 'عناصر الإدارة',
      'Search by name or code...': 'ابحث بالاسم أو الرمز...',
      'Select all on page': 'تحديد كل ما في الصفحة',
      'Clear selection': 'إلغاء التحديد',
      'Select {name}': 'تحديد {name}',
      'Nothing matches your search': 'لا توجد نتائج مطابقة',
      '{n} selected': '{n} محددة',
      'No initiative': 'بدون مبادرة',
      '{n} OKPIs': '{n} مؤشرات تشغيلية',
      '{n} OKPIs in this department': '{n} مؤشرات تشغيلية في هذه الإدارة',
      '1 OKPI in this department': 'مؤشر تشغيلي واحد في هذه الإدارة',
      'Review Operation KPI': 'مراجعة مؤشر الأداء التشغيلي',
      'Required when rejecting': 'مطلوب عند الرفض',
      'OKPIs': 'مؤشرات الأداء التشغيلية',
      'Operation KPIs': 'مؤشرات الأداء التشغيلية',
      'Operational indicators owned by a department': 'مؤشرات تشغيلية تتبع إدارة معينة',
      'Add OKPI': 'إضافة مؤشر تشغيلي',
      'Create OKPI': 'إنشاء مؤشر تشغيلي',
      'Total OKPIs': 'إجمالي المؤشرات التشغيلية',
      'Search OKPIs...': 'ابحث في المؤشرات التشغيلية...',
      'No OKPIs match your filters': 'لا توجد مؤشرات تشغيلية مطابقة للتصفية',
      'No Operation KPIs Yet': 'لا توجد مؤشرات أداء تشغيلية بعد',
      'Add operational indicators and assign them to a department': 'أضف المؤشرات التشغيلية واربطها بإدارة',
      'Add Operation KPI': 'إضافة مؤشر أداء تشغيلي',
      'Edit Operation KPI': 'تعديل مؤشر الأداء التشغيلي',
      'Reject Operation KPI': 'رفض مؤشر الأداء التشغيلي',
      'Operation KPI created': 'تم إنشاء مؤشر الأداء التشغيلي',
      'Operation KPI updated': 'تم تحديث مؤشر الأداء التشغيلي',
      'Operation KPI deleted': 'تم حذف مؤشر الأداء التشغيلي',
      'Are you sure you want to delete this OKPI?': 'هل أنت متأكد من حذف هذا المؤشر التشغيلي؟',
      'Failed to load operation KPIs': 'فشل تحميل مؤشرات الأداء التشغيلية',
      'Failed to save the operation KPI': 'فشل حفظ مؤشر الأداء التشغيلي',
      'Failed to submit the operation KPI': 'فشل إرسال مؤشر الأداء التشغيلي',
      'Failed to review the operation KPI': 'فشل مراجعة مؤشر الأداء التشغيلي',
      'Failed to delete the operation KPI': 'فشل حذف مؤشر الأداء التشغيلي',
      'Operation KPI submitted for approval': 'تم إرسال مؤشر الأداء التشغيلي للاعتماد',
      'Timeline & Targets': 'الجدول الزمني والمستهدفات',
      'Data & Governance': 'البيانات والحوكمة',
      'KPI Level': 'مستوى المؤشر',
      'KPI Types': 'أنواع المؤشر',
      'Dimension': 'البعد',
      'Review Period': 'دورة المراجعة',
      'Cumulative': 'تراكمي',
      'Requires Attachments': 'يتطلب مرفقات',
      'Data Available': 'البيانات متاحة',
      'Effective Date': 'تاريخ السريان',
      'Baseline Year': 'سنة خط الأساس',
      'Data Owner': 'مالك البيانات',
      'Card Approver': 'معتمد البطاقة',
      'Data Sources': 'مصادر البيانات',
      'Collection Methods': 'طرق الجمع',
      'Limitations': 'القيود',
      'Comments & Supporting Evidence': 'الملاحظات والأدلة الداعمة',
      'Type and press Enter': 'اكتب ثم اضغط Enter',
      'Explain what needs to change': 'وضّح ما الذي يجب تعديله',
      'e.g. 2026-2028': 'مثال: 2026-2028',
      '1 initiative project in this department': 'مشروع مبادرة واحد في هذه الإدارة',
      'Show More': 'عرض المزيد',
      '{n} initiative projects in this department': '{n} مشاريع مبادرات في هذه الإدارة',
      'Search departments or projects...': 'ابحث في الإدارات أو المشاريع...',
      'Search divisions, departments or activities...': 'ابحث في القطاعات أو الإدارات أو الأنشطة...',
      'Filter departments': 'تصفية الإدارات',
      'Filter divisions': 'تصفية القطاعات',
      'With Projects': 'بها مشاريع',
      'Without Projects': 'بدون مشاريع',
      'With Work': 'بها أعمال',
      'Without Work': 'بدون أعمال',
      'No departments yet': 'لا توجد إدارات بعد',
      'No divisions found': 'لم يتم العثور على قطاعات',
      'No divisions yet': 'لا توجد قطاعات بعد',
      'Divisions appear here with the work from their departments': 'تظهر القطاعات هنا مع أعمال الإدارات التابعة لها',
      'Failed to load divisions': 'فشل تحميل القطاعات',
      '{n} departments': '{n} إدارات',
      'Departments appear here with the initiative projects that belong to them': 'تظهر الإدارات هنا مع مشاريع المبادرات التابعة لها',
      'No initiative projects yet': 'لا توجد مشاريع مبادرات بعد',
      'Show all {n} projects': 'عرض كل المشاريع ({n})',
      'Without department': 'بدون إدارة',
      'Initiative Projects': 'مشاريع المبادرة',
      'Create the projects that deliver this initiative.': 'أنشئ المشاريع التي تنفذ هذه المبادرة.',
      'Describe the project': 'صف المشروع',
      'Describe the activity': 'صف النشاط',
      'Project created': 'تم إنشاء المشروع',
      'Activity created': 'تم إنشاء النشاط',
      'Edit Activity': 'تعديل النشاط',
      'Save as Active': 'حفظ كنشط',
      'Activity saved as draft': 'تم حفظ النشاط كمسودة',
      'Activity saved as active': 'تم حفظ النشاط كنشط',
      'Reviewer Comment': 'تعليق المراجع',
      'Managed as a defined project': 'يُدار كمشروع محدد',
      'Recurring operational activity': 'نشاط تشغيلي متكرر',
      'Approved Plan Items': 'عناصر الخطة المعتمدة',
      'BAU Activities': 'أنشطة العمل المعتاد',
      'Search divisions or plan items...': 'ابحث في القطاعات أو عناصر الخطة...',
      'No approved Annual Plan items found': 'لا توجد عناصر معتمدة في الخطة السنوية',
      'Approved projects, OPIs, and classified activities will appear here by division.': 'ستظهر المشاريع ومؤشرات الأداء التشغيلية والأنشطة المصنفة المعتمدة هنا حسب القطاع.',
      'Project Activities': 'أنشطة المشاريع',
      'Annual Projects and OPIs': 'المشاريع السنوية ومؤشرات الأداء التشغيلية',
      'Approved projects, project activities, and OPIs': 'المشاريع وأنشطة المشاريع ومؤشرات الأداء التشغيلية المعتمدة',
      'Annual Projects and Activities': 'المشاريع والأنشطة السنوية',
      'Approved initiative projects and department project activities': 'مشاريع المبادرات وأنشطة مشاريع الإدارات المعتمدة',
      'OPIs by Division': 'مؤشرات الأداء التشغيلية حسب القطاع',
      'Approved operational performance indicators, grouped by the division that owns them': 'مؤشرات الأداء التشغيلية المعتمدة، مصنفة حسب القطاع المالك',
      'On track': 'على المسار',
      'Need attention': 'تحتاج إلى متابعة',
      'Behind target': 'دون المستهدف',
      'Not reported': 'لم يتم الإبلاغ',
      'Filter OPIs': 'تصفية مؤشرات الأداء التشغيلية',
      '{n} OPIs': '{n} مؤشرات تشغيلية',
      'over 100%': 'تجاوز 100٪',
      'under 100%': 'أقل من 100٪',
      'No OPIs match this filter': 'لا توجد مؤشرات تشغيلية تطابق هذا التصفية',
      'No approved OPIs': 'لا توجد مؤشرات أداء تشغيلية معتمدة',
      'No approved projects or activities': 'لا توجد مشاريع أو أنشطة معتمدة',
      'Filter by change type': 'تصفية حسب نوع التغيير',
      'Update the approved Master Plan': 'تحديث الخطة الرئيسية المعتمدة',
      'Choose the change first. The form will show only the information needed for that request.': 'اختر نوع التغيير أولاً، وسيعرض النموذج البيانات المطلوبة فقط.',
      'Choose change type': 'اختر نوع التغيير',
      'Search divisions...': 'ابحث في القطاعات...',
      'Enter the proposed project name': 'أدخل اسم المشروع المقترح',
      'Describe the proposed project': 'صف المشروع المقترح',
      'Enter a clear request title': 'أدخل عنواناً واضحاً للطلب',
      'Explain why this change is needed': 'وضح سبب الحاجة إلى هذا التغيير',
      'Select start quarter': 'اختر ربع البداية',
      'Select end quarter': 'اختر ربع النهاية',
      'Describe the impact if this request is not approved': 'صف الأثر في حال عدم اعتماد هذا الطلب',
      'Review and manage approved projects in this plan': 'راجع المشاريع المعتمدة في هذه الخطة وأدرها',
      'Raise and track controlled changes to the plan': 'ارفع التغييرات المنضبطة على الخطة وتابعها',
      'Master Plan overview': 'ملخص الخطة الرئيسية',
      'Cards Ready': 'البطاقات الجاهزة',
      'Need Assignment': 'تحتاج إلى إسناد',
      'Project Register': 'سجل المشاريع',
      'Search projects by name or code...': 'ابحث عن مشروع بالاسم أو الرمز...',
      'Search projects': 'البحث عن المشاريع',
      'Try a different search or filter': 'جرب عبارة بحث أو تصفية مختلفة',
      'Create a controlled update to the approved Master Plan': 'أنشئ تحديثاً منضبطاً للخطة الرئيسية المعتمدة',
      'Change Setup': 'إعداد التغيير',
      'Request Details': 'تفاصيل الطلب',
      'Approved recurring operational activities': 'الأنشطة التشغيلية المتكررة المعتمدة',
      'No approved projects or OPIs': 'لا توجد مشاريع أو مؤشرات أداء تشغيلية معتمدة',
      'No approved BAU activities': 'لا توجد أنشطة عمل معتاد معتمدة',
      'Project Activity': 'نشاط مشروع',
      'BAU Activity': 'نشاط عمل معتاد',
      'Failed to load Annual Plan': 'فشل تحميل الخطة السنوية',
      'Project-classified activities and approved initiative projects appear here from Business Demand': 'تظهر هنا الأنشطة المصنفة كمشاريع ومشاريع المبادرات المعتمدة القادمة من طلب الأعمال',
      'Activity moved to Master Plan': 'تم نقل النشاط إلى الخطة الرئيسية',
      'BAU activity kept in Business Demand': 'تم الإبقاء على نشاط العمل المعتاد في طلب الأعمال',
      'Activity Name': 'اسم النشاط',
      'Enter activity name': 'أدخل اسم النشاط',
      'Activity name is required': 'اسم النشاط مطلوب',
      'The activity is created for your department as a draft; the remaining fields stay available on the activity record.': 'يُنشأ النشاط لإدارتك كمسودة، وتبقى بقية الحقول متاحة في سجل النشاط.',
      'Activities are always created for your own department.': 'تُنشأ الأنشطة دائمًا لإدارتك.',
      'Failed to create the project': 'فشل إنشاء المشروع',
      'Failed to update activity': 'فشل تحديث النشاط',
      'Only the Division Head may edit Business Demand activities': 'يمكن لرئيس القطاع فقط تعديل أنشطة طلب الأعمال',
      'You may edit only activities and approved OPIs in your division': 'يمكنك تعديل الأنشطة ومؤشرات الأداء التشغيلية المعتمدة في قطاعك فقط',
      'The selected OPI has no department in your division': 'مؤشر الأداء التشغيلي المحدد لا يتبع إدارة في قطاعك',
      '{n} projects': '{n} مشاريع',
      'No projects or activities yet': 'لا توجد مشاريع أو أنشطة بعد',
      '1 project or activity in this department': 'مشروع أو نشاط واحد في هذه الإدارة',
      '{n} projects and activities in this department': '{n} مشاريع وأنشطة في هذه الإدارة',
      '1 project or activity in this division': 'مشروع أو نشاط واحد في هذا القطاع',
      '{n} projects and activities in this division': '{n} مشاريع وأنشطة في هذا القطاع',
      'Department Plans': 'خطط الإدارات',
      'Operation KPI': 'مؤشر الأداء التشغيلي',
      'Select an operation KPI': 'اختر مؤشر أداء تشغيلي',
      'No OKPIs in this department yet': 'لا توجد مؤشرات تشغيلية في هذه الإدارة بعد',
      'Choose an operation KPI that belongs to this department': 'اختر مؤشر أداء تشغيلي يتبع هذه الإدارة',
      'Accept Request': 'قبول الطلب',
      'Reject Request': 'رفض الطلب',
      '(optional)': '(اختياري)',
      'Add a note for the department': 'أضف ملاحظة للإدارة',
      'Accepted': 'تم القبول',
      'Failed to review the request': 'فشل مراجعة الطلب',
      'Department Projects': 'مشاريع الإدارة',
      'No department projects yet': 'لا توجد مشاريع إدارة بعد',
      'No activities yet': 'لا توجد أنشطة بعد',
      'AP': 'الخطة السنوية',
      'BAU': 'الأعمال المعتادة',
      'AP (Annual Plan)': 'الخطة السنوية',
      'Business As Usual': 'الأعمال المعتادة',
      'Activity Owner': 'مالك النشاط',
      'Enter the activity owner name': 'أدخل اسم مالك النشاط',
      'Activity owner is required': 'مالك النشاط مطلوب',
      'Budget is required': 'الميزانية مطلوبة',
      'Categorise as...': 'التصنيف...',
      'Categorise every selected project first': 'صنّف كل مشروع محدد أولًا',
      '{projects} projects and {kpis} OKPIs sent to the VP': 'تم إرسال {projects} مشاريع و{kpis} مؤشرات تشغيلية إلى نائب الرئيس',
      'Failed to send the plan to the VP': 'فشل إرسال الخطة إلى نائب الرئيس',
      'Send to VP': 'إرسال إلى نائب الرئيس',
      'Send to PMO': 'إرسال إلى مكتب إدارة المشاريع',
      'Sent to VP': 'تم الإرسال إلى نائب الرئيس',
      'With the SMO': 'لدى مكتب إدارة الاستراتيجية',
      '{n} projects could not be sent': 'تعذّر إرسال {n} مشاريع',
      'Projects are always created for your own department.': 'تُنشأ المشاريع دائمًا لإدارتك.',
      'Select all': 'تحديد الكل',
      'Unassign': 'إلغاء التعيين',
      'View mode': 'طريقة العرض',
      'Accept selected': 'قبول المحدد',
      'Reject selected': 'رفض المحدد',
      'Accept or reject at least one item': 'اقبل أو ارفض عنصرًا واحدًا على الأقل',
      'Categorise every accepted project first': 'صنّف كل مشروع مقبول أولًا',
      '{n} decided': 'تم تحديد {n}',
      'Every rejection needs a comment': 'كل رفض يحتاج إلى تعليق',
      'Send to CEO': 'إرسال إلى الرئيس التنفيذي',
      'Approve & Move to Master Plan': 'الاعتماد والنقل إلى الخطة الرئيسية',
      'Reviewed': 'تمت المراجعة',
      'Failed to review the plan': 'فشل مراجعة الخطة',
      'Activities': 'الأنشطة',
      'Assigned later': 'يُعيَّن لاحقًا',
      'Leave empty to file it from department assignment later.': 'اتركه فارغًا لتعيينه لاحقًا من صفحة تعيين الإدارات.',
      'Initiative Projects to assign': 'مشاريع المبادرات للتعيين',
      'Operation KPIs to assign': 'مؤشرات الأداء التشغيلية للتعيين',
      'Unassigned': 'غير معيّن',
      'Failed to unassign the item': 'فشل إلغاء تعيين العنصر',
      'Send to SMO': 'إرسال إلى مكتب إدارة الاستراتيجية',
      'Nothing here yet': 'لا يوجد شيء هنا بعد',
      'Create an activity for your division.': 'أنشئ نشاطًا لقطاعك.',
      'Division activities will appear here': 'ستظهر أنشطة القطاع هنا',
      '{n} activities': '{n} أنشطة',
      'Approved OPIs': 'مؤشرات الأداء التشغيلية المعتمدة',
      '{n} approved OPIs': '{n} مؤشرات أداء تشغيلية معتمدة',
      '{n} initiative projects': '{n} مشاريع مبادرات',
      'No active initiative projects yet': 'لا توجد مشاريع مبادرات نشطة بعد',
      '{n} activities sent to the SMO': 'تم إرسال {n} أنشطة إلى مكتب إدارة الاستراتيجية',
      'All draft activities identified by your division will be submitted to the SMO.': 'سيتم إرسال جميع الأنشطة المسودة التي حددها قطاعك إلى مكتب إدارة الاستراتيجية.',
      'Classification': 'التصنيف',
      'Add comment (optional)': 'أضف تعليقًا (اختياري)',
      'Classification Required': 'التصنيف مطلوب',
      'Select Project or BAU': 'اختر مشروع أو عمل اعتيادي',
      'Activity classification saved': 'تم حفظ تصنيف النشاط',
      'Failed to save classification': 'فشل حفظ التصنيف',
      'Only division activities can be classified as Project or BAU': 'يمكن تصنيف أنشطة القطاع فقط كمشروع أو عمل اعتيادي',
      'SMO and PMO may review activities but cannot create them': 'يمكن لمكتبي إدارة الاستراتيجية والمشاريع مراجعة الأنشطة ولكن لا يمكنهما إنشاؤها',
      'Initiative Project': 'مشروع مبادرة',
      'Division Activity': 'نشاط القطاع',
      'Create a project for your department, or wait for the SMO to send items to it.': 'أنشئ مشروعًا لإدارتك، أو انتظر أن يرسل مكتب إدارة الاستراتيجية عناصر إليها.',
      'Projects and OKPIs sent to your department will appear here': 'ستظهر هنا المشاريع والمؤشرات التشغيلية المُرسلة إلى إدارتك',
      'Choose the department this project belongs to': 'اختر الإدارة التي يتبع لها هذا المشروع',
      'Choose the department this activity belongs to': 'اختر الإدارة التي يتبع لها هذا النشاط',
      'Sent to SMO': 'تم الإرسال إلى مكتب إدارة الاستراتيجية',
      'The projects your department created and everything the SMO sent to it are submitted together in one request.': 'تُرسل المشاريع التي أنشأتها إدارتك وكل ما أرسله مكتب إدارة الاستراتيجية إليها معًا في طلب واحد.',
      'The activities your department created and everything the SMO sent to it are submitted together in one request.': 'تُرسل الأنشطة التي أنشأتها إدارتك وكل ما أرسله مكتب إدارة الاستراتيجية إليها معًا في طلب واحد.',
      '{n} of them are already under review or approved and will be skipped.': '{n} منها قيد المراجعة أو معتمدة بالفعل وسيتم تخطيها.',
      'Failed to send the items to the SMO': 'فشل إرسال العناصر إلى مكتب إدارة الاستراتيجية',
      '{projects} projects and {kpis} OKPIs sent to the SMO': 'تم إرسال {projects} مشاريع و{kpis} مؤشرات تشغيلية إلى مكتب إدارة الاستراتيجية',
      'Projects Summary': 'ملخص المشاريع',
      'Not sent yet': 'لم تُرسل بعد',
      'Under review': 'قيد المراجعة',
      'Approved and in delivery': 'معتمدة وقيد التنفيذ',
      'Filter by source': 'تصفية حسب المصدر',
      'Department project': 'مشروع إدارة',
      'Projects sent to your department will appear here': 'ستظهر هنا المشاريع المُرسلة إلى إدارتك',
      'Other category': 'تصنيف آخر',
      'Type the category': 'اكتب التصنيف',
      'Add the risks that could affect this plan, with their impact, probability and owner.': 'أضف المخاطر التي قد تؤثر على هذه الخطة مع الأثر والاحتمالية والمسؤول.',
      'No risks recorded yet': 'لم يتم تسجيل مخاطر بعد',
      'Remove "{name}" from the risk register?': 'هل تريد حذف "{name}" من سجل المخاطر؟',
      'Risk added': 'تمت إضافة الخطر',
      'Risk removed': 'تم حذف الخطر',
      'Risk updated': 'تم تحديث الخطر',
      'What could go wrong?': 'ما الذي قد يحدث من مخاطر؟',
      'Failed to load the risk register': 'فشل تحميل سجل المخاطر',
      'Failed to save the risk': 'فشل حفظ الخطر',
      'Failed to remove the risk': 'فشل حذف الخطر',
      'Hide summary': 'إخفاء الملخص',
      'Show summary': 'إظهار الملخص',
      'Scroll left': 'التمرير لليسار',
      'Scroll right': 'التمرير لليمين',
      'No stakeholders recorded yet': 'لم يتم تسجيل أصحاب مصلحة بعد',
      'Plan Details': 'تفاصيل الخطة',
      'Plan Overview': 'نظرة عامة على الخطة',
      'Plan Period Elapsed': 'المنقضي من مدة الخطة',
      'Manage Initiative': 'إدارة المبادرة',
      'The initiative can be managed after VP and CEO approval.': 'يمكن إدارة المبادرة بعد موافقة نائب الرئيس والرئيس التنفيذي.',
      'Create Initiative KPIs': 'إنشاء مؤشرات المبادرة',
      'Green Threshold': 'الحد الأخضر',
      'Yellow Threshold': 'الحد الأصفر',
      'Red Threshold': 'الحد الأحمر',
      'Performance Thresholds': 'حدود الأداء',
      'Fixed achievement bands, the same for every KPI.': 'نطاقات إنجاز ثابتة، وهي نفسها لكل مؤشر.',
      'Detailed KPI Card': 'بطاقة المؤشر التفصيلية',
      'KPI Overview': 'نظرة عامة على المؤشر',
      'Data Profile': 'ملف البيانات',
      'Targets & Thresholds': 'المستهدفات والحدود',
      'Identity': 'الهوية',
      'Approval': 'الاعتماد',
      'No comments recorded.': 'لا توجد ملاحظات مسجلة.',
      'Definition, data profile and targets for this KPI': 'التعريف وملف البيانات والمستهدفات لهذا المؤشر',
      '{n} projects · {w}% weight assigned': '{n} مشاريع · {w}% من الأوزان مُسندة',
      'The projects that deliver this initiative; their weights must add up to 100%.': 'المشاريع التي تنفّذ هذه المبادرة؛ يجب أن يبلغ مجموع أوزانها 100%.',
      'No initiative KPIs yet. Create the fixed set to start recording results.': 'لا توجد مؤشرات للمبادرة بعد. أنشئ المجموعة الثابتة لبدء تسجيل النتائج.',
      'Initiative KPIs created': 'تم إنشاء مؤشرات المبادرة',
      'Unable to load initiative KPIs': 'تعذر تحميل مؤشرات المبادرة',
      'Unable to create initiative KPIs': 'تعذر إنشاء مؤشرات المبادرة',
      'KPI updated': 'تم تحديث المؤشر',
      'Unable to save the KPI': 'تعذر حفظ المؤشر',
      'Track this KPI': 'تتبع هذا المؤشر',
      'Not tracked': 'غير متتبع',
      'Initiative KPI': 'مؤشر المبادرة',
      'Initiative Definition': 'تعريف المبادرة',
      'Ownership': 'الملكية',
      'Depends On': 'يعتمد على',
      'Delivery': 'التنفيذ',
      'Avg. Completion': 'متوسط الإنجاز',
      'No milestones recorded yet.': 'لم يتم تسجيل معالم بعد.',
      'Initiative Overview': 'نظرة عامة على المبادرة',
      "Weighted completion of this initiative's projects": 'نسبة الإنجاز الموزونة لمشاريع هذه المبادرة',
      'Update results, deliver projects and raise change requests': 'حدّث النتائج ونفّذ المشاريع وارفع طلبات التغيير',
      'Initiative Change History': 'سجل تغييرات المبادرة',
      'Discard changes?': 'تجاهل التغييرات؟',
      'This form has unsaved changes. Leaving now will discard them.': 'يحتوي هذا النموذج على تغييرات غير محفوظة. الخروج الآن سيؤدي إلى تجاهلها.',
      'Keep editing': 'متابعة التعديل',
      'Discard': 'تجاهل',
      'Elapsed': 'المنقضي',
      'Strategy Summary': 'ملخص الاستراتيجية',
      'Created on': 'أُنشئت في',
      'Plan Change History': 'سجل تغييرات الخطة',
      'Plan Report': 'تقرير الخطة',
      'This plan workspace opens once the plan is approved and assigned': 'تُفتح مساحة عمل الخطة بعد اعتماد الخطة وإسنادها',
      'This register will be added to the plan workspace in a later step.': 'سيُضاف هذا السجل إلى مساحة عمل الخطة في مرحلة لاحقة.',
      'All milestones completed': 'اكتملت جميع المعالم',
      'Approved Master Plans': 'الخطط الرئيسية المعتمدة',
      'Approved Master Plans appear here once the Project Owner assigns them to a project manager': 'تظهر الخطط الرئيسية المعتمدة هنا بعد إسنادها من مالك المشروع إلى مدير مشروع',
      'Assigned to Me': 'المسندة إليّ',
      'Failed to load plans': 'فشل تحميل الخطط',
      'Filter plans': 'تصفية الخطط',
      'Milestones started, not finished': 'معالم بدأت ولم تكتمل',
      'Mine': 'خططي',
      'No plans assigned yet': 'لا توجد خطط مسندة بعد',
      'Plan Management': 'إدارة الخطط',
      'Plans you manage': 'الخطط التي تديرها',
      'Annual Business Plan Approval History': 'سجل اعتماد خطة الأعمال السنوية',
      'Assigned Master Plans': 'الخطط الرئيسية المسندة',
      'Coming soon': 'قريبًا',
      'Failed to load the Master Plan': 'فشل تحميل الخطة الرئيسية',
      'Master Plan Workspace': 'مساحة عمل الخطة الرئيسية',
      'No decisions recorded yet': 'لم تُسجل أي قرارات بعد',
      'Not available': 'غير متاح',
      'Not available yet': 'غير متاح بعد',
      'Plan Navigation': 'تنقل الخطة',
      'This Master Plan workspace opens once the plan is approved and assigned': 'تُفتح مساحة عمل الخطة الرئيسية بعد اعتماد الخطة وإسنادها',
      'This register will be added to the Master Plan workspace in a later step.': 'سيُضاف هذا السجل إلى مساحة عمل الخطة الرئيسية في مرحلة لاحقة.',
      'Scope & Delivery': 'النطاق والتنفيذ',
      'Assign to Project Manager': 'إسناد إلى مدير المشروع',
      'Choosing a different manager replaces the current one; clearing it removes the assignment.': 'اختيار مدير آخر يحل محل المدير الحالي، ومسح الاختيار يلغي الإسناد.',
      'Failed to load project managers': 'فشل تحميل مديري المشاريع',
      'No project manager assigned yet': 'لم يتم إسناد مدير مشروع بعد',
      'Reassign': 'إعادة الإسناد',
      'Select project manager': 'اختر مدير المشروع',
      'The project manager is notified once the plan is assigned.': 'يتم إشعار مدير المشروع بمجرد إسناد الخطة.',
      'Master Plan assigned': 'تم إسناد الخطة الرئيسية',
      'Failed to assign the Master Plan': 'فشل إسناد الخطة الرئيسية',
      'A department belongs to one division; selecting it here moves it to this division': 'تتبع الإدارة قطاعًا واحدًا فقط؛ اختيارها هنا ينقلها إلى هذا القطاع',
      'All Divisions': 'جميع القطاعات',
      'Are you sure you want to delete the division': 'هل أنت متأكد من حذف القطاع',
      'Assigned Departments': 'الإدارات المرتبطة',
      'Create Division': 'إنشاء قطاع',
      'Division "{name}" has been deleted successfully': 'تم حذف القطاع "{name}" بنجاح',
      'Division Code': 'رمز القطاع',
      'Division Head': 'رئيس القطاع',
      'Division Name': 'اسم القطاع',
      'Division created successfully': 'تم إنشاء القطاع بنجاح',
      'Division updated successfully': 'تم تحديث القطاع بنجاح',
      'Divisions': 'القطاعات',
      'Divisions Management': 'إدارة القطاعات',
      'Divisions group related departments. Create a division, then assign its departments.': 'تجمع القطاعات الإدارات المرتبطة. أنشئ قطاعًا ثم اربط إداراته.',
      'Edit Division': 'تعديل القطاع',
      'Failed to delete division': 'فشل حذف القطاع',
      'Failed to save division': 'فشل حفظ القطاع',
      'Getting Started with Divisions': 'البدء مع القطاعات',
      'In {name}': 'ضمن {name}',
      'Letters only; generated from the name when creating': 'أحرف فقط؛ يُنشأ من الاسم عند الإضافة',
      'Linked to a division': 'مرتبطة بقطاع',
      'Manage organizational divisions and the departments that belong to each division.': 'إدارة قطاعات المؤسسة والإدارات التابعة لكل قطاع.',
      'No Divisions Found': 'لم يتم العثور على قطاعات',
      'No departments assigned': 'لا توجد إدارات مرتبطة',
      'Not linked to a division': 'غير مرتبطة بقطاع',
      'Search divisions by name or code...': 'ابحث في القطاعات بالاسم أو الرمز...',
      'Select departments': 'اختر الإدارات',
      'Select division (optional)': 'اختر القطاع (اختياري)',
      'This division has {count} department(s). They will be kept but no longer belong to a division.': 'يحتوي هذا القطاع على {count} إدارة. سيتم الإبقاء عليها لكنها لن تتبع أي قطاع.',
      'Total Divisions': 'إجمالي القطاعات',
      'Unassigned Departments': 'إدارات غير مرتبطة',
      'With Departments': 'بها إدارات',
      'Without Departments': 'بدون إدارات',
      'e.g. SPM': 'مثال: SPM',
      '{count} more departments': '{count} إدارات أخرى',
      'Division name is required': 'اسم القطاع مطلوب',
      'Division code is required': 'رمز القطاع مطلوب',
      'Division code must contain letters only': 'يجب أن يحتوي رمز القطاع على أحرف فقط',
      'Division': 'القطاع',
      'Select division': 'اختر القطاع',
      'Division, classification, prerequisites, dependencies, procurement and complexity': 'القطاع والتصنيف والمتطلبات المسبقة والاعتماديات والمشتريات ودرجة التعقيد',
      'Strategy & PMO': 'الاستراتيجية ومكتب إدارة المشاريع',
      'Investment': 'الاستثمار',
      'Real Estate Asset Management': 'إدارة الأصول العقارية',
      'Real Estate Development': 'التطوير العقاري',
      'Legal & Compliance': 'الشؤون القانونية والامتثال',
      'Marketing & Communications': 'التسويق والاتصال',
      'Human Capital': 'رأس المال البشري',
      'Shared Services': 'الخدمات المشتركة',
      'Cyber Security': 'الأمن السيبراني',
      'Audit & Risk Management': 'التدقيق وإدارة المخاطر',
      'Delivery Details': 'تفاصيل التنفيذ',
      'Enter classification': 'أدخل التصنيف',
      'Enter vendor name': 'أدخل اسم المورد',
      'Is there an IT solution?': 'هل يوجد حل تقني؟',
      'Prerequisites': 'المتطلبات المسبقة',
      'RfP Completion Target Date': 'التاريخ المستهدف لإكمال طلب العروض',
      'Vendor (if any)': 'المورد (إن وجد)',
      'What must be in place before the plan can start?': 'ما الذي يجب توفره قبل بدء الخطة؟',
      'Which plans, projects or teams does this depend on?': 'على أي خطط أو مشاريع أو فرق تعتمد هذه الخطة؟',
      'CEO Direction': 'توجيه الرئيس التنفيذي',
      'Flag this as a high-priority project requiring directive PMO engagement': 'صنّف هذا كمشروع عالي الأولوية يتطلب مشاركة توجيهية من مكتب إدارة المشاريع',
      'Engagement': 'المشاركة',
      'PMO Engagement Level': 'مستوى مشاركة مكتب إدارة المشاريع',
      'Directive': 'توجيهي',
      'Priority score ≤ 60% · No CEO direction': 'درجة الأولوية ≤ 60% · دون توجيه من الرئيس التنفيذي',
      'Priority score > 60% · No CEO direction': 'درجة الأولوية > 60% · دون توجيه من الرئيس التنفيذي',
      'CEO direction given': 'يوجد توجيه من الرئيس التنفيذي',
      'Rank every criterion or set the CEO direction to determine the engagement level': 'رتّب جميع المعايير أو حدد توجيه الرئيس التنفيذي لتحديد مستوى المشاركة',
      'PMO engagement: Directive when the CEO gave a direction; otherwise Controlling up to 60% and Supportive above 60%.': 'مشاركة مكتب إدارة المشاريع: توجيهية عند وجود توجيه من الرئيس التنفيذي؛ وإلا رقابية حتى 60% وداعمة لما فوق 60%.',
      'Rank each criterion from 1 to 5; the priority score and PMO engagement level are calculated automatically': 'رتّب كل معيار من 1 إلى 5، وتُحسب درجة الأولوية ومستوى مشاركة مكتب إدارة المشاريع تلقائيًا',
      'Budget, Scope & Outcomes': 'الميزانية والنطاق والنتائج',
      'Complexity': 'التعقيد',
      'Controlling': 'رقابي',
      'Supportive': 'داعم',
      'Criteria': 'المعايير',
      'Rank': 'الترتيب',
      'Each criterion is ranked 1–5 and multiplied by its weight. A link to an audit finding adds 10%. The score is capped at 100%.': 'يُرتَّب كل معيار من 1 إلى 5 ويُضرب في وزنه. يضيف الارتباط بملاحظة تدقيق 10%. الحد الأقصى للنتيجة 100%.',
      'Estimate the budget and describe what the plan covers and the results it will deliver': 'قدّر الميزانية وصف ما تغطيه الخطة والنتائج التي ستحققها',
      'Financial threshold': 'الحد المالي',
      'Auto-calculated': 'محسوبة تلقائيًا',
      'How is the priority calculated?': 'كيف تُحسب الأولوية؟',
      'Impact on corporate level': 'الأثر على مستوى المؤسسة',
      'Linked to Audit Finding?': 'مرتبطة بملاحظة تدقيق؟',
      'Multiple related projects': 'المشاريع المرتبطة',
      'Number of stakeholders': 'عدد أصحاب المصلحة',
      'Overall Priority Score': 'درجة الأولوية الإجمالية',
      'Priority Assessment': 'تقييم الأولوية',
      'Priority criteria have not been ranked yet': 'لم يتم ترتيب معايير الأولوية بعد',
      'Rank every criterion to calculate the priority score': 'رتّب جميع المعايير لحساب درجة الأولوية',
      'Rank every priority criterion': 'رتّب جميع معايير الأولوية',
      'Add the Master Plan details. All plans are submitted for VP and CEO approval together.': 'أضف تفاصيل الخطة الرئيسية. تُرسل جميع الخطط معًا لاعتماد نائب الرئيس والرئيس التنفيذي.',
      'Add the mandatory details to every plan, then submit them all for approval together.': 'أضف التفاصيل الإلزامية لكل خطة، ثم أرسلها جميعًا للاعتماد معًا.',
      'All mandatory details are filled in': 'تم تعبئة جميع التفاصيل الإلزامية',
      'All plans are ready for approval': 'جميع الخطط جاهزة للاعتماد',
      'Cards': 'بطاقات',
      'Master Plan details saved. Complete the mandatory fields before submitting all plans.': 'تم حفظ تفاصيل الخطة الرئيسية. أكمل الحقول الإلزامية قبل إرسال جميع الخطط.',
      'Master Plan details saved. They are ready to be submitted with the other plans.': 'تم حفظ تفاصيل الخطة الرئيسية وهي جاهزة للإرسال مع بقية الخطط.',
      'Missing Details': 'تفاصيل ناقصة',
      'Plan': 'الخطة',
      'Ready': 'جاهزة',
      'Ready — will be submitted with the other plans from the Master Plan page': 'جاهزة — ستُرسل مع بقية الخطط من صفحة الخطة الرئيسية',
      'Save Details': 'حفظ التفاصيل',
      'Saving keeps these details in draft. Once every plan has its mandatory details, submit them all for approval together from the Master Plan page.': 'يحفظ الحفظ هذه التفاصيل كمسودة. عند اكتمال التفاصيل الإلزامية لجميع الخطط، أرسلها كلها للاعتماد معًا من صفحة الخطة الرئيسية.',
      'Show incomplete': 'عرض غير المكتملة',
      'Show all': 'عرض الكل',
      'No incomplete project cards': 'لا توجد بطاقات مشاريع غير مكتملة',
      'All project cards are complete': 'جميع بطاقات المشاريع مكتملة',
      'Still missing': 'ما زال ناقصًا',
      'Submit all Master Plans for VP and CEO approval in one go.': 'أرسل جميع الخطط الرئيسية لاعتماد نائب الرئيس والرئيس التنفيذي دفعة واحدة.',
      'These {n} Master Plans will be sent for VP approval together. Plans returned by the CEO go straight back to the CEO.': 'سيتم إرسال هذه الخطط الرئيسية ({n}) لاعتماد نائب الرئيس معًا. الخطط المعادة من الرئيس التنفيذي تعود إليه مباشرة.',
      'Waiting to be submitted with all plans': 'بانتظار الإرسال مع جميع الخطط',
      '{n} missing': '{n} ناقصة',
      '{ready} of {total} plans ready': '{ready} من {total} خطط جاهزة',
      'Master Plan submitted for approval': 'تم إرسال الخطة الرئيسية للاعتماد',
      '{n} Master Plans submitted for approval': 'تم إرسال {n} خطط رئيسية للاعتماد',
      'Add the mandatory Master Plan details to every plan before submitting them for approval': 'أضف تفاصيل الخطة الرئيسية الإلزامية لكل خطة قبل إرسالها للاعتماد',
      'There are no Master Plans waiting to be submitted': 'لا توجد خطط رئيسية بانتظار الإرسال',
      'Failed to submit Master Plans': 'فشل إرسال الخطط الرئيسية',
      'Edit Master Plan Details': 'تعديل تفاصيل الخطة الرئيسية',
      'Add priority, budget, scope, alignment and ownership to the approved plan': 'أضف الأولوية والميزانية والنطاق والمواءمة والملكية إلى الخطة المعتمدة',
      'The approved plan these Master Plan details belong to': 'الخطة المعتمدة التي تتبع لها تفاصيل الخطة الرئيسية',
      'Rate the plan priority and estimate its total budget': 'حدد أولوية الخطة وقدّر إجمالي ميزانيتها',
      'Describe what the plan covers and the results it will deliver': 'صف ما تغطيه الخطة والنتائج التي ستحققها',
      'Strategic Alignment': 'المواءمة الاستراتيجية',
      'Link the plan to its portfolio, program, objectives, initiatives and KPIs': 'اربط الخطة بالمحفظة والبرنامج والأهداف والمبادرات ومؤشرات الأداء',
      'Assign who sponsors and owns the plan, and who is involved': 'حدد راعي الخطة ومالكها والجهات المعنية بها',
      'Approved Annual Business Plan': 'خطة الأعمال السنوية المعتمدة',
      'Add Master Plan Details': 'إضافة تفاصيل الخطة الرئيسية',
      'Add another': 'إضافة آخر',
      'Add the Master Plan details, then submit them for VP and CEO approval.': 'أضف تفاصيل الخطة الرئيسية ثم قدمها لموافقة نائب الرئيس والرئيس التنفيذي.',
      'Alignment': 'المواءمة',
      'Approve Master Plan Details': 'اعتماد تفاصيل الخطة الرئيسية',
      'Approved annual business plans': 'خطط الأعمال السنوية المعتمدة',
      'CapEx + OpEx · Full project lifecycle': 'النفقات الرأسمالية + التشغيلية · دورة حياة المشروع كاملة',
      'Continue Details': 'متابعة التفاصيل',
      'Describe target measurable outcomes': 'صف النتائج المستهدفة القابلة للقياس',
      'Describe target scope, workstreams, details, etc.': 'صف النطاق المستهدف ومسارات العمل والتفاصيل وغيرها',
      'Edit Details': 'تعديل التفاصيل',
      'Estimated budget is required': 'الميزانية التقديرية مطلوبة',
      'Failed to load form options': 'فشل تحميل خيارات النموذج',
      'Final approval — the Master Plan details will be approved': 'الاعتماد النهائي — سيتم اعتماد تفاصيل الخطة الرئيسية',
      'In Review': 'قيد المراجعة',
      'Link annual KPIs': 'ربط مؤشرات الأداء السنوية',
      'Link initiatives': 'ربط المبادرات',
      'Link strategic objectives': 'ربط الأهداف الاستراتيجية',
      'Linked Annual KPIs': 'مؤشرات الأداء السنوية المرتبطة',
      'Linked Strategic Objectives and Initiatives': 'الأهداف الاستراتيجية والمبادرات المرتبطة',
      'Master Plan Approval History': 'سجل اعتماد الخطة الرئيسية',
      'Master Plan Details': 'تفاصيل الخطة الرئيسية',
      'Master Plan approved': 'الخطة الرئيسية معتمدة',
      'Needs Details': 'بحاجة إلى تفاصيل',
      'Nothing linked yet': 'لا يوجد ربط بعد',
      'Outcomes': 'النتائج',
      'Outcomes are required': 'النتائج مطلوبة',
      'Owner is required': 'المالك مطلوب',
      'PMO has not added the Master Plan details yet.': 'لم يضف مكتب إدارة المشاريع تفاصيل الخطة الرئيسية بعد.',
      'Priority Score': 'درجة الأولوية',
      'Priority score is required': 'درجة الأولوية مطلوبة',
      'Reject Master Plan Details': 'رفض تفاصيل الخطة الرئيسية',
      'Saving...': 'جارٍ الحفظ...',
      'Scope & Outcomes': 'النطاق والنتائج',
      'Scope is required': 'النطاق مطلوب',
      'Select owner': 'اختر المالك',
      'Select portfolio': 'اختر المحفظة',
      'Select sponsor': 'اختر الراعي',
      'Sponsor is required': 'الراعي مطلوب',
      'Submitting...': 'جارٍ التقديم...',
      'The details have been returned to PMO to update and resubmit.': 'أُعيدت التفاصيل إلى مكتب إدارة المشاريع لتحديثها وإعادة تقديمها.',
      'The details have been returned to the VP to update and resubmit.': 'أُعيدت التفاصيل إلى نائب الرئيس لتحديثها وإعادة تقديمها.',
      'The details will be returned to PMO to update and resubmit': 'ستُعاد التفاصيل إلى مكتب إدارة المشاريع لتحديثها وإعادة تقديمها',
      'The details will be returned to the VP to update and resubmit': 'ستُعاد التفاصيل إلى نائب الرئيس لتحديثها وإعادة تقديمها',
      'These Master Plan details cannot be edited right now': 'لا يمكن تعديل تفاصيل الخطة الرئيسية حاليًا',
      'Type a name and press Enter': 'اكتب اسمًا واضغط Enter',
      'Update the details and resubmit them to start the approval again from the VP.': 'حدّث التفاصيل وأعد تقديمها لبدء الاعتماد من جديد لدى نائب الرئيس.',
      'Update the details and resubmit them to the CEO.': 'حدّث التفاصيل وأعد تقديمها للرئيس التنفيذي.',
      'VP approval — the details will move to CEO review': 'موافقة نائب الرئيس — ستنتقل التفاصيل إلى مراجعة الرئيس التنفيذي',
      'Waiting for PMO': 'بانتظار مكتب إدارة المشاريع',
      'With PMO for changes': 'لدى مكتب إدارة المشاريع للتعديل',
      'e.g. 12,500,000': 'مثال: 12,500,000',
      'Details Needed': 'بحاجة إلى تفاصيل',
      'Details Draft': 'مسودة التفاصيل',
      'Master Plan Approved': 'الخطة الرئيسية معتمدة',
      'Master Plan details saved': 'تم حفظ تفاصيل الخطة الرئيسية',
      'Master Plan details submitted for VP approval': 'تم تقديم تفاصيل الخطة الرئيسية لموافقة نائب الرئيس',
      'Master Plan details resubmitted for VP approval': 'تمت إعادة تقديم تفاصيل الخطة الرئيسية لموافقة نائب الرئيس',
      'Master Plan details resubmitted for CEO approval': 'تمت إعادة تقديم تفاصيل الخطة الرئيسية لموافقة الرئيس التنفيذي',
      'Master Plan details approved by VP and sent to CEO': 'اعتمد نائب الرئيس تفاصيل الخطة الرئيسية وأُرسلت إلى الرئيس التنفيذي',
      'Master Plan details approved by CEO': 'اعتمد الرئيس التنفيذي تفاصيل الخطة الرئيسية',
      'Master Plan details rejected by VP and returned to PMO': 'رفض نائب الرئيس تفاصيل الخطة الرئيسية وأُعيدت إلى مكتب إدارة المشاريع',
      'Master Plan details rejected by CEO and returned to the VP': 'رفض الرئيس التنفيذي تفاصيل الخطة الرئيسية وأُعيدت إلى نائب الرئيس',
      'Complete the Master Plan details before submitting them for approval': 'أكمل تفاصيل الخطة الرئيسية قبل تقديمها للاعتماد',
      'Master Plan details can only be added after the annual business plan is approved': 'لا يمكن إضافة تفاصيل الخطة الرئيسية إلا بعد اعتماد خطة الأعمال السنوية',
      'Master Plan details under review or approved cannot be changed': 'لا يمكن تغيير تفاصيل الخطة الرئيسية قيد المراجعة أو المعتمدة',
      'Only PMO or Super Admin can add or submit Master Plan details': 'يمكن لمكتب إدارة المشاريع أو المدير العام فقط إضافة تفاصيل الخطة الرئيسية أو تقديمها',
      'These Master Plan details were rejected by the CEO and returned to the VP. Only the VP can update and resubmit them': 'رفض الرئيس التنفيذي تفاصيل الخطة الرئيسية وأُعيدت إلى نائب الرئيس. يمكن لنائب الرئيس فقط تحديثها وإعادة تقديمها',
      'Annual business plans approved by the VP and CEO': 'خطط الأعمال السنوية المعتمدة من نائب الرئيس والرئيس التنفيذي',
      'Plans': 'خطط',
      'Filtered': 'مُصفّى',
      'New Plan': 'خطة جديدة',
      'All Plans': 'كل الخطط',
      'Needs Your Action': 'بحاجة إلى إجراء منك',
      'Waiting on you': 'بانتظارك',
      'For You': 'لك',
      '{n} plans': '{n} خطط',
      'Milestones completed': 'المعالم المنجزة',
      'Pagination': 'التنقل بين الصفحات',
      'Previous page': 'الصفحة السابقة',
      'Next page': 'الصفحة التالية',
      'Per page': 'لكل صفحة',
      'Plan navigation': 'التنقل في الخطة',
      'Length': 'المدة الزمنية',
      'Submitted on': 'تاريخ التقديم',
      'Approved on': 'تاريخ الاعتماد',
      'Review the plan below, then approve or reject it with a comment.': 'راجع الخطة أدناه، ثم اعتمدها أو ارفضها مع إضافة تعليق.',
      '{n} month': '{n} شهر',
      '{n} months': '{n} أشهر',
      '{n} years': '{n} سنوات',
      'No milestones': 'لا توجد معالم',
      '+{n} more': '+{n} أخرى',
      'Step 2 of 4': 'الخطوة 2 من 4',
      'Step 3 of 4': 'الخطوة 3 من 4',
      'Returned': 'مُعادة',
      'Awaiting PMO review': 'بانتظار مراجعة مكتب إدارة المشاريع',
      'Awaiting VP approval': 'بانتظار موافقة نائب الرئيس',
      'Awaiting CEO approval': 'بانتظار موافقة الرئيس التنفيذي',
      'Awaiting VP Approval': 'بانتظار موافقة نائب الرئيس',
      'Awaiting CEO Approval': 'بانتظار موافقة الرئيس التنفيذي',
      'Awaiting Division Head Approval': 'بانتظار موافقة رئيس القطاع',
      'No approved KPI Change Requests': 'لا توجد طلبات تغيير مؤشرات معتمدة',
      'Approved KPI change requests will appear here after CEO acceptance.': 'ستظهر طلبات تغيير المؤشرات المعتمدة هنا بعد موافقة الرئيس التنفيذي.',
      'With the VP for changes': 'لدى نائب الرئيس للتعديل',
      'With SMO for changes': 'لدى مكتب إدارة الاستراتيجية للتعديل',
      'Edit & Resubmit to CEO': 'تعديل وإعادة التقديم للرئيس التنفيذي',
      '{done} of {total}': '{done} من {total}',
      'Resubmit to CEO': 'إعادة التقديم للرئيس التنفيذي',
      'Resubmit to CEO without changes': 'إعادة التقديم للرئيس التنفيذي دون تعديلات',
      'Resubmit this plan to the CEO without changes?': 'هل تريد إعادة تقديم هذه الخطة للرئيس التنفيذي دون تعديلات؟',
      'Update the plan and resubmit it to the CEO.': 'حدّث الخطة وأعد تقديمها للرئيس التنفيذي.',
      'The plan has been returned to the VP to update and resubmit.': 'أُعيدت الخطة إلى نائب الرئيس لتحديثها وإعادة تقديمها.',
      'The plan has been returned to SMO to update and resubmit.': 'أُعيدت الخطة إلى مكتب إدارة الاستراتيجية لتحديثها وإعادة تقديمها.',
      'The plan will be returned to the VP to update and resubmit': 'ستُعاد الخطة إلى نائب الرئيس لتحديثها وإعادة تقديمها',
      'Updated and resubmitted to CEO by VP': 'حدّثها نائب الرئيس وأعاد تقديمها للرئيس التنفيذي',
      'Annual business plan resubmitted for CEO approval': 'تمت إعادة تقديم خطة الأعمال السنوية لموافقة الرئيس التنفيذي',
      'Annual business plan rejected by CEO and returned to the VP': 'رفض الرئيس التنفيذي خطة الأعمال السنوية وأُعيدت إلى نائب الرئيس',
      'Annual business plan rejected by VP and returned to SMO': 'رفض نائب الرئيس خطة الأعمال السنوية وأُعيدت إلى مكتب إدارة الاستراتيجية',
      'This plan was rejected by the CEO and returned to the VP. Only the VP can update and resubmit it': 'رفض الرئيس التنفيذي هذه الخطة وأُعيدت إلى نائب الرئيس. يمكن لنائب الرئيس فقط تحديثها وإعادة تقديمها',
      'Only SMO or Super Admin can edit or submit this annual business plan': 'يمكن لمكتب إدارة الاستراتيجية أو المدير العام فقط تعديل خطة الأعمال السنوية هذه أو تقديمها',
      'Showing {from}–{to} of {total}': 'عرض {from}–{to} من {total}',
      'Not submitted for approval yet': 'لم تُقدم للاعتماد بعد',
      'Across approved plans': 'عبر الخطط المعتمدة',
      'Add your approval comment': 'أضف تعليق الموافقة',
      'Annual business plans appear here once the CEO approves them': 'تظهر خطط الأعمال السنوية هنا بعد اعتمادها من الرئيس التنفيذي',
      'Approval Workflow': 'مسار الاعتماد',
      'Approve Annual Business Plan': 'اعتماد خطة الأعمال السنوية',
      'Approved by CEO': 'اعتمدها الرئيس التنفيذي',
      'Approved by VP': 'اعتمدها نائب الرئيس',
      'Rejected by VP': 'رفضها نائب الرئيس',
      'Rejected by CEO': 'رفضها الرئيس التنفيذي',
      'Pending VP Approval': 'بانتظار موافقة نائب الرئيس',
      'Pending CEO Approval': 'بانتظار موافقة الرئيس التنفيذي',
      'VP Approval': 'موافقة نائب الرئيس',
      'CEO Approval': 'موافقة الرئيس التنفيذي',
      'In progress': 'قيد التنفيذ',
      'Awaiting Your Review': 'بانتظار مراجعتك',
      'Awaiting your review': 'بانتظار مراجعتك',
      'Comment is required': 'التعليق مطلوب',
      'Executed internally': 'تُنفذ داخليًا',
      'Explain what needs to change before resubmission': 'وضّح ما يجب تعديله قبل إعادة التقديم',
      'Final approval — the plan will be published to the Master Plan': 'الاعتماد النهائي — ستُنشر الخطة في الخطة الرئيسية',
      'In the Master Plan': 'في الخطة الرئيسية',
      'Milestones Completed': 'المعالم المنجزة',
      'No approval activity yet': 'لا يوجد نشاط اعتماد بعد',
      'No approved plans yet': 'لا توجد خطط معتمدة بعد',
      'Not editable': 'غير قابلة للتعديل',
      'Only SMO or Super Admin can create or edit annual business plans': 'يمكن لمكتب إدارة الاستراتيجية أو المدير العام فقط إنشاء خطط الأعمال السنوية أو تعديلها',
      'Only draft or rejected plans can be edited': 'يمكن تعديل الخطط المسودة أو المرفوضة فقط',
      'Only draft or rejected plans can be submitted': 'يمكن تقديم الخطط المسودة أو المرفوضة فقط',
      'Plans submitted for approval will appear here': 'ستظهر هنا الخطط المقدمة للاعتماد',
      'Published to the Master Plan': 'منشورة في الخطة الرئيسية',
      'Reject Annual Business Plan': 'رفض خطة الأعمال السنوية',
      'Resubmit this plan for VP approval without changes?': 'هل تريد إعادة تقديم هذه الخطة لموافقة نائب الرئيس دون تعديلات؟',
      'Resubmit without changes': 'إعادة التقديم دون تعديلات',
      'Resubmitted for approval': 'أُعيد تقديمها للاعتماد',
      'Submit this plan for VP approval? It cannot be edited while under review.': 'هل تريد تقديم هذه الخطة لموافقة نائب الرئيس؟ لا يمكن تعديلها أثناء المراجعة.',
      'Submitted for approval': 'قُدمت للاعتماد',
      'The plan will be returned to SMO to update and resubmit': 'ستُعاد الخطة إلى مكتب إدارة الاستراتيجية لتحديثها وإعادة تقديمها',
      'This plan is a draft and has not been submitted yet.': 'هذه الخطة مسودة ولم تُقدم بعد.',
      'Update the plan and resubmit it to start the approval again from the VP.': 'حدّث الخطة وأعد تقديمها لبدء الاعتماد من جديد لدى نائب الرئيس.',
      'VP and CEO review': 'مراجعة نائب الرئيس والرئيس التنفيذي',
      'VP approval — the plan will move to CEO review': 'موافقة نائب الرئيس — ستنتقل الخطة إلى مراجعة الرئيس التنفيذي',
      'Waiting to be resubmitted': 'بانتظار إعادة التقديم',
      'Annual business plan submitted for VP approval': 'تم تقديم خطة الأعمال السنوية لموافقة نائب الرئيس',
      'Annual business plan resubmitted for VP approval': 'تمت إعادة تقديم خطة الأعمال السنوية لموافقة نائب الرئيس',
      'Annual business plan approved by VP and sent to CEO': 'اعتمد نائب الرئيس خطة الأعمال السنوية وأُرسلت إلى الرئيس التنفيذي',
      'Annual business plan approved by CEO': 'اعتمد الرئيس التنفيذي خطة الأعمال السنوية',
      'Annual business plan rejected by VP': 'رفض نائب الرئيس خطة الأعمال السنوية',
      'Annual business plan rejected by CEO': 'رفض الرئيس التنفيذي خطة الأعمال السنوية',
      'Complete the plan before submitting it for approval': 'أكمل الخطة قبل تقديمها للاعتماد',
      'Plans under review or approved can only be deleted by Super Admin': 'لا يمكن حذف الخطط قيد المراجعة أو المعتمدة إلا بواسطة المدير العام',
      'Only the VP can review this plan at the current stage': 'يمكن لنائب الرئيس فقط مراجعة هذه الخطة في المرحلة الحالية',
      'Only the CEO can review this plan at the current stage': 'يمكن للرئيس التنفيذي فقط مراجعة هذه الخطة في المرحلة الحالية',
      'This plan is not awaiting approval': 'هذه الخطة ليست بانتظار الاعتماد',
      'Generated automatically when the plan is created': 'يُنشأ تلقائيًا عند إنشاء الخطة',
      'The plan code cannot be changed': 'لا يمكن تغيير رمز الخطة',
      'Name the plan, describe it and choose how it will be executed': 'حدد اسم الخطة ووصفها واختر طريقة تنفيذها',
      'Annual Business Plans': 'خطط الأعمال السنوية',
      'Add First Milestone': 'أضف أول معلم',
      'Add at least one milestone': 'أضف معلمًا واحدًا على الأقل',
      'Add details about this milestone': 'أضف تفاصيل حول هذا المعلم',
      'Add quarterly milestones to build the plan timeline': 'أضف معالم ربع سنوية لبناء الجدول الزمني للخطة',
      'Annual business plan created successfully': 'تم إنشاء خطة الأعمال السنوية بنجاح',
      'Annual business plan deleted successfully': 'تم حذف خطة الأعمال السنوية بنجاح',
      'Annual business plan saved as draft': 'تم حفظ خطة الأعمال السنوية كمسودة',
      'Annual business plan updated successfully': 'تم تحديث خطة الأعمال السنوية بنجاح',
      'Choose whether the plan is delivered internally or by an external party': 'اختر ما إذا كان تنفيذ الخطة داخليًا أو من قبل جهة خارجية',
      'Create Annual Business Plan': 'إنشاء خطة أعمال سنوية',
      'Current Phase': 'المرحلة الحالية',
      'Dates not set': 'لم يتم تحديد التواريخ',
      'Define the plan, its timeline and how it will be executed': 'حدد الخطة وجدولها الزمني وطريقة تنفيذها',
      'Describe the goals and scope of this plan': 'صف أهداف هذه الخطة ونطاقها',
      'Edit Annual Business Plan': 'تعديل خطة الأعمال السنوية',
      'End date must be on or after the start date': 'يجب أن يكون تاريخ الانتهاء في تاريخ البدء أو بعده',
      'Enter plan name': 'أدخل اسم الخطة',
      'Executed by an external party': 'تُنفذ من قبل جهة خارجية',
      'Execution mode is required': 'طريقة التنفيذ مطلوبة',
      'Failed to delete annual business plan': 'فشل حذف خطة الأعمال السنوية',
      'Failed to load annual business plan': 'فشل تحميل خطة الأعمال السنوية',
      'Failed to load annual business plans': 'فشل تحميل خطط الأعمال السنوية',
      'Failed to save annual business plan': 'فشل حفظ خطة الأعمال السنوية',
      'Filter by execution': 'تصفية حسب التنفيذ',
      'Get started by creating your first annual business plan': 'ابدأ بإنشاء أول خطة أعمال سنوية',
      'Milestone completed': 'تم إنجاز المعلم',
      'Milestone title is required': 'عنوان المعلم مطلوب',
      'Name and describe the annual business plan': 'حدد اسم خطة الأعمال السنوية ووصفها',
      'No Milestones Added': 'لم تتم إضافة معالم',
      'No description provided.': 'لم يتم تقديم وصف.',
      'Not yet published': 'لم تُنشر بعد',
      'Phase is required': 'المرحلة مطلوبة',
      'Plan Name': 'اسم الخطة',
      'Plan name is required': 'اسم الخطة مطلوب',
      'Plans in progress': 'خطط قيد التنفيذ',
      'Project Duration': 'مدة المشروع',
      'Project execution mode': 'طريقة تنفيذ المشروع',
      'Quarter is required': 'الربع مطلوب',
      'Remove Milestone': 'إزالة المعلم',
      'Search plans...': 'ابحث في الخطط...',
      'Set the plan duration, current phase and quarterly milestones': 'حدد مدة الخطة والمرحلة الحالية والمعالم ربع السنوية',
      'Update Milestone': 'تحديث المعلم',
      'Update your annual business plan details': 'حدّث تفاصيل خطة الأعمال السنوية',
      'e.g. Contract award by March': 'مثال: ترسية العقد بحلول مارس',
      '{done} of {total} completed': 'تم إنجاز {done} من {total}',
      'Plan and track the yearly business objectives and activities':  'خطط وتابع أهداف وأنشطة الأعمال السنوية',
      'No annual business plans yet':                                  'لا توجد خطط أعمال سنوية بعد',
      'Annual business plans will appear here once they are added.':  'ستظهر خطط الأعمال السنوية هنا بمجرد إضافتها.',
      'Stakeholder Type':                                              'نوع صاحب المصلحة',
      'External Username':                                             'اسم المستخدم الخارجي',
      'Enter external username':                                       'أدخل اسم المستخدم الخارجي',
      'External username is required':                                 'اسم المستخدم الخارجي مطلوب',
      'External username must not exceed 255 characters':              'يجب ألا يتجاوز اسم المستخدم الخارجي 255 حرفًا',
      'Add stakeholder information including type, name, role, and contact details.': 'أضف معلومات صاحب المصلحة بما في ذلك النوع والاسم والدور وبيانات التواصل.',
    }
  }

  const t = (key, params = {}) => {
    let translation = translations[currentLanguage.value][key] || key
    
    // Handle string interpolation for parameters like {count}
    if (typeof translation === 'string' && Object.keys(params).length > 0) {
      translation = translation.replace(/\{(\w+)\}/g, (match, paramKey) => {
        return params[paramKey] !== undefined ? params[paramKey] : match
      })
    }
    
    return translation
  }

  // Initialize from localStorage or default to Arabic
  const initializeLanguage = () => {
    const savedLanguage = localStorage.getItem('app-language')
    const savedDirection = localStorage.getItem('app-direction')
    const defaultLanguageVersion = localStorage.getItem('app-language-default-version')
    
    if (savedLanguage && languages[savedLanguage] && defaultLanguageVersion === DEFAULT_LANGUAGE_VERSION) {
      currentLanguage.value = savedLanguage
      direction.value = savedDirection || languages[savedLanguage].direction
      isRtl.value = direction.value === 'rtl'
    } else {
      setLanguage(DEFAULT_LANGUAGE)
    }
    
    // Apply direction to document
    applyDirection()
  }

  // Set language and direction
  const setLanguage = (languageCode) => {
    if (!languages[languageCode]) {
      console.warn(`Language ${languageCode} is not supported`)
      return
    }

    const selectedLanguage = languages[languageCode]
    currentLanguage.value = languageCode
    direction.value = selectedLanguage.direction
    isRtl.value = direction.value === 'rtl'

    // Persist to localStorage
    localStorage.setItem('app-language', languageCode)
    localStorage.setItem('app-direction', direction.value)
    localStorage.setItem('app-language-default-version', DEFAULT_LANGUAGE_VERSION)

    // Apply direction to document
    applyDirection()
  }

  // Toggle between languages
  const toggleLanguage = () => {
    const newLanguage = currentLanguage.value === 'en' ? 'ar' : 'en'
    setLanguage(newLanguage)
  }

  // Apply direction to document root
  const applyDirection = () => {
    const htmlElement = document.documentElement
    htmlElement.setAttribute('lang', currentLanguage.value)
    
    if (isRtl.value) {
      htmlElement.setAttribute('dir', 'rtl')
      htmlElement.classList.add('rtl')
      htmlElement.classList.remove('ltr')
    } else {
      htmlElement.setAttribute('dir', 'ltr')
      htmlElement.classList.add('ltr')
      htmlElement.classList.remove('rtl')
    }

    // Fix PrimeVue overlay positioning after direction change
    setTimeout(() => {
      fixPrimeVueOverlayPositioning()
    }, 100)

    // Emit custom event for other components to listen
    window.dispatchEvent(new CustomEvent('language-changed', {
      detail: {
        language: currentLanguage.value,
        direction: direction.value,
        isRtl: isRtl.value
      }
    }))
  }


  const fixPrimeVueOverlayPositioning = () => {
    const overlays = document.querySelectorAll('.p-menu-overlay, .p-dropdown-panel, .p-overlaypanel')
    
    overlays.forEach(overlay => {
      if (isRtl.value) {
        // Force RTL positioning
        overlay.style.left = 'auto'
        overlay.style.right = '0'
        overlay.style.transformOrigin = 'top right'
        overlay.style.direction = 'rtl'
      } else {
        // Reset to LTR positioning
        overlay.style.left = ''
        overlay.style.right = ''
        overlay.style.transformOrigin = 'top left'
        overlay.style.direction = 'ltr'
      }
    })
  }

  // Get current language object
  const getCurrentLanguage = () => {
    return languages[currentLanguage.value]
  }

  // Get direction-aware classes
  const getDirectionClasses = (baseClasses) => {
    if (typeof baseClasses === 'string') {
      return isRtl.value ? baseClasses.replace(/-(left|right)/g, (match, side) => {
        return side === 'left' ? '-right' : '-left'
      }) : baseClasses
    }
    return baseClasses
  }

  // Get current locale text (for bilingual content)
  const getCurrentLocaleText = (enText, arText) => {
    return currentLanguage.value === 'ar' ? arText || enText : enText || arText
  }

  // Get opposite locale text (for showing alternative language)
  const getOppositeLocaleText = (enText, arText) => {
    return currentLanguage.value === 'ar' ? enText : arText
  }

  // Watch for changes and apply them
  watch([currentLanguage, direction], () => {
    applyDirection()
  })

  return {
    // State
    currentLanguage,
    direction,
    isRtl,
    languages,
    
    // Computed
    isArabic,
    isEnglish,
    oppositeLanguage,
    currentLanguageLabel,
    
    // Actions
    setLanguage,
    toggleLanguage,
    initializeLanguage,
    getCurrentLanguage,
    getDirectionClasses,
    getCurrentLocaleText,
    getOppositeLocaleText,
    applyDirection,
    fixPrimeVueOverlayPositioning,
    
    // Translation
    t
  }
})
